import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { supabase, uploadPublic, listCertificateTemplates, verifyCertificatePublic, getSignedUrl } from './lib/supabaseClient';
import { plainTextToHtml, safeJsonParse, buildVerificationLink, downloadAssessmentQuestionTemplate, exportAssessmentQuestionsToExcel, exportRowsToExcel } from './lib/utils';
import { getLocalCourseProgress, resolveUnifiedProgress } from './lib/courseProgress';
import { isAdminRole, isPartnerScopedRole, scopeParamsForRole } from './lib/authorization';
import { formatCourseWorkloadHHMM } from './lib/courseWorkload';
import { buildEnrollmentExpirationAtIso, formatEnrollmentExpiresAt, formatEnrollmentExpiresAtTime, getPersistedEnrollmentStatus, isEnrollmentAccessBlocked, normalizeEnrollmentStatus } from './lib/enrollmentStatus';
import { listMyVisibleEnrollments, COURSE_VISIBLE_ENROLLMENT_STATUSES } from './lib/hybridEnrollments';
import { buildCorePeopleCandidates } from './lib/coreBase';
import {
  getCourseCompletionEligibility,
  shouldSyncCertificateFinalGrade,
} from './lib/assessmentOutcome';
import {
  addDaysToYmd,
  buildEnrollmentEditFormState,
  buildIsoFromYmdEndOfDay,
  buildIsoFromYmdStartOfDay,
  computeDaysBetween,
  getAdminEnrollmentDisplayStatus,
  getAdminEnrollmentStartAt,
  getEnrollmentEditSubmitLabel,
  getSaoPauloDatePart,
  resolveUserLifecycleStatusForEnrollmentPeriod,
} from './lib/adminEnrollmentEdit';
import { resolveEffectiveReportLastAccess } from './lib/adminReportAccess';
import {
  buildAdminReportStudentAutocompleteOptions,
  buildDefaultAdminReportFilters,
  filterAdminReportStudentsByName,
  hasActiveReportFilters,
  normalizeAdminReportStudentName
} from './lib/adminReportFilters';
import AppErrorBoundary from './components/AppErrorBoundary.jsx';
import { clearRouteState, hasInviteHintsInUrl, isSessionValid, loadRouteState, parseInternalRouteFromLocation, parseVerifyFromLocation, resolveRestoredView, saveRouteState, viewToPath, sanitizeView } from './lib/routePersistence';
import { usePersistentTab } from './hooks/usePersistentTab';
import { logError } from './lib/logger';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { getAcceptForType, validateFileForType } from './lib/libraryFiles';
import { useAuth } from './auth/AuthContext.jsx';
import LoginView from './components/LoginView.jsx';
import ClassInviteAccessView from './components/ClassInviteAccessView.jsx';
import ResetPasswordView from './components/ResetPasswordView.jsx';
import AcceptInvitationView from './components/AcceptInvitationView.jsx';
import { completeCentralLogin, describeCentralLoginError } from './lib/centralAuth';
import PublicWhatsAppSupportButton from './components/PublicWhatsAppSupportButton.jsx';
import Notification from './components/Notification.jsx';
import AccessDenied from './components/AccessDenied.jsx';
import CourseWorkloadBadge from './components/CourseWorkloadBadge.jsx';
import ConfirmModal from './components/ConfirmModal.jsx';
import InfoModal from './components/InfoModal.jsx';
import CertificatePreviewModal from './components/CertificatePreviewModal.jsx';
import ReportStudentAutocomplete from './components/ReportStudentAutocomplete.jsx';
import UserNoticesModal from './components/UserNoticesModal.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import { hydrateCourses, loadCompleted, loadNotes, saveCompleted, saveCoursePatch, saveNotes } from './lib/localPersistence';
import readXlsxFile from 'read-excel-file/browser';
import { 
  Play, Info, ChevronDown, Search, Bell, User, UserCheck, LogOut, Loader2, Edit2,
  Settings, Building, Users, BookOpen, Award, 
  FileText, FileCode, Video, Headphones, Link as LinkIcon, 
  FileSpreadsheet, File, ChevronLeft, ChevronRight,
  CheckCircle, Plus, Upload, Move, Trash2, X,
  Image as ImageIcon, QrCode, List, RefreshCcw, Download, Eye, Filter, Shield, Menu,
  MessageCircle, Briefcase, Mail, UserPlus, FileUp, MoreHorizontal, MoreVertical, PauseCircle,
  UploadCloud, AlertCircle, Check, Globe, BarChart2, NotebookPen, Clock, Lock,
  Languages, Send, MessageSquare,
  Type as TypeIcon, Palette as PaletteIcon, HelpCircle,
  Minus, Plus as PlusIcon, Star, ArrowUpDown, ChevronUp, Zap, Plug, GraduationCap, AlertTriangle, Calendar,
  CalendarClock, Hourglass, Ban, CircleSlash, Copy, KeyRound, ExternalLink
} from 'lucide-react';
import LessonTimer from './LessonTimer';
import VideoEmbed from './components/VideoEmbed';
import DailyReportsList from './components/DailyReportsList.jsx';
import AdminCategories from './views/admin/AdminCategories.jsx'
import AdminConnections from './views/admin/AdminConnections.jsx'
import AdminUsers, { SummaryCard } from './views/admin/AdminUsers.jsx'
import AdminServiceOrders from './views/admin/AdminServiceOrders.jsx'
import AdminCommunicationsView from './views/admin/AdminCommunications.jsx'
import CoreBaseTab from './views/admin/components/CoreBaseTab.jsx';
import LibraryPreviewModal from './components/LibraryPreviewModal';
import AdminAIAssistantWidget from './components/AdminAIAssistantWidget.jsx';
import DataTablePagination from './components/DataTablePagination.jsx';
import { invokeEdgeFunction } from './lib/edgeInvoke';
import { invokeAdminManageUser } from './lib/adminManageUserClient';

// --- DADOS INICIAIS (MOCK) ---

const DEFAULT_PARTNER = { id: null, name: 'Sofera', cnpj: '', whatsapp: '', email: '', color: '#f97316', status: 'Ativo', site: '', defaultUrl: '' };

const getEnrollmentStatusBadgeClass = (status) => {
  switch (String(status || '')) {
    case 'Programado':
      return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
    case 'Pendente':
      return 'bg-rose-500/10 text-rose-300 border-rose-500/20';
    case 'Ativa':
      return 'bg-teal-500/10 text-teal-400 border-teal-500/20';
    case 'Inativada':
      return 'bg-slate-500/10 text-slate-300 border-slate-500/30';
    case 'Expirada':
      return 'bg-amber-500/10 text-amber-300 border-amber-500/30';
    case 'Cancelada':
    default:
      return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
  }
};

// Tom visual de cada status de progresso no relatório (texto/ícone e barra de progresso).
const getReportStatusTone = (status) => {
  switch (String(status || '')) {
    case 'Concluído':
      return { text: 'text-teal-400', bar: 'bg-teal-500' };
    case 'Em andamento':
      return { text: 'text-orange-300', bar: 'bg-orange-500' };
    case 'Programado':
      return { text: 'text-amber-300', bar: 'bg-amber-500' };
    case 'Não iniciado':
      return { text: 'text-slate-400', bar: 'bg-slate-500' };
    case 'Pendente':
      return { text: 'text-rose-400', bar: 'bg-rose-500' };
    default:
      return { text: 'text-slate-300', bar: 'bg-slate-600' };
  }
};

const getReportDisplayStatus = (reportStatus, userLifecycleStatus) => (
  String(userLifecycleStatus || '').trim() === 'Programado'
    ? 'Programado'
    : (reportStatus || 'Não iniciado')
);

const getEnrollmentDisplayStatus = (enrollmentStatus, userLifecycleStatus, expiresAt, startAt = null) => (
  getAdminEnrollmentDisplayStatus(enrollmentStatus, userLifecycleStatus, expiresAt, startAt)
);

const fetchScopedUserStatusMap = async ({ profileRole, profile, companyId = null, partnerId = null }) => {
  const scopedParams = scopeParamsForRole(
    { profileRole, profile },
    { p_company_id: companyId, p_partner_id: partnerId },
  );
  const { data, error } = await supabase.rpc('admin_list_users_by_scope', {
    p_company_id: scopedParams?.p_company_id || null,
    p_partner_id: scopedParams?.p_partner_id || null,
  });
  if (error) throw error;
  const statusMap = new Map();
  for (const row of (data || [])) {
    const userId = String(row?.id || '').trim();
    if (!userId) continue;
    statusMap.set(userId, {
      status: String(row?.status || '').trim(),
      lastSignInAt: row?.last_sign_in_at || null,
      lastRealSignInAt: row?.last_real_sign_in_at || null,
      firstLoginAt: row?.first_login_at || null,
    });
  }
  return statusMap;
};

const shortOrgLabel = (value) => {
  const text = String(value || '-');
  return text.includes(' - ') ? text.split(' - ')[0].trim() : text;
};

const formatCompanyLabel = (value) => {
  const baseText = shortOrgLabel(value);
  return baseText
    .replace(/\bLimitada\b/gi, 'Ltda')
    .replace(/\bLtda\.?\b/gi, 'Ltda')
    .replace(/\bCom[eé]rcio\b/gi, 'Com.')
    .replace(/\bMontagens\b/gi, 'Mont.')
    .replace(/\bRestaurantes\b/gi, 'Rest.')
    .trim();
};

const formatShortDateTime = (value) => {
  if (!value) return 'Nunca acessou';
  try {
    const date = new Date(value);
    const day = date.toLocaleDateString('pt-BR');
    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${day} ${time}`;
  } catch {
    return String(value || '');
  }
};

const formatReportDeadline = (value) => {
  if (!value) return 'Sem prazo';
  return new Date(value).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

const isReportDeadlineExpired = (value) => {
  if (!value) return false;
  const deadlineKey = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));
  const todayKey = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return deadlineKey < todayKey;
};

const MOCK_COURSES = [
  { 
    id: 1, 
    title: "Liderança na Era Digital", 
    description: "Gestão de equipes remotas e OKRs.", 
    progress: 45, 
    thumb: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800", 
    workload: "40:00", 
    instructor: "Marcos Tavares", 
    coordinator: "Helena Castro", 
    templateId: 1, 
    status: 'published', 
    sequentialEnforcement: false,
    modules: [
      { id: 10, title: "Módulo I: Introdução à Liderança" },
      { id: 11, title: "Módulo II: OKRs e Metas" },
      { id: 12, title: "Módulo III: Gestão de Equipes" }
    ]
  },
  { 
    id: 2, 
    title: "Vendas B2B", 
    description: "Fechamento de contratos complexos.", 
    progress: 0, 
    thumb: "https://images.unsplash.com/photo-1556761175-5973dc0f32d7?auto=format&fit=crop&q=80&w=800", 
    workload: "60:00", 
    instructor: "Helena Castro", 
    coordinator: "Marcos Tavares", 
    templateId: 1, 
    status: 'published', 
    sequentialEnforcement: false,
    modules: [
      { id: 20, title: "Módulo I: Prospecção" },
      { id: 21, title: "Módulo II: Negociação" },
      { id: 22, title: "Módulo III: Fechamento" }
    ]
  },
];

const MOCK_CLASSES = [
  { id: 1, name: "Turma Alpha 2026", duration: "6 meses", students: [1], courses: [1, 2], status: "Ativo", companyId: 2, partnerId: 1 },
  { id: 2, name: "Turma Beta 2026", duration: "4 meses", students: [1], courses: [1], status: "Ativo", companyId: 1, partnerId: 0 },
];

const MOCK_CATEGORIES = [
  { id: 1, name: "Gestão", description: "Cursos relacionados a gestão de pessoas e empresas", status: "Ativo" },
  { id: 2, name: "Vendas", description: "Técnicas e estratégias de vendas", status: "Ativo" },
  { id: 3, name: "Tecnologia", description: "Desenvolvimento e ferramentas tecnológicas", status: "Ativo" },
];

const MOCK_CERTS = [{ id: 1, name: "Padrão Liderança 2026", coursesLinked: 2 }];
const MOCK_ISSUED_CERTS = [{ id: 'CERT-2026-001', student: "Ana Beatriz", course: "Liderança na Era Digital", date: "24/02/2026", company: "Stark Industries", status: "Válido", workload: "40:00", instructor: "Marcos Tavares" }];

const MOCK_TEMPLATES = [
  { id: 1, name: 'Boas-vindas Padrão', channel: 'E-mail', subject: 'Bem-vindo à Sofera!', content: 'Olá {aluno}, seja bem-vindo ao nosso ecossistema de aprendizado.' },
  { id: 2, name: 'Lembrete de Aula', channel: 'WhatsApp', content: 'Olá {aluno}, sua aula começa em 15 minutos. Não se atrase!' },
  { id: 3, name: 'Convite - Bem-vindo', channel: 'E-mail', subject: 'Seu acesso está pronto', content: 'Olá {nomeUsuario}, seu convite está disponível em {linkConvite}. Caso precise, acesse a plataforma em {linkPlataforma}.', transactional: true, meta: { tipo: 'Convite', headerUrl: '', rodapeUrl: '', botao: { titulo: 'Acessar Agora', link: 'https://app.sofera.com/login' }, observacao: 'E-mail automático, não responda.', variaveis: ['nomeUsuario','linkConvite','linkPlataforma'], parceirosSAS: [], anexos: [] } },
];

// --- COMPONENTES GERAIS ---

const VerifyCertificate = ({ defaultCode = '', partner }) => {
  const [code, setCode] = useState(defaultCode || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [error, setError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (defaultCode) handleVerify();
  }, [defaultCode]);

  const handleVerify = async () => {
    setLoading(true); setError(''); setResult(null); setHasChecked(true);
    try {
      const rows = await verifyCertificatePublic(code.trim());
      const row = rows && rows.length ? rows[0] : null;
      setResult(row);
    } catch (e) {
      setError('Falha ao verificar certificado.');
    } finally { setLoading(false); }
  };

  const handleDownloadPdf = async () => {
    if (!result?.id) return;
    setIsDownloading(true);
    try {
      // Como é público, chamamos a função passando o ID. 
      // Se a Edge Function exigir auth, talvez precisemos ajustar a função lá. 
      // Mas a Edge Function atual só exige a key anon que passamos.
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pdfshift`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ certificate_id: result.id }),
      });

      if (!response.ok) {
         const errData = await response.json();
         throw new Error(errData.error || 'Erro ao gerar PDF no servidor');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificado-${result.validation_code}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      alert('Erro ao baixar PDF: ' + err.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const statusTag = (s) => s === 'issued' ? 'Válido' : s === 'revoked' ? 'Revogado' : 'Pendente';
  const accent = partner?.color || '#f97316';
  
  // Verifica se a URL tem o parâmetro action=download
  const isDownloadAction = new URLSearchParams(window.location.search).get('action') === 'download';

  return (
    <div className="p-6 md:p-16 max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-6 rounded" style={{ backgroundColor: accent }}></div>
          <h1 className="text-3xl font-bold text-white">Verificação de Certificado</h1>
        </div>
        {partner?.name && <p className="text-slate-500 mt-1">Powered by <span className="font-bold" style={{ color: accent }}>{partner.name}</span></p>}
      </div>
      <p className="text-slate-400 mb-6">Informe o código de verificação para conferir a validade.</p>
      <div className="flex gap-3 mb-6">
        <input 
          value={code} 
          onChange={(e)=>{
            setCode(e.target.value);
            setResult(null);
            setHasChecked(false);
          }} 
          placeholder="Código de verificação" 
          className="flex-1 bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none" 
        />
        <button onClick={handleVerify} disabled={loading || !code} className="px-6 py-3 rounded-lg text-white font-bold disabled:opacity-50" style={{ backgroundColor: accent }}>Verificar</button>
      </div>
      {error && <div className="text-rose-400 mb-4">{error}</div>}
      {loading && <div className="text-slate-400">Verificando...</div>}
      {!loading && result && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{result.recipient_name}</h2>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${result.status === 'issued' ? 'bg-teal-500/10 text-teal-400' : result.status === 'revoked' ? 'bg-rose-500/10 text-rose-400' : 'bg-slate-700 text-slate-300'}`}>{statusTag(result.status)}</span>
          </div>
          <p className="text-slate-300 mb-1"><span className="text-slate-500">Curso:</span> {result.course_title}</p>
          {result.company_name && (
            <p className="text-slate-300 mb-1"><span className="text-slate-500">Empresa:</span> {result.company_name}</p>
          )}
          <p className="text-slate-300 mb-1"><span className="text-slate-500">Emitido em:</span> {new Date(result.issued_at).toLocaleDateString('pt-BR')}</p>
          {isDownloadAction && (
            result.pdf_path ? (
              <a href={result.pdf_path} target="_blank" className="inline-block mt-4 px-4 py-2 rounded bg-slate-800 text-white hover:bg-slate-700">Abrir PDF</a>
            ) : (
              <button 
                onClick={handleDownloadPdf} 
                disabled={isDownloading} 
                className="inline-block mt-4 px-4 py-2 rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
              >
                {isDownloading ? 'Gerando PDF...' : 'Baixar Certificado'}
              </button>
            )
          )}
        </div>
      )}
      {!loading && result === null && code && !error && hasChecked && (
        <div className="text-slate-400">Nenhum certificado encontrado para este código.</div>
      )}
    </div>
  );
};

const StudentEnrollments = ({ courses, classes }) => {
  const auth = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [enrollmentFilters, setEnrollmentFilters] = useState({
    course: '',
    class: '',
    startDate: '',
    endDate: '',
    status: ''
  });

  const fetchEnrollments = async () => {
    setLoadingEnrollments(true);
    try {
      const uid = auth.session?.user?.id;
      if (!uid) {
        setEnrollments([]);
        return;
      }

      const ownVisibleEnrollments = await listMyVisibleEnrollments(auth);

      let filtered = ownVisibleEnrollments;
      if (enrollmentFilters.startDate) {
        filtered = filtered.filter((enrollment) => {
          const createdAt = new Date(enrollment?.created_at || 0).getTime();
          return createdAt >= new Date(`${enrollmentFilters.startDate}T00:00:00`).getTime();
        });
      }
      if (enrollmentFilters.endDate) {
        filtered = filtered.filter((enrollment) => {
          const createdAt = new Date(enrollment?.created_at || 0).getTime();
          return createdAt <= new Date(`${enrollmentFilters.endDate}T23:59:59`).getTime();
        });
      }
      if (enrollmentFilters.status) {
        filtered = filtered.filter((enrollment) => enrollment.status === enrollmentFilters.status);
      }
      if (enrollmentFilters.course) {
        filtered = filtered.filter(e => e.offering?.course_id === enrollmentFilters.course);
      }
      if (enrollmentFilters.class) {
        filtered = filtered.filter(e => e.classId === enrollmentFilters.class);
      }

      setEnrollments(filtered);
    } catch (err) {
      console.error('Error fetching my enrollments:', err);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  useEffect(() => {
    if (auth.bootLoading) return;
    if (auth.isAuthenticated && auth.authzLoading) return;
    fetchEnrollments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.bootLoading,
    auth.authzLoading,
    auth.isAuthenticated,
    auth.profileRole,
    auth.profile?.companyId,
    auth.session?.user?.id,
    auth.session?.user?.email,
    enrollmentFilters,
  ]);

  const paginatedEnrollments = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return enrollments.slice(startIndex, startIndex + itemsPerPage);
  }, [enrollments, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [enrollmentFilters, itemsPerPage]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(enrollments.length / itemsPerPage));
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [enrollments.length, itemsPerPage]);

  const classFilterOptions = React.useMemo(() => {
    const fallbackOptions = Array.isArray(classes) ? classes : [];
    const visibleClassIds = new Set(enrollments.map((enrollment) => String(enrollment?.classId || '')).filter(Boolean));
    const visibleClasses = fallbackOptions.filter((classItem) => visibleClassIds.has(String(classItem.id)));

    if (visibleClasses.length > 0) return visibleClasses;

    const derivedClasses = new Map();
    for (const enrollment of enrollments) {
      if (!enrollment?.classId) continue;
      derivedClasses.set(String(enrollment.classId), {
        id: enrollment.classId,
        name: enrollment.className || '-',
      });
    }
    return Array.from(derivedClasses.values());
  }, [classes, enrollments]);

  const handleDownloadStudentPDF = async (cert) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pdfshift`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ certificate_id: cert.id }),
      });

      if (!response.ok) {
         const errData = await response.json();
         throw new Error(errData.error || 'Erro ao gerar PDF no servidor');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificado-${(cert.course_title || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar PDF via API:', err);
      // Fallback: visualização modal que tem o gerador local embutido
      handleViewCertificate(cert);
    }
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Minhas Matrículas</h1>
          <p className="text-slate-400">Visualize suas matrículas em turmas e cursos.</p>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase font-bold text-slate-500">Curso</label>
          <select
            value={enrollmentFilters.course}
            onChange={e => setEnrollmentFilters({ ...enrollmentFilters, course: e.target.value })}
            className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-orange-500"
          >
            <option value="">Todos os cursos</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>{course.title}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase font-bold text-slate-500">Turma</label>
          <select
            value={enrollmentFilters.class}
            onChange={e => setEnrollmentFilters({ ...enrollmentFilters, class: e.target.value })}
            className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-orange-500"
          >
            <option value="">Todas as turmas</option>
            {classFilterOptions.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase font-bold text-slate-500">Início</label>
          <input
            type="date"
            value={enrollmentFilters.startDate}
            onChange={e => setEnrollmentFilters({ ...enrollmentFilters, startDate: e.target.value })}
            className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-orange-500 [color-scheme:dark]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase font-bold text-slate-500">Fim</label>
          <input
            type="date"
            value={enrollmentFilters.endDate}
            onChange={e => setEnrollmentFilters({ ...enrollmentFilters, endDate: e.target.value })}
            className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-orange-500 [color-scheme:dark]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase font-bold text-slate-500">Status</label>
          <select
            value={enrollmentFilters.status}
            onChange={e => setEnrollmentFilters({ ...enrollmentFilters, status: e.target.value })}
            className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-white outline-none focus:border-orange-500"
          >
            <option value="">Todos</option>
            <option value="Ativa">Ativa</option>
            <option value="Inativada">Inativada</option>
            <option value="Cancelada">Cancelada</option>
            <option value="Expirada">Expirada</option>
          </select>
        </div>
      </div>

      {loadingEnrollments ? (
        <div className="flex justify-center p-12 text-slate-500">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : enrollments.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 md:p-16 flex flex-col items-center justify-center text-center shadow-lg">
          <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center mb-6 border border-slate-700">
            <BookOpen size={40} className="text-slate-500" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">Nenhuma matrícula encontrada</h3>
          <p className="text-slate-400 max-w-md text-base leading-relaxed">
            Você ainda não possui registros de matrículas em turmas ou cursos. Quando houver uma matrícula, ela aparecerá aqui.
          </p>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold border-b border-primary/20">
              <tr>
                <th className="p-5">Aluno</th>
                <th className="p-5">Curso</th>
                <th className="p-5">Turma</th>
                <th className="p-5">Data Matrícula</th>
                <th className="p-5">Validade</th>
                <th className="p-5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {paginatedEnrollments.map((enrollment) => (
                <tr key={enrollment.id} className="hover:bg-slate-800/50 transition text-slate-300">
                  <td className="p-5">
                    <div className="font-bold text-white">{enrollment.student?.full_name || 'Eu'}</div>
                    <div className="text-xs opacity-50">{enrollment.student?.email}</div>
                  </td>
                  <td className="p-5">
                    {enrollment.offering?.course?.name || 'Curso não encontrado'}
                  </td>
                  <td className="p-5">
                    {enrollment.className || '-'}
                  </td>
                  <td className="p-5">
                    {new Date(enrollment.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="p-5">
                    {enrollment.expires_at
                      ? (
                        <div className="leading-tight">
                          <div>{formatEnrollmentExpiresAt(enrollment.expires_at, { includeTime: false })}</div>
                          <div className="text-xs text-slate-400">
                            {formatEnrollmentExpiresAtTime(enrollment.expires_at)} SP-BR
                          </div>
                        </div>
                      )
                      : <span className="text-slate-500">Ilimitado</span>}
                  </td>
                  <td className="p-5">
                    <span className={`w-fit px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getEnrollmentStatusBadgeClass(enrollment.status)}`}>
                      {enrollment.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 pb-6">
            <DataTablePagination
              totalItems={enrollments.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onItemsPerPageChange={setItemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};
const detectProvider = (url) => {
  const yt = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  const vm = /(?:vimeo\.com\/)(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)(?:$|\/|\?)/
  const ytMatch = url.match(yt)
  if (ytMatch) return { type: 'youtube', id: ytMatch[1] }
  const vmMatch = url.match(vm)
  if (vmMatch) return { type: 'vimeo', id: vmMatch[1] }
  if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(url)) return { type: 'html5' }
  return { type: 'iframe' }
}

const PreviewModal = ({ isOpen, onClose, course, partner }) => {
  const videoRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isFinished, setIsFinished] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState({ type: 'html5' });

  useEffect(() => {
    if (isOpen && course?.id && course.id !== 'hero') {
      const fetchPreview = async () => {
        setLoading(true);
        setPreviewUrl(null);
        try {
          // Fetch lessons for the course to find a video content
          const { data: lessons, error } = await supabase
            .from('course_lessons')
            .select('contents')
            .eq('course_id', course.id)
            .limit(10); // Check first few lessons

          if (error) throw error;

          let foundUrl = null;
          if (lessons && lessons.length > 0) {
            for (const lesson of lessons) {
              if (lesson.contents && Array.isArray(lesson.contents)) {
                const videoContent = lesson.contents.find(c => (c.type === 'video' || c.type === 'vimeo' || c.type === 'youtube') && c.url);
                if (videoContent) {
                  foundUrl = videoContent.url;
                  break;
                }
              }
            }
          }
          
          if (foundUrl) {
            setPreviewUrl(foundUrl);
            setProvider(detectProvider(foundUrl));
          } else {
            console.log("No preview video found for course", course.id);
            // Fallback or keep null to show message
          }
        } catch (err) {
          console.error("Error fetching preview video:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchPreview();
    }
  }, [isOpen, course]);

  useEffect(() => {
    if (isOpen && videoRef.current && previewUrl && provider.type === 'html5') {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(e => console.log("Autoplay blocked", e));
    }
    
    if (isOpen && previewUrl) {
        setTimeLeft(30);
        setIsFinished(false);
    }
  }, [isOpen, previewUrl, provider]);

  useEffect(() => {
    if (!isOpen || isFinished || !previewUrl) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (videoRef.current && provider.type === 'html5') videoRef.current.pause();
          setIsFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, isFinished, previewUrl, provider]);

  if (!isOpen || !course) return null;

  const renderPlayer = () => {
      if (provider.type === 'youtube') {
          const params = new URLSearchParams({ 
            autoplay: '1', 
            controls: '1', 
            modestbranding: '1', 
            rel: '0', 
            showinfo: '0', 
            iv_load_policy: '3',
            fs: '0',
            disablekb: '1'
          });
          return (
              <iframe 
                src={`https://www.youtube.com/embed/${provider.id}?${params.toString()}`}
                className="w-full h-full object-contain"
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title={course.title}
              />
          );
      }
      
      if (provider.type === 'vimeo') {
          return (
              <iframe 
                src={`https://player.vimeo.com/video/${provider.id}?autoplay=1&title=0&byline=0&portrait=0`}
                className="w-full h-full object-contain"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={course.title}
              />
          );
      }

      if (provider.type === 'html5') {
          return (
             <>
                <video 
                    ref={videoRef}
                    src={previewUrl} 
                    className="w-full h-full object-contain"
                    controls={false}
                    playsInline
                />
                 <div className="absolute inset-0 bg-transparent" onClick={(e) => {
                    if(videoRef.current) {
                        if(videoRef.current.paused) videoRef.current.play();
                        else videoRef.current.pause();
                    }
                 }}></div>
             </>
          );
      }

      // Fallback iframe
      return (
          <iframe 
            src={previewUrl}
            className="w-full h-full object-contain"
            frameBorder="0"
            allow="autoplay; fullscreen"
          />
      );
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl shadow-2xl overflow-hidden relative">
        <div className="flex justify-between items-center p-4 border-b border-slate-800 bg-slate-950/50">
           <h3 className="text-xl font-bold text-white flex items-center gap-2">
             <Play size={20} style={{ color: partner?.color || '#f97316' }} /> Prévia: {course.title}
           </h3>
           <button onClick={onClose} className="text-slate-400 hover:text-white transition"><X size={24} /></button>
        </div>
        
        <div className="relative aspect-video bg-black flex items-center justify-center">
           {loading ? (
             <div className="text-slate-400 animate-pulse">Carregando prévia...</div>
           ) : !previewUrl ? (
             <div className="text-slate-400">Nenhuma prévia de vídeo disponível para este curso.</div>
           ) : !isFinished ? (
               <>
                 {renderPlayer()}
                 <div className="absolute top-4 right-4 bg-black/70 backdrop-blur text-white px-3 py-1 rounded-full text-sm font-mono border border-white/10 z-10">
                    Prévia: {timeLeft}s
                 </div>
               </>
           ) : (
               <div className="flex flex-col items-center justify-center h-full p-8 animate-fade-in bg-slate-950/80">
                  <p className="text-slate-500 text-xs md:text-sm font-bold uppercase tracking-[0.2em] mb-4 text-center">Orgulhosamente desenvolvido por</p>
                  <div className="flex flex-col items-center">
                    <span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent font-black text-4xl md:text-6xl tracking-tighter mb-1 drop-shadow-2xl">SOFERA</span>
                    <span className="text-white font-bold text-[10px] md:text-xs tracking-[0.5em] select-none uppercase w-full text-center border-t border-slate-800/50 pt-2 opacity-80">Evolução Inteligente</span>
                  </div>
               </div>
           )}
        </div>
      </div>
    </div>
  );
};

const StudentCertificates = ({ partner }) => {
  const auth = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState(null);
  const [pendingCourseTitles, setPendingCourseTitles] = useState([]);

  const normalizeProgramContent = (value) => {
    const lines = String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '').trim())
      .filter(Boolean)
    return lines.join('\n')
  }

  const reconcileEligibleCertificates = async () => {
    const userId = auth.session?.user?.id
    if (!userId) return

    try {
      const { data: enrollmentRows, error } = await supabase
        .from('enrollments')
        .select(`
          progress_percent,
          status,
          expires_at,
          offering:offering_id (
            course_id
          )
        `)
        .eq('student_id', userId)

      if (error) throw error

      const uniqueCourseIds = Array.from(new Set(
        (enrollmentRows || [])
          .filter((row) => normalizeEnrollmentStatus(row?.status, row?.expires_at) === 'Ativa')
          .map((row) => row?.offering?.course_id)
          .filter(Boolean)
          .map(String)
      ))

      const courseProgressMap = new Map()
      for (const row of (enrollmentRows || []).filter((entry) => normalizeEnrollmentStatus(entry?.status, entry?.expires_at) === 'Ativa')) {
        const courseId = String(row?.offering?.course_id || '')
        if (!courseId) continue
        courseProgressMap.set(
          courseId,
          Math.max(courseProgressMap.get(courseId) || 0, Number(row?.progress_percent) || 0)
        )
      }

      const { data: courseRows } = await supabase
        .from('courses')
        .select('id, min_grade')
        .in('id', uniqueCourseIds)

      const minGradeByCourse = new Map(
        (courseRows || []).map((row) => [String(row.id), Number(row.min_grade ?? 7)])
      )

      const { data: attemptRows } = await supabase
        .from('assessment_attempts')
        .select('course_id, score')
        .eq('student_id', userId)
        .is('lesson_id', null)
        .eq('status', 'completed')
        .in('course_id', uniqueCourseIds)

      const bestScoreByCourse = new Map()
      for (const row of attemptRows || []) {
        const courseId = String(row?.course_id || '')
        if (!courseId) continue
        const currentValue = bestScoreByCourse.get(courseId) ?? null
        const nextValue = Number(row?.score)
        if (!Number.isFinite(nextValue)) continue
        bestScoreByCourse.set(courseId, currentValue == null ? nextValue : Math.max(currentValue, nextValue))
      }

      const email = auth.session?.user?.email || ''
      const [{ data: profileRow }, { data: existingCertificates }] = await Promise.all([
        supabase
          .from('profiles')
          .select(`
            full_name,
            email,
            cpf,
            sas_partner:sas_partner_id (
              name
            )
          `)
          .eq('id', userId)
          .single(),
        supabase
          .from('certificates')
          .select('id, course_id, course_title, recipient_email, status, workload_hours, final_grade, end_date, instructor_name, coordinator_name, program_content')
          .ilike('recipient_email', email)
      ])

      const issuedByCourseId = new Set(
        (existingCertificates || [])
          .filter((cert) => cert.status === 'issued' && cert.course_id)
          .map((cert) => String(cert.course_id))
      )

      const activeTemplateFallback = async () => {
        const { data: defaultTemplate } = await supabase
          .from('certificate_templates')
          .select('id')
          .eq('active', true)
          .eq('is_default', true)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (defaultTemplate?.id) return defaultTemplate.id

        const { data: anyTemplate } = await supabase
          .from('certificate_templates')
          .select('id')
          .eq('active', true)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        return anyTemplate?.id || null
      }

      for (const courseId of uniqueCourseIds) {
        if (issuedByCourseId.has(String(courseId))) continue

        const { error: rpcError } = await supabase.rpc('ensure_course_completion_eligibility', {
          p_student_id: userId,
          p_course_id: courseId,
          p_created_by: userId
        })

        if (!rpcError) continue

        if (rpcError.code === 'PGRST202') {
          const progressPercent = courseProgressMap.get(courseId) || 0
          const bestScore = bestScoreByCourse.get(courseId) ?? null
          const minGrade = minGradeByCourse.get(courseId) ?? 7
          const { canComplete: shouldForceLegacyCompletion } = getCourseCompletionEligibility({
            progressPercent,
            assessmentEnabled: Boolean(courseMap.get(courseId)?.assessment_enabled || courseMap.get(courseId)?.assessmentEnabled),
            assessmentId: courseMap.get(courseId)?.assessment_id || courseMap.get(courseId)?.assessmentId || null,
            bestScore,
            minGrade,
          })

          if (shouldForceLegacyCompletion) {
            const { error: legacyError } = await supabase.rpc('update_student_course_progress', {
              p_course_id: courseId,
              p_progress: 100
            })

            if (legacyError && legacyError.code !== 'PGRST202') {
              console.error('Error forcing legacy certificate completion:', legacyError)
            }
          }
          continue
        }

        if (rpcError.code !== 'PGRST202') {
          console.error('Error reconciling certificate eligibility:', rpcError)
        }
      }

      const { data: refreshedCertificates } = await supabase
        .from('certificates')
        .select('id, course_id, status, workload_hours, final_grade, end_date, instructor_name, coordinator_name, program_content')
        .ilike('recipient_email', email)

      const refreshedIssuedByCourseId = new Set(
        (refreshedCertificates || [])
          .filter((cert) => cert.status === 'issued' && cert.course_id)
          .map((cert) => String(cert.course_id))
      )

      const { data: courseRowsHydrated } = await supabase
        .from('courses')
        .select(`
          id,
          name,
          description,
          workload_hours,
          instructor_profile:profiles!courses_responsible_instructor_id_fkey(full_name),
          coordinator_profile:profiles!courses_coordinator_id_fkey(full_name)
        `)
        .in('id', uniqueCourseIds)

      const { data: lessonRows } = await supabase
        .from('course_lessons')
        .select('course_id, title')
        .in('course_id', uniqueCourseIds)

      const { data: moduleRows } = await supabase
        .from('course_modules')
        .select('course_id, title, order_index')
        .in('course_id', uniqueCourseIds)
        .order('course_id', { ascending: true })
        .order('order_index', { ascending: true })

      const courseById = new Map((courseRowsHydrated || []).map((courseRow) => [String(courseRow.id), courseRow]))
      const lessonTitlesByCourse = new Map()
      for (const row of lessonRows || []) {
        const key = String(row?.course_id || '')
        if (!key) continue
        const arr = lessonTitlesByCourse.get(key) || []
        if (row?.title) arr.push(String(row.title))
        lessonTitlesByCourse.set(key, arr)
      }

      const moduleTitlesByCourse = new Map()
      for (const row of moduleRows || []) {
        const key = String(row?.course_id || '')
        if (!key) continue
        const arr = moduleTitlesByCourse.get(key) || []
        if (row?.title) arr.push(String(row.title))
        moduleTitlesByCourse.set(key, arr)
      }

      const wrapTextLines = (lines = [], maxCharsPerLine = 54, maxLines = 8) => {
        const output = []
        for (const source of lines) {
          const normalized = String(source || '').replace(/\s+/g, ' ').trim()
          if (!normalized) continue
          const words = normalized.split(' ')
          let current = ''
          for (const word of words) {
            const candidate = current ? `${current} ${word}` : word
            if (candidate.length <= maxCharsPerLine) {
              current = candidate
            } else {
              if (current) output.push(current)
              current = word
            }
            if (output.length >= maxLines) break
          }
          if (current && output.length < maxLines) output.push(current)
          if (output.length >= maxLines) break
        }
        if (output.length >= maxLines) {
          output[maxLines - 1] = `${output[maxLines - 1].replace(/\.+$/, '')}...`
        }
        return output.slice(0, maxLines)
      }

      const buildProgramContent = (courseId) => {
        const moduleTitles = moduleTitlesByCourse.get(String(courseId)) || []
        if (moduleTitles.length) {
          return wrapTextLines(moduleTitles, 54, 8).join('\n')
        }
        const lessonTitles = lessonTitlesByCourse.get(String(courseId)) || []
        if (lessonTitles.length) {
          return wrapTextLines(lessonTitles, 54, 8).join('\n')
        }
        const description = courseById.get(String(courseId))?.description
        if (!description) return null
        return wrapTextLines([description], 54, 8).join('\n')
      }

      const buildCertificatePayload = (courseId) => {
        const courseRow = courseById.get(String(courseId))
        const bestScore = bestScoreByCourse.get(String(courseId)) ?? null
        return {
          course_title: courseRow?.name || 'Curso',
          workload_hours: courseRow?.workload_hours ?? null,
          final_grade: bestScore != null ? Number(bestScore).toFixed(1) : null,
          end_date: new Date().toISOString().slice(0, 10),
          instructor_name: courseRow?.instructor_profile?.full_name || null,
          coordinator_name: courseRow?.coordinator_profile?.full_name || null,
          program_content: buildProgramContent(courseId)
        }
      }

      for (const cert of refreshedCertificates || []) {
        if (cert?.status !== 'issued' || !cert?.course_id) continue
        const bestScore = bestScoreByCourse.get(String(cert.course_id)) ?? null
        const missingCoreFields = (
          cert.workload_hours == null
          || !String(cert.final_grade || '').trim()
          || !String(cert.program_content || '').trim()
          || !String(cert.instructor_name || '').trim()
          || !String(cert.coordinator_name || '').trim()
          || cert.end_date == null
        )
        const malformedProgramContent = (
          String(cert.program_content || '').includes(' | ')
          || String(cert.program_content || '').length > 480
          || /^\s*[-•]?\s*\d+\./m.test(String(cert.program_content || ''))
        )
        const hasOutdatedFinalGrade = shouldSyncCertificateFinalGrade({
          currentFinalGrade: cert.final_grade,
          bestScore,
        })
        if (!missingCoreFields && !malformedProgramContent && !hasOutdatedFinalGrade) continue

        const payload = {
          ...buildCertificatePayload(cert.course_id),
          program_content: normalizeProgramContent(buildCertificatePayload(cert.course_id)?.program_content || '')
        }
        const { error: patchError } = await supabase
          .from('certificates')
          .update(payload)
          .eq('id', cert.id)

        if (patchError) {
          console.error('Error patching incomplete certificate fields:', patchError)
        }
      }

      let templateIdCache = null

      for (const courseId of uniqueCourseIds) {
        if (refreshedIssuedByCourseId.has(String(courseId))) continue

        const progressPercent = courseProgressMap.get(courseId) || 0
        const bestScore = bestScoreByCourse.get(courseId) ?? null
        const minGrade = minGradeByCourse.get(courseId) ?? 7
        const { canComplete: isEligible } = getCourseCompletionEligibility({
          progressPercent,
          assessmentEnabled: Boolean(courseMap.get(courseId)?.assessment_enabled || courseMap.get(courseId)?.assessmentEnabled),
          assessmentId: courseMap.get(courseId)?.assessment_id || courseMap.get(courseId)?.assessmentId || null,
          bestScore,
          minGrade,
        })
        if (!isEligible) continue

        let templateId = null
        const { data: templateRpcResult, error: templateRpcError } = await supabase.rpc('pick_certificate_template_for_student', {
          p_student_id: userId
        })
        if (!templateRpcError) {
          templateId = typeof templateRpcResult === 'string'
            ? templateRpcResult
            : (Array.isArray(templateRpcResult) ? templateRpcResult[0] : null)
        }

        if (!templateId) {
          if (!templateIdCache) {
            templateIdCache = await activeTemplateFallback()
          }
          templateId = templateIdCache
        }

        if (!templateId) continue

        const recipientName = profileRow?.full_name || auth.session?.user?.user_metadata?.full_name || 'Aluno'
        const recipientEmail = profileRow?.email || email
        const partnerName = profileRow?.sas_partner?.name || null
        const payload = {
          ...buildCertificatePayload(courseId),
          program_content: normalizeProgramContent(buildCertificatePayload(courseId)?.program_content || '')
        }

        if (!recipientEmail) continue

        const { error: insertCertificateError } = await supabase
          .from('certificates')
          .insert({
            template_id: templateId,
            recipient_name: recipientName,
            recipient_email: recipientEmail,
            recipient_document: profileRow?.cpf || null,
            ...payload,
            course_id: courseId,
            partner_name: partnerName,
            status: 'issued',
            created_by: userId
          })

        if (insertCertificateError) {
          console.error('Error backfilling missing certificate:', insertCertificateError)
        }
      }
    } catch (err) {
      console.error('Error reconciling eligible certificates:', err)
    }
  }

  const fetchMyCertificates = async () => {
    setLoading(true);
    try {
      await reconcileEligibleCertificates();

      const email = auth.session?.user?.email || '';
      if (!email) {
        setRows([]);
        setPendingCourseTitles([]);
        return;
      }
      const { data, error } = await supabase
        .from('certificates')
        .select('id, recipient_name, recipient_email, course_title, status, issued_at, pdf_path, validation_code, hash_code, template_id, workload_hours, final_grade, end_date, instructor_name, coordinator_name, partner_name, program_content')
        .ilike('recipient_email', email)
        .order('issued_at', { ascending: false });
      if (error) throw error;
      const issuedRows = data || []
      setRows(issuedRows);

      const issuedCourseTitles = new Set(issuedRows.map((row) => String(row.course_title || '').trim()).filter(Boolean))
      const { data: completionRows, error: completionError } = await supabase
        .from('course_completions')
        .select(`
          course_id,
          courses:course_id (
            name
          )
        `)
        .eq('student_id', auth.session.user.id)

      if (completionError) throw completionError

      const pendingTitles = (completionRows || [])
        .map((row) => row?.courses?.name || null)
        .filter(Boolean)
        .filter((title) => !issuedCourseTitles.has(String(title).trim()))

      setPendingCourseTitles(Array.from(new Set(pendingTitles)))
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setRows([]);
      setPendingCourseTitles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.session?.user?.email]);

  const handleViewCertificate = async (c) => {
    if (!c.template_id) {
      if (c.pdf_path) {
        window.open(c.pdf_path, '_blank');
      } else {
        alert('Este certificado não possui modelo ou PDF associado.');
      }
      return;
    }
    
    setLoadingPreviewId(c.id);
    try {
      const { data: template, error } = await supabase
        .from('certificate_templates')
        .select('*')
        .eq('id', c.template_id)
        .single();
        
      if (error) throw error;
      
      const [frontUrl, backUrl] = await Promise.all([
        getSignedUrl('certificate-templates', template.front_image_path),
        getSignedUrl('certificate-templates', template.back_image_path)
      ]);
      
      const meta = template.meta || {};
      const sec = meta.back_sections || meta.back || {};
      const legacyHtml = meta.back_html || '';
      const stripLegacyDeclarationContainer = (html) => String(html || '').replace(/<div[^>]*data-averbacao=\"true\"[^>]*>[\s\S]*?<\/div>/, '').trim();
      const extractLegacyDeclarationHtml = (html) => {
        const m = String(html || '').match(/<div[^>]*data-averbacao=\"true\"[^>]*>([\s\S]*?)<\/div>/);
        return m ? String(m[1] || '').trim() : '';
      };
      
      const mappedModel = {
        id: template.id,
        name: template.name,
        useHtml: (typeof template.use_html === 'boolean' ? template.use_html : (typeof meta.use_html === 'boolean' ? meta.use_html : true)),
        frontImage: frontUrl,
        backImage: backUrl,
        customTextFront: template.html || '',
        customTextBack: meta.back_html || '',
        meta: meta,
        backHeaderHtml: sec.header_html || '',
        backMainHtml: sec.main_html || stripLegacyDeclarationContainer(legacyHtml) || '',
        backInstitutionalHtml: sec.institutional_html || '',
        backDeclarationHtml: sec.declaration_html || (meta.averbacao_text ? plainTextToHtml(meta.averbacao_text) : extractLegacyDeclarationHtml(legacyHtml)),
        backShowQrCode: (typeof sec.show_qr_code === 'boolean')
              ? sec.show_qr_code
              : (typeof meta.back_qr_enabled === 'boolean')
                ? meta.back_qr_enabled
                : /data-qr=\"true\"|qrserver|qrcode_url/i.test(legacyHtml),
        lineHeightFront: (meta.layout_front && typeof meta.layout_front.lineHeight === 'number') ? meta.layout_front.lineHeight : 1.6,
        lineHeightBack: (meta.layout_back && typeof meta.layout_back.lineHeight === 'number') ? meta.layout_back.lineHeight : 1.6,
        xFront: (meta.layout_front && typeof meta.layout_front.x === 'number') ? meta.layout_front.x : 15,
        yFront: (meta.layout_front && typeof meta.layout_front.y === 'number') ? meta.layout_front.y : 18,
        widthFront: (meta.layout_front && typeof meta.layout_front.width === 'number') ? meta.layout_front.width : 70,
        xBack: (meta.layout_back && typeof meta.layout_back.x === 'number') ? meta.layout_back.x : 15,
        yBack: (meta.layout_back && typeof meta.layout_back.y === 'number') ? meta.layout_back.y : 18,
        widthBack: (meta.layout_back && typeof meta.layout_back.width === 'number') ? meta.layout_back.width : 70,
        backQrX: (meta.layout_back && meta.layout_back.qr && typeof meta.layout_back.qr.x === 'number') ? meta.layout_back.qr.x : 85,
        backQrY: (meta.layout_back && meta.layout_back.qr && typeof meta.layout_back.qr.y === 'number') ? meta.layout_back.qr.y : 50,
        backDeclarationX: (meta.layout_back && meta.layout_back.declaration && typeof meta.layout_back.declaration.x === 'number') ? meta.layout_back.declaration.x : 50,
        backDeclarationY: (meta.layout_back && meta.layout_back.declaration && typeof meta.layout_back.declaration.y === 'number') ? meta.layout_back.declaration.y : 85,
        backDeclarationWidth: (meta.layout_back && meta.layout_back.declaration && typeof meta.layout_back.declaration.width === 'number') ? meta.layout_back.declaration.width : ((meta.layout_back && typeof meta.layout_back.width === 'number') ? meta.layout_back.width : 70)
      };
        
      const formatCertificateDatePtBr = (value) => {
        const raw = String(value || '').trim();
        if (!raw) return '';
        const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const dateValue = dateOnlyMatch
          ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
          : new Date(raw);
        if (Number.isNaN(dateValue.getTime())) return '';
        return dateValue.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
      };

      const overrides = {
        aluno: c.recipient_name,
        curso: c.course_title,
        carga_horaria: formatCourseWorkloadHHMM(c.workload_hours ?? c.workload ?? null),
        nota_final: c.final_grade || '',
        data_conclusao: c.end_date ? formatCertificateDatePtBr(c.end_date) : formatCertificateDatePtBr(c.issued_at),
        codigo_verificacao: c.validation_code,
        certId: c.id,
        instrutor: c.instructor_name || '',
        coordenador: c.coordinator_name || '',
        parceiro_sas: c.partner_name || '',
        empresa: c.partner_name || '',
        conteudo_programatico: normalizeProgramContent(c.program_content || ''),
        hash_code: c.hash_code || '',
        qrcode_url: (typeof window !== 'undefined' && window.location?.origin)
          ? `${window.location.origin}/validar?code=${c.validation_code}`
          : `/validar?code=${c.validation_code}`,
      };

      setPreviewData({ model: mappedModel, overrides });
    } catch (err) {
      console.error('Error fetching template:', err);
      alert('Erro ao carregar o modelo do certificado.');
    } finally {
      setLoadingPreviewId(null);
    }
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Meus Certificados</h1>
          <p className="text-slate-400">Visualize e valide seus certificados emitidos.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-12 text-slate-500">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : rows.length === 0 ? (
        <div className="space-y-4">
          {pendingCourseTitles.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-5 text-orange-100">
              <div className="font-bold text-sm uppercase tracking-wider text-orange-300 mb-2">Certificado em processamento</div>
              <p className="text-sm text-orange-100">
                O curso foi concluído, mas o certificado ainda não apareceu como emitido para:
                {' '}
                {pendingCourseTitles.join(', ')}.
              </p>
            </div>
          )}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 md:p-12 flex flex-col items-center justify-center text-slate-500 border-dashed">
            <FileText size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">Nenhum certificado disponível no momento.</p>
          </div>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold border-b border-primary/20">
              <tr>
                <th className="p-5">Curso</th>
                <th className="p-5">Emissão</th>
                <th className="p-5">Status</th>
                <th className="p-5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {rows.map((c) => (
                <tr key={c.id} className="text-slate-300 hover:bg-primary/5 transition group/row">
                  <td className="p-5">{c.course_title || '-'}</td>
                  <td className="p-5">{new Date(c.issued_at).toLocaleDateString('pt-BR')}</td>
                  <td className="p-5">
                    <span className={`font-bold px-2 py-1 rounded text-xs uppercase ${
                      c.status === 'issued' ? 'bg-primary-solid' :
                      c.status === 'revoked' ? 'text-rose-400 bg-rose-400/10' :
                      'text-slate-400 bg-slate-400/10'
                    }`}>{c.status === 'issued' ? 'Válido' : c.status === 'revoked' ? 'Revogado' : 'Pendente'}</span>
                  </td>
                  <td className="p-5 text-right text-slate-500 flex justify-end gap-3">
                    <button title="Visualizar Certificado" onClick={() => handleViewCertificate(c)} disabled={loadingPreviewId === c.id} className="hover:text-white transition disabled:opacity-50">
                      {loadingPreviewId === c.id ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {previewData && (
        <CertificatePreviewModal 
          model={previewData.model} 
          overrides={previewData.overrides} 
          onClose={() => setPreviewData(null)} 
          readonly={true} 
        />
      )}
    </div>
  );
};
const CourseDetailsModal = ({ isOpen, onClose, course, partner, onViewStudent, users, categories }) => {
  const [evalsExpanded, setEvalsExpanded] = useState(false);
  const [evalsSortOrder, setEvalsSortOrder] = useState('recent'); // 'recent' or 'oldest'
  const [evalsStarFilter, setEvalsStarFilter] = useState(null);
  const [evalsPage, setEvalsPage] = useState(1);
  const EVALS_PAGE_SIZE = 5;

  React.useEffect(() => {
    setEvalsPage(1);
  }, [evalsStarFilter, evalsSortOrder, evalsExpanded]);

  if (!isOpen || !course) return null;

  const filteredEvaluations = (course.evaluations || [])
    .filter(ev => !evalsStarFilter || ev.rating === evalsStarFilter)
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return evalsSortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });

  const totalPages = Math.max(1, Math.ceil(filteredEvaluations.length / EVALS_PAGE_SIZE));
  const safePage = Math.min(evalsPage, totalPages);
  const displayedEvaluations = evalsExpanded
    ? filteredEvaluations.slice((safePage - 1) * EVALS_PAGE_SIZE, safePage * EVALS_PAGE_SIZE)
    : [];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4 animate-fade-in">
      <div className="relative w-full max-w-3xl md:max-w-4xl shadow-2xl bg-slate-900 rounded-xl overflow-hidden flex flex-col max-h-[90vh] border border-primary/20 glow-primary">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 z-[210] bg-slate-950/60 hover:bg-slate-950 backdrop-blur-md text-white p-2 rounded-full transition-all border border-white/10 flex items-center gap-2 pr-4 pl-3 group shadow-xl"
        >
          <X size={20} className="group-hover:rotate-90 transition-transform duration-300" /> 
          <span className="text-xs font-bold uppercase tracking-widest">Fechar</span>
        </button>
        <div className="overflow-y-auto custom-scrollbar">
          <CourseManagementCard 
            course={course} 
            partner={partner} 
            onViewStudent={onViewStudent} 
            users={users}
            categories={categories}
          />
          
          {course.evaluations && course.evaluations.length > 0 && (
            <div className="p-6 border-t border-slate-800 bg-slate-950">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <h4 className="text-white font-bold flex items-center gap-2">
                  <Star className="text-yellow-400 fill-yellow-400" size={20} />
                  Avaliações ({course.ratingCount})
                </h4>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Filtrar por estrela</span>
                  <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setEvalsStarFilter(evalsStarFilter === star ? null : star)}
                        className={`p-1.5 rounded transition-all ${
                          evalsStarFilter === star 
                            ? 'bg-primary text-white shadow-lg glow-primary' 
                            : 'text-slate-500 hover:text-primary'
                        }`}
                        title={`${star} estrelas`}
                      >
                        <Star size={14} className={evalsStarFilter === star ? 'fill-white' : ''} />
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setEvalsSortOrder('recent')}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${
                        evalsSortOrder === 'recent' ? 'bg-slate-800' : 'text-slate-500 hover:text-slate-300'
                      }`}
                      style={evalsSortOrder === 'recent' ? { color: partner.color } : {}}
                    >
                      Mais Recente
                    </button>
                    <div className="w-[1px] h-4 bg-slate-800"></div>
                    <button
                      onClick={() => setEvalsSortOrder('oldest')}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${
                        evalsSortOrder === 'oldest' ? 'bg-slate-800' : 'text-slate-500 hover:text-slate-300'
                      }`}
                      style={evalsSortOrder === 'oldest' ? { color: partner.color } : {}}
                    >
                      Mais Antigo
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {evalsExpanded ? (
                  displayedEvaluations.length === 0 ? (
                    <div className="text-center py-8 bg-slate-900/50 rounded-xl border border-dashed border-slate-800">
                      <p className="text-slate-500 text-sm">Nenhuma avaliação com este filtro.</p>
                    </div>
                  ) : (
                    displayedEvaluations.map((ev, idx) => (
                      <div key={idx} className="bg-slate-900 border border-slate-800 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-slate-200 font-medium text-sm">{ev.profiles?.full_name || ev.author_name || ev.email || 'Usuário'}</p>
                            <div className="flex text-yellow-400 mt-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  size={10} 
                                  className={i < ev.rating ? "fill-yellow-400 text-yellow-400" : "text-slate-600"} 
                                />
                              ))}
                            </div>
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(ev.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {ev.comment && (
                          <p className="text-slate-400 text-sm mt-2 italic">"{ev.comment}"</p>
                        )}
                      </div>
                    ))
                  )
                ) : null}

                {evalsExpanded && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <button
                      onClick={() => setEvalsPage(Math.max(1, safePage - 1))}
                      disabled={safePage === 1}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-slate-700 transition ${
                        safePage === 1 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                      title="Página anterior"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Página {safePage} de {totalPages}
                    </span>
                    <button
                      onClick={() => setEvalsPage(Math.min(totalPages, safePage + 1))}
                      disabled={safePage === totalPages}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-lg border border-slate-700 transition ${
                        safePage === totalPages ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                      title="Próxima página"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                )}
                {filteredEvaluations.length > 3 && (
                  <button
                    onClick={() => setEvalsExpanded(!evalsExpanded)}
                    className="w-full py-3 mt-2 border border-slate-800 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest"
                  >
                    {evalsExpanded ? (
                      <>
                        <ChevronUp size={16} /> Recolher
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} /> Expandir todas as {filteredEvaluations.length} avaliações
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- COMPONENTES DE INTERFACE ---

const Navbar = ({ partner, role, currentView, setCurrentView, user, onMenuClick, isMobileMenuOpen, isSidebarCollapsed, isAuthenticated, onLogin, onLogout, onOpenProfile, auth, setAppNotification }) => {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Bom dia';
    if (hour >= 12 && hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const [impersonationNonce, setImpersonationNonce] = useState(() => {
    try { return window.sessionStorage.getItem('sofera:impersonation:return_nonce') || null; } catch { return null; }
  });
  const [returningToAdmin, setReturningToAdmin] = useState(false);

  useEffect(() => {
    const sync = () => {
      try { setImpersonationNonce(window.sessionStorage.getItem('sofera:impersonation:return_nonce') || null); } catch {}
    };
    sync();
    window.addEventListener('focus', sync);
    return () => window.removeEventListener('focus', sync);
  }, []);

  const canShowImpersonation = !!(isAuthenticated && impersonationNonce && currentView !== 'login' && currentView !== 'reset_password' && currentView !== 'accept_invitation' && currentView !== 'invitation_confirmed' && currentView !== 'invitation_invalid');

  const handleReturnToAdmin = async () => {
    if (!impersonationNonce || returningToAdmin) return;
    const accessToken = auth?.session?.access_token || null;
    if (!accessToken) {
      try { setAppNotification?.({ message: 'Sessão inválida. Faça login novamente.', type: 'error' }); } catch {}
      return setReturningToAdmin(false);
    }
    setReturningToAdmin(true);
    try {
      const redirectTo = `${window.location.origin}/impersonation/callback`;
      const data = await invokeEdgeFunction('admin-impersonate-return', {
        body: { nonce: impersonationNonce, redirect_to: redirectTo, access_token: accessToken },
        accessToken
      })
      const otp = data?.email_otp || null;
      const email = data?.email || null;
      if (otp && email) {
        const { error: vErr } = await supabase.auth.verifyOtp({ type: 'magiclink', token: otp, email });
        if (vErr) throw vErr;
        try { window.sessionStorage.removeItem('sofera:impersonation:return_nonce'); } catch {}
        try { window.sessionStorage.removeItem('sofera:impersonation:strict'); } catch {}
        setImpersonationNonce(null);
        return;
      }
      const link = data?.action_link || null;
      if (!link) throw new Error('Link de retorno não foi gerado.');
      try { window.sessionStorage.removeItem('sofera:impersonation:return_nonce'); } catch {}
      try { window.sessionStorage.removeItem('sofera:impersonation:strict'); } catch {}
      setImpersonationNonce(null);
      window.location.href = link;
    } catch (err) {
      try { setAppNotification?.({ message: String(err?.message || err || 'Falha ao retornar ao Admin.'), type: 'error' }); } catch {}
      setReturningToAdmin(false);
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-900 px-4 md:px-12 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4 md:gap-8">
        {role !== 'guest' && (
           <button onClick={onMenuClick} className="md:hidden text-slate-400 hover:text-white transition">
             {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
           </button>
        )}
        {currentView !== 'login' && (
          <div className="flex flex-col cursor-pointer" onClick={() => setCurrentView(role === 'admin' ? 'admin_dashboard' : 'student_home')}>
            <span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent font-black text-2xl tracking-tighter">SOFERA</span>
            <span className="text-white font-bold text-[10px] tracking-[0.25em] select-none -mt-0.5 ml-0.5 uppercase">EVOLUÇÃO INTELIGENTE</span>
          </div>
        )}
      </div>

      {currentView !== 'login' && currentView !== 'reset_password' && currentView !== 'accept_invitation' && currentView !== 'invitation_confirmed' && currentView !== 'invitation_invalid' && (
        <div className={`hidden md:flex items-center gap-3 absolute md:left-72 top-1/2 -translate-y-1/2 text-slate-300 font-medium text-lg transition-all duration-300`}>
          <span>
            {getGreeting()}, <strong className="text-white font-bold">{user?.name || 'Visitante'}</strong>!
          </span>
          {user?.profile && (
            <span className="px-2 py-0.5 rounded bg-white/10 text-slate-400 text-[10px] font-black uppercase tracking-widest border border-white/5">
              {user.profile}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 md:gap-6 text-white">
        {canShowImpersonation && auth?.isImpersonatingStrict && (
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200"
            title="Modo de impersonação estrita ativo"
          >
            <Shield size={14} />
            <span className="text-[10px] md:text-xs font-black uppercase tracking-widest">Impersonação Estrita</span>
          </div>
        )}
        {canShowImpersonation && (
          <button
            onClick={handleReturnToAdmin}
            disabled={returningToAdmin}
            className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 hover:bg-rose-500/15 transition disabled:opacity-60"
            title="Voltar ao Admin"
          >
            {returningToAdmin ? <Loader2 size={18} className="animate-spin" /> : <UserCheck size={18} />}
            <span className="text-xs font-black uppercase tracking-widest">Voltar ao Admin</span>
          </button>
        )}
        {isAuthenticated && currentView !== 'login' && currentView !== 'reset_password' && currentView !== 'accept_invitation' && currentView !== 'invitation_confirmed' && currentView !== 'invitation_invalid' && (
          <>
            <button
              onClick={onLogout}
              title="Sair"
              className="flex items-center gap-2 bg-slate-800/70 hover:bg-slate-800 border border-slate-700 p-1.5 md:px-3 md:py-1.5 rounded-lg text-slate-200 transition"
            >
              <LogOut size={18} className="md:w-4 md:h-4" /> <span className="hidden md:inline text-xs font-bold uppercase">Sair</span>
            </button>
            <div className="relative cursor-pointer flex items-center gap-2" onClick={onOpenProfile} title="Meu Perfil">
              <div className="w-8 h-8 rounded flex items-center justify-center shadow-lg transition-all duration-500 hover:scale-110" style={{ background: `linear-gradient(to top right, ${partner.color}, #111)` }}>
                <User size={18} />
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

const AdminSidebar = ({ currentView, setCurrentView, partner, isOpen, onClose, isCollapsed, onToggleCollapse, role, auth }) => {
  const [activeMenu, setActiveMenu] = useState(() => {
    if (currentView === 'admin_enrollments' && (auth?.profileRole === 'Ponto Focal' || isPartnerScopedRole(auth?.profileRole))) return null;
    if (['admin_courses', 'admin_enrollments'].includes(currentView)) return 'gestao_cursos';
    if (['student_courses', 'student_enrollments'].includes(currentView)) return auth?.profileRole === 'Aluno' ? 'gestao_cursos' : 'gestao_cursos_aluno';
    if (['admin_companies', 'admin_users', 'admin_communications', 'admin_whatsapp', 'admin_partners', 'admin_categories', 'admin_connections', 'admin_invites'].includes(currentView)) return 'gestao_admin';
    if (['admin_certs', 'admin_certs_issued', 'admin_certs_manual', 'admin_certs_models', 'public_verify'].includes(currentView)) return 'admin_certs';
    if (['admin_library', 'admin_assessments'].includes(currentView)) return 'admin_library_group';
    return null;
  });

  const toggleMenu = (id) => {
    setActiveMenu(prev => prev === id ? null : id);
  };

  const menu = [
    { id: 'admin_dashboard', icon: <Play size={20} />, label: 'Vitrini' },
    { id: 'admin_reports', icon: <BarChart2 size={20} />, label: 'Relatórios' },
    {
      id: 'gestao_cursos',
      icon: <BookOpen size={20} />,
      label: 'Cursos & Matrículas',
      subItems: [
        { id: 'admin_courses', icon: <BookOpen size={16} />, label: 'Gestão de Cursos' },
        { id: 'admin_enrollments', icon: <Users size={16} />, label: 'Adm Matrículas' },
      ]
    },
    { id: 'admin_idiomas', icon: <Languages size={20} />, label: '+ Conteúdos' },
    { id: 'admin_classes', icon: <Users size={20} />, label: 'Turmas' },
    {
      id: 'admin_library_group',
      icon: <FileText size={20} />,
      label: 'Biblioteca',
      subItems: [
        { id: 'admin_library', icon: <FileText size={16} />, label: 'Conteúdos' },
        { id: 'admin_assessments', icon: <CheckCircle size={16} />, label: 'Provas & Avaliações' },
      ]
    },
    {
      id: 'admin_certs',
      icon: <Award size={20} />,
      label: 'Certificados',
      subItems: [
        { id: 'admin_certs_issued', icon: <CheckCircle size={16} />, label: 'Emitidos' },
        { id: 'admin_certs_manual', icon: <Edit2 size={16} />, label: 'Emissão Manual' },
        { id: 'admin_certs_models', icon: <File size={16} />, label: 'Modelos' },
        { id: 'public_verify', icon: <LinkIcon size={16} />, label: 'Validação' },
      ]
    },
    { id: 'admin_service_orders', icon: <NotebookPen size={20} />, label: 'Gestão de OS' },
    {
      id: 'gestao_admin',
      icon: <Shield size={20} />,
      label: 'Gestão Admin',
      subItems: [
        { id: 'admin_companies', icon: <Building size={16} />, label: 'Empresas & Ciclos' },
        { id: 'admin_users', icon: <Users size={16} />, label: 'Usuários' },
        { id: 'admin_communications', icon: <MessageCircle size={16} />, label: 'Comunicações' },
        { id: 'admin_partners', icon: <Briefcase size={16} />, label: 'Parceiros SAS' },
        { id: 'admin_categories', icon: <List size={16} />, label: 'Categorias' },
        { id: 'admin_connections', icon: <Plug size={16} />, label: 'Integrações' },
      ]
    }
  ];
  let displayedMenu = [];
  const p = auth?.permissionsJson || {};
  const isSuperAdmin = auth?.isAdmin;
  const roleName = auth?.profileRole || 'Aluno';

  if (isSuperAdmin) {
    displayedMenu = menu;
  } else if (roleName === 'Aluno') {
    displayedMenu = menu
      .filter(m => ['admin_dashboard','gestao_cursos','admin_certs'].includes(m.id))
      .map(m => 
        m.id === 'gestao_cursos'
          ? { 
              ...m, 
              subItems: [
                { id: 'student_courses', icon: <BookOpen size={16} />, label: 'Meus Cursos' },
                { id: 'student_enrollments', icon: <Users size={16} />, label: 'Matrículas' },
              ] 
            }
          : m.id === 'admin_certs'
            ? { id: 'student_certs', icon: <Award size={20} />, label: 'Meus Certificados' }
            : m
      );
  } else {
    // Custom roles (Parceiro SAS, Coordenador, etc)
    const customMenu = [];
    
    // Vitrini (Everyone)
    customMenu.push(menu.find(m => m.id === 'admin_dashboard'));
    
    // Relatórios
    if (p.view_linked_companies_reports || p.export_reports || p.view_students_progress || p.view_own_progress) {
      customMenu.push(menu.find(m => m.id === 'admin_reports'));
      // Ponto Focal: logo após Relatórios, inserir Matrículas
      if (roleName === 'Ponto Focal') {
        customMenu.push({ id: 'admin_enrollments', icon: <Users size={20} />, label: 'Matrículas' });
      }
    }
    // Certificados (Ponto Focal: botão direto para Emitidos, sem submenu)
    if (roleName === 'Ponto Focal') {
      customMenu.push({ id: 'admin_certs_issued', icon: <Award size={20} />, label: 'Certificados' });
      // Botão "Usuários" logo abaixo de Certificados com edição escopada
      customMenu.push({ id: 'admin_users', icon: <Users size={20} />, label: 'Usuários' });
    }

    // Matrículas (SAS): item de nível superior logo após Relatórios (PF já foi inserido acima)
    if (isPartnerScopedRole(roleName)) {
      customMenu.push({ id: 'admin_enrollments', icon: <Users size={20} />, label: 'Matrículas' });
    }
    
    // Cursos & Matrículas
    const cursosSubItems = [];
    if (p.access_courses && !isPartnerScopedRole(roleName)) {
      cursosSubItems.push({ id: 'student_courses', icon: <BookOpen size={16} />, label: 'Meus Cursos' });
      cursosSubItems.push({ id: 'student_enrollments', icon: <Users size={16} />, label: 'Minhas Matrículas' });
    }
    if (p.create_courses || p.edit_courses) {
      cursosSubItems.push({ id: 'admin_courses', icon: <BookOpen size={16} />, label: 'Gestão de Cursos' });
    }
    // Para outros perfis, mantém "Adm Matrículas" dentro do grupo
    if (p.enroll_students) {
      cursosSubItems.push({ id: 'admin_enrollments', icon: <Users size={16} />, label: 'Adm Matrículas' });
    }
    if (cursosSubItems.length > 0 && roleName !== 'Ponto Focal') {
      customMenu.push({ ...menu.find(m => m.id === 'gestao_cursos'), subItems: cursosSubItems });
    }
    
    // Turmas
    if (p.create_classes) {
      customMenu.push(menu.find(m => m.id === 'admin_classes'));
    }
    
    // Biblioteca (oculta para Ponto Focal)
    if (roleName !== 'Ponto Focal') {
      const biblioSubs = [];
      if (p.create_lessons || p.download_materials) biblioSubs.push({ id: 'admin_library', icon: <FileText size={16} />, label: 'Conteúdos' });
      if (p.create_exams) biblioSubs.push({ id: 'admin_assessments', icon: <CheckCircle size={16} />, label: 'Provas & Avaliações' });
      if (biblioSubs.length > 0) {
        customMenu.push({ ...menu.find(m => m.id === 'admin_library_group'), subItems: biblioSubs });
      }
    }
    
    // Certificados (demais perfis)
    if (isPartnerScopedRole(roleName)) {
      // SAS: botão direto para Emitidos, sem submenu
      customMenu.push({ id: 'admin_certs_issued', icon: <Award size={20} />, label: 'Certificados' });
    } else if (roleName !== 'Ponto Focal') {
      const certSubs = [];
      if (p.view_own_certificates && !isPartnerScopedRole(roleName)) {
        certSubs.push({ id: 'student_certs', icon: <Award size={20} />, label: 'Meus Certificados' });
      }
      if (p.view_company_certificates || p.download_company_certificates) {
        certSubs.push({ id: 'admin_certs_issued', icon: <CheckCircle size={16} />, label: 'Emitidos' });
        if (!isPartnerScopedRole(roleName)) {
          certSubs.push({ id: 'public_verify', icon: <LinkIcon size={16} />, label: 'Validação' });
        }
      }
      if (p.issue_certificates) {
        const insertIdx = certSubs.findIndex(s => s.id === 'public_verify');
        const manualItem = { id: 'admin_certs_manual', icon: <Edit2 size={16} />, label: 'Emissão Manual' };
        if (insertIdx >= 0) certSubs.splice(insertIdx, 0, manualItem);
        else certSubs.push(manualItem);
      }
      if (certSubs.length > 0) {
        if (certSubs.length === 1 && certSubs[0].id === 'student_certs') {
          customMenu.push(certSubs[0]);
        } else {
          customMenu.push({ ...menu.find(m => m.id === 'admin_certs'), subItems: certSubs });
        }
      }
    }
    
    // Gestão de OS e Gestão Admin
    // Requisito: Ponto Focal não deve ver e nem renderizar esses botões
    if (roleName !== 'Ponto Focal') {
      if (p.open_os || p.view_own_os || p.manage_all_os) {
        customMenu.push(menu.find(m => m.id === 'admin_service_orders'));
      }

      const adminSubs = [];
      if (p.view_linked_companies || p.create_company || p.edit_company) adminSubs.push({ id: 'admin_companies', icon: <Building size={16} />, label: 'Empresas & Ciclos' });
      if (roleName === 'Parceiro SAS') {
        adminSubs.push({ id: 'admin_users', icon: <Users size={16} />, label: 'Usuários' });
      } else if (isPartnerScopedRole(roleName)) {
        adminSubs.push({ id: 'admin_users_ro', icon: <Users size={16} />, label: 'Usuários' });
      } else if (p.create_users || p.edit_users || p.deactivate_users) {
        adminSubs.push({ id: 'admin_users', icon: <Users size={16} />, label: 'Usuários' });
      }
      if (p.configure_system) {
        adminSubs.push({ id: 'admin_communications', icon: <MessageCircle size={16} />, label: 'Comunicações' });
        adminSubs.push({ id: 'admin_partners', icon: <Briefcase size={16} />, label: 'Parceiros SAS' });
        adminSubs.push({ id: 'admin_categories', icon: <List size={16} />, label: 'Categorias' });
        adminSubs.push({ id: 'admin_connections', icon: <Plug size={16} />, label: 'Integrações' });
      }
      if (adminSubs.length > 0) {
        customMenu.push({ ...menu.find(m => m.id === 'gestao_admin'), subItems: adminSubs });
      }
    }
    
    // Ambiente específico de aluno para o Ponto Focal
    if (roleName === 'Ponto Focal') {
      customMenu.push({ isDivider: true, id: 'divider_pontofocal' });
      customMenu.push({ isLabel: true, id: 'label_meu_ambiente_pf', label: 'MEU AMBIENTE' });
      
      customMenu.push({
        id: 'gestao_cursos_aluno',
        icon: <BookOpen size={20} />,
        label: 'Cursos & Matrículas',
        subItems: [
          { id: 'student_courses', icon: <BookOpen size={16} />, label: 'Meus Cursos' },
          { id: 'student_enrollments', icon: <Users size={16} />, label: 'Matrículas' },
        ]
      });
      
      customMenu.push({
        id: 'student_certs',
        icon: <Award size={20} />,
        label: 'Meus Certificados'
      });
    }
    
    // Ambiente específico de aluno para o Parceiro SAS
    if (isPartnerScopedRole(roleName)) {
      customMenu.push({ isDivider: true, id: 'divider_parceiro' });
      customMenu.push({ isLabel: true, id: 'label_meu_ambiente', label: 'MEU AMBIENTE' });
      
      customMenu.push({
        id: 'gestao_cursos_aluno',
        icon: <BookOpen size={20} />,
        label: 'Cursos & Matrículas',
        subItems: [
          { id: 'student_courses', icon: <BookOpen size={16} />, label: 'Meus Cursos' },
          { id: 'student_enrollments', icon: <Users size={16} />, label: 'Matrículas' },
        ]
      });
      
      customMenu.push({
        id: 'student_certs',
        icon: <Award size={20} />,
        label: 'Meus Certificados'
      });
    }
    
    displayedMenu = customMenu;
  }

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-64'} bg-slate-950 border-r border-slate-800 h-screen fixed left-0 top-0 pt-[72px] flex flex-col z-40 transform transition-transform duration-300 md:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="px-3 md:px-4 mb-6 hidden md:block">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-end'} mt-3`}>
          <button
            onClick={onToggleCollapse}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-700 bg-slate-800/70 hover:bg-slate-700 transition"
            style={{ color: partner.color }}
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      </div>

      <div className={`${isCollapsed ? 'px-2' : 'px-6'} flex-1 overflow-y-auto custom-scrollbar`}> 
        <div className="flex flex-col gap-2">
          {displayedMenu.map(item => {
            if (item.isDivider) {
              return <div key={item.id} className="my-2 border-t border-slate-800" />;
            }
            if (item.isLabel) {
              return !isCollapsed && <div key={item.id} className="px-4 py-2 mt-2 mb-1 text-xs font-black tracking-[0.2em] text-slate-400 bg-slate-900/50 rounded-lg mx-2 border border-slate-800/50 text-center uppercase">{item.label}</div>;
            }
            if (item.subItems) {
              const isActiveGroup = item.subItems.some(sub => sub.id === currentView) || item.id === currentView;
              const isOpen = activeMenu === item.id;
              return (
                <div key={item.id}>
                  <div className="relative group w-full flex items-center">
                    <button onClick={() => toggleMenu(item.id)} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActiveGroup ? 'text-white' : 'text-slate-400 hover:text-white hover:bg-slate-900'}`} title={isCollapsed ? item.label : undefined}>
                      <div className="flex items-center gap-3">
                        <span style={{ color: isActiveGroup ? partner.color : 'inherit' }} className="transition-colors duration-300">{item.icon}</span>
                        {!isCollapsed && item.label}
                      </div>
                      {!isCollapsed && <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />}
                    </button>
                    {isCollapsed && (
                      <span className="hidden group-hover:block fixed left-[72px] bg-slate-900 border border-slate-800 text-slate-200 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-[9999] pointer-events-none">{item.label}</span>
                    )}
                  </div>
                  {isOpen && (
                    <div className={`flex flex-col gap-1 ${isCollapsed ? 'pl-0 ml-0' : 'pl-4 mt-1 border-l border-slate-800 ml-6'} animate-fade-in`}>
                      {item.subItems.map(sub => (
                        <div key={sub.id} className="relative group w-full flex items-center">
                          <button onClick={() => setCurrentView(sub.id)} className={`w-full ${isCollapsed ? 'text-center flex justify-center' : 'text-left'} px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${currentView === sub.id ? 'bg-primary-solid shadow-primary' : ''}`} style={currentView === sub.id ? { backgroundColor: partner.color, color: 'white' } : { color: '#94a3b8' }} title={isCollapsed ? sub.label : undefined}>
                            {isCollapsed ? sub.icon : sub.label}
                          </button>
                          {isCollapsed && (
                            <span className="hidden group-hover:block fixed left-[72px] bg-slate-900 border border-slate-800 text-slate-200 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-[9999] pointer-events-none">{sub.label}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const isActive = currentView === item.id || (item.id === 'admin_dashboard' && currentView === 'student_home');
            return (
              <div key={item.id} className="relative group w-full flex items-center">
                <button onClick={() => setCurrentView(item.id)} className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'gap-3'} px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-primary-solid shadow-primary' : ''}`} style={isActive ? { backgroundColor: partner.color, color: 'white' } : { color: '#94a3b8' }} title={isCollapsed ? item.label : undefined}>
                  <span>{item.icon}</span>
                  {!isCollapsed && <span>{item.label}</span>}
                </button>
                {isCollapsed && (
                  <span className="hidden group-hover:block fixed left-[72px] bg-slate-900 border border-slate-800 text-slate-200 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-[9999] pointer-events-none">{item.label}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Breaking News oculto para todos os perfis */}
      </div>
    </div>
  );
};

const NewsWidget = ({ isCollapsed, partner, variant = 'sidebar', allowAdminActions = false }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [meta, setMeta] = useState(null);
  const [activeCategory, setActiveCategory] = useState('Brasil');
  const scrollRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const didFetchRef = useRef(false);
  const isAdminUser = !!allowAdminActions;
  const [manageOpen, setManageOpen] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const [feedsError, setFeedsError] = useState(null);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newFeedCategory, setNewFeedCategory] = useState('Brasil');
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    const fetchNews = async () => {
      const STORAGE_KEY = 'sofera_hotnews_cache_v1';
      const readCache = () => {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (!raw) return null;
          return JSON.parse(raw);
        } catch {
          return null;
        }
      };
      const writeCache = (items, m) => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, meta: m, savedAt: new Date().toISOString() }));
        } catch {}
      };

      const cached = readCache();
      const hasCached = Array.isArray(cached?.items) && cached.items.length > 0;
      if (hasCached) {
        setNews(cached.items);
        setMeta(cached.meta || null);
        setError(null);
        setLoading(false);
        const nextAtMs = cached?.meta?.nextAt ? Date.parse(cached.meta.nextAt) : 0;
        // Mantém cache para render rápido, mas continua com revalidação em background.
        // Isso evita "congelar" o widget até o fim do TTL quando novos feeds são inseridos.
      }

      const getRssList = async () => {
        try {
          const q = supabase.from('news_feeds').select('id,url,is_active,partner_id,category').eq('is_active', true).order('created_at', { ascending: false });
          const { data: rows, error: ferr } = await q;
          if (ferr) throw ferr;
          const filtered = Array.isArray(rows) ? rows.filter(r => !partner?.id || !r.partner_id || r.partner_id === partner.id) : [];
          const items = filtered.map(r => ({ url: r.url, category: r.category })).filter(u => /^https?:\/\//i.test(u.url));
          if (items.length > 0) return items;
        } catch {}
        return [
          { url: 'https://g1.globo.com/dynamo/brasil/rss2.xml', category: 'Brasil' },
          { url: 'https://reporterbrasil.org.br/categorias/noticias/feed/', category: 'Brasil' },
          { url: 'https://reporterbrasil.org.br/feed/', category: 'Brasil' },
        ];
      };

      try {
        if (!hasCached) setLoading(true);
        const rssList = await getRssList();
        const { data, error: err } = await supabase.functions.invoke('get_latest_news', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: {
            mode: 'headlines',
            country: 'BR',
            lang: 'pt',
            query: 'Brasil OR Brazil',
            limit: 500,
            rss: rssList,
          },
        });
        if (err) throw err;
        if (data?.error) throw new Error(String(data.error));

        const articles = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data) || Array.isArray(data?.articles) || Array.isArray(data?.news) || Array.isArray(data?.results)
            ? (data?.data || data?.articles || data?.news || data?.results)
            : [];
        const m = {
          fetchedAt: data?.__meta?.fetched_at,
          nextAt: data?.__meta?.cache_until,
          ttlMs: data?.__meta?.cache_ttl_ms,
          cacheStatus: data?.__meta?.cache_status,
          rss_sources: data?.__meta?.rss_sources ?? null,
          rss_errors: data?.__meta?.rss_errors ?? null,
        };
        if (Array.isArray(articles) && articles.length > 0) {
          setNews(articles);
          setMeta(m);
          setError(null);
          writeCache(articles, m);
        } else if (!hasCached) {
          setNews([]);
          setMeta(m);
        }
      } catch (err) {
        console.error("Erro ao buscar notícias:", err);
        if (!hasCached) {
          const message = err instanceof Error ? err.message : "Não foi possível carregar as notícias agora.";
          setError(message || "Não foi possível carregar as notícias agora.");
        }
      } finally {
        if (!hasCached) setLoading(false);
      }
    };

    fetchNews();
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (isCollapsed) return;
    if (loading || error) return;
    if (!Array.isArray(news) || news.length <= 3) return;
    let id = setInterval(() => {
      if (isHovering) return;
      const el = scrollRef.current;
      if (!el) return;
      
      const isMobile = window.innerWidth < 768; // Tailwind md breakpoint
      
      if (isMobile) {
        el.scrollLeft += 1;
        if (el.scrollLeft + el.clientWidth + 2 >= el.scrollWidth) {
          el.scrollLeft = 0;
        }
      } else {
        el.scrollTop += 1;
        if (el.scrollTop + el.clientHeight + 2 >= el.scrollHeight) {
          el.scrollTop = 0;
        }
      }
    }, 30);
    return () => clearInterval(id);
  }, [news, loading, error, isCollapsed, isHovering]);

  useEffect(() => {
    if (!Array.isArray(news)) return;
    if (activeCategory === 'Brasil') return;
    const any = news.some(n => n && (resolveItemCategory(n) === activeCategory));
    if (!any && news.length > 0) setActiveCategory('Brasil');
  }, [news, activeCategory]);

  useEffect(() => {
    if (!selectedNews) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedNews(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedNews]);

  const formatDate = (value) => {
    const d = new Date(value || Date.now());
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR');
  };

  const getUrl = (item) => item?.url || item?.link || '';
  const getSource = (item) => item?.source || item?.publisher || item?.site || 'Notícia';
  const getSummary = (item) => item?.description || item?.summary || item?.snippet || item?.content || item?.text || '';
  const getTitle = (item) => item?.title || item?.headline || item?.name || item?.title_text || '';
  const getHost = (item) => {
    const u = getUrl(item);
    try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; }
  };
  const getFavicon = (item) => {
    const host = getHost(item);
    return host ? `https://icons.duckduckgo.com/ip3/${host}.ico` : '';
  };

  const loadFeeds = async () => {
    setFeedsLoading(true);
    setFeedsError(null);
    try {
      const q = supabase.from('news_feeds').select('id,url,is_active,partner_id,category').order('created_at', { ascending: false });
      const { data: rows, error: ferr } = await q;
      if (ferr) throw ferr;
      setFeeds(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setFeedsError(e?.message || 'Falha ao carregar feeds');
      setFeeds([]);
    } finally {
      setFeedsLoading(false);
    }
  };

  const updateFeedCategory = async (f, newCategory) => {
    try {
      const { error: upErr } = await supabase.from('news_feeds').update({ category: newCategory }).eq('id', f.id);
      if (upErr) throw upErr;
      await loadFeeds();
      fetchNewsRef.current?.();
    } catch (e) {
      setFeedsError(e?.message || 'Falha ao atualizar categoria');
    }
  };

  const addFeed = async () => {
    const url = String(newFeedUrl || '').trim();
    if (!/^https?:\/\//i.test(url)) return;
    try {
      const payload = { url, is_active: true, category: newFeedCategory };
      if (partner?.id) payload.partner_id = partner.id;
      const { error: insErr } = await supabase.from('news_feeds').insert(payload);
      if (insErr) throw insErr;
      setNewFeedUrl('');
      await loadFeeds();
      fetchNewsRef.current?.();
    } catch (e) {
      setFeedsError(e?.message || 'Falha ao adicionar feed');
    }
  };

  const toggleFeed = async (f) => {
    try {
      const { error: upErr } = await supabase.from('news_feeds').update({ is_active: !f.is_active }).eq('id', f.id);
      if (upErr) throw upErr;
      await loadFeeds();
      fetchNewsRef.current?.();
    } catch (e) {
      setFeedsError(e?.message || 'Falha ao atualizar');
    }
  };

  const deleteFeed = async (f) => {
    try {
      const { error: delErr } = await supabase.from('news_feeds').delete().eq('id', f.id);
      if (delErr) throw delErr;
      await loadFeeds();
      fetchNewsRef.current?.();
    } catch (e) {
      setFeedsError(e?.message || 'Falha ao remover');
    }
  };

  const renderManageModal = () => {
    if (!manageOpen) return null;
    return createPortal(
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
        <button type="button" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setManageOpen(false); fetchNewsRef.current?.(); }} aria-label="Fechar modal" />
        <div className="relative w-full max-w-2xl rounded-2xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-950">
          <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings size={18} className="text-red-400" />
              <span className="text-sm font-bold text-white uppercase tracking-widest">Feeds RSS</span>
            </div>
            <button type="button" onClick={() => { setManageOpen(false); fetchNewsRef.current?.(); }} className="h-9 w-9 rounded-lg border border-slate-800 bg-slate-900/60 hover:bg-slate-800/60 text-slate-200 flex items-center justify-center transition" aria-label="Fechar">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex flex-col gap-3 p-4 rounded-xl border border-slate-800 bg-slate-900/40">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Adicionar novo feed</span>
              <div className="flex gap-2">
                <input value={newFeedUrl} onChange={e => setNewFeedUrl(e.target.value)} placeholder="https://exemplo.com/rss.xml" className="flex-1 bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none text-sm" />
                <select 
                  value={newFeedCategory} 
                  onChange={e => setNewFeedCategory(e.target.value)}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none text-sm"
                >
                  <option value="Brasil">Brasil</option>
                  <option value="Mundo">Mundo</option>
                </select>
                <button onClick={addFeed} className="px-4 py-3 rounded-lg bg-red-500 text-white font-bold text-sm shadow-lg shadow-red-500/20 active:scale-95 transition whitespace-nowrap">Adicionar</button>
              </div>
            </div>

            {feedsLoading ? (
              <div className="text-slate-400 text-sm py-8 text-center">Carregando...</div>
            ) : feedsError ? (
              <div className="text-rose-400 text-sm py-4">{feedsError}</div>
            ) : (
              <div className="space-y-2 max-h-[320px] overflow-auto pr-1 custom-scrollbar">
                {feeds.map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900 group">
                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`${f.is_active ? 'text-teal-400 bg-teal-400/10' : 'text-slate-500 bg-slate-500/10'} text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded`}>
                          {f.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                        <select
                          value={(f.category && ['Brasil','Mundo'].includes(f.category)) ? f.category : 'Mundo'}
                          onChange={(e) => updateFeedCategory(f, e.target.value)}
                          className="text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-slate-950 border border-slate-700 text-slate-200 hover:border-slate-500 transition"
                          title="Categoria"
                        >
                          <option value="Brasil">Brasil</option>
                          <option value="Mundo">Mundo</option>
                        </select>
                      </div>
                      <span className="text-xs text-slate-200 truncate font-mono opacity-80">{f.url}</span>
                    </div>
                    <button onClick={() => toggleFeed(f)} className="px-3 py-1.5 rounded bg-slate-800 text-slate-200 text-xs font-bold hover:bg-slate-700 transition">{f.is_active ? 'Desativar' : 'Ativar'}</button>
                    <button onClick={() => deleteFeed(f)} className="px-3 py-1.5 rounded bg-rose-600/10 text-rose-500 hover:bg-rose-600 hover:text-white text-xs font-bold transition border border-rose-600/20">Remover</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const fetchNewsRef = React.useRef(null);
  fetchNewsRef.current = () => {
    didFetchRef.current = false;
    setTimeout(() => {
      const STORAGE_KEY = 'sofera_hotnews_cache_v1';
      const writeCache = (items, m) => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, meta: m, savedAt: new Date().toISOString() }));
        } catch {}
      };
      const fn = async () => {
        try {
          setLoading(true);
          setError(null);
          const q = supabase.from('news_feeds').select('url,is_active,partner_id,category').eq('is_active', true);
          const { data: rows } = await q;
          const filteredRows = Array.isArray(rows) ? rows.filter(r => !partner?.id || !r.partner_id || r.partner_id === partner.id) : [];
          let rss = filteredRows.map(r => ({ url: r.url, category: r.category })).filter(u => u && /^https?:\/\//i.test(u.url));
          if (!rss || rss.length === 0) {
            rss = [
              { url: 'https://g1.globo.com/dynamo/brasil/rss2.xml', category: 'Brasil' },
              { url: 'https://reporterbrasil.org.br/categorias/noticias/feed/', category: 'Brasil' },
              { url: 'https://reporterbrasil.org.br/feed/', category: 'Brasil' },
            ];
          }
          const { data, error: invokeErr } = await supabase.functions.invoke('get_latest_news', { body: { mode: 'headlines', country: 'BR', lang: 'pt', query: 'Brasil OR Brazil', limit: 500, rss } });
          if (invokeErr) throw invokeErr;
          if (data?.error) throw new Error(String(data.error));
          const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.data) ? data.data : []);
          const m = {
            fetchedAt: data?.__meta?.fetched_at,
            nextAt: data?.__meta?.cache_until,
            ttlMs: data?.__meta?.cache_ttl_ms,
            cacheStatus: data?.__meta?.cache_status,
            rss_sources: data?.__meta?.rss_sources ?? null,
            rss_errors: data?.__meta?.rss_errors ?? null,
          };
          setNews(items || []);
          setMeta(m);
          writeCache(items || [], m);
        } catch (e) {
          setError(e?.message || 'Falha ao atualizar notícias');
        } finally {
          setLoading(false);
        }
      };
      fn();
    }, 0);
  };

  const renderModal = () => {
    if (!selectedNews) return null;
    return createPortal(
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
        <button
          type="button"
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={() => setSelectedNews(null)}
          aria-label="Fechar modal"
        />
        <div
          role="dialog"
          aria-modal="true"
          className="relative w-full max-w-6xl h-[90vh] rounded-2xl overflow-hidden border border-red-500/20 shadow-2xl bg-slate-950 flex flex-col"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 via-rose-500/10 to-transparent pointer-events-none"></div>
          
          <div className="relative p-4 md:p-5 border-b border-slate-800/50 shrink-0 bg-slate-950/80 backdrop-blur-md z-10">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-200/90 bg-red-500/15 border border-red-500/25 px-2 py-0.5 rounded-full">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-400"></span>
                    </span>
                    Notícia
                  </span>
                  <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                    <Globe size={12} className="text-slate-500" />
                    {getSource(selectedNews)}
                  </span>
                  <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                    <Clock size={12} className="text-slate-500" />
                    {formatDate(selectedNews.published_at || selectedNews.publishedAt)}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-black text-white leading-tight break-words">
                  {getTitle(selectedNews)}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedNews(null)}
                className="shrink-0 h-9 w-9 rounded-lg border border-slate-800 bg-slate-900/60 hover:bg-slate-800/60 text-slate-200 flex items-center justify-center transition"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="mt-3 p-2.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-2">
              <AlertCircle size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-200/90 leading-snug">
                <strong className="text-yellow-400 font-bold">Aviso:</strong> Alguns portais bloqueiam a exibição da notícia dentro do sistema. Se o conteúdo abaixo não carregar ou aparecer recusado, clique no botão <strong className="text-white">"Abrir na fonte original"</strong> no rodapé.
              </p>
            </div>
          </div>

          <div className="relative flex-1 w-full bg-white z-0 overflow-hidden">
            {getUrl(selectedNews) ? (
              <iframe 
                src={getUrl(selectedNews)} 
                className="w-full h-full border-0 absolute inset-0"
                style={{ width: '100%', height: '100%' }}
                title={getTitle(selectedNews) || 'Notícia'}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-8 text-center text-slate-400 bg-slate-950">
                Link indisponível para carregar a notícia.
              </div>
            )}
          </div>

          <div className="relative p-4 border-t border-slate-800/50 shrink-0 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-950/80 backdrop-blur-md z-10">
            <div className="text-[10px] font-mono text-slate-500 truncate max-w-full">
              {getUrl(selectedNews) ? getUrl(selectedNews) : ''}
            </div>
            {getUrl(selectedNews) ? (
              <a
                href={getUrl(selectedNews)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-black transition whitespace-nowrap shadow-lg shadow-red-500/20"
              >
                <LinkIcon size={16} />
                Abrir na fonte original
              </a>
            ) : (
              <button
                type="button"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm font-black cursor-not-allowed whitespace-nowrap"
                disabled
              >
                <LinkIcon size={16} />
                Sem link
              </button>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };



  const normalizeCategory = (c) => {
    const raw = (c || '').toString().trim();
    if (!raw) return 'Mundo';
    const v = raw.toLowerCase();
    if (['geral', 'general', 'world', 'global', 'internacional', 'internacionales', 'mundo'].includes(v)) return 'Mundo';
    if (['brasil', 'brazil', 'br', 'nacional'].includes(v)) return 'Brasil';
    return raw;
  };

  const resolveItemCategory = (item) => {
    const byTag = normalizeCategory(item?.category);
    if (byTag === 'Brasil' || byTag === 'Mundo') return byTag;
    const host = (getHost(item) || '').toLowerCase();
    if (/(^|\.)g1\.globo\.com$/.test(host) || host.includes('reporterbrasil') || host.endsWith('.br')) return 'Brasil';
    return 'Mundo';
  };

  const filteredNews = Array.isArray(news)
    ? (activeCategory === 'Todos' ? news : news.filter(n => resolveItemCategory(n) === activeCategory))
    : [];

  useEffect(() => {
    if (isCollapsed) return;
    const id = setInterval(() => {
      fetchNewsRef.current?.();
    }, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [isCollapsed]);

  return (
    <>
      <div className={`mt-2 mb-3 relative group rounded-3xl overflow-hidden transition-all duration-500 flex-shrink-0 bg-slate-950/40 border border-white/5 backdrop-blur-md shadow-2xl ${isCollapsed ? 'mx-0 p-2.5 flex justify-center w-16' : 'mx-0 p-3 w-full max-w-[560px]'}`}>
        <div className={`${isCollapsed ? 'hidden' : 'flex'} absolute top-2 right-3 flex-row items-center gap-2 z-50`}>
          {isAdminUser && (
            <button 
              type="button" 
              onClick={() => { setManageOpen(true); loadFeeds(); }}
              className="p-1.5 rounded-full bg-slate-900/50 border border-white/5 text-slate-400 hover:text-white hover:border-red-500/50 transition-all shrink-0 shadow-lg"
              title="Gerenciar feeds RSS"
            >
              <Settings size={14} />
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-red-600/10 blur-[80px] rounded-full"></div>
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-red-900/10 blur-[80px] rounded-full"></div>
          </div>
        )}
      
        <div className="relative z-10 flex flex-col gap-2 md:gap-3">
          <div className={`flex flex-col items-center ${isCollapsed ? 'justify-center' : 'justify-between gap-4'}`}>
            <div className={`flex flex-col ${isCollapsed ? 'items-center justify-center' : 'items-start md:items-end w-full'}`}>
              {!isCollapsed && (
                <div className="flex items-center justify-start md:justify-end gap-2 mb-1 md:mb-0 mt-0 w-full border-b border-red-600/40 pb-1 md:pb-0">
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90">Breaking</span>
                  <span className="px-2 py-0.5 bg-red-600 text-white text-[12px] font-black uppercase tracking-[0.15em] shadow-[0_0_12px_rgba(220,38,38,0.35)] rounded">
                    News
                  </span>
                </div>
              )}
              {isCollapsed && (
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-red-600/10 border border-red-600/20 group-hover:border-red-600/40 transition-colors">
                  <div className="absolute inset-0 bg-red-600 blur-xl opacity-20 animate-pulse rounded-full"></div>
                  <Zap size={20} className="text-red-500 relative z-10" />
                </div>
              )}
            </div>
            {/* Botões movidos para a barra de filtros para evitar sobrepor o título */}
          </div>

          {!isCollapsed && (
            <div className="flex items-center justify-start -mt-1 px-0">
              <div className="flex gap-2 bg-transparent flex-1 border-b border-white/10 pb-0">
                {['Todos', 'Brasil', 'Mundo'].map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className={`pb-1 text-[11px] font-black uppercase tracking-widest transition-all border-b-2 relative -mb-[9px] ${
                      activeCategory === cat 
                        ? 'border-red-500 text-white' 
                        : 'border-transparent text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}
        
          {!isCollapsed && (
            <div
              className="flex md:flex-col overflow-x-auto md:overflow-x-hidden md:overflow-y-auto gap-3 md:gap-2 pb-2 md:pb-0 max-h-[340px] pr-0 custom-scrollbar md:snap-none"
              ref={scrollRef}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              onTouchStart={() => setIsHovering(true)}
              onTouchEnd={() => setIsHovering(false)}
            >
              {loading ? (
                <div className="flex md:flex-col gap-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="flex gap-3 snap-start min-w-[280px] md:min-w-0">
                      <div className="w-16 h-12 bg-slate-800/50 rounded-lg animate-pulse flex-shrink-0"></div>
                      <div className="flex-1 flex flex-col gap-2 py-1">
                        <div className="h-3 bg-slate-800/70 rounded animate-pulse w-full"></div>
                        <div className="h-3 bg-slate-800/50 rounded animate-pulse w-2/3"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-center min-w-[280px] md:min-w-0">
                  <AlertCircle size={20} className="text-red-400 mx-auto mb-2 opacity-80" />
                  <p className="text-xs text-red-200/90 leading-snug">{error}</p>
                </div>
              ) : filteredNews.length > 0 ? (
                filteredNews.map((item, idx) => (
                  <div
                    key={idx}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedNews(item)}
                    className="text-left w-full cursor-pointer rounded-xl border border-red-500/30 bg-white/[0.08] transition-all p-4 group/news flex gap-4 items-start relative overflow-hidden h-[120px] md:min-h-[120px] min-w-[280px] md:min-w-0 flex-shrink-0 md:flex-shrink"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-red-500"></div>
                    <img
                      src={getFavicon(item)}
                      alt={getSource(item)}
                      className="w-5 h-5 rounded-sm object-contain absolute top-2 right-2"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div className="flex-1 min-w-0 flex flex-col">
                      <h4 className="text-[11px] font-semibold text-slate-100 leading-snug whitespace-normal break-words group-hover/news:text-white transition-colors clamp-2">
                        {getTitle(item) || 'Título indisponível'}
                      </h4>
                      {getSummary(item) && (
                        <p className="text-[11px] text-slate-300 mt-1.5 leading-relaxed whitespace-normal break-words clamp-3">
                          {getSummary(item).replace(/<[^>]*>?/gm, '').trim()}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 min-w-[280px] md:min-w-0">
                  <Globe size={24} className="text-slate-600 mx-auto mb-2 opacity-50" />
                  <p className="text-xs text-slate-400">Nenhuma notícia no momento.</p>
                </div>
              )}
            </div>
          )}
        </div>
      
        {isCollapsed && (
          <span className="hidden group-hover:block fixed left-[72px] bg-slate-900 border border-slate-800 text-slate-200 text-xs px-3 py-2 rounded shadow-xl whitespace-nowrap z-[9999] pointer-events-none">
            <strong className="text-red-400 block mb-1">HotNews</strong>
            {loading ? 'Carregando...' : error ? 'Falha ao carregar' : `${news.length} novas notícias`}
          </span>
        )}
      </div>

      {selectedNews && createPortal(
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelectedNews(null)}
            aria-label="Fechar modal"
          />
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-6xl h-[90vh] rounded-2xl overflow-hidden border border-red-500/20 shadow-2xl bg-slate-950 flex flex-col"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 via-rose-500/10 to-transparent pointer-events-none"></div>
            
            <div className="relative p-4 md:p-5 border-b border-slate-800/50 shrink-0 bg-slate-950/80 backdrop-blur-md z-10">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-200/90 bg-red-500/15 border border-red-500/25 px-2 py-0.5 rounded-full">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-400"></span>
                      </span>
                      Notícia
                    </span>
                    <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                      <Globe size={12} className="text-slate-500" />
                      {getSource(selectedNews)}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                      <Clock size={12} className="text-slate-500" />
                      {formatDate(selectedNews.published_at || selectedNews.publishedAt)}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-black text-white leading-tight break-words">
                    {getTitle(selectedNews)}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedNews(null)}
                  className="shrink-0 h-9 w-9 rounded-lg border border-slate-800 bg-slate-900/60 hover:bg-slate-800/60 text-slate-200 flex items-center justify-center transition"
                  aria-label="Fechar"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="mt-3 p-2.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-2">
                <AlertCircle size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-200/90 leading-snug">
                  <strong className="text-yellow-400 font-bold">Aviso:</strong> Alguns portais bloqueiam a exibição da notícia dentro do sistema. Se o conteúdo abaixo não carregar ou aparecer recusado, clique no botão <strong className="text-white">"Abrir na fonte original"</strong> no rodapé.
                </p>
              </div>
            </div>

            <div className="relative flex-1 w-full bg-white z-0 overflow-hidden">
              {getUrl(selectedNews) ? (
                <iframe 
                  src={getUrl(selectedNews)} 
                  className="w-full h-full border-0 absolute inset-0"
                  style={{ width: '100%', height: '100%' }}
                  title={getTitle(selectedNews) || 'Notícia'}
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-8 text-center text-slate-400 bg-slate-950">
                  Link indisponível para carregar a notícia.
                </div>
              )}
            </div>

            <div className="relative p-4 border-t border-slate-800/50 shrink-0 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-950/80 backdrop-blur-md z-10">
              <div className="text-[10px] font-mono text-slate-500 truncate max-w-full">
                {getUrl(selectedNews) ? getUrl(selectedNews) : ''}
              </div>
              {getUrl(selectedNews) ? (
                <a
                  href={getUrl(selectedNews)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-black transition whitespace-nowrap shadow-lg shadow-red-500/20"
                >
                  <LinkIcon size={16} />
                  Abrir na fonte original
                </a>
              ) : (
                <button
                  type="button"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm font-black cursor-not-allowed whitespace-nowrap"
                  disabled
                >
                  <LinkIcon size={16} />
                  Sem link
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      {renderManageModal()}
    </>
  );
};

// --- TELAS ESPECÍFICAS ---

// Janela (em dias) para exibir a tarja "Novo" na Vitrine: cursos publicados/editados
// recentemente. Sem coluna published_at, usa-se o mesmo timestamp de "Acabaram de chegar".
const NEW_COURSE_WINDOW_DAYS = 30;
const NEW_COURSE_WINDOW_MS = NEW_COURSE_WINDOW_DAYS * 24 * 60 * 60 * 1000;

const getCourseRecencyTs = (course) => Date.parse(String(course.updated_at || course.created_at || '')) || 0;

const AdminDashboard = ({ partner, courses = [], onEditCourse, onViewStudent, users, categories, canViewCourse }) => {
  const [previewCourse, setPreviewCourse] = useState(null);
  const [detailsCourse, setDetailsCourse] = useState(null);

  // Filtrar rascunhos da vitrine (apenas cursos publicados)
  const publishedCourses = React.useMemo(() => {
    return (courses || []).filter(c => c.status === 'published');
  }, [courses]);

  // Filtrar e ordenar categorias da vitrine
  const vitriniCategories = React.useMemo(() => {
    if (!categories || !Array.isArray(categories)) return [];
    return categories
      .filter(c => c.domain === 'vitrini' && c.status === true)
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  }, [categories]);

  // Função para pegar cursos de uma categoria
  const getCoursesForCategory = (cat) => {
    const linkedIds = (cat.linked_category_ids || []).map(String);
    if (!linkedIds.length) return [];
    
    return publishedCourses.filter(c => {
       const cCats = c.categories || (c.category_id ? [c.category_id] : []) || [];
       const cCatsStr = Array.isArray(cCats) ? cCats.map(String) : [];
       return linkedIds.some(lid => cCatsStr.includes(lid));
    });
  };

  const FeaturedCarousel = ({ courses }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      if (courses.length <= 1) return;
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % courses.length);
      }, 5000);
      return () => clearInterval(interval);
    }, [courses]);

    if (!courses.length) return null;

    const currentCourse = courses[currentIndex];

    return (
      <div className="relative rounded-2xl overflow-hidden mb-12 shadow-2xl border border-slate-800 h-[300px] md:h-[400px] group transition-all duration-500">
        <CourseWorkloadBadge
          workload={currentCourse.workload_hours || currentCourse.workload}
          wrapperClassName="absolute top-4 right-4 z-20"
        />
        <img 
          src={currentCourse.thumb || currentCourse.image_url} 
          alt={currentCourse.title || currentCourse.name} 
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 p-6 md:p-10 max-w-5xl animate-fade-in">
          <span 
            className="text-white text-[10px] md:text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2 md:mb-3 inline-block shadow-primary glow-primary"
            style={{ 
              backgroundColor: partner.color
            }}
          >
            Em Destaque
          </span>
          <h1 className="text-xl sm:text-2xl md:text-4xl font-black text-white mb-2 md:mb-4 leading-tight break-words line-clamp-2 md:line-clamp-none">{currentCourse.title || currentCourse.name}</h1>
          {currentCourse.ratingCount > 0 && (
            <div className="flex items-center gap-1 mb-2 md:mb-3">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={10} 
                    className={`md:w-3 md:h-3 ${i < Math.round(currentCourse.averageRating) ? "fill-yellow-400 text-yellow-400" : "text-slate-600"}`} 
                  />
                ))}
              </div>
              <span className="text-[10px] md:text-xs text-slate-400 ml-1">
                {currentCourse.averageRating?.toFixed(1)} ({currentCourse.ratingCount})
              </span>
            </div>
          )}
          <p className="text-sm md:text-lg text-slate-300 mb-4 md:mb-8 leading-relaxed drop-shadow-md line-clamp-2 md:line-clamp-3">
            {currentCourse.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto pr-4 md:pr-0">
            <button onClick={() => setPreviewCourse(currentCourse)} className="bg-white text-slate-950 hover:bg-slate-200 px-4 md:px-8 py-2.5 md:py-3 rounded-lg font-bold flex justify-center items-center gap-2 transition shadow-lg shadow-white/10 text-sm md:text-base w-full sm:w-auto">
              <Play size={16} className="fill-slate-950 md:w-5 md:h-5" /> Assistir Prévia
            </button>
            <button onClick={() => setDetailsCourse(currentCourse)} className="bg-slate-800/50 backdrop-blur-md text-white hover:bg-slate-700/50 border border-slate-700 px-4 md:px-8 py-2.5 md:py-3 rounded-lg font-bold flex justify-center items-center gap-2 transition text-sm md:text-base w-full sm:w-auto">
              <Info size={16} className="md:w-5 md:h-5" /> Mais Detalhes
            </button>
          </div>
        </div>
        
        {/* Indicators */}
        {courses.length > 1 && (
          <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex gap-1.5 md:gap-2 z-10">
            {courses.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all duration-300 ${idx === currentIndex ? 'w-8 glow-primary-intense' : 'bg-white/30 w-2 hover:bg-white/50'}`}
                style={idx === currentIndex ? { backgroundColor: partner.color } : {}}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const featuredCourses = publishedCourses.filter(c => c.isFeatured || c.is_featured);

  // Categoria virtual "Acabaram de chegar": os 10 cursos publicados mais recentes,
  // ordenados do mais novo para o mais antigo (updated_at reflete a última publicação/edição).
  const justArrivedCourses = React.useMemo(() => {
    return [...publishedCourses]
      .sort((a, b) => {
        const timeA = Date.parse(String(a.updated_at || a.created_at || '')) || 0;
        const timeB = Date.parse(String(b.updated_at || b.created_at || '')) || 0;
        return timeB - timeA;
      })
      .slice(0, 10);
  }, [publishedCourses]);

  // Categoria virtual "Os mais bem avaliados": até 10 cursos com avaliação dos alunos,
  // do mais bem avaliado para o menos avaliado (desempate por quantidade de avaliações).
  const bestRatedCourses = React.useMemo(() => {
    return publishedCourses
      .filter(c => Number(c.ratingCount || 0) > 0)
      .sort((a, b) => {
        const ratingA = Number(a.averageRating || 0);
        const ratingB = Number(b.averageRating || 0);
        if (ratingB !== ratingA) return ratingB - ratingA;
        return Number(b.ratingCount || 0) - Number(a.ratingCount || 0);
      })
      .slice(0, 10);
  }, [publishedCourses]);

  const CourseCard = ({ course }) => {
    const isNewCourse = (() => {
      const ts = getCourseRecencyTs(course);
      return ts > 0 && (Date.now() - ts) <= NEW_COURSE_WINDOW_MS;
    })();

    return (
    <div 
      onClick={() => setPreviewCourse(course)} 
      className="min-w-[280px] md:min-w-[320px] bg-slate-900 rounded-lg overflow-hidden border border-slate-800 hover:scale-105 transition-all duration-300 cursor-pointer group shadow-lg hover-glow-primary"
      onMouseEnter={(e) => e.currentTarget.style.borderColor = `${partner.color}50`}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = '#1e293b'}
    >
      <div className="relative h-48 overflow-hidden">
        <CourseWorkloadBadge workload={course.workload_hours || course.workload} />
        <img src={course.thumb} alt={course.title} className="w-full h-full object-cover group-hover:opacity-75 transition-opacity" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80"></div>
        <div className="absolute bottom-3 left-3 right-3">
          {isNewCourse && (
            <span 
              className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded border backdrop-blur-sm mb-2 inline-block"
              style={{ 
                color: partner.color, 
                backgroundColor: `${partner.color}1a`, 
                borderColor: `${partner.color}33` 
              }}
            >
              Novo
            </span>
          )}
          <h3 className="text-white font-bold text-lg leading-tight shadow-black drop-shadow-md">{course.title}</h3>
        </div>
        <button className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
           <div className="rounded-full p-3 shadow-xl transform scale-75 group-hover:scale-100 transition-transform" style={{ backgroundColor: partner.color }}>
              <Play size={24} className="text-white fill-white ml-1" />
           </div>
        </button>
      </div>
      <div className="p-4">
        <p className="text-slate-400 text-sm line-clamp-2 mb-3 h-10">{course.description}</p>
        
        {course.ratingCount > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={12} 
                  className={i < Math.round(course.averageRating) ? "fill-yellow-400 text-yellow-400" : "text-slate-600"} 
                />
              ))}
            </div>
            <span className="text-xs text-slate-400 ml-1">
              {course.averageRating.toFixed(1)} ({course.ratingCount})
            </span>
          </div>
        )}

        <div className="flex items-center justify-end text-xs text-slate-500 font-medium">
          <span className="flex items-center gap-1 max-w-[150px] truncate" title={users.find(u => u.id === course.instructor || u.name === course.instructor)?.name || (typeof course.instructor === 'string' ? course.instructor : '-')}>
            <User size={14} /> {users.find(u => u.id === course.instructor || u.name === course.instructor)?.name || (typeof course.instructor === 'string' ? course.instructor : '-')}
          </span>
        </div>
        <div className="mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); setDetailsCourse(course); }}
            className="px-4 py-2 rounded-lg border border-slate-700 bg-slate-800/50 text-white hover:bg-slate-700/50 transition font-bold text-xs inline-flex items-center gap-2"
          >
            <Info size={14} /> Mais Detalhes
          </button>
        </div>
      </div>
    </div>
    );
  };

  const Section = ({ title, courses }) => {
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowLeftArrow(scrollLeft > 0);
        // Using a small buffer (10px) for float inconsistencies
        setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
      }
    };

    const scroll = (direction) => {
      if (scrollContainerRef.current) {
        const { clientWidth } = scrollContainerRef.current;
        const scrollAmount = direction === 'left' ? -clientWidth / 1.5 : clientWidth / 1.5;
        scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    };
    
    // Check initial scroll state
    useEffect(() => {
        handleScroll();
        window.addEventListener('resize', handleScroll);
        return () => window.removeEventListener('resize', handleScroll);
    }, [courses]);

    return (
      <div className="mb-10 relative group/section">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2 pl-1 border-l-4" style={{ borderColor: partner.color }}>
          {title} <ChevronRight size={20} className="text-slate-600" />
        </h2>
        
        <div className="relative">
            {showLeftArrow && (
                <button 
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-0 bottom-0 z-20 bg-black/50 hover:bg-black/70 text-white p-2 opacity-0 group-hover/section:opacity-100 transition-opacity duration-300 flex items-center justify-center w-12 rounded-r-lg"
                    aria-label="Scroll left"
                >
                    <ChevronLeft size={32} />
                </button>
            )}
            
            <div 
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex gap-6 overflow-x-auto pb-6 scrollbar-hide snap-x scroll-smooth"
            >
                {courses.map((course, idx) => (
                <div key={`${course.id}-${idx}`} className="snap-start">
                    <CourseCard course={course} />
                </div>
                ))}
            </div>

            {showRightArrow && (
                <button 
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-0 bottom-0 z-20 bg-black/50 hover:bg-black/70 text-white p-2 opacity-0 group-hover/section:opacity-100 transition-opacity duration-300 flex items-center justify-center w-12 rounded-l-lg"
                    aria-label="Scroll right"
                >
                    <ChevronRight size={32} />
                </button>
            )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in pb-20">
      
      
      {/* Hero Banner */}
      {featuredCourses.length > 0 && (
        <FeaturedCarousel courses={featuredCourses} />
      )}

      {/* Acabaram de chegar */}
      {justArrivedCourses.length > 0 && (
        <Section title="Acabaram de chegar" courses={justArrivedCourses} />
      )}

      {/* Os mais bem avaliados */}
      {bestRatedCourses.length > 0 && (
        <Section title="Os mais bem avaliados" courses={bestRatedCourses} />
      )}

      {vitriniCategories.length > 0 ? (
        vitriniCategories.map(cat => {
            const catCourses = getCoursesForCategory(cat);
            if (catCourses.length === 0) return null;
            return <Section key={cat.id} title={cat.name} courses={catCourses} />;
        })
      ) : (
        publishedCourses.length > 0 && <Section title="Todos os Cursos" courses={publishedCourses} />
      )}
      <PreviewModal isOpen={!!previewCourse} onClose={() => setPreviewCourse(null)} course={previewCourse} partner={partner} />
      <CourseDetailsModal 
        isOpen={!!detailsCourse} 
        onClose={() => setDetailsCourse(null)} 
        course={detailsCourse} 
        partner={partner} 
        onViewStudent={canViewCourse && detailsCourse && !canViewCourse(detailsCourse) ? undefined : onViewStudent}
        users={users}
        categories={categories}
      />
    </div>
  );
};

const StudentDetailsModal = ({ isOpen, student, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [coursesData, setCoursesData] = useState([]);
  const selectedCourseId = student?.report_course_id || null
  const selectedStudentId = student?.student_id || student?.id || null
  const totalTimeMs = Number(student?.report_course_total_time_ms ?? (student?.total_time_ms || 0))
  const totalTimeLabel = (() => {
    const totalSeconds = Math.floor(totalTimeMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  })()

  const normalizeCourseDetails = React.useCallback((course) => {
    const modules = Array.isArray(course.modules) ? course.modules : []
    const allLessonIds = modules.flatMap((module) =>
      (Array.isArray(module.course_lessons) ? module.course_lessons : []).map((lesson) => lesson.id)
    )
    const completedLessonIds = new Set(course.completedLessonIds instanceof Set ? Array.from(course.completedLessonIds) : [])
    const minGrade = Number(course.min_grade ?? course.minGrade ?? 7)
    const attemptScores = (Array.isArray(course.assessment_attempts) ? course.assessment_attempts : [])
      .map((attempt) => Number(attempt?.score))
      .filter((score) => Number.isFinite(score))
    const bestAssessmentScore = attemptScores.length
      ? Math.max(...attemptScores)
      : (Number.isFinite(Number(course.assessment?.score)) ? Number(course.assessment.score) : null)
    const completionEligibility = getCourseCompletionEligibility({
      progressPercent: resolveUnifiedProgress(
        course.progressPercent,
        course.db_progress_percent,
        course.ui_progress_percent
      ),
      assessmentEnabled: Boolean(course.assessment_enabled || course.assessmentEnabled || course.assessment_id || course.assessmentId),
      assessmentId: course.assessment_id || course.assessmentId || null,
      bestScore: bestAssessmentScore,
      minGrade,
    })
    const isReferenceCourse = student?.report_course_id === course.id
    const shouldForceComplete = (
      (isReferenceCourse && student?.result === 'Aprovado' && student?.score !== '-' && student?.score != null)
      || completionEligibility.canComplete
    )

    if (!shouldForceComplete) {
      return {
        ...course,
        modules,
        completedLessonIds
      }
    }

    allLessonIds.forEach((lessonId) => completedLessonIds.add(lessonId))

    return {
      ...course,
      modules: modules.map((module) => ({
        ...module,
        course_lessons: (Array.isArray(module.course_lessons) ? module.course_lessons : []).map((lesson) => ({
          ...lesson,
          is_completed: true
        }))
      })),
      completedLessonIds,
      db_progress_percent: 100,
      ui_progress_percent: 100,
      progressPercent: 100
    }
  }, [student])

  const filterCoursesForSelectedRow = React.useCallback((courses) => {
    const normalizedCourses = Array.isArray(courses) ? courses : []
    if (!selectedCourseId) return normalizedCourses
    return normalizedCourses.filter((course) => String(course?.id || '') === String(selectedCourseId))
  }, [selectedCourseId])

  useEffect(() => {
    const sid = selectedStudentId;
    if (isOpen && sid) {
      loadDetails();
    } else {
      setCoursesData([]);
    }
  }, [isOpen, selectedStudentId, student]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const sid = selectedStudentId;
      console.log("Buscando detalhes para aluno:", sid, student?.student);
      
      const { data, error } = await supabase.rpc('get_student_details_for_report', { p_student_id: sid });
      
      if (error) {
        console.error("Erro no RPC, tentando fallback manual:", error);
        
        // Fallback manual caso a migration não tenha sido aplicada
        const { data: enrollments, error: enrollError } = await supabase
          .from('enrollments')
          .select(`
            id,
            offering_id,
            progress_percent,
            course_offerings (
              course_id,
              courses (
                id,
                name,
                assessment_id,
                max_attempts
              )
            )
          `)
          .eq('student_id', sid);

        if (enrollError) throw enrollError;

        const courses = enrollments
          ?.map(e => {
            const offering = Array.isArray(e.course_offerings) ? e.course_offerings[0] : e.course_offerings;
            const course = Array.isArray(offering?.courses) ? offering.courses[0] : offering?.courses;
            if (!course) return null;
            return { enrollment_id: e.id, ui_progress_percent: e.progress_percent, ...course };
          })
          .filter(c => c !== null) || [];

        const details = await Promise.all(courses.map(async (course) => {
          const { data: modules } = await supabase
            .from('course_modules')
            .select(`
              id, title, order_index,
              course_lessons (id, title, order_index)
            `)
            .eq('course_id', course.id)
            .order('order_index');
          
          const modulesWithSortedLessons = modules?.map(m => ({
            ...m,
            course_lessons: m.course_lessons?.sort((a, b) => a.order_index - b.order_index) || []
          })) || [];

          const { data: progress } = await supabase
            .from('lesson_progress')
            .select('lesson_id')
            .eq('student_id', sid)
            .not('completed_at', 'is', null);
          
          const completedLessonIds = new Set(progress?.map(p => p.lesson_id) || []);

          let totalLessons = 0;
          let completedCount = 0;
          modulesWithSortedLessons.forEach(m => {
             m.course_lessons.forEach(l => {
                totalLessons++;
                if (completedLessonIds.has(l.id)) completedCount++;
             });
          });
          const dbProgressPercent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;
          const progressPercent = resolveUnifiedProgress(dbProgressPercent, course.ui_progress_percent)

          let assessment = null;
          let assessment_attempts = [];
          if (course.assessment_id) {
            const { data: attempt } = await supabase
              .from('assessment_attempts')
              .select('score, status, completed_at')
              .eq('assessment_id', course.assessment_id)
              .eq('student_id', student.id)
              .is('lesson_id', null)
              .order('created_at', { ascending: false })
              .maybeSingle();
            assessment = attempt;
            const { data: attemptsList } = await supabase
              .from('assessment_attempts')
              .select('score, status, completed_at, created_at')
              .eq('assessment_id', course.assessment_id)
              .eq('student_id', student.id)
              .is('lesson_id', null)
              .order('created_at', { ascending: true });
            assessment_attempts = attemptsList || [];
          }

          return {
            ...course,
            modules: modulesWithSortedLessons,
            completedLessonIds,
            db_progress_percent: dbProgressPercent,
            progressPercent,
            assessment,
            assessment_attempts
          };
        }));
        const orderedDetails = details.map(normalizeCourseDetails).sort((a, b) => {
          if (a.id === student?.report_course_id) return -1;
          if (b.id === student?.report_course_id) return 1;
          return 0;
        });
        setCoursesData(filterCoursesForSelectedRow(orderedDetails));
        return;
      }

      console.log("Dados do RPC:", data);

      const rpcCourses = Array.isArray(data) ? data : [];
      if (!Array.isArray(rpcCourses) || rpcCourses.length === 0) {
        setCoursesData([]);
      } else {
        const processedData = rpcCourses.map(course => normalizeCourseDetails({
          ...course,
          modules: Array.isArray(course.modules) ? course.modules : [],
          completedLessonIds: new Set(
            (Array.isArray(course.modules) ? course.modules : [])
              .flatMap(m => (Array.isArray(m.course_lessons) ? m.course_lessons : []))
              .filter(l => l.is_completed)
              .map(l => l.id)
          )
        })).sort((a, b) => {
          if (a.id === student?.report_course_id) return -1;
          if (b.id === student?.report_course_id) return 1;
          return 0;
        });
        setCoursesData(filterCoursesForSelectedRow(processedData));
      }

    } catch (err) {
      console.error("Erro ao carregar detalhes do aluno:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950">
          <div>
            <h2 className="text-xl font-bold text-white">Detalhes do Aluno</h2>
            <p className="text-slate-400 text-sm">
              {student?.student} - {student?.report_course_name || 'Progresso Detalhado'}
            </p>
            <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900/70 px-2.5 py-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">TA</span>
              <span className="font-mono text-xs text-slate-200">{totalTimeLabel}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition">
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="animate-spin text-teal-500" size={32} />
              <span className="ml-3 text-slate-400">Carregando detalhes...</span>
            </div>
          ) : coursesData.length === 0 ? (
             <div className="text-center text-slate-500 py-12">Nenhum curso encontrado para este aluno.</div>
          ) : (
            coursesData.map(course => {
              const unifiedProgress = resolveUnifiedProgress(
                course.progressPercent,
                course.db_progress_percent,
                course.ui_progress_percent
              );
              const minGrade = Number(course.min_grade ?? course.minGrade ?? 7);
              const assessmentScore = Number(course.assessment?.score ?? 0);
              const isAssessmentApproved = Boolean(course.assessment) && assessmentScore >= minGrade;
              const hasPendingFinalAssessment = Boolean(course.assessment_id) && !isAssessmentApproved;

              return (
              <div key={course.id} className="bg-slate-950/50 border border-slate-800 rounded-lg overflow-hidden">
                <div className="p-4 bg-slate-900 border-b border-slate-800 flex justify-between items-center">
                   <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-bold text-white text-lg">{course.name}</h3>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                               <div className="h-full bg-teal-500" style={{ width: `${unifiedProgress}%` }}></div>
                            </div>
                            <span>{unifiedProgress}% Concluído</span>
                         </div>
                         {unifiedProgress >= 100 && hasPendingFinalAssessment && (
                           <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300">
                             Prova pendente
                           </span>
                         )}
                      </div>
                   </div>
                </div>
                
                <div className="p-4 space-y-4">
                  {course.modules.map(module => (
                    <div key={module.id} className="border-l-2 border-slate-800 pl-4">
                      <h4 className="text-slate-300 font-semibold text-sm mb-2">{module.title}</h4>
                      <div className="space-y-1">
                        {module.course_lessons.length > 0 ? (
                          module.course_lessons.map(lesson => {
                            const isCompleted = course.completedLessonIds.has(lesson.id);
                            return (
                              <div key={lesson.id} className="flex items-center gap-2 text-sm py-1">
                                {isCompleted ? (
                                  <CheckCircle size={14} className="text-teal-500" />
                                ) : (
                                  <div className="w-3.5 h-3.5 rounded-full border border-slate-600" />
                                )}
                                <span className={isCompleted ? "text-slate-400" : "text-slate-500"}>
                                  {lesson.title}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-xs text-slate-600 italic">Sem aulas neste módulo</div>
                        )}
                      </div>
                    </div>
                  ))}

                  {course.assessment_id && (
                    <div className="border-t border-slate-800 pt-4 mt-4">
                       <h4 className="text-white font-bold text-sm mb-2 flex items-center gap-2">
                         <Award size={16} className="text-orange-500" /> Prova Final
                       </h4>
                       {course.assessment ? (
                         <div className="bg-slate-900 p-3 rounded border border-slate-800 space-y-3">
                          {(() => {
                            const minGrade = Number(course.min_grade ?? course.minGrade ?? 7)
                            const assessmentScore = Number(course.assessment.score || 0)
                            const isApproved = assessmentScore >= minGrade
                            return (
                              <>
                           <div className="flex items-center justify-between">
                             <div>
                               <div className="text-xs text-slate-400 uppercase font-bold">Nota</div>
                              <div className={`text-lg font-bold ${isApproved ? 'text-teal-400' : 'text-rose-400'}`}>
                                 {course.assessment.score != null ? Number(course.assessment.score).toFixed(1) : '-'}
                               </div>
                             </div>
                             <div className="text-right">
                               <div className="text-xs text-slate-400 uppercase font-bold">Status</div>
                               <div className="text-sm text-white">
                                {isApproved ? 'Aprovado' : (course.assessment.status === 'completed' ? 'Reprovado' : 'Em andamento')}
                               </div>
                             </div>
                             <div className="text-right">
                               <div className="text-xs text-slate-400 uppercase font-bold">Data</div>
                               <div className="text-sm text-slate-400">
                                 {course.assessment.completed_at ? new Date(course.assessment.completed_at).toLocaleDateString('pt-BR') : '-'}
                               </div>
                             </div>
                           </div>
                           
                           {(course.assessment_attempts || course.assessmentAttempts)?.length > 0 && (
                             <div className="pt-2 border-t border-slate-800">
                               <div className="text-xs text-slate-400 uppercase font-bold mb-2">Notas por tentativa</div>
                               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                 {(course.assessment_attempts || course.assessmentAttempts).map((a, idx) => (
                                   <div key={`${a.created_at || idx}-${idx}`} className="flex items-center justify-between bg-slate-950/70 border border-slate-800 rounded-lg p-2">
                                     <span className="text-xs text-slate-400">Tentativa {idx + 1}</span>
                                    <span className={`text-sm font-semibold ${Number(a.score || 0) >= minGrade ? 'text-teal-400' : 'text-rose-400'}`}>
                                       {a.score != null ? Number(a.score).toFixed(1) : '-'}
                                     </span>
                                     <span className="text-xs text-slate-500">
                                       {a.completed_at ? new Date(a.completed_at).toLocaleDateString('pt-BR') : '-'}
                                     </span>
                                   </div>
                                 ))}
                               </div>
                             </div>
                           )}

                          {!isApproved && (
                             <div className="pt-2 border-t border-slate-800">
                               {(() => {
                                 const maxAttempts = course.max_attempts ?? course.maxAttempts ?? null;
                                 const used = (course.assessment_attempts || course.assessmentAttempts || []).length;
                                 if (maxAttempts != null) {
                                   const remaining = Math.max(0, maxAttempts - used);
                                   return (
                                     <div className="text-xs text-slate-400">
                                       {remaining > 0
                                         ? `Restam ${remaining} tentativa${remaining > 1 ? 's' : ''}.`
                                         : 'Sem tentativas restantes.'}
                                     </div>
                                   );
                                 }
                                 return null;
                               })()}
                             </div>
                           )}
                              </>
                            )
                          })()}
                         </div>
                       ) : (
                         <div className="text-sm text-slate-500 italic">Prova ainda não iniciada.</div>
                       )}
                    </div>
                  )}
                </div>
              </div>
            )})
          )}
        </div>
        <div className="p-4 border-t border-slate-800 bg-slate-950 flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-bold transition">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportMultiSelect = ({ options = [], value = [], onChange, placeholder = 'Selecionar', allLabel = 'Todos' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuPos, setMenuPos] = useState(null);
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const updateMenuPos = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const maxH = 320;
    const openUp = spaceBelow < maxH && rect.top > spaceBelow;
    setMenuPos({
      top: openUp ? Math.max(8, rect.top - maxH - 8) : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      openUp,
      maxH: Math.min(maxH, openUp ? rect.top - 16 : viewportH - rect.bottom - 16),
    });
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    const onResize = () => updateMenuPos();
    const onScroll = () => updateMenuPos();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const normalizedValue = (value || []).map(String).filter(Boolean);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? options.filter((o) => String(o.name || '').toLowerCase().includes(normalizedSearch))
    : options;
  const toggle = (id) => {
    const selected = new Set(normalizedValue);
    if (selected.has(String(id))) selected.delete(String(id));
    else selected.add(String(id));
    onChange(Array.from(selected));
  };
  const selectedOptions = options.filter((option) => normalizedValue.includes(String(option.id)));
  const triggerLabel = selectedOptions.length === 0
    ? placeholder
    : selectedOptions.length === 1
      ? selectedOptions[0].name
      : `${selectedOptions.length} selecionados`;
  const closeDropdown = () => { setOpen(false); setSearch(''); };
  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => { setOpen(v => { const next = !v; if (next) setTimeout(updateMenuPos, 0); return next; }); }}
        className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] flex items-center gap-2 text-left hover:border-slate-700"
        title={selectedOptions.map((option) => option.name).join(', ')}
      >
        <Search size={16} className="text-slate-500 flex-shrink-0" />
        <span className={`${selectedOptions.length === 0 ? 'text-slate-500' : 'text-white'} truncate flex-1`}>
          {triggerLabel}
        </span>
        {selectedOptions.length > 0 && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            className="cursor-pointer text-slate-400 hover:text-white text-sm font-bold px-1"
            title="Limpar seleção"
          >
            ×
          </span>
        )}
        <ChevronDown size={16} className={`text-slate-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && menuPos && (
        <>
          <div
            className="fixed inset-0 z-[150] cursor-default"
            onClick={closeDropdown}
            onMouseDown={(e) => { e.preventDefault(); closeDropdown(); }}
            aria-hidden="true"
          />
          <div
            className="fixed overflow-auto bg-slate-950 border border-slate-700 rounded-xl shadow-2xl z-[160] ring-1 ring-black/50"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxH,
            }}
          >
            <div className="sticky top-0 bg-slate-950 border-b border-slate-800 p-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Digite para buscar..."
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-primary/50 transition placeholder:text-slate-600"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800/80 border-b border-slate-800 font-medium"
            >
              {allLabel}
            </button>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                Nenhum resultado para "{search}"
              </div>
            ) : (
              filteredOptions.map((option) => (
                <label key={option.id} className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-800/60 cursor-pointer transition select-none">
                  <input
                    type="checkbox"
                    className="accent-orange-500 w-4 h-4 rounded flex-shrink-0"
                    checked={normalizedValue.includes(String(option.id))}
                    onChange={() => toggle(option.id)}
                  />
                  <span className="truncate">{option.name}</span>
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
const ReportCompaniesMultiSelect = ({ options = [], value = [], onChange, placeholder = 'Selecionar empresas' }) => (
  <ReportMultiSelect
    options={options}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    allLabel="Todas as Empresas"
  />
);

const AdminReports = ({ partner }) => {
  const auth = useAuth();
  const isAdmin = auth.isAdmin;
  const profileRole = auth.profileRole;
  const pontoFocalCompanyIds = React.useMemo(() => {
    const ids = Array.isArray(auth.profile?.companyIds) ? auth.profile.companyIds : [];
    const primaryCompanyId = auth.profile?.companyId;
    return [...new Set([...ids, ...(primaryCompanyId ? [primaryCompanyId] : [])].filter(Boolean).map(String))];
  }, [auth.profile?.companyId, auth.profile?.companyIds]);
  const reportFiltersStorageKey = `sofera:admin_reports:filters:v3:${auth.session?.user?.id || 'anon'}:${profileRole || 'unknown'}:${partner?.id || 'all'}`;
  const defaultReportFilters = React.useMemo(() => buildDefaultAdminReportFilters(), []);
  const hasHydratedReportFilters = useRef(false);
  const isRestoringReportFilters = useRef(false);
  const [data, setData] = useState({
    engagement: [],
    completion_rate: 0,
    certificates: [],
    relation_students: [],
    all_students: [],
    students: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = usePersistentTab('admin_reports', 'geral');
  const [isConfirmingReport, setIsConfirmingReport] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // v2-system-modals
  const [filters, setFilters] = useState(() => defaultReportFilters);
  const [showEngagementHelp, setShowEngagementHelp] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [reportSortConfig, setReportSortConfig] = useState(null);
  const [courses, setCourses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [reportClasses, setReportClasses] = useState([]);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
  const pontoFocalCompanies = React.useMemo(() => {
    const screenCompanies = Array.isArray(companies) ? companies : [];
    if (screenCompanies.length > 0) {
      return screenCompanies
        .map((company) => ({
          id: String(company?.id || ''),
          name: company?.name || '',
        }))
        .filter((company) => company.id);
    }

    const profileCompanies = Array.isArray(auth.profile?.linkedCompanies) ? auth.profile.linkedCompanies : [];
    const mapped = profileCompanies
      .map((company) => ({
        id: String(company?.id || ''),
        name: company?.name || '',
      }))
      .filter((company) => company.id);

    if (mapped.length > 0) return mapped;

    if (auth.profile?.companyId) {
      return [{
        id: String(auth.profile.companyId),
        name: auth.profile?.companyName || '',
      }];
    }

    return [];
  }, [companies, auth.profile?.companyId, auth.profile?.companyName, auth.profile?.linkedCompanies]);

  useEffect(() => {
    hasHydratedReportFilters.current = false;
    isRestoringReportFilters.current = false;
  }, [reportFiltersStorageKey]);

  useEffect(() => {
    if (hasHydratedReportFilters.current) return;

    const defaults = { ...defaultReportFilters };
    let restored = defaults;

    try {
      const raw = localStorage.getItem(reportFiltersStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          restored = { ...defaults, ...parsed };
          restored.company_ids = Array.isArray(parsed.company_ids)
            ? parsed.company_ids.map(String)
            : (parsed.company_id ? [String(parsed.company_id)] : []);
          restored.company_id = restored.company_ids[0] || '';
          restored.course_ids = Array.isArray(parsed.course_ids)
            ? parsed.course_ids.map(String)
            : (parsed.course_id ? [String(parsed.course_id)] : []);
          restored.course_id = restored.course_ids[0] || '';
          restored.class_ids = Array.isArray(parsed.class_ids)
            ? parsed.class_ids.map(String)
            : (parsed.class_id ? [String(parsed.class_id)] : []);
          restored.class_id = restored.class_ids[0] || '';
          restored.partner_ids = Array.isArray(parsed.partner_ids)
            ? parsed.partner_ids.map(String)
            : (parsed.partner_id ? [String(parsed.partner_id)] : []);
          restored.partner_id = restored.partner_ids[0] || '';
          restored.statuses = Array.isArray(parsed.statuses)
            ? parsed.statuses.filter(Boolean)
            : (parsed.status ? [parsed.status] : []);
          restored.status = restored.statuses[0] || '';
          restored.pareceres = Array.isArray(parsed.pareceres)
            ? parsed.pareceres.filter(Boolean)
            : (parsed.parecer ? [parsed.parecer] : []);
          restored.parecer = restored.pareceres[0] || '';
          restored.enrollment_statuses = Array.isArray(parsed.enrollment_statuses)
            ? parsed.enrollment_statuses.filter(Boolean)
            : (parsed.enrollment_status ? [parsed.enrollment_status] : []);
          restored.enrollment_status = restored.enrollment_statuses[0] || '';
        }
      }
    } catch {
      restored = { ...defaultReportFilters };
    }

    if (profileRole === 'Ponto Focal') {
      const normalizedCompanyIds = Array.isArray(restored.company_ids)
        ? restored.company_ids.map(String).filter(Boolean)
        : [];
      restored.company_ids = normalizedCompanyIds.filter((id) => pontoFocalCompanyIds.includes(id));
      restored.company_id = restored.company_ids[0] || '';
      if (normalizedCompanyIds.length > 0 && restored.company_ids.length === 0) {
        restored.company_id = '';
      }
    }

    isRestoringReportFilters.current = true;
    setFilters(restored);
    hasHydratedReportFilters.current = true;
  }, [defaultReportFilters, reportFiltersStorageKey, isAdmin, profileRole, pontoFocalCompanyIds]);

  useEffect(() => {
    if (!hasHydratedReportFilters.current) return;
    if (isRestoringReportFilters.current) {
      isRestoringReportFilters.current = false;
      return;
    }
    try {
      localStorage.setItem(reportFiltersStorageKey, JSON.stringify(filters));
    } catch {
      // ignore localStorage errors
    }
  }, [filters, reportFiltersStorageKey]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        let companiesQuery = supabase.from('companies').select('id, name, central_company_id').eq('status', 'Ativo');
        
        if (isPartnerScopedRole(profileRole)) {
          const partnerId = auth.profile?.sasPartnerId;
          companiesQuery = supabase.rpc('admin_list_companies_by_scope', {
            p_company_id: null,
            p_partner_id: partnerId || null,
          });
        } else if (profileRole === 'Ponto Focal') {
          if (pontoFocalCompanyIds.length > 0) {
            companiesQuery = supabase.from('companies').select('id, name').eq('status', 'Ativo').in('id', pontoFocalCompanyIds);
          } else {
            companiesQuery = Promise.resolve({ data: [], error: null });
          }
        }

        const [c, co, p] = await Promise.all([
          supabase.from('courses').select('id, name').eq('status', 'Ativo'),
          companiesQuery,
          supabase.from('sas_partners').select('id, name').eq('status', 'Ativo')
        ]);
        if (c.data) setCourses(c.data);
        if (co.data) setCompanies(co.data);
        if (p.data) setPartners(p.data);
      } catch (e) {
        console.error("Error loading options", e);
      }
    };
    fetchOptions();
  }, [profileRole, auth.profile?.companyId, auth.profile?.companyIds, auth.profile?.sasPartnerId, pontoFocalCompanyIds]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const { data: classRows, error } = await supabase
          .from('classes')
          .select('id, name, company_id, company_ids, partner_id, partner_ids, status, class_courses(course_id), class_students(student_id)')
          .order('name');
        if (error) throw error;

        const classes = (classRows || []).map((cls) => ({
          id: String(cls.id),
          name: cls.name || '',
          companyIds: [...new Set([...(cls.company_ids || []), cls.company_id].map(String).filter(Boolean))],
          partnerIds: [...new Set([...(cls.partner_ids || []), cls.partner_id].map(String).filter(Boolean))],
          courseIds: (cls.class_courses || []).map((cc) => String(cc.course_id || '')).filter(Boolean),
          studentIds: (cls.class_students || []).map((cs) => String(cs.student_id || '')).filter(Boolean),
        })).filter((cls) => cls.id && cls.name);

        if (isPartnerScopedRole(profileRole)) {
          const partnerId = String(auth.profile?.sasPartnerId || '');
          setReportClasses(partnerId ? classes.filter((cls) => cls.partnerIds.includes(partnerId)) : []);
          return;
        }
        if (profileRole === 'Ponto Focal') {
          const allowedCompanyIds = pontoFocalCompanyIds.map(String);
          setReportClasses(
            allowedCompanyIds.length === 0
              ? []
              : classes.filter((cls) => cls.companyIds.some((id) => allowedCompanyIds.includes(id)))
          );
          return;
        }

        setReportClasses(classes);
      } catch (e) {
        console.error('Error loading report classes', e);
      }
    };
    fetchClasses();
  }, [profileRole, auth.profile?.sasPartnerId, pontoFocalCompanyIds]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const selectedCourseIds = Array.isArray(filters.course_ids) ? filters.course_ids.map(String).filter(Boolean) : [];
        const selectedClassIds = Array.isArray(filters.class_ids) ? filters.class_ids.map(String).filter(Boolean) : [];
        const selectedPartnerIds = Array.isArray(filters.partner_ids) ? filters.partner_ids.map(String).filter(Boolean) : [];
        const selectedCompanyIds = Array.isArray(filters.company_ids) ? filters.company_ids.map(String).filter(Boolean) : [];
        const selectedStatuses = Array.isArray(filters.statuses) ? filters.statuses.filter(Boolean) : [];
        const selectedPareceres = Array.isArray(filters.pareceres) ? filters.pareceres.filter(Boolean) : [];
        const selectedEnrollmentStatuses = Array.isArray(filters.enrollment_statuses) ? filters.enrollment_statuses.filter(Boolean) : [];
        const backendStatusFilter = selectedStatuses.length === 1 && selectedStatuses[0] !== 'Programado'
          ? selectedStatuses[0]
          : null;
        const rawParams = {
          p_company_id: isPartnerScopedRole(profileRole)
            ? null
            : ((Array.isArray(filters.company_ids) && filters.company_ids.length === 1)
              ? filters.company_ids[0]
              : ((Array.isArray(filters.company_ids) && filters.company_ids.length > 1)
                  ? null
                  : (filters.company_id || null))),
          p_partner_id: isPartnerScopedRole(profileRole)
            ? (auth.profile?.sasPartnerId || null)
            : (selectedPartnerIds.length === 1 ? selectedPartnerIds[0] : null),
          p_course_id: selectedCourseIds.length === 1 ? selectedCourseIds[0] : null,
          // "Programado" is a profile lifecycle state normalized in the UI, not a native status from the reports RPC.
          p_status: backendStatusFilter,
          p_period: filters.period || defaultReportFilters.period,
          p_start_date: filters.start_date || null,
          p_end_date: filters.end_date || null,
          p_is_admin: !!isAdmin
        };

        const params = scopeParamsForRole({ profileRole, profile: auth.profile }, rawParams);
        const relationParams = scopeParamsForRole(
          { profileRole, profile: auth.profile },
          {
            p_company_id: null,
            p_partner_id: isPartnerScopedRole(profileRole) ? (auth.profile?.sasPartnerId || null) : null,
            p_course_id: null,
            p_status: null,
            p_period: filters.period || defaultReportFilters.period,
            p_start_date: filters.start_date || null,
            p_end_date: filters.end_date || null,
            p_is_admin: !!isAdmin
          }
        );

        const [{ data: reportData, error: reportErr }, { data: relationData }, { data: certData }, statusMap] = await Promise.all([
          supabase.rpc('get_admin_reports_data', params),
          supabase.rpc('get_admin_reports_scope_options', relationParams),
          supabase.from('certificates').select('course_title'),
          fetchScopedUserStatusMap({
            profileRole,
            profile: auth.profile,
            companyId: rawParams.p_company_id,
            partnerId: rawParams.p_partner_id,
          }).catch(() => new Map()),
        ]);
        if (reportErr) throw reportErr;

        const certCounts = {};
        (certData || []).forEach(c => certCounts[c.course_title] = (certCounts[c.course_title] || 0) + 1);
        const certificates = Object.entries(certCounts)
          .map(([course_title, count]) => ({ course_title, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 4);

        const engagement = Array.isArray(reportData?.engagement) ? reportData.engagement : [];
        const completion_rate = Number(reportData?.completion_rate || 0);
        let students = Array.isArray(reportData?.students) ? reportData.students : [];

        const normalizedStudents = students.map((student) => {
          const studentLifecycle = statusMap.get(String(student?.student_id || '')) || null
          const studentLifecycleStatus = studentLifecycle?.status || ''
          const hasApprovedResult = student.result === 'Aprovado' && student.score !== '-' && student.score != null
          const normalizedProgress = hasApprovedResult
            ? 100
            : Number(student.report_course_progress ?? (student.progress_avg || 0))
          const baseStatus = hasApprovedResult
            ? 'Concluído'
            : (student.status || 'Não iniciado')
          const normalizedStatus = getReportDisplayStatus(
            baseStatus,
            studentLifecycleStatus,
          )
          const enrollmentDisplayStatus = getEnrollmentDisplayStatus(
            student.enrollment_status,
            studentLifecycleStatus,
            student.enrollment_deadline,
          )

          return {
            ...student,
            last_access: resolveEffectiveReportLastAccess(student, studentLifecycle),
            report_course_progress: normalizedProgress,
            progress_avg: Math.max(Number(student.progress_avg || 0), normalizedProgress),
            status: normalizedStatus,
            enrollment_status_display: enrollmentDisplayStatus,
          }
        })

        students = normalizedStudents

        if (selectedPareceres.length > 0) {
          const parecerSet = new Set(selectedPareceres);
          students = students.filter((s) => parecerSet.has(s.result));
        }
        if (selectedStatuses.length > 0) {
          const statusSet = new Set(selectedStatuses);
          students = students.filter((s) => statusSet.has(s.status));
        }
        if (selectedEnrollmentStatuses.length > 0) {
          const enrollmentStatusSet = new Set(selectedEnrollmentStatuses);
          students = students.filter((s) => enrollmentStatusSet.has(s.enrollment_status_display));
        }
        if (selectedCourseIds.length > 0) {
          const courseIdSet = new Set(selectedCourseIds.map(String));
          students = students.filter((s) => courseIdSet.has(String(s.report_course_id || '')));
        }
        if (selectedClassIds.length > 0 && reportClasses.length > 0) {
          const selectedClassSet = new Set(selectedClassIds);
          const classesInScope = reportClasses.filter((cls) => selectedClassSet.has(String(cls.id)));
          students = students.filter((s) => {
            const studentId = String(s.student_id || '');
            const courseId = String(s.report_course_id || '');
            // O aluno entra pelo vínculo com a turma (class_students). A restrição por curso
            // só é aplicada quando a turma define cursos e o aluno possui matrícula neles.
            return classesInScope.some((cls) => (
              cls.studentIds.includes(studentId)
              && (cls.courseIds.length === 0 || cls.courseIds.includes(courseId))
            ));
          });
        }
        if (selectedPartnerIds.length > 0 && !isPartnerScopedRole(profileRole) && profileRole !== 'Ponto Focal') {
          const selectedPartnerNames = new Set(
            (partners || [])
              .filter((partnerOption) => selectedPartnerIds.includes(String(partnerOption?.id || '')))
              .map((partnerOption) => String(partnerOption?.name || ''))
          );
          students = students.filter((s) => selectedPartnerNames.has(String(s.partner || '')));
        }
        if (selectedCompanyIds.length > 0) {
          const selectedCompanyNames = new Set(
            [...(profileRole === 'Ponto Focal' ? pontoFocalCompanies : companies)]
              .filter((companyOption) => selectedCompanyIds.includes(String(companyOption?.id || '')))
              .map((companyOption) => String(companyOption?.name || ''))
          );
          if (selectedCompanyNames.size > 0) {
            students = students.filter((s) => selectedCompanyNames.has(String(s.company || '')));
          }
        }
        if (filters.never_accessed) students = students.filter(s => !s.last_access);
        const studentsInCurrentScope = [...students]
          .sort((a, b) => String(a.student || '').localeCompare(String(b.student || ''), 'pt-BR', { sensitivity: 'base' }));

        if (filters.student_name) {
          students = filterAdminReportStudentsByName(students, filters.student_name);
        }

        setData({
          engagement,
          completion_rate,
          certificates,
          relation_students: Array.isArray(relationData?.students) ? relationData.students : [],
          all_students: studentsInCurrentScope,
          students: students
            .sort((a, b) => String(a.student || '').localeCompare(String(b.student || ''), 'pt-BR', { sensitivity: 'base' }))
        });
      } catch (err) {
        console.error("Failed to load reports:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, partners, companies, pontoFocalCompanies, reportClasses, profileRole, isPartnerScopedRole]);

  const engagementValues = data.engagement?.map(d => Number(d.count || 0)) || [];
  // Agora o back-end já retorna percentuais (0–100) por dia da semana somando 100%.
  // Usamos diretamente esses valores como altura das barras, com minHeight para exibir zeros.
  const normalizedEngagement = engagementValues.map(v => Math.max(0, Math.min(100, v)));
  
  const engagementLabels = data.engagement?.map(d => d.day_label) || [];
  const progressColumnLabel = 'PROGRESSO'
  const reportCompanyOptions = React.useMemo(
    () => [...(profileRole === 'Ponto Focal' ? pontoFocalCompanies : companies)]
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'pt-BR', { sensitivity: 'base' })),
    [profileRole, pontoFocalCompanies, companies],
  );
  const reportCourseOptions = React.useMemo(
    () => (courses || []).map((course) => ({ id: String(course?.id || ''), name: course?.name || '' })).filter((course) => course.id),
    [courses]
  );
  const reportPartnerOptions = React.useMemo(
    () => (partners || []).map((partnerOption) => ({ id: String(partnerOption?.id || ''), name: partnerOption?.name || '' })).filter((partnerOption) => partnerOption.id),
    [partners]
  );
  const reportStatusOptions = React.useMemo(() => ([
    { id: 'Concluído', name: 'Concluído' },
    { id: 'Em andamento', name: 'Em andamento' },
    { id: 'Programado', name: 'Programado' },
    { id: 'Não iniciado', name: 'Não iniciado' },
  ]), []);
  const reportParecerOptions = React.useMemo(() => ([
    { id: 'Aprovado', name: 'Aprovado' },
    { id: 'Reprovado', name: 'Reprovado' },
  ]), []);
  const reportEnrollmentStatusOptions = React.useMemo(() => ([
    { id: 'Programado', name: 'Programado' },
    { id: 'Ativa', name: 'Ativa' },
    { id: 'Inativada', name: 'Inativada' },
    { id: 'Expirada', name: 'Expirada' },
    { id: 'Cancelada', name: 'Cancelada' },
  ]), []);
  const relationReportStudents = React.useMemo(
    () => (Array.isArray(data.relation_students) ? data.relation_students : []),
    [data.relation_students]
  );
  const selectedCompanyIds = React.useMemo(
    () => (Array.isArray(filters.company_ids) ? filters.company_ids.map(String).filter(Boolean) : []),
    [filters.company_ids]
  );
  const selectedCourseIds = React.useMemo(
    () => (Array.isArray(filters.course_ids) ? filters.course_ids.map(String).filter(Boolean) : []),
    [filters.course_ids]
  );
  const selectedPartnerIds = React.useMemo(
    () => (Array.isArray(filters.partner_ids) ? filters.partner_ids.map(String).filter(Boolean) : []),
    [filters.partner_ids]
  );
  const companyNameById = React.useMemo(
    () => new Map(reportCompanyOptions.map((company) => [String(company?.id || ''), String(company?.name || '')])),
    [reportCompanyOptions]
  );
  const partnerNameById = React.useMemo(
    () => new Map(reportPartnerOptions.map((partnerOption) => [String(partnerOption?.id || ''), String(partnerOption?.name || '')])),
    [reportPartnerOptions]
  );
  const selectedCompanyNames = React.useMemo(
    () => new Set(selectedCompanyIds.map((id) => companyNameById.get(String(id)) || '').filter(Boolean)),
    [selectedCompanyIds, companyNameById]
  );
  const selectedPartnerNames = React.useMemo(
    () => new Set(selectedPartnerIds.map((id) => partnerNameById.get(String(id)) || '').filter(Boolean)),
    [selectedPartnerIds, partnerNameById]
  );
  const filteredCompanyOptions = React.useMemo(() => {
    if (relationReportStudents.length === 0) return reportCompanyOptions;
    const allowedNames = new Set(
      relationReportStudents
        .filter((student) =>
          (selectedPartnerNames.size === 0 || selectedPartnerNames.has(String(student?.partner || ''))) &&
          (selectedCourseIds.length === 0 || selectedCourseIds.includes(String(student?.report_course_id || '')))
        )
        .map((student) => String(student?.company || ''))
        .filter(Boolean)
    );
    return reportCompanyOptions.filter((company) => allowedNames.has(String(company?.name || '')));
  }, [relationReportStudents, reportCompanyOptions, selectedPartnerNames, selectedCourseIds]);
  const filteredCourseOptions = React.useMemo(() => {
    if (relationReportStudents.length === 0) return reportCourseOptions;
    const allowedIds = new Set(
      relationReportStudents
        .filter((student) =>
          (selectedPartnerNames.size === 0 || selectedPartnerNames.has(String(student?.partner || ''))) &&
          (selectedCompanyNames.size === 0 || selectedCompanyNames.has(String(student?.company || '')))
        )
        .map((student) => String(student?.report_course_id || ''))
        .filter(Boolean)
    );
    return reportCourseOptions.filter((course) => allowedIds.has(String(course?.id || '')));
  }, [relationReportStudents, reportCourseOptions, selectedPartnerNames, selectedCompanyNames]);
  const filteredPartnerOptions = React.useMemo(() => {
    if (relationReportStudents.length === 0) return reportPartnerOptions;
    const allowedNames = new Set(
      relationReportStudents
        .filter((student) =>
          (selectedCompanyNames.size === 0 || selectedCompanyNames.has(String(student?.company || ''))) &&
          (selectedCourseIds.length === 0 || selectedCourseIds.includes(String(student?.report_course_id || '')))
        )
        .map((student) => String(student?.partner || ''))
        .filter(Boolean)
    );
    return reportPartnerOptions.filter((partnerOption) => allowedNames.has(String(partnerOption?.name || '')));
  }, [relationReportStudents, reportPartnerOptions, selectedCompanyNames, selectedCourseIds]);
  const reportClassOptions = React.useMemo(
    () => [...reportClasses].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'pt-BR', { sensitivity: 'base' })),
    [reportClasses]
  );
  const filteredClassOptions = React.useMemo(
    () => reportClassOptions.filter((cls) => (
      (selectedCompanyIds.length === 0 || cls.companyIds.some((id) => selectedCompanyIds.includes(id))) &&
      (selectedPartnerIds.length === 0 || cls.partnerIds.some((id) => selectedPartnerIds.includes(id))) &&
      (selectedCourseIds.length === 0 || cls.courseIds.some((id) => selectedCourseIds.includes(id)))
    )),
    [reportClassOptions, selectedCompanyIds, selectedPartnerIds, selectedCourseIds]
  );
  useEffect(() => {
    const allowedCompanyIds = new Set(filteredCompanyOptions.map((company) => String(company?.id || '')).filter(Boolean));
    const allowedCourseIds = new Set(filteredCourseOptions.map((course) => String(course?.id || '')).filter(Boolean));
    const allowedPartnerIds = new Set(filteredPartnerOptions.map((partnerOption) => String(partnerOption?.id || '')).filter(Boolean));
    const allowedClassIds = new Set(filteredClassOptions.map((cls) => String(cls?.id || '')).filter(Boolean));
    setFilters((prev) => {
      const nextCompanyIds = (Array.isArray(prev.company_ids) ? prev.company_ids : []).map(String).filter((id) => allowedCompanyIds.has(id));
      const nextCourseIds = (Array.isArray(prev.course_ids) ? prev.course_ids : []).map(String).filter((id) => allowedCourseIds.has(id));
      const nextPartnerIds = (Array.isArray(prev.partner_ids) ? prev.partner_ids : []).map(String).filter((id) => allowedPartnerIds.has(id));
      const nextClassIds = reportClassOptions.length === 0
        ? (Array.isArray(prev.class_ids) ? prev.class_ids : [])
        : (Array.isArray(prev.class_ids) ? prev.class_ids : []).map(String).filter((id) => allowedClassIds.has(id));
      const changed =
        nextCompanyIds.length !== (Array.isArray(prev.company_ids) ? prev.company_ids.length : 0) ||
        nextCourseIds.length !== (Array.isArray(prev.course_ids) ? prev.course_ids.length : 0) ||
        nextPartnerIds.length !== (Array.isArray(prev.partner_ids) ? prev.partner_ids.length : 0) ||
        nextClassIds.length !== (Array.isArray(prev.class_ids) ? prev.class_ids.length : 0);
      if (!changed) return prev;
      return {
        ...prev,
        company_ids: nextCompanyIds,
        company_id: nextCompanyIds[0] || '',
        course_ids: nextCourseIds,
        course_id: nextCourseIds[0] || '',
        partner_ids: nextPartnerIds,
        partner_id: nextPartnerIds[0] || '',
        class_ids: nextClassIds,
        class_id: nextClassIds[0] || '',
      };
    });
  }, [filteredCompanyOptions, filteredCourseOptions, filteredPartnerOptions, filteredClassOptions, reportClassOptions]);
  const reportStudents = React.useMemo(() => {
    const rows = Array.isArray(data.students) ? data.students : [];
    if (selectedCompanyIds.length === 0) return rows;
    return rows.filter((student) => selectedCompanyNames.has(String(student?.company || '')));
  }, [data.students, selectedCompanyIds, selectedCompanyNames]);
  const reportStudentAutocompleteOptions = React.useMemo(
    () => buildAdminReportStudentAutocompleteOptions(data.all_students),
    [data.all_students]
  );
  const sortedReportStudents = React.useMemo(() => {
    const rows = Array.isArray(reportStudents) ? reportStudents : [];
    if (!reportSortConfig?.field || !reportSortConfig?.direction) return rows;
    const sortField = String(reportSortConfig?.field || 'student');
    const direction = reportSortConfig?.direction === 'desc' ? -1 : 1;

    const getSortValue = (student) => {
      switch (sortField) {
        case 'student':
          return String(student?.student || '');
        case 'company':
          return String(formatCompanyLabel(student?.company || ''));
        case 'course':
          return String(student?.report_course_name || student?.courses_enrolled || '');
        case 'partner':
          return String(shortOrgLabel(student?.partner || ''));
        case 'progress':
          return Number(student?.report_course_progress ?? (student?.progress_avg || 0));
        case 'status':
          return String(student?.status || '');
        case 'parecer':
          return String(student?.result || '');
        case 'score':
          return student?.score === '-' || student?.score == null ? Number.NEGATIVE_INFINITY : Number(student.score);
        case 'last_access':
          return student?.last_access ? new Date(student.last_access).getTime() : Number.NaN;
        case 'deadline':
          return student?.enrollment_deadline ? new Date(student.enrollment_deadline).getTime() : Number.NaN;
        case 'enrollment_status':
          return String(student?.enrollment_status_display || '');
        default:
          return String(student?.student || '');
      }
    };

    return rows
      .map((student, index) => ({ student, index, value: getSortValue(student) }))
      .sort((a, b) => {
        const aValue = a.value;
        const bValue = b.value;
        const aIsNumber = typeof aValue === 'number';
        const bIsNumber = typeof bValue === 'number';

        if (aIsNumber && bIsNumber) {
          const aValid = Number.isFinite(aValue);
          const bValid = Number.isFinite(bValue);
          if (aValid && bValid && aValue !== bValue) return (aValue - bValue) * direction;
          if (aValid !== bValid) return aValid ? -1 : 1;
          return a.index - b.index;
        }

        const compare = String(aValue || '').localeCompare(String(bValue || ''), 'pt-BR', { sensitivity: 'base' });
        if (compare !== 0) return compare * direction;
        return a.index - b.index;
      })
      .map(({ student }) => student);
  }, [reportStudents, reportSortConfig]);
  const reportTotalPages = Math.max(1, Math.ceil(sortedReportStudents.length / itemsPerPage));
  const paginatedReportStudents = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedReportStudents.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedReportStudents, currentPage, itemsPerPage]);
  const statusTotals = reportStudents.reduce((acc, student) => {
    const status = String(student?.status || 'Não iniciado');
    acc.total += 1;
    acc.progressSum += Number(student?.report_course_progress ?? (student?.progress_avg || 0));
    if (status === 'Concluído') acc.concluido += 1;
    else if (status === 'Em andamento') acc.emAndamento += 1;
    else if (status === 'Programado') acc.programado += 1;
    else acc.naoIniciado += 1;
    if (!student?.last_access) acc.semAcesso += 1;
    return acc;
  }, {
    total: 0,
    concluido: 0,
    emAndamento: 0,
    programado: 0,
    naoIniciado: 0,
    semAcesso: 0,
    progressSum: 0
  });
  const avgProgress = statusTotals.total > 0 ? Math.round(statusTotals.progressSum / statusTotals.total) : 0;
  const completionByFilter = statusTotals.total > 0 ? Math.round((statusTotals.concluido / statusTotals.total) * 100) : 0;
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, itemsPerPage, reportSortConfig]);
  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, reportTotalPages));
  }, [reportTotalPages]);
  const quickFilterIsActive = Boolean((Array.isArray(filters.statuses) && filters.statuses.length > 0) || filters.never_accessed);
  const applyQuickStatus = (status) => {
    const isSameQuickStatus = Array.isArray(filters.statuses) && filters.statuses.length === 1 && filters.statuses[0] === status;
    setFilters(prev => ({
      ...prev,
      status: isSameQuickStatus ? '' : status,
      statuses: isSameQuickStatus ? [] : [status],
      never_accessed: false
    }));
  };
  const toggleNeverAccessed = () => {
    setFilters(prev => ({
      ...prev,
      never_accessed: !prev.never_accessed,
      status: '',
      statuses: []
    }));
  };
  const clearQuickFilters = () => {
    setFilters(prev => ({ ...prev, status: '', statuses: [], never_accessed: false }));
  };
  const clearAllReportFilters = () => {
    setFilters({ ...defaultReportFilters });
  };
  const reportHasActiveFilters = React.useMemo(
    () => hasActiveReportFilters(filters, defaultReportFilters),
    [defaultReportFilters, filters]
  );
  const toggleReportSort = (field) => {
    setReportSortConfig((current) => {
      if (!current || current.field !== field) return { field, direction: 'asc' };
      if (current.direction === 'asc') return { field, direction: 'desc' };
      return null;
    });
  };
  const getReportSortButtonClassName = (field) => {
    const isActive = reportSortConfig?.field === field;
    return `inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
      isActive
        ? 'border-cyan-400/70 bg-cyan-400/12 text-cyan-300 shadow-[0_0_0_1px_rgba(34,211,238,0.08)]'
        : 'border-slate-700/80 text-slate-500 hover:border-slate-500 hover:text-slate-200'
    }`;
  };
  const getReportSortIndicator = (field) => {
    if (reportSortConfig?.field !== field) return <ArrowUpDown size={10} strokeWidth={2.1} />;
    if (reportSortConfig?.direction === 'asc') return <ChevronUp size={10} strokeWidth={2.4} />;
    if (reportSortConfig?.direction === 'desc') return <ChevronDown size={10} strokeWidth={2.4} />;
    return <ArrowUpDown size={10} strokeWidth={2.1} />;
  };
  const getReportSortTitle = (label, field) => {
    if (reportSortConfig?.field !== field) return `Classificar ${label} em ordem crescente`;
    if (reportSortConfig?.direction === 'asc') return `Classificar ${label} em ordem decrescente`;
    return `Remover ordenação de ${label}`;
  };
  const renderReportSortableHeader = (label, field, alignClass = '') => {
    const isCentered = alignClass.includes('justify-center');
    const sortButton = (
      <button
        type="button"
        onClick={() => toggleReportSort(field)}
        className={getReportSortButtonClassName(field)}
        title={getReportSortTitle(label, field)}
        aria-label={getReportSortTitle(label, field)}
      >
        {getReportSortIndicator(field)}
      </button>
    );
    const labelText = (
      <span className="shrink-0 whitespace-nowrap text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-100">
        {label}
      </span>
    );

    if (!isCentered) {
      return (
        <div className="flex w-full items-center gap-1.5">
          {labelText}
          {sortButton}
        </div>
      );
    }

    return (
      <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center">
        <span aria-hidden="true" />
        <span className="shrink-0 whitespace-nowrap text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-100">
          {label}
        </span>
        <span className="justify-self-end">{sortButton}</span>
      </div>
    );
  };
  const handleExportExcel = () => {
    if (!reportStudents || reportStudents.length === 0) {
      alert("Não há dados para exportar.");
      return;
    }

    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const formatTime = (value) => {
      const totalMs = Number(value || 0);
      const totalSeconds = Math.floor(totalMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const rowsHtml = reportStudents.map(student => `
      <tr>
        <td>${escapeHtml(student.student || '')}</td>
        <td>${escapeHtml(student.company || '-')}</td>
        <td>${escapeHtml(student.partner || '-')}</td>
        <td>${escapeHtml(student.report_course_name || student.courses_enrolled || '-')}</td>
        <td style="mso-number-format:'0'">${Math.round(Number(student.report_course_progress ?? (student.progress_avg || 0)))}</td>
        <td style="mso-number-format:'\\@'">${escapeHtml(formatTime(student.report_course_total_time_ms ?? (student.total_time_ms || 0)))}</td>
        <td>${escapeHtml(student.result || '')}</td>
        <td>${escapeHtml(student.score || '')}</td>
        <td>${escapeHtml(student.last_access ? new Date(student.last_access).toLocaleString('pt-BR') : 'Nunca acessou')}</td>
        <td>${escapeHtml(formatReportDeadline(student.enrollment_deadline))}</td>
        <td>${escapeHtml(student.enrollment_status_display || '')}</td>
        <td>${escapeHtml(student.status || '')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8" /></head><body>
      <table border="1">
        <thead><tr>
          <th>Aluno</th>
          <th>Empresa</th>
          <th>Parceiro</th>
          <th>Curso</th>
          <th>Progresso (%)</th>
          <th>TA</th>
          <th>Parecer</th>
          <th>Nota Final</th>
          <th>Acesso</th>
          <th>Prazo Final</th>
          <th>Matrícula</th>
          <th>Status</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </body></html>`;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `relatorio_detalhado_alunos_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  return (
  <div className="p-4 md:p-8 pb-20 md:pb-28 animate-fade-in">
    <div className="flex justify-between items-center mb-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Relatórios</h1>
        <p className="text-slate-400">Análise detalhada de desempenho e engajamento.</p>
      </div>

    </div>

    {isAdmin && (
      <div className="mb-6 border-b border-slate-800">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('geral')}
            className={`px-4 py-2 text-sm font-bold rounded-t-lg ${
              activeTab === 'geral'
                ? 'bg-slate-800 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            GERAL
          </button>

        </div>
      </div>
    )}

    {(!isAdmin || activeTab === 'geral') && (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
       <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-lg hover-glow-primary transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest" title="Engajamento aqui representa o percentual médio de logins por dia da semana (dados acumulados). A soma das barras totaliza 100%.">Distribuição Semanal de Engajamento (%)</h3>
            <button onClick={() => setShowEngagementHelp(v => !v)} className="text-xs text-slate-400 hover:text-white border border-slate-700 rounded px-2 py-1 transition hover-glow-primary">Saiba mais</button>
          </div>
          <div className="h-40 flex items-end gap-2 justify-between px-2" role="img" aria-label="Distribuição semanal de engajamento em porcentagem">
             {normalizedEngagement.length > 0 ? normalizedEngagement.map((h, i) => {
                // Se o valor for 0%, mantemos a cor base. Se tiver algum valor, deixamos a barra destacada na cor do parceiro
                const isZero = engagementValues[i] === 0;
                const bgColor = isZero ? 'bg-slate-800' : 'bg-primary-solid';
                
                return (
                  <div key={i} className={`w-full rounded-t transition-all relative group ${bgColor} hover:brightness-110`} style={{ height: `${h}%`, minHeight: '4px', boxShadow: !isZero ? `0 0 15px ${partner.color}33` : 'none' }}>
                     <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-950 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none border border-slate-800 whitespace-nowrap z-10 shadow-xl">
                       {engagementValues[i]}%
                     </div>
                  </div>
                )
             }) : <p className="text-slate-500 text-xs w-full text-center self-center">Sem dados</p>}
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500 font-mono">
             {engagementLabels.map((l, i) => <span key={i}>{l}</span>)}
          </div>
       </div>

       <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-lg hover-glow-primary transition-all duration-300">
          <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">Conclusão de Cursos</h3>
          <div className="flex items-center justify-center h-40 relative">
             <div className="w-32 h-32 rounded-full border-8 border-slate-800 rotate-45" style={{ borderTopColor: partner.color, borderRightColor: partner.color, filter: `drop-shadow(0 0 8px ${partner.color}4d)` }}></div>
             <div className="absolute inset-0 flex flex-col items-center justify-center">
               <span className="text-3xl font-black text-white" style={{ textShadow: `0 0 20px ${partner.color}4d` }}>{completionByFilter}%</span>
                <span className="text-xs text-slate-500 uppercase font-bold">Taxa Geral</span>
             </div>
          </div>
       </div>

       <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-lg hover-glow-primary transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest">Certificados Emitidos</h3>
            <span className="text-primary text-sm font-bold glow-primary px-2 py-0.5 rounded bg-primary-light">{(data.certificates||[]).reduce((a,c)=>a+Number(c.count||0),0)}</span>
          </div>
          <div className="flex flex-col gap-4">
             {data.certificates?.length > 0 ? data.certificates.map((cert, i) => (
                <div key={i} className={`flex justify-between items-center ${i < data.certificates.length - 1 ? 'border-b border-slate-800 pb-2' : ''}`}>
                   <span className="text-slate-300 text-sm truncate max-w-[200px]">{cert.course_title}</span>
                   <span className="text-primary font-bold">{cert.count}</span>
                </div>
             )) : <p className="text-slate-500 text-sm">Nenhum certificado emitido.</p>}
          </div>
       </div>
    </div>
    )}

    {showEngagementHelp && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60" onClick={() => setShowEngagementHelp(false)}></div>
        <div className="relative bg-slate-950 border border-slate-800 rounded-xl w-full max-w-xl mx-4 p-5 shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-bold">O que é Engajamento</h4>
            <button onClick={() => setShowEngagementHelp(false)} className="text-slate-400 hover:text-white">✕</button>
          </div>
          <div className="text-slate-300 text-sm space-y-3">
            <p>Este indicador calcula o engajamento exclusivamente pelo percentual médio de logins em cada dia da semana. Os dados são acumulados (histórico completo) e refletem a distribuição relativa de acessos.</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Cada barra representa a porcentagem de logins atribuída a aquele dia da semana.</li>
              <li>A soma dos dias totaliza 100% dentro do escopo selecionado (Empresa, Parceiro, Curso).</li>
              <li>Período não afeta este gráfico: sempre consideramos dados acumulados de login.</li>
            </ul>
          </div>
          <div className="mt-4 text-right">
            <button onClick={() => setShowEngagementHelp(false)} className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded">Fechar</button>
          </div>
        </div>
      </div>
    )}
    {(!isAdmin || activeTab === 'geral') && (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-6">
      <button
        type="button"
        onClick={clearQuickFilters}
        className={`text-left bg-slate-900 border rounded-xl p-4 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/40 hover:shadow-[0_0_22px_rgba(249,115,22,0.18)] ${!quickFilterIsActive ? 'border-primary/50 ring-1 ring-primary/30' : 'border-slate-800'}`}
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">TT Registros</p>
          <Users size={16} className="text-primary" />
        </div>
        <p className="text-3xl font-black text-white mt-2">{statusTotals.total}</p>
        <p className="text-[11px] text-slate-500 mt-1">Linhas filtradas no recorte atual</p>
      </button>
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-orange-400/40 hover:shadow-[0_0_22px_rgba(251,146,60,0.16)]">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">TT Por Status</p>
          <Filter size={15} className="text-orange-300" />
        </div>
        <div className="space-y-1">
          <button type="button" onClick={() => applyQuickStatus('Concluído')} className={`block text-xs transition ${Array.isArray(filters.statuses) && filters.statuses.includes('Concluído') ? 'text-teal-300 font-bold' : 'text-teal-400 hover:text-teal-300'}`}>Concluído: {statusTotals.concluido}</button>
          <button type="button" onClick={() => applyQuickStatus('Em andamento')} className={`block text-xs transition ${Array.isArray(filters.statuses) && filters.statuses.includes('Em andamento') ? 'text-orange-200 font-bold' : 'text-orange-300 hover:text-orange-200'}`}>Em andamento: {statusTotals.emAndamento}</button>
          <button type="button" onClick={() => applyQuickStatus('Programado')} className={`block text-xs transition ${Array.isArray(filters.statuses) && filters.statuses.includes('Programado') ? 'text-amber-200 font-bold' : 'text-amber-300 hover:text-amber-200'}`}>Programado: {statusTotals.programado}</button>
          <button type="button" onClick={() => applyQuickStatus('Não iniciado')} className={`block text-xs transition ${Array.isArray(filters.statuses) && filters.statuses.includes('Não iniciado') ? 'text-slate-200 font-bold' : 'text-slate-400 hover:text-slate-300'}`}>Não iniciado: {statusTotals.naoIniciado}</button>
        </div>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-cyan-400/40 hover:shadow-[0_0_22px_rgba(34,211,238,0.14)]">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Progresso Médio</p>
          <BarChart2 size={16} className="text-cyan-300" />
        </div>
        <p className="text-3xl font-black text-primary mt-2">{avgProgress}%</p>
        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden mt-2">
          <div className="h-full bg-primary-solid transition-all duration-500" style={{ width: `${Math.max(0, Math.min(100, avgProgress))}%` }}></div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">Média de progresso no período filtrado</p>
      </div>
      <button
        type="button"
        onClick={() => applyQuickStatus('Concluído')}
        className={`text-left bg-slate-900 border rounded-xl p-4 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-teal-400/40 hover:shadow-[0_0_22px_rgba(45,212,191,0.16)] ${Array.isArray(filters.statuses) && filters.statuses.includes('Concluído') ? 'border-teal-400/60 ring-1 ring-teal-400/30' : 'border-slate-800'}`}
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Taxa de Conclusão</p>
          <CheckCircle size={16} className="text-teal-400" />
        </div>
        <p className="text-3xl font-black text-teal-400 mt-2">{completionByFilter}%</p>
        <p className="text-[11px] text-slate-500 mt-1">Com base no total de registros filtrados</p>
      </button>
      <button
        type="button"
        onClick={toggleNeverAccessed}
        className={`text-left bg-slate-900 border rounded-xl p-4 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-rose-400/40 hover:shadow-[0_0_22px_rgba(251,113,133,0.16)] ${filters.never_accessed ? 'border-rose-400/60 ring-1 ring-rose-400/30' : 'border-slate-800'}`}
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Nunca Acessaram</p>
          <Lock size={16} className="text-rose-400" />
        </div>
        <p className="text-3xl font-black text-rose-400 mt-2">{statusTotals.semAcesso}</p>
        <p className="text-[11px] text-slate-500 mt-1">Sem registro de último acesso</p>
      </button>
    </div>
    )}
    {(!isAdmin || activeTab === 'geral') && (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
       <div className="p-6 border-b border-slate-800 flex flex-col gap-4">
          <div className="flex justify-between items-center">
             <h3 className="text-lg font-bold text-white">Relatório Detalhado de Alunos</h3>
             <div className="flex items-center gap-2">
               <span title={reportHasActiveFilters ? '' : 'Nenhum filtro aplicado'}>
                 <button
                   type="button"
                   onClick={clearAllReportFilters}
                   disabled={!reportHasActiveFilters}
                   className={`px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 ${
                     reportHasActiveFilters
                       ? 'bg-rose-600 border border-rose-500 text-white hover:bg-rose-500'
                       : 'bg-rose-950/25 border border-rose-900/40 text-rose-300/45 opacity-55 cursor-not-allowed'
                   }`}
                 >
                   <X size={16} /> Limpar filtros
                 </button>
               </span>
               <button onClick={handleExportExcel} className="bg-emerald-600/10 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-600/20 hover:border-emerald-500/50 transition flex items-center gap-2">
                  <Download size={16} /> Exportar Excel
               </button>
             </div>
          </div>
          <div className="flex flex-col gap-6">
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Aluno</p>
                 <ReportStudentAutocomplete
                   value={filters.student_name}
                   onChange={(studentName) => setFilters({ ...filters, student_name: studentName })}
                   options={reportStudentAutocompleteOptions}
                   placeholder="Buscar aluno por nome"
                 />
               </div>
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Período</p>
                 <div className="flex flex-col gap-2">
                   <select 
                     value={filters.period} 
                     onChange={e => setFilters({...filters, period: e.target.value})}
                     className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] hover:border-slate-700"
                   >
                     <option value="this_month">Período: Este Mês</option>
                     <option value="last_3_months">Últimos 3 Meses</option>
                     <option value="this_year">Este Ano</option>
                     <option value="all_time">Todo o Período</option>
                     {isAdmin && <option value="custom">Personalizado</option>}
                   </select>
                   {isAdmin && filters.period === 'custom' && (
                     <div className="flex gap-2">
                       <input 
                         type="date" 
                         value={filters.start_date}
                         onChange={e => setFilters({...filters, start_date: e.target.value})}
                         className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] hover:border-slate-700"
                         title="Data Inicial"
                       />
                       <input 
                         type="date" 
                         value={filters.end_date}
                         onChange={e => setFilters({...filters, end_date: e.target.value})}
                         className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] hover:border-slate-700"
                         title="Data Final"
                       />
                     </div>
                   )}
                 </div>
               </div>
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Empresa</p>
                 <ReportCompaniesMultiSelect
                  options={filteredCompanyOptions}
                   value={Array.isArray(filters.company_ids) ? filters.company_ids : []}
                   onChange={(next) => setFilters({ ...filters, company_ids: next, company_id: next[0] || '' })}
                   placeholder={profileRole === 'Ponto Focal' ? 'Todas as Empresas Vinculadas' : 'Todas as Empresas'}
                 />
               </div>
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Curso</p>
                <ReportMultiSelect
                  options={filteredCourseOptions}
                  value={Array.isArray(filters.course_ids) ? filters.course_ids : []}
                  onChange={(next) => setFilters({ ...filters, course_ids: next, course_id: next[0] || '' })}
                  placeholder="Todos os Cursos"
                  allLabel="Todos os Cursos"
                />
               </div>
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Turma</p>
                <ReportMultiSelect
                  options={filteredClassOptions}
                  value={Array.isArray(filters.class_ids) ? filters.class_ids : []}
                  onChange={(next) => setFilters({ ...filters, class_ids: next, class_id: next[0] || '' })}
                  placeholder="Todas as Turmas"
                  allLabel="Todas as Turmas"
                />
               </div>
               {!isPartnerScopedRole(profileRole) && profileRole !== 'Ponto Focal' && (
                 <div>
                   <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Parceiro</p>
                  <ReportMultiSelect
                    options={filteredPartnerOptions}
                    value={Array.isArray(filters.partner_ids) ? filters.partner_ids : []}
                    onChange={(next) => setFilters({ ...filters, partner_ids: next, partner_id: next[0] || '' })}
                    placeholder="Todos os Parceiros"
                    allLabel="Todos os Parceiros"
                  />
                 </div>
               )}
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Status do Curso</p>
                <ReportMultiSelect
                  options={reportStatusOptions}
                  value={Array.isArray(filters.statuses) ? filters.statuses : []}
                  onChange={(next) => setFilters({ ...filters, statuses: next, status: next[0] || '' })}
                  placeholder="Todos os Status"
                  allLabel="Todos os Status"
                />
               </div>
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Parecer</p>
                <ReportMultiSelect
                  options={reportParecerOptions}
                  value={Array.isArray(filters.pareceres) ? filters.pareceres : []}
                  onChange={(next) => setFilters({ ...filters, pareceres: next, parecer: next[0] || '' })}
                  placeholder="Todos os Pareceres"
                  allLabel="Todos os Pareceres"
                />
               </div>
               <div>
                 <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Matrícula</p>
                <ReportMultiSelect
                  options={reportEnrollmentStatusOptions}
                  value={Array.isArray(filters.enrollment_statuses) ? filters.enrollment_statuses : []}
                  onChange={(next) => setFilters({ ...filters, enrollment_statuses: next, enrollment_status: next[0] || '' })}
                  placeholder="Todas as Matrículas"
                  allLabel="Todas as Matrículas"
                />
               </div>
             </div>
          </div>
       </div>
      <table className="w-full table-fixed text-left">
          <thead className="bg-slate-950/90 text-slate-200 text-xs uppercase font-semibold shadow-[inset_0_-1px_0_rgba(51,65,85,0.55)]">
             <tr>
                <th className="px-3 py-3 w-[175px]">{renderReportSortableHeader('Aluno', 'student')}</th>
                <th className="px-3 py-3 w-[150px]">{renderReportSortableHeader('Empresa', 'company')}</th>
                <th className="px-3 py-3 w-[185px]">{renderReportSortableHeader('Curso', 'course')}</th>
                {!isPartnerScopedRole(profileRole) && <th className="px-3 py-3 w-[110px]">{renderReportSortableHeader('Parceiro', 'partner')}</th>}
                <th className="px-3 py-3 w-[115px]">{renderReportSortableHeader('Progr.', 'progress')}</th>
                <th className="px-3 py-3 w-[116px] text-center">{renderReportSortableHeader('Status', 'status', 'justify-center')}</th>
                <th className="px-3 py-3 w-[128px] text-center">{renderReportSortableHeader('Parecer', 'parecer', 'justify-center')}</th>
                <th className="px-3 py-3 w-[104px] text-center">{renderReportSortableHeader('Nota', 'score', 'justify-center')}</th>
                <th className="px-3 py-3 w-[120px] text-center">{renderReportSortableHeader('Acesso', 'last_access', 'justify-center')}</th>
                <th className="px-3 py-3 w-[112px] text-center">{renderReportSortableHeader('Prazo', 'deadline', 'justify-center')}</th>
                <th className="px-3 py-3 w-[112px] text-center">{renderReportSortableHeader('Matr.', 'enrollment_status', 'justify-center')}</th>
                <th className="px-3 py-3 w-[60px] text-center">Det.</th>
             </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50 text-sm">
             {loading ? (
                <tr><td colSpan={isPartnerScopedRole(profileRole) ? 11 : 12} className="p-8 text-center text-slate-500">Carregando dados...</td></tr>
            ) : reportStudents.length > 0 ? (
              paginatedReportStudents.map((student, i) => (
                <tr key={`${student.id || student.enrollment_id || `${student.student_id || 'student'}-${student.report_course_id || student.report_course_name || 'course'}-${i}`}`} className="hover:bg-slate-800/50 transition">
                  <td className="px-3 py-3 text-white font-medium">
                    <span className="truncate block max-w-[175px]" title={student.student || '-'}>
                      {student.student}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-400">
                    <span className="truncate block max-w-[150px]" title={student.company || '-'}>
                      {formatCompanyLabel(student.company)}
                    </span>
                  </td>
                   <td className="px-3 py-3 text-slate-300">
                      <span className="truncate block max-w-[185px]" title={student.report_course_name || student.courses_enrolled || '-'}>
                        {student.report_course_name || student.courses_enrolled || '-'}
                      </span>
                   </td>
                  {!isPartnerScopedRole(profileRole) && (
                    <td className="px-3 py-3 text-slate-400">
                      <span className="truncate block max-w-[110px]" title={student.partner || '-'}>
                        {shortOrgLabel(student.partner)}
                      </span>
                    </td>
                  )}
                   <td className="px-3 py-3">
                      {(() => {
                        const progressValue = Math.round(Number(student.report_course_progress ?? (student.progress_avg || 0)))
                        const { text: statusTextClass, bar: statusBarClass } = getReportStatusTone(student.status)

                        return (
                          <div className="flex items-center gap-2">
                             <div className="w-20 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div className={`h-full ${statusBarClass}`} style={{ width: `${progressValue}%` }}></div>
                             </div>
                             <span className={`text-xs ${statusTextClass}`}>{progressValue}%</span>
                          </div>
                        )
                      })()}
                   </td>
                   <td className="px-3 py-3 text-center">
                      {(() => {
                        const status = student.status || 'Indefinido'
                        const statusClass = getReportStatusTone(status).text

                        const iconProps = {
                          size: 16,
                          className: `${statusClass} transition`
                        }

                        const statusIcon = (() => {
                          if (status === 'Concluído') return <CheckCircle {...iconProps} />
                          if (status === 'Em andamento') return <Clock {...iconProps} />
                          if (status === 'Programado') return <PauseCircle {...iconProps} />
                          if (status === 'Não iniciado') return <Minus {...iconProps} />
                          if (status === 'Pendente') return <AlertCircle {...iconProps} />
                          return <HelpCircle {...iconProps} />
                        })()

                        return (
                          <span className="inline-flex items-center justify-center cursor-help" title={status} aria-label={status}>
                            {statusIcon}
                          </span>
                        )
                      })()}
                   </td>
                   <td className="px-3 py-3 text-center">
                      <span className={`text-xs font-bold ${student.result === 'Aprovado' ? 'text-teal-400' : 'text-slate-500'}`}>{student.result}</span>
                   </td>
                   <td className="px-3 py-3 text-white font-bold text-center">{student.score}</td>
                   <td className="px-3 py-3 text-slate-400 text-xs text-center">
                      {student.last_access ? (
                         <span
                           className="block leading-tight font-mono text-slate-300 cursor-help"
                           title={`Último acesso: ${new Date(student.last_access).toLocaleString('pt-BR')}`}
                         >
                           {new Date(student.last_access).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                         </span>
                      ) : (
                         <span
                           className="inline-flex items-center justify-center text-rose-400 bg-rose-500/10 px-2 py-1 rounded border border-rose-500/20 text-[10px] uppercase font-bold cursor-help"
                           title="Nunca acessou curso"
                         >
                           NA
                         </span>
                      )}
                   </td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap text-center">
                     <span className={`font-mono ${isReportDeadlineExpired(student.enrollment_deadline) ? 'text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-1 rounded font-bold' : 'text-slate-300'}`}>
                       {student.enrollment_deadline
                         ? new Date(student.enrollment_deadline).toLocaleDateString('pt-BR', {
                             timeZone: 'America/Sao_Paulo',
                             day: '2-digit',
                             month: '2-digit',
                             year: '2-digit',
                           })
                         : 'Sem prazo'}
                     </span>
                   </td>
                   <td className="px-3 py-3 text-xs text-center">
                     {(() => {
                       const enrollmentStatus = student.enrollment_status_display || ''
                       const enrollmentIconProps = (className) => ({ size: 16, className: `${className} transition` })

                       const enrollmentIcon = (() => {
                         if (enrollmentStatus === 'Ativa') return <UserCheck {...enrollmentIconProps('text-teal-400')} />
                         if (enrollmentStatus === 'Programado') return <CalendarClock {...enrollmentIconProps('text-amber-300')} />
                         if (enrollmentStatus === 'Pendente') return <Hourglass {...enrollmentIconProps('text-rose-300')} />
                         if (enrollmentStatus === 'Expirada') return <AlertTriangle {...enrollmentIconProps('text-amber-300')} />
                         if (enrollmentStatus === 'Inativada') return <CircleSlash {...enrollmentIconProps('text-slate-300')} />
                         if (enrollmentStatus === 'Cancelada') return <Ban {...enrollmentIconProps('text-rose-400')} />
                         return <Info {...enrollmentIconProps('text-slate-400')} />
                       })()

                       const enrollmentLabel = enrollmentStatus || 'Sem status de matrícula'
                       return (
                         <span className="inline-flex items-center justify-center cursor-help" title={enrollmentLabel} aria-label={enrollmentLabel}>
                           {enrollmentIcon}
                         </span>
                       )
                     })()}
                   </td>
                   <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => setSelectedStudentDetails(student)}
                        className="inline-flex items-center justify-center text-teal-400 hover:text-teal-300 transition"
                        title="Detalhes"
                        aria-label="Ver detalhes"
                      >
                        <Eye size={16} />
                      </button>
                   </td>
                </tr>
               ))
             ) : (
                <tr><td colSpan={isPartnerScopedRole(profileRole) ? 11 : 12} className="p-8 text-center text-slate-500">Nenhum aluno encontrado para os filtros selecionados.</td></tr>
             )}
          </tbody>
       </table>
       <div className="px-6 pb-6">
         <DataTablePagination
           totalItems={reportStudents.length}
           itemsPerPage={itemsPerPage}
           currentPage={currentPage}
           onItemsPerPageChange={setItemsPerPage}
           onPageChange={setCurrentPage}
           activePageClass="bg-primary text-white"
           selectFocusClass="focus:border-primary"
         />
       </div>
    </div>
    )}
    

    <StudentDetailsModal 
      isOpen={!!selectedStudentDetails} 
      student={selectedStudentDetails} 
      onClose={() => setSelectedStudentDetails(null)} 
    />
  </div>
)};

const formatCNPJ = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const match = digits.match(/^(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})$/);
  if (!match) return digits;
  return !match[2] ? match[1] 
       : !match[3] ? `${match[1]}.${match[2]}` 
       : !match[4] ? `${match[1]}.${match[2]}.${match[3]}` 
       : !match[5] ? `${match[1]}.${match[2]}.${match[3]}/${match[4]}` 
       : `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}`;
};

const validateCNPJ = (cnpj) => {
  const digits = String(cnpj).replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  let size = digits.length - 2;
  let numbers = digits.substring(0, size);
  const digitsPart = digits.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digitsPart.charAt(0))) return false;

  size += 1;
  numbers = digits.substring(0, size);
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digitsPart.charAt(1))) return false;

  return true;
};

const AdminCompanies = ({ partner, companies, setCompanies, role, auth }) => {
  const [activeTab, setActiveTab] = usePersistentTab('admin_companies_tabs', 'partner_companies');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [configuring, setConfiguring] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', cnpj: '', site: '' });
  const [filters, setFilters] = useState({ name: '', status: '', startDate: '', endDate: '', companyIds: [] });

  const [cycles, setCycles] = useState([]);
  const [loadingCycles, setLoadingCycles] = useState(false);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [cycleForm, setCycleForm] = useState({ name: '', days: '' });
  const [editingCycle, setEditingCycle] = useState(null);
  const canManageCycles = !isPartnerScopedRole(role);

  const canManageCatalog = !isPartnerScopedRole(role);
  const [catalogCompany, setCatalogCompany] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCompanies, setCatalogCompanies] = useState([]);
  const [loadingCatalogCompanies, setLoadingCatalogCompanies] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [cargoInput, setCargoInput] = useState('');
  const [areaInput, setAreaInput] = useState('');

  const normalizeCatalogText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const loadCatalogCompanies = async () => {
    setLoadingCatalogCompanies(true);
    try {
      let rows = [];
      if (isPartnerScopedRole(role) || role === 'Ponto Focal') {
        const { data, error } = await supabase.rpc('admin_list_companies_by_scope', {
          p_company_id: role === 'Ponto Focal' ? (auth?.profile?.companyId || null) : null,
          p_partner_id: isPartnerScopedRole(role) ? (auth?.profile?.sasPartnerId || partner?.id || null) : null,
        });
        if (error) throw error;
        rows = (data || []).map(r => ({ id: r.id, name: r.name, cnpj: r.cnpj }));
      } else {
        const { data, error } = await supabase
          .from('companies')
          .select('id, name, cnpj')
          .order('name', { ascending: true });
        if (error) throw error;
        rows = data || [];
      }

      const [cargosRes, areasRes] = await Promise.all([
        supabase.from('company_cargos').select('company_id'),
        supabase.from('company_areas').select('company_id'),
      ]);
      const cargosCount = {};
      const areasCount = {};
      for (const item of cargosRes.data || []) cargosCount[item.company_id] = (cargosCount[item.company_id] || 0) + 1;
      for (const item of areasRes.data || []) areasCount[item.company_id] = (areasCount[item.company_id] || 0) + 1;

      setCatalogCompanies(rows.map(r => ({
        ...r,
        cargosCount: cargosCount[r.id] || 0,
        areasCount: areasCount[r.id] || 0,
      })));
    } catch (err) {
      setNotification({ message: 'Erro ao carregar empresas.', type: 'error' });
      setCatalogCompanies([]);
    } finally {
      setLoadingCatalogCompanies(false);
    }
  };

  const filteredCatalogCompanies = React.useMemo(() => {
    const term = normalizeCatalogText(catalogSearch);
    const base = catalogCompanies
      .filter(c => (c.cargosCount || 0) + (c.areasCount || 0) > 0)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
    if (!term) return base;
    return base.filter(c => normalizeCatalogText(c.name).includes(term) || normalizeCatalogText(c.cnpj).includes(term));
  }, [catalogCompanies, catalogSearch]);

  const loadCatalogItems = async (companyId) => {
    if (!companyId) {
      setCargos([]);
      setAreas([]);
      return;
    }
    setLoadingCatalog(true);
    try {
      const [cargosRes, areasRes] = await Promise.all([
        supabase.from('company_cargos').select('*').eq('company_id', companyId).order('name', { ascending: true }),
        supabase.from('company_areas').select('*').eq('company_id', companyId).order('name', { ascending: true }),
      ]);
      if (cargosRes.error) throw cargosRes.error;
      if (areasRes.error) throw areasRes.error;
      setCargos(cargosRes.data || []);
      setAreas(areasRes.data || []);
    } catch (err) {
      setNotification({ message: 'Erro ao carregar cargos e áreas.', type: 'error' });
    } finally {
      setLoadingCatalog(false);
    }
  };

  const handleSelectCatalogCompany = (company) => {
    setCatalogCompany(company);
    setCargoInput('');
    setAreaInput('');
    loadCatalogItems(company.id);
  };

  const handleCloseCatalogModal = () => {
    setCatalogCompany(null);
    setCargos([]);
    setAreas([]);
    setCargoInput('');
    setAreaInput('');
    loadCatalogCompanies();
  };

  const parseCatalogInput = (text) => {
    const parts = String(text || '').split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const part of parts) {
      const key = part.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(part);
    }
    return out;
  };

  const exportCatalogXls = () => {
    if (!catalogCompany) return;
    try {
      const esc = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      const total = Math.max(cargos.length, areas.length);
      const rowsHtml = Array.from({ length: total }).map((_, i) => `<tr>
          <td>${esc(cargos[i]?.name || '')}</td>
          <td>${esc(areas[i]?.name || '')}</td>
        </tr>`).join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8" /></head><body>
        <table border="1">
          <thead><tr><th colspan="2">${esc(catalogCompany.name)}</th></tr>
          <tr><th>Cargo</th><th>Área</th></tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body></html>`;
      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = String(catalogCompany.name || 'empresa').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
      a.href = url; a.download = `cargos_areas_${slug}_${new Date().toISOString().split('T')[0]}.xls`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { alert('Erro ao exportar cargos e áreas.'); }
  };

  const handleSaveCatalog = async (kind) => {
    if (!catalogCompany) return;
    if (!canManageCatalog) {
      setNotification({ message: 'Seu perfil possui acesso somente para visualização.', type: 'error' });
      return;
    }
    const table = kind === 'cargo' ? 'company_cargos' : 'company_areas';
    const label = kind === 'cargo' ? 'cargo' : 'área';
    const names = parseCatalogInput(kind === 'cargo' ? cargoInput : areaInput);
    if (names.length === 0) {
      setNotification({ message: `Cole ao menos um ${label} para adicionar.`, type: 'error' });
      return;
    }
    try {
      const rows = names.map(name => ({ company_id: catalogCompany.id, name }));
      const { error } = await supabase.from(table).upsert(rows, { onConflict: 'company_id,name', ignoreDuplicates: true });
      if (error) throw error;
      if (kind === 'cargo') setCargoInput(''); else setAreaInput('');
      setNotification({ message: `${names.length} ${label}(s) adicionado(s).`, type: 'success' });
      await loadCatalogItems(catalogCompany.id);
    } catch (err) {
      setNotification({ message: `Erro ao adicionar ${label}(s).`, type: 'error' });
    }
  };

  const handleDeleteCatalogItem = async (kind, id) => {
    if (!canManageCatalog) {
      setNotification({ message: 'Seu perfil possui acesso somente para visualização.', type: 'error' });
      return;
    }
    const table = kind === 'cargo' ? 'company_cargos' : 'company_areas';
    try {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      await loadCatalogItems(catalogCompany?.id);
    } catch (err) {
      setNotification({ message: 'Erro ao remover item.', type: 'error' });
    }
  };

  const reloadCycles = async () => {
    setLoadingCycles(true);
    try {
      if (isPartnerScopedRole(role) || role === 'Ponto Focal') {
        const { data, error } = await supabase.rpc('admin_list_cycles_by_scope', {
          p_company_id: role === 'Ponto Focal' ? (auth?.profile?.companyId || null) : null,
          p_partner_id: isPartnerScopedRole(role) ? (auth?.profile?.sasPartnerId || partner?.id || null) : null,
        });
        if (!error) {
          setCycles(data || []);
          return;
        }
      }

      const { data, error } = await supabase.from('company_cycles').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setCycles(data || []);
    } catch (err) {
      setNotification({ message: 'Erro ao carregar ciclos.', type: 'error' });
    } finally {
      setLoadingCycles(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'cycles') {
      reloadCycles();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'cargos_areas') {
      loadCatalogCompanies();
    }
  }, [activeTab, role]);

  const handleSaveCycle = async (e) => {
    e.preventDefault();
    if (!canManageCycles) {
      setNotification({ message: 'Seu perfil possui acesso somente para visualização de ciclos.', type: 'error' });
      return;
    }
    try {
      if (editingCycle?.id) {
        const { error } = await supabase.from('company_cycles').update({
          name: cycleForm.name,
          days: parseInt(cycleForm.days, 10)
        }).eq('id', editingCycle.id);
        if (error) throw error;
        setNotification({ message: 'Ciclo atualizado com sucesso!', type: 'success' });
      } else {
        const { error } = await supabase.from('company_cycles').insert({
          name: cycleForm.name,
          days: parseInt(cycleForm.days, 10)
        });
        if (error) throw error;
        setNotification({ message: 'Ciclo criado com sucesso!', type: 'success' });
      }
      setShowCycleModal(false);
      setEditingCycle(null);
      setCycleForm({ name: '', days: '' });
      reloadCycles();
    } catch (err) {
      setNotification({ message: 'Erro ao salvar ciclo.', type: 'error' });
    }
  };

  const handleDeleteCycle = async (id) => {
    if (!canManageCycles) {
      setNotification({ message: 'Seu perfil possui acesso somente para visualização de ciclos.', type: 'error' });
      return;
    }
    if (!window.confirm('Tem certeza que deseja excluir este ciclo?')) return;
    try {
      const { error } = await supabase.from('company_cycles').delete().eq('id', id);
      if (error) throw error;
      setNotification({ message: 'Ciclo excluído com sucesso!', type: 'success' });
      reloadCycles();
    } catch (err) {
      setNotification({ message: 'Erro ao excluir ciclo.', type: 'error' });
    }
  };


  const CompaniesMultiSelect = ({ options = [], value = [], onChange, placeholder = 'Selecionar empresas' }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      const handler = (e) => { if (!ref.current) return; if (!ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);
    const toggle = (id) => {
      const s = new Set((value || []).map(String));
      if (s.has(String(id))) s.delete(String(id));
      else s.add(String(id));
      onChange(Array.from(s));
    };
    const clearOne = (id) => onChange((value || []).filter(v => String(v) !== String(id)));
    const chips = options.filter(o => (value || []).map(String).includes(String(o.id)));
    return (
      <div ref={ref} className="relative">
        <button type="button" onClick={() => setOpen(v => !v)} className="min-w-[220px] bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] flex items-center gap-2 flex-wrap">
          {chips.length === 0 ? <span className="text-slate-500 text-xs">{placeholder}</span> : chips.map(c => (
            <span key={c.id} className="flex items-center gap-1 bg-slate-800 border border-slate-700 px-2 py-0.5 rounded text-[10px]">{c.name}<span onClick={(e)=>{e.stopPropagation(); clearOne(c.id);}} className="cursor-pointer text-slate-400">×</span></span>
          ))}
          <span className="ml-auto text-slate-500 text-[10px]">{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div className="absolute mt-1 w-full max-h-48 overflow-auto bg-slate-950 border border-slate-800 rounded shadow-lg z-50">
            {options.map(opt => (
              <label key={opt.id} className="flex items-center gap-2 px-3 py-2 text-xs text-slate-200 hover:bg-slate-800 cursor-pointer">
                <input type="checkbox" className="accent-orange-500" checked={(value || []).map(String).includes(String(opt.id))} onChange={() => toggle(opt.id)} />
                <span>{opt.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const CatalogPanel = ({ title, subtitle, items, loading, value, onValueChange, onSave, onRemove, placeholder, canManage, partnerColor, testId }) => (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl flex flex-col">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{title}</h2>
            <p className="text-sm text-slate-400">{subtitle}</p>
          </div>
          <span className="text-xs font-bold text-slate-500 uppercase whitespace-nowrap">{items.length} {items.length === 1 ? 'item' : 'itens'}</span>
        </div>
      </div>

      {canManage && (
        <div className="p-6 border-b border-slate-800 flex flex-col gap-3">
          <label className="text-xs font-bold text-slate-500 uppercase">Colar lista</label>
          <textarea
            rows={4}
            value={value}
            onChange={e => onValueChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testId ? `${testId}-input` : undefined}
            className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white text-sm outline-none focus:border-white transition resize-y"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">Um item por linha (ou separados por vírgula).</span>
            <button
              type="button"
              onClick={onSave}
              data-testid={testId ? `${testId}-add` : undefined}
              style={{ backgroundColor: partnerColor }}
              className="text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition text-sm"
            >
              <Plus size={18} /> Adicionar
            </button>
          </div>
        </div>
      )}

      <div className="p-6 flex-1">
        {loading ? (
          <p className="text-slate-500 text-sm">Carregando...</p>
        ) : items.length === 0 ? (
          <p className="text-slate-500 text-sm">Nenhum item cadastrado para esta empresa.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map(item => (
              <span key={item.id} className="flex items-center gap-2 bg-slate-800 border border-slate-700 px-3 py-1.5 rounded-lg text-sm text-slate-200">
                {item.name}
                {canManage && (
                  <button type="button" onClick={() => onRemove(item)} title="Remover" className="text-slate-500 hover:text-rose-400 transition">
                    <X size={14} />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const reloadCompanies = async () => {
    setLoading(true);
    try {
      if (isPartnerScopedRole(role) || role === 'Ponto Focal') {
        const { data: scopedCompanies, error: scopedError } = await supabase.rpc('admin_list_companies_by_scope', {
          p_company_id: role === 'Ponto Focal' ? (auth?.profile?.companyId || null) : null,
          p_partner_id: isPartnerScopedRole(role) ? (auth?.profile?.sasPartnerId || partner?.id || null) : null,
        });

        if (!scopedError) {
          let rows = Array.isArray(scopedCompanies) ? scopedCompanies : [];
          const name = String(filters.name || '').trim().toLowerCase();
          if (name) rows = rows.filter((r) => String(r.name || '').toLowerCase().includes(name));
          if (filters.status) rows = rows.filter((r) => String(r.status || '') === filters.status);
          if (Array.isArray(filters.companyIds) && filters.companyIds.length > 0) {
            const selected = new Set(filters.companyIds.map(String));
            rows = rows.filter((r) => selected.has(String(r.id)));
          }
          if (filters.startDate) {
            const from = Date.parse(`${filters.startDate}T00:00:00.000Z`);
            rows = rows.filter((r) => Date.parse(String(r.created_at || '')) >= from);
          }
          if (filters.endDate) {
            const to = Date.parse(`${filters.endDate}T23:59:59.999Z`);
            rows = rows.filter((r) => Date.parse(String(r.created_at || '')) <= to);
          }

          setCompanies(
            rows.map((c) => ({
              id: c.id,
              name: c.name,
              cnpj: c.cnpj,
              site: c.site,
              status: c.status,
              createdAt: c.created_at,
              users: Number(c.users_count || 0),
              courses: Number(c.courses_count || 0),
            }))
          );
          return;
        }
      }

      let query = supabase
        .from('companies')
        .select('id, name, cnpj, site, status, created_at')
        .order('created_at', { ascending: false });

      if (isPartnerScopedRole(role)) {
        const partnerId = auth?.profile?.sasPartnerId || partner?.id;
        
        if (partnerId) {
          const [{ data: pData }, { data: cData }] = await Promise.all([
            supabase.from('profiles').select('company_id').eq('sas_partner_id', partnerId).not('company_id', 'is', null),
            supabase.from('classes').select('company_id').eq('partner_id', partnerId).not('company_id', 'is', null)
          ]);
            
          const compIdsFromProfiles = pData ? pData.map(p => p.company_id) : [];
          const compIdsFromClasses = cData ? cData.map(c => c.company_id) : [];
          const allowedCompanyIds = [...new Set([...compIdsFromProfiles, ...compIdsFromClasses])];
          
          if (allowedCompanyIds.length > 0) {
            query = query.in('id', allowedCompanyIds);
          } else {
            setCompanies([]);
            setLoading(false);
            return;
          }
        } else {
          setCompanies([]);
          setLoading(false);
          return;
        }
      }

      const name = String(filters.name || '').trim();
      if (name) query = query.ilike('name', `%${name}%`);
      if (filters.status) query = query.eq('status', filters.status);
      if (Array.isArray(filters.companyIds) && filters.companyIds.length > 0) query = query.in('id', filters.companyIds);
      if (filters.startDate) query = query.gte('created_at', `${filters.startDate}T00:00:00.000Z`);
      if (filters.endDate) query = query.lte('created_at', `${filters.endDate}T23:59:59.999Z`);

      const { data, error } = await query;
      if (error) throw error;

      const rows = Array.isArray(data) ? data : [];
      const withCounts = await Promise.all(
        rows.map(async (row) => {
          try {
            const [usersRes, classesRes] = await Promise.all([
              supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('company_id', row.id),
              supabase
                .from('classes')
                .select('id, class_courses(course_id)')
                .eq('company_id', row.id)
            ]);
            const usersCount = usersRes?.count || 0;
            const courseIds = new Set();
            for (const cl of classesRes?.data || []) {
              const links = cl?.class_courses || [];
              for (const lk of links) if (lk?.course_id != null) courseIds.add(String(lk.course_id));
            }
            return { ...row, users_count: usersCount, courses_count: courseIds.size };
          } catch {
            return { ...row, users_count: 0, courses_count: 0 };
          }
        })
      );

      setCompanies(
        withCounts.map((c) => ({
          id: c.id,
          name: c.name,
          cnpj: c.cnpj,
          site: c.site,
          status: c.status,
          createdAt: c.created_at,
          users: c.users_count || 0,
          courses: c.courses_count || 0,
        }))
      );
    } catch (err) {
      setCompanies([]);
      setNotification({ message: String(err?.message || err || 'Falha ao carregar empresas.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadCompanies();
  }, [filters.name, filters.status, filters.startDate, filters.endDate, filters.companyIds]);

  const exportCompaniesXls = () => {
    try {
      const rowsHtml = (companies || []).map((c) => {
        const cnpjDigits = String(c.cnpj || '').replace(/\D/g, '');
        const created = c.createdAt ? new Date(c.createdAt).toLocaleDateString('pt-BR') : '';
        return `<tr>
          <td>${(c.name || '').replace(/&/g,'&amp;').replace(/</g,'&lt;')}</td>
          <td style="mso-number-format:'0';">${cnpjDigits}</td>
          <td>${created}</td>
          <td style="mso-number-format:'0';">${Number(c.users || 0)}</td>
          <td style="mso-number-format:'0';">${Number(c.courses || 0)}</td>
          <td>${c.status || ''}</td>
        </tr>`;
      }).join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8" /></head><body>
        <table border="1">
          <thead><tr>
            <th>Organização</th><th>CNPJ</th><th>Criado em</th><th>Usuários</th><th>Cursos</th><th>Status</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body></html>`;
      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `empresas_${new Date().toISOString().split('T')[0]}.xls`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { alert('Erro ao exportar planilha de empresas.'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const cnpjToValidate = editing ? editing.cnpj : form.cnpj;
    if (!validateCNPJ(cnpjToValidate)) {
      setNotification({ message: 'CNPJ inválido. Verifique os números digitados.', type: 'error' });
      return;
    }

    try {
      if (editing?.id) {
        const payload = {
          name: String(editing.name || '').trim(),
          cnpj: String(editing.cnpj || '').trim(),
          site: String(editing.site || '').trim() || null,
          status: editing.status || 'Ativo',
        };
        const { error } = await supabase.from('companies').update(payload).eq('id', editing.id);
        if (error) throw error;
        setNotification({ message: 'Empresa atualizada com sucesso!', type: 'success' });
        setEditing(null);
      } else {
        const payload = {
          name: String(form.name || '').trim(),
          cnpj: String(form.cnpj || '').trim(),
          site: String(form.site || '').trim() || null,
          status: 'Ativo',
        };
        const { error } = await supabase.from('companies').insert(payload);
        if (error) throw error;
        setNotification({ message: 'Empresa criada com sucesso!', type: 'success' });
        setShowModal(false);
        setForm({ name: '', cnpj: '', site: '' });
      }

      await reloadCompanies();
    } catch (err) {
      setNotification({ message: String(err?.message || err || 'Falha ao salvar empresa.'), type: 'error' });
    }
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div><h1 className="text-3xl font-bold text-white mb-2">Gestão B2B</h1><p className="text-slate-400">Administração de empresas da plataforma {partner.name}.</p></div>
      </div>

      {activeTab === 'partner_companies' && (
        <div className="mb-6 max-w-[160px]">
          <SummaryCard
            label="Empresas"
            value={loading ? '...' : (companies || []).length}
            accentClass="text-sky-300"
            testId="companies-summary-total"
          />
        </div>
      )}

      <div className="flex gap-6 mb-8 border-b border-slate-800">
        <button onClick={() => setActiveTab('partner_companies')} className={`pb-4 px-2 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeTab === 'partner_companies' ? 'text-white border-white' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
          Empresas Parceiras
        </button>
        <button onClick={() => setActiveTab('cycles')} className={`pb-4 px-2 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeTab === 'cycles' ? 'text-white border-white' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
          Ciclos
        </button>
        <button onClick={() => setActiveTab('cargos_areas')} className={`pb-4 px-2 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeTab === 'cargos_areas' ? 'text-white border-white' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
          Cargos &amp; Áreas
        </button>
        <button onClick={() => setActiveTab('core_companies')} className={`pb-4 px-2 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeTab === 'core_companies' ? 'text-white border-white' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
          Empresas Core
        </button>
      </div>

      {activeTab === 'partner_companies' && (
        <>
          <div className="flex gap-4 mb-6 bg-slate-900 p-4 rounded-xl border border-slate-800 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Nome</label>
              <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                <Search size={16} className="text-slate-500 mr-2" />
                <input 
                  type="text" 
                  placeholder="Buscar por nome..." 
                  value={filters.name}
                  onChange={e => setFilters({...filters, name: e.target.value})}
                  className="bg-transparent text-white outline-none text-sm w-48"
                />
              </div>
            </div>

            {isPartnerScopedRole(role) && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Empresas</label>
                <CompaniesMultiSelect
                  options={(companies || []).map(c => ({ id: c.id, name: c.name }))}
                  value={filters.companyIds}
                  onChange={(vals) => setFilters({ ...filters, companyIds: vals })}
                />
              </div>
            )}

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Status</label>
              <select 
                value={filters.status} 
                onChange={e => setFilters({...filters, status: e.target.value})}
                className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-32"
              >
                <option value="">Todos</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Período (Início)</label>
              <input 
                type="date" 
                value={filters.startDate}
                onChange={e => setFilters({...filters, startDate: e.target.value})}
                className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Período (Fim)</label>
              <input 
                type="date" 
                value={filters.endDate}
                onChange={e => setFilters({...filters, endDate: e.target.value})}
                className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px]"
              />
            </div>

            <div className="flex items-end pb-0.5">
              <button 
                onClick={() => setFilters({ name: '', status: '', startDate: '', endDate: '', companyIds: [] })}
                className="text-slate-400 hover:text-white text-sm font-medium px-3 py-2 transition"
              >
                Limpar Filtros
              </button>
            </div>

            <div className="flex items-end ml-auto pb-0.5">
              {isPartnerScopedRole(role) && (
                <button onClick={exportCompaniesXls} className="bg-slate-800 text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 border border-slate-700 hover:bg-slate-700 transition text-sm h-[38px]">
                  <FileSpreadsheet size={16} /> Exportar Excel
                </button>
              )}
              {!isPartnerScopedRole(role) && (
                <button onClick={() => setShowModal(true)} style={{ backgroundColor: partner.color }} className="text-white px-5 py-2 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition text-sm h-[38px]">
                  <Plus size={18} /> Adicionar Empresa
                </button>
              )}
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
                <tr><th className="p-5">Organização</th><th className="p-5">Criado em</th><th className="p-5">Usuários</th><th className="p-5">Cursos</th><th className="p-5">Status</th>{!isPartnerScopedRole(role) && <th className="p-5 text-right">Gerenciar</th>}</tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {companies.map(c => (
                  <tr key={c.id} className="hover:bg-slate-800/50 transition text-slate-300">
                    <td className="p-5"><div className="font-bold text-white">{c.name}</div><div className="text-xs font-mono opacity-50">{c.cnpj}</div></td>
                    <td className="p-5 text-sm text-slate-400">{c.createdAt ? new Date(c.createdAt).toLocaleDateString('pt-BR') : '-'}</td>
                    <td className="p-5 font-mono">{c.users}</td><td className="p-5 font-mono">{c.courses}</td>
                    <td className="p-5"><span className={`px-2 py-1 rounded text-[10px] font-bold border uppercase ${c.status === 'Ativo' ? 'border-teal-500/20 bg-teal-500/10 text-teal-400' : 'border-rose-500/20 bg-rose-500/10 text-rose-400'}`}>{c.status}</span></td>
                    {!isPartnerScopedRole(role) && (
                      <td className="p-5 text-right flex justify-end gap-3 text-slate-500">
                        <Edit2 size={18} className="cursor-pointer hover:text-white" onClick={() => setEditing(c)} />
                        <Settings size={18} className="cursor-pointer hover:text-white" onClick={() => setConfiguring(c)} />
                      </td>
                    )}
                  </tr>
                ))}
                {loading && (
                  <tr>
                    <td colSpan={isPartnerScopedRole(role) ? "5" : "6"} className="p-8 text-center text-slate-500">
                      Carregando...
                    </td>
                  </tr>
                )}
                {!loading && companies.length === 0 && (
                  <tr>
                    <td colSpan={isPartnerScopedRole(role) ? "5" : "6"} className="p-8 text-center text-slate-500">
                      Nenhuma empresa encontrada com os filtros selecionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'cycles' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
          <div className="p-6 border-b border-slate-800 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Ciclos</h2>
              <p className="text-sm text-slate-400">Gerenciamento de ciclos das empresas.</p>
            </div>
            {canManageCycles && (
              <button
                onClick={() => { setEditingCycle(null); setCycleForm({ name: '', days: '' }); setShowCycleModal(true); }}
                style={{ backgroundColor: partner.color }}
                className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition text-sm"
              >
                <Plus size={18} /> +Ciclo
              </button>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
                <tr>
                  <th className="p-5">Nome do Ciclo</th>
                  <th className="p-5">Dias</th>
                  <th className="p-5">Criado em</th>
                  {canManageCycles && <th className="p-5 text-right">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {loadingCycles ? (
                  <tr>
                    <td colSpan={canManageCycles ? 4 : 3} className="p-8 text-center text-slate-500">Carregando ciclos...</td>
                  </tr>
                ) : cycles.length === 0 ? (
                  <tr>
                    <td colSpan={canManageCycles ? 4 : 3} className="p-8 text-center text-slate-500">Nenhum ciclo encontrado.</td>
                  </tr>
                ) : (
                  cycles.map(cycle => (
                    <tr key={cycle.id} className="hover:bg-slate-800/50 transition text-slate-300">
                      <td className="p-5 font-bold text-white">{cycle.name}</td>
                      <td className="p-5">{cycle.days} dias</td>
                      <td className="p-5 text-sm text-slate-400">{new Date(cycle.created_at).toLocaleDateString('pt-BR')}</td>
                      {canManageCycles && (
                        <td className="p-5 text-right flex justify-end gap-3 text-slate-500">
                          <Edit2
                            size={18}
                            className="cursor-pointer hover:text-white"
                            onClick={() => {
                              setEditingCycle(cycle);
                              setCycleForm({ name: cycle.name, days: String(cycle.days) });
                              setShowCycleModal(true);
                            }}
                          />
                          <Trash2
                            size={18}
                            className="cursor-pointer hover:text-rose-400 transition-colors"
                            onClick={() => handleDeleteCycle(cycle.id)}
                          />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'cargos_areas' && (
        <div className="flex flex-col gap-6">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="w-full sm:w-96">
                <label className="text-xs font-bold text-slate-500 uppercase">Buscar Empresa</label>
                <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 mt-2">
                  <Search size={16} className="text-slate-500 mr-2" />
                  <input
                    type="text"
                    placeholder="Digite o nome ou CNPJ da empresa..."
                    value={catalogSearch}
                    onChange={e => setCatalogSearch(e.target.value)}
                    className="bg-transparent text-white outline-none text-sm w-full"
                  />
                  {catalogSearch && (
                    <button type="button" onClick={() => setCatalogSearch('')} className="text-slate-500 hover:text-white">
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
              <span className="text-xs font-bold text-slate-500 uppercase whitespace-nowrap sm:mt-7">
                {filteredCatalogCompanies.length} {filteredCatalogCompanies.length === 1 ? 'empresa' : 'empresas'}
              </span>
            </div>
          </div>

          {loadingCatalogCompanies ? (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center shadow-xl">
              <Loader2 size={28} className="mx-auto text-slate-600 animate-spin mb-3" />
              <p className="text-slate-400 text-sm">Carregando empresas...</p>
            </div>
          ) : filteredCatalogCompanies.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center shadow-xl">
              <Building size={32} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-400 text-sm">{catalogSearch.trim() ? 'Nenhuma empresa encontrada para a busca.' : 'Nenhuma empresa com cargos ou áreas cadastrados.'}</p>
            </div>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
              <div className="divide-y divide-slate-800/50">
                {filteredCatalogCompanies.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleSelectCatalogCompany(c)}
                    data-testid={`catalog-company-${c.id}`}
                    className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-800/50 transition"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                      <Building size={16} className="text-slate-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-white truncate">{c.name}</div>
                      {c.cnpj && <div className="text-xs font-mono text-slate-500">{c.cnpj}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold uppercase px-2 py-1 rounded border border-slate-700 bg-slate-800 text-slate-300">{c.cargosCount} cargos</span>
                      <span className="text-[10px] font-bold uppercase px-2 py-1 rounded border border-slate-700 bg-slate-800 text-slate-300">{c.areasCount} áreas</span>
                    </div>
                    <ChevronRight size={18} className="text-slate-600 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'core_companies' && (
        <CoreBaseTab entities={['companies', 'productLinks']} />
      )}

      {catalogCompany && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-800 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-xl font-bold text-white truncate">{catalogCompany.name}</h3>
                {catalogCompany.cnpj && <p className="text-[11px] font-mono text-slate-500 mt-1">{catalogCompany.cnpj}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={exportCatalogXls}
                  data-testid="catalog-export-xls"
                  className="bg-slate-800 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 border border-slate-700 hover:bg-slate-700 transition text-sm"
                >
                  <Download size={16} /> Exportar Excel
                </button>
                <button type="button" onClick={handleCloseCatalogModal} className="text-slate-500 hover:text-white transition">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CatalogPanel
                title="Cargos"
                subtitle="Cargos vinculados à empresa selecionada."
                items={cargos}
                loading={loadingCatalog}
                value={cargoInput}
                onValueChange={setCargoInput}
                onSave={() => handleSaveCatalog('cargo')}
                onRemove={(item) => handleDeleteCatalogItem('cargo', item.id)}
                placeholder={'Cole aqui a lista de cargos...\nEx:\nAnalista de RH\nCoordenador de Produção'}
                canManage={canManageCatalog}
                partnerColor={partner.color}
                testId="catalog-cargos"
              />
              <CatalogPanel
                title="Áreas"
                subtitle="Áreas vinculadas à empresa selecionada."
                items={areas}
                loading={loadingCatalog}
                value={areaInput}
                onValueChange={setAreaInput}
                onSave={() => handleSaveCatalog('area')}
                onRemove={(item) => handleDeleteCatalogItem('area', item.id)}
                placeholder={'Cole aqui a lista de áreas...\nEx:\nRecursos Humanos\nProdução'}
                canManage={canManageCatalog}
                partnerColor={partner.color}
                testId="catalog-areas"
              />
            </div>
          </div>
        </div>
      )}

      {showCycleModal && canManageCycles && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6">{editingCycle ? 'Editar Ciclo' : 'Novo Ciclo'}</h3>
            <form onSubmit={handleSaveCycle} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Nome do Ciclo</label>
                <input 
                  type="text" 
                  placeholder="Ex: Ciclo 1" 
                  required 
                  value={cycleForm.name} 
                  onChange={e => setCycleForm({...cycleForm, name: e.target.value})} 
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" 
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Quantidade de Dias</label>
                <input 
                  type="number" 
                  min="1"
                  placeholder="Ex: 30" 
                  required 
                  value={cycleForm.days} 
                  onChange={e => setCycleForm({...cycleForm, days: e.target.value})} 
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" 
                />
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button type="button" onClick={() => { setShowCycleModal(false); setEditingCycle(null); }} className="px-4 py-2 text-slate-400 font-bold">Cancelar</button>
                <button type="submit" style={{ backgroundColor: partner.color }} className="px-8 py-2 rounded-lg text-white font-bold shadow-lg">Salvar Ciclo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {(showModal || editing) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6">{editing ? 'Editar Empresa' : 'Nova Empresa'}</h3>
            <form onSubmit={handleSave} className="flex flex-col gap-4">
              <input type="text" placeholder="Nome da Empresa" required value={editing ? editing.name : form.name} onChange={e => editing ? setEditing({...editing, name: e.target.value}) : setForm({...form, name: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" />
              <input type="text" placeholder="CNPJ" required maxLength={18} value={editing ? formatCNPJ(editing.cnpj) : formatCNPJ(form.cnpj)} onChange={e => editing ? setEditing({...editing, cnpj: formatCNPJ(e.target.value)}) : setForm({...form, cnpj: formatCNPJ(e.target.value)})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white font-mono focus:border-white transition outline-none" />
              <input type="text" placeholder="Site Institucional" value={editing ? (editing.site || '') : form.site} onChange={e => editing ? setEditing({...editing, site: e.target.value}) : setForm({...form, site: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" />
              <div className="flex justify-end gap-3 mt-4">
                <button type="button" onClick={() => {setShowModal(false); setEditing(null);}} className="px-4 py-2 text-slate-400 font-bold">Cancelar</button>
                <button type="submit" style={{ backgroundColor: partner.color }} className="px-8 py-2 rounded-lg text-white font-bold shadow-lg">Salvar Dados</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};







const AdminIdiomas = ({ partner }) => {
  const [activeTab, setActiveTab] = useState('livraria');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [books, setBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksError, setBooksError] = useState(null);
  const [booksLoaded, setBooksLoaded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [langFilter, setLangFilter] = useState('Todos');
  const [visibleCount, setVisibleCount] = useState(24);
  const [rssSources, setRssSources] = useState([]);
  const [showRssModal, setShowRssModal] = useState(false);
  const [newRssUrl, setNewRssUrl] = useState('');
  const [newRssCategory, setNewRssCategory] = useState('');
  const [rssErr, setRssErr] = useState('');
  const [booksMeta, setBooksMeta] = useState(null);
  const getItemLanguage = (it) => {
    const d = it?.description || it?.summary || '';
    const m = /Language:\s*([A-Za-z]+)/i.exec(d);
    if (m) return m[1];
    const src = (it?.source || '').toLowerCase();
    if (src.includes('gutenberg.org.br')) return 'Portuguese';
    try {
      const href = it?.url || it?.link || '';
      const u = new URL(href);
      const host = (u.host || '').toLowerCase();
      const pathq = (u.pathname + (u.search || '')).toLowerCase();
      if (host.endsWith('.br')) return 'Portuguese';
      if (pathq.includes('/pt-br') || pathq.includes('/ptbr') || pathq.includes('/pt/') || pathq.includes('lang=pt')) return 'Portuguese';
    } catch {}
    return '';
  };
  useEffect(() => {
    try {
      const saved = localStorage.getItem('livrariaLangFilter');
      if (saved) setLangFilter(saved);
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('livrariaLangFilter', langFilter);
    } catch {}
  }, [langFilter]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('livrariaRssSources');
      let list = [];
      if (raw) {
        const j = JSON.parse(raw);
        if (Array.isArray(j)) list = j.filter(it => it && typeof it.url === 'string');
      }
      if (!list.length) {
        list = [{ url: 'https://www.gutenberg.org/cache/epub/feeds/today.rss', category: 'Livraria' }];
      }
      setRssSources(list);
    } catch {
      setRssSources([{ url: 'https://www.gutenberg.org/cache/epub/feeds/today.rss', category: 'Livraria' }]);
    }
  }, []);

  // Forms state
  const [convForm, setConvForm] = useState({
    topic: 'restaurant',
    nativeLanguage: 'en',
    targetLanguage: 'es',
    level: 'intermediate',
    situationType: 'casual'
  });

  const [exForm, setExForm] = useState({
    nativeLanguage: 'en',
    targetLanguage: 'es',
    level: 'intermediate',
    topic: 'business',
    exerciseType: 'conversation'
  });

  const [feedForm, setFeedForm] = useState({
    userResponse: 'I go to the store yesterday',
    exerciseType: 'grammar',
    nativeLanguage: 'en',
    targetLanguage: 'es',
    level: 'intermediate'
  });

  const callApi = async (endpoint, data) => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const { data: resultData, error: apiErr } = await supabase.functions.invoke('language-tutor', {
        body: { endpoint, ...data }
      });

      if (apiErr) throw apiErr;
      setResult(resultData);
    } catch (err) {
      console.error('Erro ao chamar API de Idiomas via Supabase:', err);
      setError(err.message || 'Erro ao processar solicitação');
    } finally {
      setLoading(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;

    // Se for o formato de Conversação (baseado no screenshot)
    if (activeTab === 'conversation' && result.result?.conversation) {
      const { scenario, conversation } = result.result;
      return (
        <div className="space-y-6 animate-fade-in">
          {/* Header do Cenário */}
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-5">
            <h3 className="text-orange-500 font-bold text-lg mb-1 flex items-center gap-2">
              <Globe size={18} /> {scenario.title}
            </h3>
            <p className="text-slate-300 text-sm italic">{scenario.context}</p>
            <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
              <Clock size={12} /> Local: {scenario.setting}
            </div>
          </div>

          {/* Chat de Diálogo */}
          <div className="space-y-4">
            {conversation.map((msg, idx) => (
              <div key={idx} className={`flex flex-col ${idx % 2 === 0 ? 'items-start' : 'items-end'}`}>
                <div className={`max-w-[85%] rounded-2xl p-4 shadow-lg ${
                  idx % 2 === 0 
                    ? 'bg-slate-800 border border-slate-700 text-white rounded-tl-none' 
                    : 'bg-orange-600 border border-orange-500 text-white rounded-tr-none'
                }`}>
                  <div className="flex items-center gap-2 mb-2 opacity-60 text-[10px] font-bold uppercase tracking-tighter">
                    <User size={12} /> {msg.speaker}
                  </div>
                  <p className="text-lg font-medium leading-tight mb-2">{msg.text}</p>
                  
                  <div className="pt-2 mt-2 border-t border-white/10 space-y-2">
                    <div className="flex items-start gap-2 text-xs opacity-80 italic">
                      <Languages size={14} className="shrink-0 mt-0.5" />
                      <span>{msg.translation}</span>
                    </div>
                    {msg.pronunciation && (
                      <div className="flex items-start gap-2 text-[10px] opacity-60 font-mono">
                        <Headphones size={12} className="shrink-0 mt-0.5" />
                        <span>{msg.pronunciation}</span>
                      </div>
                    )}
                  </div>
                </div>
                {msg.notes && (
                  <div className="mt-1 px-3 text-[10px] text-slate-500 italic max-w-[80%]">
                    Tip: {msg.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Se for Exercícios
    if (activeTab === 'exercise') {
      const exercises = result.result?.exercises || (Array.isArray(result) ? result : null);
      if (exercises) {
        return (
          <div className="space-y-4 animate-fade-in">
            {exercises.map((ex, idx) => (
              <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 hover:border-orange-500/50 transition-colors group">
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-orange-500/20 text-orange-400 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest">
                    Q{idx + 1}
                  </span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                    <button className="p-1 hover:text-orange-500 text-slate-500"><Check size={14} /></button>
                  </div>
                </div>
                <p className="text-white font-medium mb-4">{ex.question || ex.text || (typeof ex === 'string' ? ex : 'Exercício gerado')}</p>
                {ex.options && (
                  <div className="grid grid-cols-1 gap-2">
                    {ex.options.map((opt, oIdx) => (
                      <div key={oIdx} className="bg-slate-950/50 border border-slate-800 p-3 rounded-lg text-sm text-slate-400 hover:text-white hover:border-slate-600 transition cursor-pointer">
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }
    }

    // Se for Feedback
    if (activeTab === 'feedback') {
      const fb = result.result || result;
      return (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <MessageSquare size={16} className="text-orange-500" /> Análise da Frase
            </h4>
            
            <div className="space-y-6">
              <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest block mb-2">Original</span>
                <p className="text-slate-300 italic">{fb.originalText || feedForm.userResponse}</p>
              </div>

              <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
                <span className="text-[10px] font-bold text-green-400 uppercase tracking-widest block mb-2">Sugestão / Correção</span>
                <p className="text-white font-bold text-lg">{fb.correctedText || fb.suggestion || 'Excelente! Nenhuma correção necessária.'}</p>
              </div>

              {fb.explanation && (
                <div className="p-4 bg-orange-500/5 border border-orange-500/10 rounded-lg">
                  <span className="text-[10px] font-bold text-orange-400 uppercase tracking-widest block mb-2 flex items-center gap-1">
                    <Info size={12} /> Explicação
                  </span>
                  <p className="text-slate-400 text-sm leading-relaxed">{fb.explanation}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Fallback para outros formatos ou erro inesperado
    return (
      <div className="bg-slate-950 border border-slate-800 rounded-lg p-6 font-mono text-sm text-slate-300 whitespace-pre-wrap leading-relaxed shadow-inner">
        {typeof result === 'object' ? JSON.stringify(result, null, 2) : result}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Languages className="text-orange-500" size={32} />
            + Conteúdos
          </h1>
          <p className="text-slate-400">Tutor de idiomas inteligente para o ecossistema {partner.name}.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 mb-6 overflow-x-auto">
        {[
          { id: 'livraria', label: 'Livraria', icon: <BookOpen size={18} /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
            className={`flex items-center gap-2 px-6 py-4 font-bold text-sm uppercase tracking-widest transition-all border-b-2 whitespace-nowrap ${
              activeTab === tab.id 
                ? 'border-orange-500 text-orange-500 bg-orange-500/5' 
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 md:p-8 shadow-xl">
        {activeTab === 'livraria' && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Feeds RSS</div>
                <span className="text-xs text-slate-500">•</span>
                <span className="text-xs text-slate-400">{Array.isArray(books) ? books.length : 0} itens</span>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="relative flex-1 md:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input
                    value={searchText}
                    onChange={(e)=>{ setSearchText(e.target.value); setVisibleCount(24); }}
                    placeholder="Pesquisar título..."
                    className="w-full bg-slate-950 border border-slate-800 pl-9 pr-3 py-2 rounded-lg text-sm text-white outline-none focus:border-orange-500 transition"
                  />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={16} />
                  <select
                    value={langFilter}
                    onChange={(e)=>{ setLangFilter(e.target.value); setVisibleCount(24); }}
                    className="appearance-none bg-slate-950 border border-slate-800 pl-9 pr-8 py-2 rounded-lg text-sm text-white outline-none focus:border-orange-500 transition"
                  >
                    <option value="Todos">Todos</option>
                    {Array.from(new Set((books||[]).map(b => getItemLanguage(b)).filter(Boolean))).sort().map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => { setLangFilter(prev => prev === 'Portuguese' ? 'Todos' : 'Portuguese'); setVisibleCount(24); }}
                  className={`px-3 py-2 rounded-lg text-xs font-bold transition ${langFilter==='Portuguese' ? 'bg-orange-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-100'}`}
                  title="Somente Português (toggle)"
                >
                  PT
                </button>
              <button
                type="button"
                onClick={() => { setBooksLoaded(false); }}
                className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-bold transition flex items-center gap-2"
              >
                <RefreshCcw size={14} /> Atualizar
              </button>
              <button
                type="button"
                onClick={() => { setShowRssModal(true); setRssErr(''); }}
                className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-bold transition flex items-center gap-2"
              >
                <Settings size={14} /> Gerenciar RSS
              </button>
              </div>
            </div>
            {(() => {
              if (!booksLoaded && !booksLoading) {
                setBooksLoading(true);
                setBooksError(null);
                supabase.functions.invoke('get_latest_news', {
                  body: {
                    rss: (rssSources && rssSources.length ? rssSources : [{ url: 'https://www.gutenberg.org/cache/epub/feeds/today.rss', category: 'Livraria' }]),
                    limit: 50
                  }
                }).then(({ data, error: err }) => {
                  if (err) throw err;
                  const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.data) ? data.data : []);
                  setBooks(Array.isArray(items) ? items : []);
                  setBooksMeta(data?.__meta || null);
                }).catch((e) => {
                  setBooksError(e?.message || 'Erro ao carregar feed');
                }).finally(() => {
                  setBooksLoading(false);
                  setBooksLoaded(true);
                });
              }
              return null;
            })()}
            {!!booksMeta && (
              <div className="mb-4 flex items-center gap-3 text-xs text-slate-500">
                <span>Fontes: {Number(booksMeta.rss_sources || 0)}</span>
                {Array.isArray(booksMeta.rss_errors) && booksMeta.rss_errors.length > 0 && (
                  <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/30">
                    Erros em {booksMeta.rss_errors.length} fonte(s)
                  </span>
                )}
              </div>
            )}
            {booksLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_,i)=>(
                  <div key={i} className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 animate-pulse">
                    <div className="h-4 bg-slate-800 rounded w-3/4 mb-3"></div>
                    <div className="h-3 bg-slate-800 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-slate-800 rounded w-full"></div>
                  </div>
                ))}
              </div>
            )}
            {!booksLoading && booksError && (
              <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
                {booksError}
              </div>
            )}
            {!booksLoading && !booksError && booksLoaded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {books
                  .filter((b)=>{
                    const t = (b?.title || '').toLowerCase();
                    const s = (b?.summary || b?.description || '').toLowerCase();
                    const txtOk = !searchText || t.includes(searchText.toLowerCase()) || s.includes(searchText.toLowerCase());
                    const lang = getItemLanguage(b);
                    const langOk = langFilter === 'Todos' || (lang && lang === langFilter);
                    if (langFilter === 'Portuguese' && !lang) return false;
                    return txtOk && langOk;
                  })
                  .slice(0, visibleCount)
                  .map((it, idx) => {
                  const dateStr = (() => {
                    const d = Date.parse(it?.published_at || it?.publishedAt || it?.time_published || '');
                    if (!isNaN(d) && d > 0) {
                      try { return new Date(d).toLocaleString('pt-BR'); } catch { return ''; }
                    }
                    return '';
                  })();
                  const host = (() => {
                    try {
                      const u = new URL(it?.url || it?.link || '');
                      return u.host.replace(/^www\./, '');
                    } catch { return it?.source || 'gutenberg.org'; }
                  })();
                  const href = it?.url || it?.link || '#';
                  const lang = getItemLanguage(it);
                  return (
                    <a
                      key={idx}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="group block rounded-lg border border-slate-800 bg-slate-950/60 p-4 hover:border-slate-700 transition"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-white font-semibold leading-snug group-hover:text-orange-400 transition">
                          {it?.title || 'Sem título'}
                        </h3>
                        <LinkIcon size={16} className="text-slate-500 group-hover:text-orange-400 shrink-0" />
                      </div>
                      <div className="mt-2 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Globe size={12} /> {host}</span>
                        {lang && <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] uppercase tracking-widest">{lang}</span>}
                        {dateStr && <span className="text-slate-500">{dateStr}</span>}
                      </div>
                      {it?.summary && (
                        <p className="mt-2 text-sm text-slate-400 line-clamp-3">{it.summary.replace(/<[^>]+>/g, '')}</p>
                      )}
                    </a>
                  );
                })}
                {books.length === 0 && (
                  <div className="col-span-full text-slate-500 text-sm">Nenhum item disponível no momento.</div>
                )}
                {books && books.filter((b)=>{
                  const t = (b?.title || '').toLowerCase();
                  const s = (b?.summary || b?.description || '').toLowerCase();
                  const txtOk = !searchText || t.includes(searchText.toLowerCase()) || s.includes(searchText.toLowerCase());
                  const m = /Language:\s*([A-Za-z]+)/i.exec(b?.description || b?.summary || '');
                  const lang = m ? m[1] : '';
                  const langOk = langFilter === 'Todos' || lang === langFilter;
                  return txtOk && langOk;
                }).length > visibleCount && (
                  <div className="col-span-full flex justify-center mt-2">
                    <button
                      type="button"
                      onClick={()=> setVisibleCount(v => v + 24)}
                      className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold transition"
                    >
                      Mostrar mais
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {showRssModal && (
        <div className="fixed inset-0 z-[99999]">
          <div className="absolute inset-0 bg-black/60" onClick={()=>setShowRssModal(false)}></div>
          <div className="absolute left-1/2 top-16 -translate-x-1/2 w-[92vw] max-w-[720px] rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="text-white font-bold">Gerenciar feeds RSS</div>
              <button type="button" onClick={()=>setShowRssModal(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2 relative">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">URL do Feed</label>
                  <input
                    value={newRssUrl}
                    onChange={(e)=>setNewRssUrl(e.target.value)}
                    placeholder="https://exemplo.com/feed.rss"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-orange-500 transition"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Categoria (opcional)</label>
                  <input
                    value={newRssCategory}
                    onChange={(e)=>setNewRssCategory(e.target.value)}
                    placeholder="Categoria"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-orange-500 transition"
                  />
                </div>
              </div>
              {rssErr && <div className="text-sm text-red-400">{rssErr}</div>}
              <div>
                <button
                  type="button"
                  onClick={()=>{
                    const url = (newRssUrl||'').trim();
                    if (!/^https?:\/\//i.test(url)) { setRssErr('Informe uma URL válida iniciando com http(s)://'); return; }
                    if ((rssSources||[]).some(s => s.url === url)) { setRssErr('Este feed já está na lista.'); return; }
                    const cat = (newRssCategory||'').trim() || undefined;
                    const next = [...(rssSources||[]), { url, category: cat }];
                    setRssSources(next);
                    setNewRssUrl('');
                    setNewRssCategory('');
                    setRssErr('');
                  }}
                  className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold transition inline-flex items-center gap-2"
                >
                  <PlusIcon size={16} /> Adicionar feed
                </button>
              </div>
              <div className="space-y-2 max-h-[40vh] overflow-auto custom-scrollbar pr-1">
                {(rssSources||[]).map((src, idx) => (
                  <div key={src.url} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{src.url}</div>
                      <div className="text-xs text-slate-500">{src.category || 'Sem categoria'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={()=>{
                        const next = (rssSources||[]).filter((s, i)=> i !== idx);
                        setRssSources(next);
                      }}
                      className="px-2 py-1 rounded bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 hover:text-white transition"
                      title="Remover"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                {(!rssSources || rssSources.length === 0) && (
                  <div className="text-slate-500 text-sm">Nenhum feed adicionado ainda.</div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-800">
              <button type="button" onClick={()=>setShowRssModal(false)} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold transition">Cancelar</button>
              <button
                type="button"
                onClick={()=>{
                  try {
                    localStorage.setItem('livrariaRssSources', JSON.stringify(rssSources||[]));
                  } catch {}
                  setShowRssModal(false);
                  setBooks([]);
                  setBooksLoaded(false);
                }}
                className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold transition"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ToggleSwitch = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    onClick={onChange}
    className={`${checked ? 'bg-teal-500' : 'bg-slate-700'} w-10 h-5 rounded-full relative transition-colors duration-300`}
    aria-pressed={checked}
    aria-label={ariaLabel}
  >
    <div className={`${checked ? 'left-6' : 'left-1'} absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}></div>
  </button>
);

if (import.meta.env.DEV) {
const RichEditor = ({ value, onChange }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);
  const cmd = (c) => { document.execCommand(c, false); onChange(ref.current?.innerHTML || ''); };

  const insertTextAtCursor = (text) => {
    if (!ref.current) return;
    ref.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range = range.cloneRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    onChange(ref.current.innerHTML || '');
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200" onClick={()=>cmd('bold')}>B</button>
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200 italic" onClick={()=>cmd('italic')}>I</button>
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200" onClick={()=>cmd('insertUnorderedList')}>• Lista</button>
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200" onClick={()=>cmd('insertOrderedList')}>1. Lista</button>
      </div>
      <div
        ref={ref}
        contentEditable
        className="min-h-[260px] bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition prose prose-invert max-w-none"
        onInput={(e)=> onChange(e.currentTarget.innerHTML)}
        onDragOver={(e)=> e.preventDefault()}
        onDrop={(e)=> {
          e.preventDefault();
          const token = e.dataTransfer.getData('text/variable-token') || e.dataTransfer.getData('text/plain');
          if (token) insertTextAtCursor(token);
        }}
      ></div>
    </div>
  );
};

const AdminCommunications = ({ partner, partners = [], templates, setTemplates, defaultEmailHeader, setDefaultEmailHeader, defaultEmailFooter, setDefaultEmailFooter }) => {
  const [activeTab, setActiveTab] = usePersistentTab('admin_communications_legacy', 'general');
  const [messages, setMessages] = useState([
    { id: 1, title: 'Boas-vindas', recipient: 'Todos os Alunos', date: '2024-02-25', status: 'Enviado', type: 'E-mail' },
    { id: 2, title: 'Manutenção Programada', recipient: 'Coordenadores', date: '2024-02-20', status: 'Enviado', type: 'WhatsApp' },
    { id: 3, title: 'Nova Turma Alpha', recipient: 'Interessados', date: '2024-02-28', status: 'Agendado', type: 'E-mail' },
  ]);
  const [editingTemplate, setEditingTemplate] = useState(null);

  
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [showTransactionalModal, setShowTransactionalModal] = useState(false);
  const [transactionalChannel, setTransactionalChannel] = useState('E-mail');
  const transactionalTypes = ['Convite','Boas Vindas','Reset Senha','Certificado','confirmação','aviso','notificação','Comunicado','Engajamento'];
  const [transactionalForm, setTransactionalForm] = useState({
    tipo: 'Convite',
    header: null,
    headerUrl: '',
    tornarHeaderPadrao: false,
    titulo: '',
    textoHtml: '',
    botaoHabilitado: false,
    botaoTitulo: '',
    botaoLink: '',
    observacaoHabilitada: false,
    observacao: '',
    rodape: null,
    rodapeUrl: '',
    tornarRodapePadrao: false,
    variaveisHabilitadas: false,
    variaveis: {
      convite: false,
      redefinicaoSenha: false,
      acessoPlataforma: false,
      certificado: false,
      nomeUsuario: false,
    },
    atribuirParceiros: false,
    parceirosSelecionados: [],
    anexos: [],
    anexosAberto: false,
  });
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const allowedAttachments = {
    'application/pdf': 5 * 1024 * 1024,
    'image/jpeg': 2 * 1024 * 1024,
    'image/png': 2 * 1024 * 1024,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 5 * 1024 * 1024,
  };
  const acceptAttr = Object.keys(allowedAttachments).map(t => {
    if (t.includes('pdf')) return '.pdf';
    if (t.includes('jpeg')) return '.jpg,.jpeg';
    if (t.includes('png')) return '.png';
    if (t.includes('wordprocessingml')) return '.docx';
    return '';
  }).filter(Boolean).join(',');

  const validateAndSetAttachments = (filesList) => {
    const current = transactionalForm.anexos || [];
    const incoming = Array.from(filesList || []);
    const combined = [...current];
    for (const f of incoming) {
      if (combined.length >= 2) break;
      const max = allowedAttachments[f.type];
      if (!max || f.size > max) continue;
      combined.push({ name: f.name, size: f.size, type: f.type });
    }
    setTransactionalForm({ ...transactionalForm, anexos: combined });
  };

  const TitleMax = 80;
  const ObsMax = 180;

  const handleTransactionalSave = async () => {
    const plainText = (transactionalForm.textoHtml || '').replace(/<[^>]+>/g,'').trim();
    const name = `${transactionalForm.tipo} - ${transactionalForm.titulo || 'Template'}`;
    let headerUrl = '';
    let rodapeUrl = '';
    if (transactionalChannel === 'E-mail') {
      headerUrl = transactionalForm.headerUrl || defaultEmailHeader || '';
      rodapeUrl = transactionalForm.rodapeUrl || defaultEmailFooter || '';
      if (transactionalForm.header) {
        const ext = transactionalForm.header.name.split('.').pop() || 'img';
        const path = `headers/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        try { headerUrl = await uploadPublic(path, transactionalForm.header); } catch {}
      }
      if (transactionalForm.rodape) {
        const ext = transactionalForm.rodape.name.split('.').pop() || 'img';
        const path = `footers/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        try { rodapeUrl = await uploadPublic(path, transactionalForm.rodape); } catch {}
      }
    }
    const newTemplate = {
      name,
      channel: transactionalChannel,
      subject: transactionalForm.titulo,
      content: plainText,
      transactional: true,
      meta: {
        tipo: transactionalForm.tipo,
        ...(transactionalChannel === 'E-mail' ? { headerUrl, rodapeUrl } : {}),
        botao: transactionalForm.botaoHabilitado ? { titulo: transactionalForm.botaoTitulo, link: transactionalForm.botaoLink } : null,
        observacao: transactionalForm.observacaoHabilitada ? transactionalForm.observacao : null,
        variaveis: transactionalForm.variaveisHabilitadas ? Object.entries(transactionalForm.variaveis).filter(([k,v])=>v).map(([k])=>k) : [],
        parceirosSAS: transactionalForm.atribuirParceiros ? transactionalForm.parceirosSelecionados : [],
        anexos: (transactionalForm.anexos || []).map(a => ({ name: a.name, size: a.size, type: a.type })),
      }
    };

    if (transactionalChannel === 'E-mail') {
      if (transactionalForm.tornarHeaderPadrao && headerUrl) {
        setDefaultEmailHeader(headerUrl);
      }
      if (transactionalForm.tornarRodapePadrao && rodapeUrl) {
        setDefaultEmailFooter(rodapeUrl);
      }
    }

    if (editingTemplate) {
      const { data: updated } = await supabase.from('templates').update(newTemplate).eq('id', editingTemplate.id).select().single();
      if (updated) setTemplates(templates.map(t => t.id === editingTemplate.id ? updated : t));
    } else {
      const { data: inserted } = await supabase.from('templates').insert(newTemplate).select().single();
      if (inserted) setTemplates([inserted, ...templates]);
    }
    if (transactionalChannel === 'E-mail' && ((transactionalForm.tornarHeaderPadrao && headerUrl) || (transactionalForm.tornarRodapePadrao && rodapeUrl))) {
      await supabase.from('email_settings').insert({ default_header_url: transactionalForm.tornarHeaderPadrao ? headerUrl : null, default_footer_url: transactionalForm.tornarRodapePadrao ? rodapeUrl : null });
    }
    setShowTransactionalModal(false);
    setEditingTemplate(null);
    setTransactionalChannel('E-mail');
    setTransactionalForm({
      tipo: 'Convite',
      header: null,
      headerUrl: '',
      tornarHeaderPadrao: false,
      titulo: '',
      textoHtml: '',
      botaoHabilitado: false,
      botaoTitulo: '',
      botaoLink: '',
      observacaoHabilitada: false,
      observacao: '',
      rodape: null,
      rodapeUrl: '',
      tornarRodapePadrao: false,
      variaveisHabilitadas: false,
      variaveis: { convite:false, redefinicaoSenha:false, acessoPlataforma:false, certificado:false, nomeUsuario:false },
      atribuirParceiros: false,
      parceirosSelecionados: [],
      anexos: [],
      anexosAberto: false,
    });
  };

  

  const handleDeleteTemplate = async (id) => {
    if (confirm('Tem certeza que deseja excluir este template?')) {
      await supabase.from('templates').delete().eq('id', id);
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  

  const sampleValues = {
    '{nomeUsuario}': 'Felipe Nunes',
    '{linkConvite}': 'https://example.com/convite/XYZ',
    '{linkPlataforma}': 'https://app.sofera.com/login',
    '{linkRedefinicaoSenha}': 'https://app.sofera.com/reset',
    '{linkCertificado}': 'https://app.sofera.com/certificados/ABC123',
  };

  const buildTransactionalHtml = (t) => {
    const meta = t.meta || {};
    const replaceTokens = (txt='') => Object.entries(sampleValues).reduce((acc,[k,v])=>acc.replaceAll(k,v), txt);
    const contentHtml = `<p style="margin:0 0 16px 0; line-height:1.6; color:#111827;">${replaceTokens((t.textoHtml || '').trim() || t.content.replaceAll('\n','<br/>'))}</p>`;
    const btn = meta.botao ? `<a href="${meta.botao.link}" style="display:inline-block; padding:12px 20px; background:#f97316; color:#fff; border-radius:8px; text-decoration:none; font-weight:700;">${meta.botao.titulo}</a>` : '';
    const obs = meta.observacao ? `<p style="margin-top:16px; font-size:12px; color:#6b7280;">${meta.observacao}</p>` : '';
    const headerImg = meta.headerUrl ? `<img src="${meta.headerUrl}" style="max-width:100%; height:auto; display:block; margin:0 auto 16px auto;"/>` : '';
    const footerImg = meta.rodapeUrl ? `<img src="${meta.rodapeUrl}" style="max-width:100%; height:auto; display:block; margin:16px auto 0 auto;"/>` : '';
    return `<!doctype html><html><head><meta charset='utf-8'/><meta name='viewport' content='width=device-width, initial-scale=1'/></head><body style="background:#e5e7eb; padding:24px;">
      <table role='presentation' cellspacing='0' cellpadding='0' border='0' align='center' width='100%' style='max-width:640px;'>
        <tr><td style="background:#ffffff; border-radius:12px; padding:24px; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;">
          ${headerImg}
          <h1 style="margin:0 0 8px 0; font-size:20px; color:#111827;">${t.subject || 'Mensagem'}</h1>
          ${contentHtml}
          ${btn}
          ${obs}
          ${footerImg}
        </td></tr>
      </table>
    </body></html>`;
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Comunicações</h1>
          <p className="text-slate-400">Gerencie e envie comunicados para seus alunos e parceiros.</p>
        </div>
        {activeTab === 'general' && (
          <button style={{ backgroundColor: partner.color }} className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition">
            <MessageCircle size={20} /> Nova Comunicação
          </button>
        )}
      </div>

      <div className="flex gap-6 mb-8 border-b border-slate-800">
        <button onClick={() => setActiveTab('general')} className={`pb-4 px-2 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeTab === 'general' ? 'text-white border-white' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
            Geral
        </button>
        <button onClick={() => setActiveTab('templates')} className={`pb-4 px-2 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeTab === 'templates' ? 'text-white border-white' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
            Templates
        </button>
      </div>

      {activeTab === 'general' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl animate-fade-in overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
                <tr>
                  <th className="p-5">Título</th>
                  <th className="p-5">Destinatário</th>
                  <th className="p-5">Canal</th>
                  <th className="p-5">Emissão</th>
                  <th className="p-5">Status</th>
                  <th className="p-5 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {messages.map(msg => (
                  <tr key={msg.id} className="hover:bg-slate-800/50 transition text-slate-300">
                    <td className="p-5 font-bold text-white">{msg.title}</td>
                    <td className="p-5">{msg.recipient}</td>
                    <td className="p-5 flex items-center gap-2">
                      {msg.type === 'WhatsApp' ? <MessageCircle size={16} className="text-green-400" /> : <Mail size={16} className="text-blue-400" />}
                      {msg.type}
                    </td>
                    <td className="p-5 text-sm text-slate-400">{new Date(msg.date).toLocaleDateString('pt-BR')}</td>
                    <td className="p-5">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold border uppercase ${
                        msg.status === 'Enviado' ? 'border-teal-500/20 bg-teal-500/10 text-teal-400' : 'border-amber-500/20 bg-amber-500/10 text-amber-400'
                      }`}>
                        {msg.status}
                      </span>
                    </td>
                    <td className="p-5 text-right">
                      <button className="text-slate-500 hover:text-white transition"><MoreHorizontal size={20} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        </div>
      )}

      {activeTab === 'templates' && (
        (() => {
          const emailTemplates = templates.filter(t => t.channel === 'E-mail');
          const whatsappTemplates = templates.filter(t => t.channel === 'WhatsApp');
          return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Canal: E-mail</h4>
                  <button
                  onClick={() => { setTransactionalChannel('E-mail'); setShowTransactionalModal(true); }}
                    className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                    style={{ backgroundColor: partner.color }}
                  >
                    + Template
                  </button>
                </div>
                <div className="flex flex-col gap-6">
                  {emailTemplates.map(t => (
                    <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:border-slate-700 transition group relative shadow-lg">
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition flex gap-2">
                        {t.transactional && (
                          <button
                            onClick={() => {
                              setPreviewTemplate(t);
                              setShowPreviewModal(true);
                              try {
                                const html = buildTransactionalHtml(t);
                                const w = window.open();
                                if (w) { w.document.open(); w.document.write(html); w.document.close(); }
                              } catch {}
                            }}
                            className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingTemplate(t);
                            setTransactionalChannel(t.channel || 'E-mail');
                            const meta = t.meta || {};
                            const vars = meta.variaveis || [];
                            setTransactionalForm({
                              tipo: meta.tipo || 'Comunicado',
                              header: null,
                              headerUrl: meta.headerUrl || '',
                              tornarHeaderPadrao: false,
                              titulo: t.subject || '',
                              textoHtml: meta.textoHtml || t.content || '',
                              botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                              botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                              botaoLink: (meta.botao && meta.botao.link) || '',
                              observacaoHabilitada: !!meta.observacao,
                              observacao: meta.observacao || '',
                              rodape: null,
                              rodapeUrl: meta.rodapeUrl || '',
                              tornarRodapePadrao: false,
                              variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                              variaveis: {
                                convite: vars.includes('convite'),
                                redefinicaoSenha: vars.includes('redefinicaoSenha'),
                                acessoPlataforma: vars.includes('acessoPlataforma'),
                                certificado: vars.includes('certificado'),
                                nomeUsuario: vars.includes('nomeUsuario'),
                              },
                              atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                              parceirosSelecionados: meta.parceirosSAS || [],
                              anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                              anexosAberto: false,
                            });
                            setShowTransactionalModal(true);
                          }}
                          className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                        ><Edit2 size={16} /></button>
                        
                        <button onClick={() => handleDeleteTemplate(t.id)} className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-rose-500/10 hover:border-rose-500/50 hover:text-rose-500 text-slate-300 transition"><Trash2 size={16} /></button>
                      </div>
                      <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-blue-500/10 text-blue-400">
                          <Mail size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-white text-lg">{t.name}</h3>
                          <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">E-mail</span>
                        </div>
                      </div>
                      {t.channel === 'E-mail' && <p className="text-slate-400 text-sm mb-3"><span className="text-slate-500 font-bold uppercase text-xs mr-2">Assunto:</span> {t.subject}</p>}
                      <div className="bg-slate-950 p-4 rounded-lg border border-slate-800/50">
                        <p className="text-slate-400 text-sm font-mono whitespace-pre-wrap">{t.content}</p>
                      </div>
                    </div>
                  ))}

                  {emailTemplates.length === 0 && (
                    <div className="text-slate-500 text-sm">Nenhum template de e-mail.</div>
                  )}

                  
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Canal: Whatsapp</h4>
                  <button
                    onClick={() => { setTransactionalChannel('WhatsApp'); setShowTransactionalModal(true); }}
                    className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                    style={{ backgroundColor: partner.color }}
                  >
                    + Template
                  </button>
                </div>
                <div className="flex flex-col gap-6">
                  {whatsappTemplates.map(t => (
                    <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:border-slate-700 transition group relative shadow-lg">
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition flex gap-2">
                        <button
                          onClick={() => {
                            setEditingTemplate(t);
                            setTransactionalChannel(t.channel || 'WhatsApp');
                            const meta = t.meta || {};
                            const vars = meta.variaveis || [];
                            setTransactionalForm({
                              tipo: meta.tipo || 'Comunicado',
                              header: null,
                              headerUrl: meta.headerUrl || '',
                              tornarHeaderPadrao: false,
                              titulo: t.subject || '',
                              textoHtml: meta.textoHtml || t.content || '',
                              botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                              botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                              botaoLink: (meta.botao && meta.botao.link) || '',
                              observacaoHabilitada: !!meta.observacao,
                              observacao: meta.observacao || '',
                              rodape: null,
                              rodapeUrl: meta.rodapeUrl || '',
                              tornarRodapePadrao: false,
                              variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                              variaveis: {
                                convite: vars.includes('convite'),
                                redefinicaoSenha: vars.includes('redefinicaoSenha'),
                                acessoPlataforma: vars.includes('acessoPlataforma'),
                                certificado: vars.includes('certificado'),
                                nomeUsuario: vars.includes('nomeUsuario'),
                              },
                              atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                              parceirosSelecionados: meta.parceirosSAS || [],
                              anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                              anexosAberto: false,
                            });
                            setShowTransactionalModal(true);
                          }}
                          className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                        ><Edit2 size={16} /></button>
                        <button onClick={() => handleDeleteTemplate(t.id)} className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-rose-500/10 hover:border-rose-500/50 hover:text-rose-500 text-slate-300 transition"><Trash2 size={16} /></button>
                      </div>
                      <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-green-500/10 text-green-400">
                          <MessageCircle size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-white text-lg">{t.name}</h3>
                          <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">WhatsApp</span>
                        </div>
                      </div>
                      <div className="bg-slate-950 p-4 rounded-lg border border-slate-800/50">
                        <p className="text-slate-400 text-sm font-mono whitespace-pre-wrap">{t.content}</p>
                      </div>
                    </div>
                  ))}
                  {whatsappTemplates.length === 0 && (
                    <div className="text-slate-500 text-sm">Nenhum template de WhatsApp.</div>
                  )}
                </div>
              </div>

              {emailTemplates.length === 0 && whatsappTemplates.length === 0 && (
                <div className="md:col-span-2 text-center py-20 text-slate-500">
                  <FileText size={48} className="mx-auto mb-4 opacity-20" />
                  <p>Nenhum template criado ainda.</p>
                </div>
              )}
            </div>
          );
        })()
      )}

      {showTransactionalModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">{editingTemplate ? 'Editar Template Transacional' : 'Novo Template Transacional'}</h3>
              <button onClick={() => setShowTransactionalModal(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-12">
                  <div className="flex items-center gap-3 mb-2">
                    <ToggleSwitch checked={transactionalForm.atribuirParceiros} onChange={()=> setTransactionalForm({...transactionalForm, atribuirParceiros: !transactionalForm.atribuirParceiros})} ariaLabel="Atribuir Parceiro SAS" />
                    <span className={`${transactionalForm.atribuirParceiros ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Atribuir Parceiro SAS</span>
                  </div>
                  {transactionalForm.atribuirParceiros && (
                    <div className="space-y-2">
                      {transactionalForm.parceirosSelecionados.length > 0 && (
                        <div className="inline-flex items-center gap-2 bg-slate-800 text-slate-200 text-xs rounded-full px-3 py-1">
                          {transactionalForm.parceirosSelecionados.length} parceiro(s) selecionado(s)
                        </div>
                      )}
                      <div className="relative inline-block">
                        <button onClick={()=> setShowPartnerDropdown(v=>!v)} className="px-4 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-200 hover:border-white/40 transition min-w-[260px] text-left">
                          {transactionalForm.parceirosSelecionados.length > 0 ? 'Gerenciar seleção...' : 'Selecionar parceiros...'}
                        </button>
                        {showPartnerDropdown && (
                          <div className="absolute z-10 mt-2 w-72 max-h-60 overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2" onMouseLeave={()=> setShowPartnerDropdown(false)}>
                            {partners.map(p => {
                              const selected = transactionalForm.parceirosSelecionados.includes(p.id);
                              return (
                                <div key={p.id} className={`flex items-center justify-between p-2 rounded cursor-pointer ${selected ? 'bg-teal-500/10' : 'hover:bg-slate-800'}`} onClick={() => {
                                  const exists = transactionalForm.parceirosSelecionados.includes(p.id);
                                  const next = exists ? transactionalForm.parceirosSelecionados.filter(id => id !== p.id) : [...transactionalForm.parceirosSelecionados, p.id];
                                  setTransactionalForm({...transactionalForm, parceirosSelecionados: next});
                                }}>
                                  <span className="text-sm text-slate-200">{p.name}</span>
                                  <ToggleSwitch checked={selected} onChange={(e)=>{
                                    e.stopPropagation();
                                    const exists = transactionalForm.parceirosSelecionados.includes(p.id);
                                    const next = exists ? transactionalForm.parceirosSelecionados.filter(id => id !== p.id) : [...transactionalForm.parceirosSelecionados, p.id];
                                    setTransactionalForm({...transactionalForm, parceirosSelecionados: next});
                                  }} ariaLabel={`Selecionar ${p.name}`} />
                                </div>
                              );
                            })}
                            {partners.length === 0 && (
                              <div className="text-xs text-slate-500 p-2">Nenhum parceiro cadastrado.</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 md:col-span-4">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</label>
                  <select value={transactionalForm.tipo} onChange={e=>setTransactionalForm({...transactionalForm,tipo:e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition">
                    {transactionalTypes.map(t=> <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5 md:col-span-8">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título ({TitleMax} max)</label>
                  <input value={transactionalForm.titulo} onChange={e=> setTransactionalForm({...transactionalForm, titulo: e.target.value.slice(0,TitleMax)})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" placeholder={transactionalChannel === 'WhatsApp' ? 'Assunto da mensagem' : 'Assunto do e-mail'} maxLength={TitleMax} />
                  <span className="text-[10px] text-slate-500">{transactionalForm.titulo.length}/{TitleMax}</span>
                </div>

                {transactionalChannel === 'E-mail' && (
                  <>
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Header (imagem)</label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="file" accept="image/*" onChange={e=>{
                          const f=e.target.files?.[0];
                          if(!f) return;
                          const url = URL.createObjectURL(f);
                          setTransactionalForm({...transactionalForm, header:f, headerUrl:url});
                        }} className="text-slate-400" />
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={transactionalForm.tornarHeaderPadrao} onChange={()=> setTransactionalForm({...transactionalForm, tornarHeaderPadrao: !transactionalForm.tornarHeaderPadrao})} ariaLabel="Tornar header padrão" />
                          <span className={`${transactionalForm.tornarHeaderPadrao ? 'text-teal-400' : 'text-slate-400'} text-sm font-bold`}>Tornar Padrão</span>
                        </div>
                      </div>
                      {(transactionalForm.headerUrl || defaultEmailHeader) && (
                        <img src={transactionalForm.headerUrl || defaultEmailHeader} alt="Header preview" className="max-h-20 object-contain rounded border border-slate-800" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Rodapé (imagem)</label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="file" accept="image/*" onChange={e=>{
                          const f=e.target.files?.[0];
                          if(!f) return;
                          const url = URL.createObjectURL(f);
                          setTransactionalForm({...transactionalForm, rodape:f, rodapeUrl:url});
                        }} className="text-slate-400" />
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={transactionalForm.tornarRodapePadrao} onChange={()=> setTransactionalForm({...transactionalForm, tornarRodapePadrao: !transactionalForm.tornarRodapePadrao})} ariaLabel="Tornar rodapé padrão" />
                          <span className={`${transactionalForm.tornarRodapePadrao ? 'text-teal-400' : 'text-slate-400'} text-sm font-bold`}>Tornar Padrão</span>
                        </div>
                      </div>
                      {(transactionalForm.rodapeUrl || defaultEmailFooter) && (
                        <img src={transactionalForm.rodapeUrl || defaultEmailFooter} alt="Rodapé preview" className="max-h-16 object-contain rounded border border-slate-800" />
                      )}
                    </div>
                  </>
                )}

                <div className="flex flex-col gap-2 md:col-span-12">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Texto</label>
                  <RichEditor value={transactionalForm.textoHtml} onChange={(html)=> setTransactionalForm({...transactionalForm, textoHtml: html})} />
                </div>

                <div className="md:col-span-12 space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.botaoHabilitado} onChange={()=> setTransactionalForm({...transactionalForm, botaoHabilitado: !transactionalForm.botaoHabilitado})} ariaLabel="Habilitar botão manual" />
                      <span className={`${transactionalForm.botaoHabilitado ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>{transactionalChannel === 'WhatsApp' ? 'Link Manual' : 'Botão Manual'}</span>
                    </div>
                    {transactionalForm.botaoHabilitado && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título do Botão</label>
                          <input value={transactionalForm.botaoTitulo} onChange={e=> setTransactionalForm({...transactionalForm, botaoTitulo: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Link do Botão</label>
                          <input value={transactionalForm.botaoLink} onChange={e=> setTransactionalForm({...transactionalForm, botaoLink: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" placeholder="https://" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.observacaoHabilitada} onChange={()=> setTransactionalForm({...transactionalForm, observacaoHabilitada: !transactionalForm.observacaoHabilitada})} ariaLabel="Habilitar observação" />
                      <span className={`${transactionalForm.observacaoHabilitada ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Observação</span>
                    </div>
                    {transactionalForm.observacaoHabilitada && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Observação ({ObsMax} max)</label>
                        <textarea value={transactionalForm.observacao} onChange={e=> setTransactionalForm({...transactionalForm, observacao: e.target.value.slice(0,ObsMax)})} rows={3} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition resize-none" maxLength={ObsMax} />
                        <span className="text-[10px] text-slate-500">{transactionalForm.observacao.length}/{ObsMax}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.variaveisHabilitadas} onChange={()=> setTransactionalForm({...transactionalForm, variaveisHabilitadas: !transactionalForm.variaveisHabilitadas})} ariaLabel="Habilitar variáveis" />
                      <span className={`${transactionalForm.variaveisHabilitadas ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Variáveis</span>
                    </div>
                    {transactionalForm.variaveisHabilitadas && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {[
                          {k:'convite', label:'Link de convite', token:'{linkConvite}'},
                          {k:'redefinicaoSenha', label:'Link de redefinição de senha', token:'{linkRedefinicaoSenha}'},
                          {k:'acessoPlataforma', label:'Link de acesso à plataforma', token:'{linkPlataforma}'},
                          {k:'certificado', label:'Link Certificado', token:'{linkCertificado}'},
                          {k:'nomeUsuario', label:'Nome do Usuário', token:'{nomeUsuario}'},
                        ].map(v => (
                          <div key={v.k} className={`border rounded-lg p-3 flex items-center justify-between ${transactionalForm.variaveis[v.k] ? 'bg-teal-500/10 border-teal-500/30' : 'bg-slate-950 border-slate-800'}`}>
                            <span
                              className={`${transactionalForm.variaveis[v.k] ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold ${transactionalForm.variaveis[v.k] ? 'cursor-grab' : 'cursor-not-allowed'}`}
                              draggable={transactionalForm.variaveis[v.k]}
                              onDragStart={(e)=> { const t=v.token; e.dataTransfer.setData('text/variable-token', t); e.dataTransfer.setData('text/plain', t); e.dataTransfer.effectAllowed='copy'; }}
                              title={transactionalForm.variaveis[v.k] ? 'Arraste para o texto' : 'Habilite para arrastar'}
                            >
                              {v.label}
                            </span>
                            <ToggleSwitch checked={transactionalForm.variaveis[v.k]} onChange={()=> setTransactionalForm({...transactionalForm, variaveis: {...transactionalForm.variaveis, [v.k]: !transactionalForm.variaveis[v.k] }})} ariaLabel={`Selecionar ${v.label}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {transactionalChannel === 'E-mail' && (
                    <div className="space-y-2 md:col-span-12">
                      <button onClick={()=> setTransactionalForm({...transactionalForm, anexosAberto: !transactionalForm.anexosAberto})} className="px-4 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-200 hover:border-white/40 transition font-bold">
                        Anexos do e-mail
                      </button>
                      {transactionalForm.anexosAberto && (
                        <div className="mt-2 p-4 rounded-xl border-2 border-dashed border-slate-700 bg-slate-950/50 space-y-3">
                          <div className="text-xs text-slate-400">
                            Arquivos válidos: PDF até 5MB, JPG/PNG até 2MB, DOCX até 5MB. Máx 2 arquivos.
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <label className="px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-sm cursor-pointer hover:bg-slate-700">
                              Escolher arquivos
                              <input type="file" multiple accept={acceptAttr} className="hidden" onChange={(e)=> validateAndSetAttachments(e.target.files)} />
                            </label>
                            {transactionalForm.anexos && transactionalForm.anexos.length > 0 && (
                              <div className="flex flex-col gap-2 w-full">
                                {transactionalForm.anexos.map((a, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
                                    <div className="text-sm text-slate-300">
                                      {a.name} <span className="text-xs text-slate-500">({(a.size/1024/1024).toFixed(2)} MB)</span>
                                    </div>
                                    <button className="text-slate-400 hover:text-rose-400 text-xs font-bold" onClick={()=> setTransactionalForm({...transactionalForm, anexos: transactionalForm.anexos.filter((_,i) => i !== idx)})}>Remover</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-800">
              <button
                onClick={()=> {
                  setShowTransactionalModal(false);
                  setEditingTemplate(null);
                }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >Cancelar</button>
              <button onClick={handleTransactionalSave} style={{backgroundColor: partner.color}} className="px-6 py-2 rounded-lg text-white font-bold shadow-lg hover:opacity-90 transition">Salvar Template</button>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && previewTemplate && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white">Pré-visualização do E-mail</h3>
              <div className="flex gap-2">
                <button onClick={()=>{
                  const html = buildTransactionalHtml(previewTemplate);
                  const w = window.open();
                  if (w) { w.document.open(); w.document.write(html); w.document.close(); }
                }} className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-sm font-bold">Abrir em nova aba</button>
                <button onClick={() => setShowPreviewModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
              </div>
            </div>
            <div className="p-4 overflow-auto bg-slate-800">
              <iframe title="preview" style={{width:'100%',height:'70vh',background:'white',borderRadius:'12px'}} srcDoc={buildTransactionalHtml(previewTemplate)} />
            </div>
          </div>
        </div>
      )}

      {/* Modal simplificado removido por solicitação: criação/edição acontece no modal transacional */}
      
    </div>
  );
};

void AdminCommunications;
}

function AdminCommunicationsLazy(props) {
  return <AdminCommunicationsView {...props} />;
}

const AdminCertificatesView = lazyWithRetry(
  () => import('./views/admin/AdminCertificates.jsx'),
  { cacheKey: 'admin-certificates' }
);

function AdminCertificatesLazy(props) {
  return (
    <Suspense fallback={<div className="p-6 text-slate-500">Carregando...</div>}>
      <AdminCertificatesView {...props} />
    </Suspense>
  );
}

const AdminCoursesView = lazyWithRetry(() =>
  import('./views/courses/CourseViews.jsx').then((m) => ({ default: m.AdminCourses }))
, { cacheKey: 'admin-courses' });

function AdminCoursesLazy(props) {
  return (
    <Suspense fallback={<div className="p-6 text-slate-500">Carregando...</div>}>
      <AdminCoursesView {...props} />
    </Suspense>
  );
}

const StudentCourseViewView = lazyWithRetry(() =>
  import('./views/courses/CourseViews.jsx').then((m) => ({ default: m.StudentCourseView }))
, { cacheKey: 'student-course-view' });

function StudentCourseViewLazy(props) {
  return (
    <Suspense fallback={<div className="p-6 text-slate-500">Carregando...</div>}>
      <StudentCourseViewView {...props} />
    </Suspense>
  );
}

const AdminPartners = ({ currentPartner, onPartnerChange, partners, setPartners }) => {
  const [editing, setEditing] = useState(null);
  const [notification, setNotification] = useState(null);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [loading, setLoading] = useState(false);

  const reloadPartners = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sas_partners')
        .select('id, name, cnpj, logo_url, site, default_url, color, status, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      const mapped = rows.map((p) => ({
        id: p.id,
        name: p.name,
        cnpj: p.cnpj,
        logo: p.logo_url,
        site: p.site,
        defaultUrl: p.default_url,
        color: p.color || '#f97316',
        status: p.status || 'Ativo',
      }));
      setPartners(mapped);

      if (!currentPartner?.id && mapped.length > 0) {
        onPartnerChange(mapped[0]);
      }
      if (currentPartner?.id && !mapped.some(p => p.id === currentPartner.id) && mapped.length > 0) {
        onPartnerChange(mapped[0]);
      }
    } catch (err) {
      setPartners([]);
      setNotification({ message: String(err?.message || err || 'Falha ao carregar parceiros.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadPartners();
  }, []);

  const handleDelete = (p) => {
    setConfirmModal({
      isOpen: true,
      title: 'Excluir Parceiro SAS',
      message: `Tem certeza que deseja excluir o parceiro "${p?.name || ''}"?`,
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('sas_partners').delete().eq('id', p.id);
          if (error) throw error;
          setNotification({ message: 'Parceiro excluído com sucesso!', type: 'success' });
          await reloadPartners();
        } catch (err) {
          setNotification({ message: String(err?.message || err || 'Falha ao excluir parceiro.'), type: 'error' });
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      },
    });
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
      <h1 className="text-3xl font-bold text-white mb-8">Rede de Parceiros SAS</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {loading && (
          <div className="p-6 bg-slate-900 border border-slate-800 rounded-xl text-slate-400">Carregando...</div>
        )}
        {partners.map(p => (
          <div key={p.id} onClick={() => onPartnerChange(p)} className={`p-6 bg-slate-900 border-2 rounded-xl cursor-pointer transition shadow-xl group ${currentPartner.id === p.id ? 'border-orange-500 ring-4 ring-orange-500/10 scale-[1.02]' : 'border-slate-800 hover:border-slate-700'}`}>
            <div className="flex justify-between items-start mb-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${p.color}20` }}>
                {p.logo ? <img src={p.logo} alt={p.name} className="w-8 h-8 object-contain" /> : <Briefcase size={24} style={{ color: p.color }} />}
              </div>
              <div className="flex gap-2">
                <button className="p-2 hover:bg-slate-800 rounded transition" onClick={(e) => { e.stopPropagation(); setEditing(p); }}><Edit2 size={16} className="text-slate-500 group-hover:text-white" /></button>
                <button className="p-2 hover:bg-rose-500/10 rounded transition text-rose-500/40 hover:text-rose-500" onClick={(e) => { e.stopPropagation(); handleDelete(p); }}><Trash2 size={16} /></button>
              </div>
            </div>
            <h3 className="font-bold text-xl text-white truncate pr-2">{p.name}</h3>
            <p className="text-slate-400 text-sm font-mono mt-2 truncate opacity-60">{p.defaultUrl}</p>
            <div className="mt-6 flex items-center justify-between">
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }}></div><span className="text-[10px] font-mono text-slate-500">{p.color.toUpperCase()}</span></div>
               {currentPartner.id === p.id && <span className="px-2 py-0.5 bg-orange-500 text-white text-[9px] font-black rounded uppercase shadow-lg shadow-orange-500/20">Logado</span>}
            </div>
          </div>
        ))}
        <div onClick={() => setEditing({ name: '', cnpj: '', site: '', defaultUrl: '', color: '#3b82f6', status: 'Ativo', logo: '' })} className="border-2 border-dashed border-slate-800 rounded-xl flex flex-col items-center justify-center p-8 hover:border-slate-600 transition cursor-pointer text-slate-500 hover:text-slate-300 min-h-[220px]">
           <Plus size={32} className="mb-2" />
           <span className="font-bold">Novo Parceiro SAS</span>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6">{editing.id ? 'Configurar Parceiro' : 'Novo Parceiro'}</h3>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nome Fantasia</label><input type="text" value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white" /></div>
              <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">CNPJ</label><input type="text" value={editing.cnpj || ''} onChange={e => setEditing({...editing, cnpj: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white font-mono" /></div>
              <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Logo URL (Opcional)</label><input type="text" value={editing.logo || ''} onChange={e => setEditing({...editing, logo: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white" placeholder="https://..." /></div>
              <div className="grid grid-cols-2 gap-4">
                 <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Site</label><input type="text" value={editing.site || ''} onChange={e => setEditing({...editing, site: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white" /></div>
                 <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">URL Padrão</label><input type="text" value={editing.defaultUrl || ''} onChange={e => setEditing({...editing, defaultUrl: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cor da Marca</label><input type="color" value={editing.color} onChange={e => setEditing({...editing, color: e.target.value})} className="w-full h-11 bg-transparent rounded cursor-pointer p-0" /></div>
                <div className="flex flex-col gap-1.5"><label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status</label><select value={editing.status} onChange={e => setEditing({...editing, status: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none"><option value="Ativo">Ativo</option><option value="Inativo">Inativo</option></select></div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setEditing(null)} className="px-5 py-2 text-slate-400 font-bold">Cancelar</button>
                <button type="button" onClick={async () => {
                  try {
                    const payload = {
                      name: String(editing.name || '').trim(),
                      cnpj: String(editing.cnpj || '').trim(),
                      logo_url: String(editing.logo || '').trim() || null,
                      site: String(editing.site || '').trim() || null,
                      default_url: String(editing.defaultUrl || '').trim() || null,
                      color: String(editing.color || '').trim() || null,
                      status: editing.status || 'Ativo',
                    };
                    if (!payload.name) throw new Error('Nome Fantasia é obrigatório.');
                    if (!payload.cnpj) throw new Error('CNPJ é obrigatório.');

                    if (editing.id) {
                      const { error } = await supabase.from('sas_partners').update(payload).eq('id', editing.id);
                      if (error) throw error;
                      setNotification({ message: 'Parceiro atualizado com sucesso!', type: 'success' });
                    } else {
                      const { error } = await supabase.from('sas_partners').insert(payload);
                      if (error) throw error;
                      setNotification({ message: 'Parceiro criado com sucesso!', type: 'success' });
                    }

                    await reloadPartners();
                    setEditing(null);
                  } catch (err) {
                    setNotification({ message: String(err?.message || err || 'Falha ao salvar parceiro.'), type: 'error' });
                  }
                }} style={{ backgroundColor: editing.color }} className="px-8 py-2 rounded-lg text-white font-bold shadow-lg">Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

 

const MOCK_LIBRARY_ITEMS = [
  { id: 1, title: 'Manual de Vendas', type: 'PDF' },
  { id: 2, title: 'Vídeo Institucional', type: 'Link' },
  { id: 3, title: 'Apostila de Liderança', type: 'PDF' },
  { id: 4, title: 'Quiz de Segurança', type: 'Form' },
];

const CourseManagementCard = ({ course, onEdit, onViewStudent, partner, users = [], categories = [] }) => {
  const instructorId = course.responsible_instructor_id || course.instructor;
  const coordinatorId = course.coordinator;
  const categoryId = course.category_id || course.category;
  const workload = course.workload_hours || course.workload;

  const instructor = users.find(u => u.id === instructorId || u.name === instructorId)?.name || (typeof course.instructor === 'string' ? course.instructor : instructorId || '-');
  const coordinator = users.find(u => u.id === coordinatorId || u.name === coordinatorId)?.name || (typeof course.coordinator === 'string' ? course.coordinator : coordinatorId || '-');
  const categoryName = categories.find(c => c.id === categoryId)?.name || (categoryId && categoryId !== 'Geral' ? categoryId : 'Geral');
  
  const modulesCount = course.modules?.length || 0;
  const lessonsCount = (course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0) + (course.standaloneLessons?.length || 0);
  const filesCount =
    (course.modules?.reduce(
      (acc, m) =>
        acc +
        (m.lessons?.reduce(
          (lAcc, l) =>
            lAcc +
            (Array.isArray(l.contents)
              ? l.contents.filter(
                  (c) =>
                    c.type !== 'quiz' &&
                    c.type !== 'video' &&
                    c.type !== 'vimeo' &&
                    c.type !== 'youtube' &&
                    !c.url?.includes('youtube.com') &&
                    !c.url?.includes('vimeo.com')
                ).length
              : 0),
          0
        ) || 0),
      0
    ) || 0) +
    (course.standaloneLessons?.reduce(
      (acc, l) =>
        acc +
        (Array.isArray(l.contents)
          ? l.contents.filter(
              (c) =>
                c.type !== 'quiz' &&
                c.type !== 'video' &&
                c.type !== 'vimeo' &&
                c.type !== 'youtube' &&
                !c.url?.includes('youtube.com') &&
                !c.url?.includes('vimeo.com')
            ).length
          : 0),
      0
    ) || 0);

  const accentColor = partner?.color || '#f97316';

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-lg hover:border-slate-700 transition group h-full flex flex-col">
      <div className="h-56 relative overflow-hidden">
         <CourseWorkloadBadge workload={workload} />
         <img src={course.thumb || course.image_url} alt={course.title || course.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
         <div className="absolute top-2 right-2 flex gap-2">
            {course.status === 'draft' && <span className="bg-amber-500/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm backdrop-blur-sm">RASCUNHO</span>}
         </div>
         {(onEdit || onViewStudent) && (
           <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition">
             {onEdit && <button onClick={() => onEdit(course)} className="bg-white/20 backdrop-blur-sm p-2 rounded-full text-white hover:bg-white/40 transition" title="Editar"><Edit2 size={20} /></button>}
             {onViewStudent && (
                <button 
                    onClick={() => onViewStudent(course)} 
                    className="backdrop-blur-sm p-2 rounded-full text-white transition shadow-lg" 
                    style={{ backgroundColor: `${accentColor}cc` }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = accentColor}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${accentColor}cc`}
                    title="Visão Aluno"
                >
                    <Eye size={20} />
                </button>
             )}
           </div>
         )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="mb-2">
           <span 
             className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border shadow-sm"
             style={{ color: accentColor, backgroundColor: `${accentColor}1a`, borderColor: `${accentColor}33`, boxShadow: `0 0 8px ${accentColor}1a` }}
           >
             {categoryName}
           </span>
        </div>
        <h3 className="text-lg font-bold text-white mb-1 line-clamp-1">{course.title || course.name}</h3>
        
        {course.ratingCount > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={14} 
                  className={i < Math.round(course.averageRating) ? "fill-yellow-400 text-yellow-400" : "text-slate-600"} 
                />
              ))}
            </div>
            <span className="text-xs text-slate-400 ml-1">
              {course.averageRating.toFixed(1)} ({course.ratingCount} {course.ratingCount === 1 ? 'avaliação' : 'avaliações'})
            </span>
          </div>
        )}

        <p className="text-slate-400 text-sm mb-4 line-clamp-2">{course.description || 'Sem descrição.'}</p>
        
        <div className="flex gap-3 mb-4 py-3 border-y border-slate-800/50">
          <div className="flex flex-col items-center gap-1 flex-1">
            <span className="text-white font-black text-sm">{modulesCount}</span>
            <span className="text-[9px] uppercase font-bold text-slate-500 tracking-tighter">Módulos</span>
          </div>
          <div className="w-px h-8 bg-slate-800"></div>
          <div className="flex flex-col items-center gap-1 flex-1">
            <span className="text-white font-black text-sm">{lessonsCount}</span>
            <span className="text-[9px] uppercase font-bold text-slate-500 tracking-tighter">Aulas</span>
          </div>
          <div className="w-px h-8 bg-slate-800"></div>
          <div className="flex flex-col items-center gap-1 flex-1">
            <span className="text-white font-black text-sm">{filesCount}</span>
            <span className="text-[9px] uppercase font-bold text-slate-500 tracking-tighter">Arquivos</span>
          </div>
        </div>

        <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2 min-0 flex-1" title={`Instrutor: ${instructor}`}>
            <User size={14} style={{ color: accentColor }} className="shrink-0" />
            <span className="truncate font-medium">{instructor}</span>
          </div>
          <div className="flex items-center gap-2 min-w-0 flex-1" title={`Coordenador: ${coordinator}`}>
            <UserCheck size={14} className="text-indigo-500 shrink-0" />
            <span className="truncate font-medium">{coordinator}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminCourses = ({ partner, users, categories, courses, setCourses, assessments, onViewStudent }) => {
  const [showModal, setShowModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [activeTab, setActiveTab] = usePersistentTab('admin_courses_legacy', 'modules');
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    thumb: null,
    instructor: '',
    coordinator: '',
    workload: '',
    category: '',
    modules: [],
    lessons: [],
    assessmentEnabled: false,
    assessmentId: '',
    minGrade: '',
    attempts: '',
    questionsPerAttempt: ''
  });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCourseForm(prev => ({ ...prev, thumb: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddModule = () => {
    setCourseForm({
      ...courseForm,
      modules: [...courseForm.modules, { id: Date.now(), title: '', description: '' }]
    });
    setActiveTab('modules');
  };

  const handleAddLesson = () => {
    setCourseForm({
      ...courseForm,
      lessons: [...courseForm.lessons, { 
        id: Date.now() + Math.random(), 
        title: '', 
        description: '', 
        type: 'video', 
        contentType: 'library', 
        contentUrl: '', 
        contentId: '',
        moduleId: '',
        minTimeEnabled: false,
        duration: ''
      }]
    });
    setActiveTab('lessons');
  };

  const updateModule = (id, field, value) => {
    setCourseForm({
      ...courseForm,
      modules: courseForm.modules.map(m => m.id === id ? { ...m, [field]: value } : m)
    });
  };

  const updateLesson = (lessonId, field, value) => {
    setCourseForm({
      ...courseForm,
      lessons: courseForm.lessons.map(l => l.id === lessonId ? { ...l, [field]: value } : l)
    });
  };

  const removeModule = (id) => {
    setCourseForm({
      ...courseForm,
      modules: courseForm.modules.filter(m => m.id !== id),
      lessons: courseForm.lessons.map(l => l.moduleId == id ? { ...l, moduleId: '' } : l)
    });
  };

  const removeLesson = (lessonId) => {
    setCourseForm({
      ...courseForm,
      lessons: courseForm.lessons.filter(l => l.id !== lessonId)
    });
  };

  const handleSave = (status) => {
    const modulesWithLessons = courseForm.modules.map(m => ({
      ...m,
      lessons: courseForm.lessons.filter(l => l.moduleId == m.id)
    }));
    const standaloneLessons = courseForm.lessons.filter(l => !l.moduleId);
    
    const courseData = {
      ...courseForm,
      progress: courseForm.progress || 0,
      thumb: courseForm.thumb || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=60",
      modules: modulesWithLessons,
      standaloneLessons: standaloneLessons,
      status: status
    };
    delete courseData.lessons;

    if (courseForm.id) {
      setCourses(courses.map(c => c.id === courseForm.id ? { ...courseData, id: courseForm.id } : c));
      setNotification({ message: 'Curso atualizado com sucesso!', type: 'success' });
    } else {
      setCourses([...courses, { ...courseData, id: Date.now() }]);
      setNotification({ message: status === 'published' ? 'Curso publicado com sucesso!' : 'Rascunho salvo com sucesso!', type: 'success' });
    }

    setShowModal(false);
    setCourseForm({ title: '', description: '', thumb: null, instructor: '', coordinator: '', workload: '', category: '', modules: [], lessons: [], assessmentEnabled: false, assessmentId: '', minGrade: '', attempts: '', questionsPerAttempt: '', sequentialEnforcement: false });
  };

  const handleEditCourse = (course) => {
    const allLessons = [
      ...(course.modules?.flatMap(m => m.lessons?.map(l => ({ ...l, moduleId: m.id })) || []) || []),
      ...(course.standaloneLessons || [])
    ];

    setCourseForm({
      id: course.id,
      title: course.title || '',
      description: course.description || '',
      thumb: course.thumb,
      instructor: course.instructor || '',
      coordinator: course.coordinator || '',
      workload: formatCourseWorkloadHHMM(course.workload || course.workload_hours || ''),
      category: course.category || '',
      modules: course.modules?.map(m => ({ id: m.id, title: m.title, description: m.description })) || [],
      lessons: allLessons,
      progress: course.progress,
      sequentialEnforcement: course.sequentialEnforcement || false,
      assessmentEnabled: course.assessmentEnabled || false,
      assessmentId: course.assessmentId || '',
      minGrade: course.minGrade || '',
      attempts: course.attempts || '',
      questionsPerAttempt: course.questionsPerAttempt || '',
      isFeatured: course.isFeatured || course.is_featured || false
    });
    setShowFileDetails(true);
    setShowModal(true);
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      <ConfirmModal 
        isOpen={confirmModal.isOpen} 
        title={confirmModal.title} 
        message={confirmModal.message} 
        onConfirm={confirmModal.onConfirm} 
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })} 
      />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Cursos & Conteúdos</h1>
          <p className="text-slate-400">Gerenciamento de cursos e trilhas de aprendizado.</p>
        </div>
        <button onClick={() => { setCourseForm({ title: '', description: '', thumb: null, instructor: '', coordinator: '', workload: '', category: '', modules: [], lessons: [], sequentialEnforcement: false, isFeatured: false, assessmentEnabled: false, assessmentId: '', minGrade: '', attempts: '', questionsPerAttempt: '' }); setShowModal(true); }} style={{ backgroundColor: partner.color }} className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition">
          <Plus size={20} /> Novo Curso
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {courses.map(course => (
          <CourseManagementCard 
            key={course.id} 
            course={course} 
            onEdit={handleEditCourse} 
            onViewStudent={onViewStudent} 
            partner={partner} 
            users={users}
            categories={categories}
          />
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl my-8 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">{courseForm.id ? 'Editar Curso' : 'Criar Novo Curso'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white"><X size={24} /></button>
            </div>
            
            <div className="p-4 md:p-8 overflow-y-auto custom-scrollbar">
              <div className="flex flex-col md:flex-row items-stretch gap-6 mb-8">
                 <div className="w-full md:w-1/2 flex flex-col gap-4">
                    <label className="block w-full aspect-video bg-slate-950 border-2 border-dashed border-slate-800 rounded-lg hover:border-slate-600 transition cursor-pointer flex flex-col items-center justify-center relative overflow-hidden group">
                      {courseForm.thumb ? (
                        <>
                          <img src={courseForm.thumb} alt="Preview Capa" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                            <span className="text-white font-bold text-sm">Trocar Capa</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <ImageIcon size={32} className="text-slate-600 mb-2" />
                          <span className="text-slate-500 text-xs font-bold uppercase">Upload Capa</span>
                        </>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                    <div className="text-xs text-slate-500 mt-1">
                      Recomendado: 1280×720 px (16:9) · Máx 2MB · JPG/PNG/WEBP
                    </div>

                    <div className={`border rounded-lg p-4 flex items-start gap-3 transition-colors ${courseForm.sequentialEnforcement ? 'bg-teal-500/10 border-teal-500/30' : 'bg-slate-950 border-slate-800'}`}>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setCourseForm({ ...courseForm, sequentialEnforcement: !courseForm.sequentialEnforcement })}
                          className={`${courseForm.sequentialEnforcement ? 'bg-teal-500' : 'bg-slate-700'} w-10 h-5 rounded-full relative transition-colors duration-300`}
                          aria-pressed={courseForm.sequentialEnforcement}
                          aria-label="Ativar Progressão Sequencial"
                        >
                          <div className={`${courseForm.sequentialEnforcement ? 'left-6' : 'left-1'} absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}></div>
                        </button>
                        <div className="flex flex-col">
                          <span className={`${courseForm.sequentialEnforcement ? 'text-teal-400' : 'text-slate-400'} text-sm font-bold`}>Progressão Sequencial</span>
                          <span className="text-xs text-slate-500 leading-tight mt-1">
                            Exige que o aluno conclua o conteúdo anterior para desbloquear o próximo.
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className={`border rounded-lg p-4 flex items-start gap-3 transition-colors ${courseForm.isFeatured ? 'bg-orange-500/10 border-orange-500/30' : 'bg-slate-950 border-slate-800'}`}>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setCourseForm({ ...courseForm, isFeatured: !courseForm.isFeatured })}
                          className={`${courseForm.isFeatured ? 'bg-orange-500' : 'bg-slate-700'} w-10 h-5 rounded-full relative transition-colors duration-300`}
                          aria-pressed={courseForm.isFeatured}
                          aria-label="Destacar Curso"
                        >
                          <div className={`${courseForm.isFeatured ? 'left-6' : 'left-1'} absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}></div>
                        </button>
                        <div className="flex flex-col">
                          <span className={`${courseForm.isFeatured ? 'text-orange-400' : 'text-slate-400'} text-sm font-bold`}>Destacar Curso</span>
                          <span className="text-xs text-slate-500 leading-tight mt-1">
                            Exibe o curso em destaque na vitrine inicial (Carrossel).
                          </span>
                        </div>
                      </div>
                    </div>
                 </div>
                <div className="w-full md:w-1/2 flex flex-col space-y-4">
                  <div className="space-y-2">
                     <label className="text-xs font-bold text-slate-500 uppercase">Nome do Curso</label>
                     <input type="text" value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" placeholder="Ex: Liderança Ágil" />
                   </div>
                  <div className="space-y-2 flex flex-col flex-1">
                     <label className="text-xs font-bold text-slate-500 uppercase">Descrição do Curso</label>
                    <textarea value={courseForm.description} onChange={e => setCourseForm({...courseForm, description: e.target.value})} className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none resize-none flex-1 min-h-[260px]" placeholder="Breve resumo do curso..." />
                   </div>
                 </div>
              </div>

              <div className={`${courseForm.assessmentEnabled ? 'bg-teal-500/10 border-teal-500/30' : 'bg-slate-950 border-slate-800'} border rounded-lg p-4 w-full mb-8`}>
                <div className="flex items-end gap-3 md:gap-4 flex-nowrap overflow-x-auto">
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => setCourseForm({ ...courseForm, assessmentEnabled: !courseForm.assessmentEnabled })}
                      className={`${courseForm.assessmentEnabled ? 'bg-teal-500' : 'bg-slate-700'} w-10 h-5 rounded-full relative transition-colors duration-300`}
                    >
                      <div className={`${courseForm.assessmentEnabled ? 'left-6' : 'left-1'} absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}></div>
                    </button>
                    <span className={`${courseForm.assessmentEnabled ? 'text-teal-400' : 'text-slate-400'} text-sm font-bold`}>Avaliação</span>
                  </div>

                  {courseForm.assessmentEnabled && (
                  <div className="flex items-end gap-3 md:gap-4 flex-nowrap w-full animate-fade-in">
                    <div className="space-y-1 flex-1 min-w-[420px] pr-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">Selecionar Avaliação</label>
                      <select 
                        value={courseForm.assessmentId}
                        onChange={e => setCourseForm({ ...courseForm, assessmentId: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none"
                      >
                        <option value="">Selecione...</option>
                        {(assessments || []).map(a => (
                          <option key={a.id} value={a.id}>{a.title} ({a.type || 'Avaliação'})</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1 w-32 md:w-36 shrink-0 pr-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">Nota Mínima</label>
                      <input 
                        type="number" min="0" max="10" step="0.1"
                        value={courseForm.minGrade}
                        onChange={e => setCourseForm({ ...courseForm, minGrade: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none"
                        placeholder="Ex: 7.0"
                      />
                    </div>
                    <div className="space-y-1 w-28 md:w-32 shrink-0 pr-1">
                      <label className="text-xs font-bold text-slate-500 uppercase">Tentativas</label>
                      <input 
                        type="number" min="1"
                        value={courseForm.attempts}
                        onChange={e => setCourseForm({ ...courseForm, attempts: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none"
                        placeholder="Ex: 3"
                      />
                    </div>
                    <div className="space-y-1 w-40 md:w-44 shrink-0">
                      <label className="text-xs font-bold text-slate-500 uppercase">Questões/Tentativa</label>
                      <input 
                        type="number" min="1"
                        value={courseForm.questionsPerAttempt}
                        onChange={e => setCourseForm({ ...courseForm, questionsPerAttempt: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none"
                        placeholder="Ex: 10"
                      />
                    </div>
                  </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                 <div className="space-y-2">
                   <label className="text-xs font-bold text-slate-500 uppercase">Categoria</label>
                   <select value={courseForm.category} onChange={e => setCourseForm({...courseForm, category: e.target.value})} className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none">
                     <option value="">Selecione...</option>
                     {(categories || []).filter(c => (c?.domain ? c.domain === 'curso' : true)).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                   </select>
                 </div>
                 <div className="space-y-2">
                   <label className="text-xs font-bold text-slate-500 uppercase">Instrutor</label>
                   <select value={courseForm.instructor} onChange={e => setCourseForm({...courseForm, instructor: e.target.value})} className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none">
                     <option value="">Selecione...</option>
                     {users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.profile})</option>)}
                   </select>
                 </div>
                 <div className="space-y-2">
                   <label className="text-xs font-bold text-slate-500 uppercase">Coordenador</label>
                   <select value={courseForm.coordinator} onChange={e => setCourseForm({...courseForm, coordinator: e.target.value})} className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none">
                     <option value="">Selecione...</option>
                     {users.map(u => <option key={u.id} value={u.name}>{u.name} ({u.profile})</option>)}
                   </select>
                 </div>
                 <div className="space-y-2">
                   <label className="text-xs font-bold text-slate-500 uppercase">Carga Horária</label>
                   <input type="text" value={courseForm.workload} onChange={e => setCourseForm({...courseForm, workload: e.target.value})} className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" placeholder="Ex: 40:00" />
                 </div>
              </div>

              <div className="border-t border-slate-800 pt-8">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-lg font-bold text-white flex items-center gap-2"><List size={20} /> Estrutura do Curso</h4>
                  <div className="flex gap-3">
                    <button onClick={handleAddModule} className={`text-xs px-3 py-2 rounded font-bold transition flex items-center gap-2 border ${activeTab === 'modules' ? 'bg-slate-700 text-white border-slate-600' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                      <Plus size={14} /> Novo Módulo
                    </button>
                    <button onClick={handleAddLesson} className={`text-xs px-3 py-2 rounded font-bold transition flex items-center gap-2 border ${activeTab === 'lessons' ? 'bg-slate-700 text-white border-slate-600' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                      <Plus size={14} /> Nova Aula
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-4 mb-6 border-b border-slate-800">
                   <button onClick={() => setActiveTab('modules')} className={`pb-2 px-4 text-sm font-bold uppercase tracking-wide transition ${activeTab === 'modules' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-slate-500 hover:text-slate-300'}`}>Módulos</button>
                   <button onClick={() => setActiveTab('lessons')} className={`pb-2 px-4 text-sm font-bold uppercase tracking-wide transition ${activeTab === 'lessons' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-slate-500 hover:text-slate-300'}`}>Aulas</button>
                </div>

                <div className="space-y-6">
                  {activeTab === 'modules' && (
                    <div className="space-y-4">
                      {courseForm.modules.map((module, mIndex) => (
                        <div key={module.id} className="bg-slate-950/50 border border-slate-800 rounded-xl p-6 relative group">
                          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition">
                             <button onClick={() => removeModule(module.id)} className="text-rose-500 hover:bg-rose-500/10 p-2 rounded"><Trash2 size={16} /></button>
                          </div>
                          <div className="flex flex-col gap-2">
                             <label className="text-xs font-bold text-slate-500 uppercase block">Nome do Módulo {mIndex + 1}</label>
                             <input type="text" value={module.title} onChange={e => updateModule(module.id, 'title', e.target.value)} className="w-full bg-slate-900 border border-slate-800 p-3 rounded-lg text-white focus:border-slate-600 transition outline-none font-bold" placeholder={`Título do Módulo ${mIndex + 1}`} />
                             <textarea rows="2" value={module.description} onChange={e => updateModule(module.id, 'description', e.target.value)} className="w-full bg-slate-900 border border-slate-800 p-3 rounded-lg text-white text-sm focus:border-slate-600 transition outline-none resize-none" placeholder="Descrição do módulo..." />
                          </div>
                        </div>
                      ))}
                      {courseForm.modules.length === 0 && (
                        <div className="text-center py-8 border-2 border-dashed border-slate-800 rounded-xl text-slate-600">
                          Nenhum módulo criado.
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'lessons' && (
                    <div className="space-y-4">
                      {courseForm.lessons.map((lesson, lIndex) => (
                        <div key={lesson.id} className="bg-slate-950/50 border border-slate-800 rounded-xl p-6 relative group">
                          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition">
                             <button onClick={() => removeLesson(lesson.id)} className="text-rose-500 hover:bg-rose-500/10 p-2 rounded"><Trash2 size={16} /></button>
                          </div>
                          
                          <div className="flex flex-col gap-4">
                             <div className="space-y-2">
                               <label className="text-xs font-bold text-slate-500 uppercase block">Título da Aula</label>
                               <div className="flex gap-3 items-center">
                                  <div className="bg-slate-900 p-2 rounded text-slate-500"><Video size={16} /></div>
                                  <input type="text" value={lesson.title} onChange={e => updateLesson(lesson.id, 'title', e.target.value)} className="flex-1 bg-slate-900 border border-slate-800 p-2 rounded text-sm text-white focus:border-slate-700 transition outline-none" placeholder={`Título da Aula ${lIndex + 1}`} />
                               </div>
                             </div>

                             <div className="space-y-2">
                               <label className="text-xs font-bold text-slate-500 uppercase block">Vincular Módulo</label>
                               <select value={lesson.moduleId || ''} onChange={e => updateLesson(lesson.id, 'moduleId', e.target.value)} className="w-full bg-slate-900 border border-slate-800 p-2 rounded text-sm text-white focus:border-slate-700 transition outline-none">
                                  <option value="">Aula Avulsa (Sem Módulo)</option>
                                  {courseForm.modules.map(m => (
                                    <option key={m.id} value={m.id}>{m.title || 'Sem Título'}</option>
                                  ))}
                               </select>
                             </div>

                             <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase block">Descrição</label>
                                <textarea rows="2" value={lesson.description} onChange={e => updateLesson(lesson.id, 'description', e.target.value)} className="w-full bg-slate-900 border border-slate-800 p-2 rounded text-xs text-slate-400 focus:text-white focus:border-slate-700 transition outline-none resize-none" placeholder="Descrição da aula..." />
                             </div>
                             
                             <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
                                <div className="flex gap-4 mb-3 border-b border-slate-800 pb-2">
                                   <button onClick={() => updateLesson(lesson.id, 'contentType', 'library')} className={`text-xs font-bold uppercase tracking-wide pb-1 transition ${lesson.contentType !== 'link' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-slate-500 hover:text-slate-300'}`}>Biblioteca</button>
                                   <button onClick={() => updateLesson(lesson.id, 'contentType', 'link')} className={`text-xs font-bold uppercase tracking-wide pb-1 transition ${lesson.contentType === 'link' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-slate-500 hover:text-slate-300'}`}>Link Externo</button>
                                </div>

                                {lesson.contentType === 'link' ? (
                                   <div className="flex items-center gap-2">
                                      <LinkIcon size={14} className="text-slate-500" />
                                      <input type="text" value={lesson.contentUrl || ''} onChange={e => updateLesson(lesson.id, 'contentUrl', e.target.value)} className="flex-1 bg-slate-950 border border-slate-800 p-2 rounded text-xs text-white focus:border-slate-700 transition outline-none" placeholder="Cole o link da aula aqui (YouTube, Vimeo, etc)..." />
                                   </div>
                                ) : (
                                   <div className="flex items-center gap-2">
                                      <FileText size={14} className="text-slate-500" />
                                      <select value={lesson.contentId || ''} onChange={e => updateLesson(lesson.id, 'contentId', e.target.value)} className="flex-1 bg-slate-950 border border-slate-800 p-2 rounded text-xs text-white focus:border-slate-700 transition outline-none">
                                         <option value="">Selecione um conteúdo da biblioteca...</option>
                                         {MOCK_LIBRARY_ITEMS.map(item => (
                                            <option key={item.id} value={item.id}>{item.title} ({item.type})</option>
                                         ))}
                                      </select>
                                   </div>
                                )}
                             </div>

                             <div className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded-lg p-3">
                                <div className="flex items-center gap-3">
                                   <button 
                                      onClick={() => updateLesson(lesson.id, 'minTimeEnabled', !lesson.minTimeEnabled)}
                                      className={`w-10 h-5 rounded-full relative transition-colors duration-300 ${lesson.minTimeEnabled ? 'bg-teal-500' : 'bg-slate-700'}`}
                                   >
                                      <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm ${lesson.minTimeEnabled ? 'left-6' : 'left-1'}`}></div>
                                   </button>
                                   <span className="text-xs font-bold text-slate-500 uppercase tracking-widest cursor-pointer select-none" onClick={() => updateLesson(lesson.id, 'minTimeEnabled', !lesson.minTimeEnabled)}>Definir Tempo Mínimo</span>
                                </div>
                                
                                {lesson.minTimeEnabled && (
                                   <div className="flex items-center gap-2 animate-fade-in pl-4 border-l border-slate-800">
                                      <Clock size={14} className="text-teal-500" />
                                      <div className="flex items-center gap-1">
                                         <input 
                                           type="text" 
                                           maxLength="2"
                                           value={(() => {
                                             if (!lesson.duration) return '';
                                             const parts = lesson.duration.toString().split(':');
                                             return parts.length === 3 ? parts[0] : '';
                                           })()}
                                           onChange={e => {
                                             const h = e.target.value.replace(/\D/g, '').slice(0, 2);
                                             const parts = (lesson.duration || '00:00:00').toString().split(':');
                                             const m = parts.length >= 2 ? parts[parts.length - 2] : '00';
                                             const s = parts.length >= 1 ? parts[parts.length - 1] : '00';
                                             updateLesson(lesson.id, 'duration', `${h.padStart(2, '0')}:${m}:${s}`);
                                           }}
                                           className="bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white focus:border-teal-500 transition outline-none w-8 text-center font-mono placeholder-slate-700" 
                                           placeholder="HH" 
                                         />
                                         <span className="text-slate-600">:</span>
                                         <input 
                                           type="text" 
                                           maxLength="2"
                                           value={(() => {
                                             if (!lesson.duration) return '';
                                             const parts = lesson.duration.toString().split(':');
                                             return parts.length >= 2 ? parts[parts.length - 2] : '';
                                           })()}
                                           onChange={e => {
                                             const m = e.target.value.replace(/\D/g, '').slice(0, 2);
                                             const parts = (lesson.duration || '00:00:00').toString().split(':');
                                             const h = parts.length === 3 ? parts[0] : '00';
                                             const s = parts.length >= 1 ? parts[parts.length - 1] : '00';
                                             updateLesson(lesson.id, 'duration', `${h}:${m.padStart(2, '0')}:${s}`);
                                           }}
                                           className="bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white focus:border-teal-500 transition outline-none w-8 text-center font-mono placeholder-slate-700" 
                                           placeholder="MM" 
                                         />
                                         <span className="text-slate-600">:</span>
                                         <input 
                                           type="text" 
                                           maxLength="2"
                                           value={(() => {
                                             if (!lesson.duration) return '';
                                             const parts = lesson.duration.toString().split(':');
                                             return parts.length >= 1 ? parts[parts.length - 1] : '';
                                           })()}
                                           onChange={e => {
                                             const s = e.target.value.replace(/\D/g, '').slice(0, 2);
                                             const parts = (lesson.duration || '00:00:00').toString().split(':');
                                             const h = parts.length === 3 ? parts[0] : '00';
                                             const m = parts.length >= 2 ? parts[parts.length - 2] : '00';
                                             updateLesson(lesson.id, 'duration', `${h}:${m}:${s.padStart(2, '0')}`);
                                           }}
                                           className="bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white focus:border-teal-500 transition outline-none w-8 text-center font-mono placeholder-slate-700" 
                                           placeholder="SS" 
                                         />
                                      </div>
                                   </div>
                                )}
                             </div>
                          </div>
                        </div>
                      ))}
                      {courseForm.lessons.length === 0 && (
                        <div className="text-center py-8 border-2 border-dashed border-slate-800 rounded-xl text-slate-600">
                          Nenhuma aula criada.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-800 bg-slate-900 rounded-b-xl flex justify-end gap-3">
               <button onClick={() => setShowModal(false)} className="px-5 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
               <button onClick={() => handleSave('draft')} className="px-6 py-2 bg-slate-800 text-white rounded-lg font-bold hover:bg-slate-700 transition border border-slate-700 shadow-lg">Salvar Rascunho</button>
               <button onClick={() => handleSave('published')} style={{ backgroundColor: partner.color }} className="px-8 py-2 rounded-lg text-white font-bold shadow-lg hover:opacity-90 transition">Publicar Curso</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NotesPanel = ({ onClose, notes, onSave, onDelete }) => {
  const [newNote, setNewNote] = useState('');

  const handleAddNote = () => {
    if (newNote.trim()) {
      onSave(newNote);
      setNewNote('');
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 animate-fade-in">
      <div className="p-6 border-b border-slate-800 bg-slate-900 sticky top-0 z-10 flex justify-between items-center">
        <h3 className="font-bold text-white text-lg">Minhas Anotações</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-white transition"><X size={20} /></button>
      </div>
      
      <div className="p-6 border-b border-slate-800 bg-slate-900">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-teal-500 transition outline-none resize-none mb-3 text-sm placeholder:text-slate-600"
          placeholder="Digite sua anotação aqui..."
          rows="4"
        />
        <button 
          onClick={handleAddNote}
          disabled={!newNote.trim()}
          className="w-full bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg font-bold transition shadow-lg flex items-center justify-center gap-2"
        >
          <Check size={18} /> Salvar Anotação
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
        {notes.length === 0 ? (
          <div className="text-center text-slate-500 py-8 flex flex-col items-center justify-center h-full">
            <Edit2 size={48} className="mb-4 opacity-20" />
            <p className="font-medium">Nenhuma anotação ainda.</p>
            <p className="text-xs mt-1">Escreva algo abaixo para começar.</p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="bg-slate-950 border border-slate-800 p-4 rounded-lg group hover:border-slate-700 transition relative">
              <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">{note.date}</span>
                <button onClick={() => onDelete(note.id)} className="text-slate-600 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition absolute top-2 right-2 p-1 rounded hover:bg-rose-500/10"><Trash2 size={14} /></button>
              </div>
              <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{note.text}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// --- MAIN APP ---

const StudentCourseView = ({ course, onBack, role }) => {
  const auth = useAuth();
  const userId = auth.session?.user?.id || auth.session?.user?.email || 'anon';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeModule, setActiveModule] = useState(course.modules?.[0]?.id || null);
  const [activeLesson, setActiveLesson] = useState(
    course.modules?.[0]?.lessons?.[0] || course.standaloneLessons?.[0] || null
  );
  const [activeContent, setActiveContent] = useState(null);

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState([]);
  const [completedContents, setCompletedContents] = useState(() => loadCompleted(userId, course.id));

  useEffect(() => {
    let mounted = true
    loadNotes(userId, course.id).then((n) => {
      if (!mounted) return
      setNotes(Array.isArray(n) ? n : [])
    }).catch(() => {})
    return () => { mounted = false }
  }, [userId, course.id])

  useEffect(() => {
    saveCompleted(userId, course.id, completedContents)
  }, [userId, course.id, completedContents])

  useEffect(() => {
    saveNotes(userId, course.id, notes)
  }, [userId, course.id, notes])

  const allContents = React.useMemo(() => {
    const contents = [];
    course.modules?.forEach(m => {
      m.lessons?.forEach(l => {
        if (l.contents?.length > 0) {
          l.contents.forEach(c => contents.push({ ...c, lessonId: l.id, moduleId: m.id, isSubContent: true }));
        } else {
          contents.push({ ...l, type: 'lesson-only', lessonId: l.id, moduleId: m.id, isSubContent: false });
        }
      });
    });
    course.standaloneLessons?.forEach(l => {
       if (l.contents?.length > 0) {
          l.contents.forEach(c => contents.push({ ...c, lessonId: l.id, moduleId: 'standalone', isSubContent: true }));
       } else {
          contents.push({ ...l, type: 'lesson-only', lessonId: l.id, moduleId: 'standalone', isSubContent: false });
       }
    });

    if (course.assessmentEnabled && course.assessmentId) {
      contents.push({
        id: `assessment-${course.assessmentId}`,
        title: 'Avaliação Final',
        type: 'assessment',
        moduleId: 'assessment',
        isSubContent: false
      });
    }

    return contents;
  }, [course]);

  const courseStats = React.useMemo(() => {
    const stats = {
      modules: course.modules?.length || 0,
      lessons: (course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0) + (course.standaloneLessons?.length || 0),
      files: allContents.filter(c => c.type === 'file').length,
      quizzes: allContents.filter(c => c.type === 'quiz').length,
      assessments: course.assessmentEnabled && course.assessmentId ? 1 : 0
    };
    return stats;
  }, [course, allContents]);

  const completedInCourse = React.useMemo(() => {
    const ids = new Set(allContents.map(c => c.id))
    return completedContents.filter(id => ids.has(id))
  }, [completedContents, allContents])

  const progressPercent = React.useMemo(() => {
    const total = allContents.length
    if (!total) return 0
    return Math.round((completedInCourse.length / total) * 100)
  }, [allContents, completedInCourse])

  const isContentUnlocked = (contentId) => {
    if (!course.sequentialEnforcement) return true;
    const index = allContents.findIndex(c => c.id === contentId);
    if (index <= 0) return true;
    const prevContent = allContents[index - 1];
    return completedContents.includes(prevContent.id);
  };

  const getNextContentInActiveLesson = (currentId) => {
    if (!activeLesson || !activeLesson.contents || activeLesson.contents.length === 0) return null;
    const idx = activeLesson.contents.findIndex(c => c.id === currentId);
    if (idx === -1) return null;
    return activeLesson.contents[idx + 1] || null;
  };

  const openNextContentInLesson = (currentId) => {
    const next = getNextContentInActiveLesson(currentId);
    if (!next) return;
    setActiveContent(next);
  };

  const markContentAsCompleted = (contentId) => {
    if (!completedContents.includes(contentId)) {
      const newCompleted = [...completedContents, contentId];
      setCompletedContents(newCompleted);
      try { saveCompleted(userId, course.id, newCompleted, { immediate: true }) } catch {}
    }
  };

  useEffect(() => {
    // INJECT MOCK DATA FOR DEMO
    if (activeLesson && !activeLesson.contents) {
        const isFirst = (course.modules?.[0]?.lessons?.[0]?.id === activeLesson.id) || (course.standaloneLessons?.[0]?.id === activeLesson.id);
        
        if (isFirst) {
            setActiveLesson(prev => ({
                ...prev,
                contents: [
                    { id: 'c1', title: 'Vídeo da Aula', type: 'video', url: prev.contentUrl },
                    { id: 'c2', title: 'Material Complementar (PDF)', type: 'file', url: '#', description: 'Resumo completo dos tópicos abordados nesta aula.' },
                    { id: 'c3', title: 'Exercício Prático', type: 'file', url: '#', description: 'Arquivos do projeto para praticar.' }
                ]
            }));
        }
    }
  }, []); // Run once on mount

  useEffect(() => {
    if (activeLesson) {
      if (activeLesson.contents && activeLesson.contents.length > 0) {
        setActiveContent(activeLesson.contents[0]);
      } else {
        setActiveContent({
          id: activeLesson.id,
          title: activeLesson.title,
          type: activeLesson.contentType,
          url: activeLesson.contentUrl,
          description: activeLesson.description
        });
      }
    } else {
      setActiveContent(null);
    }
  }, [activeLesson]);

  const handleSaveNote = (text) => {
    const createdAtMs = Date.now()
    const newNote = {
      id: Date.now(),
      text,
      createdAtMs,
      date: new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    };
    const updatedNotes = [newNote, ...notes];
    setNotes(updatedNotes);
    try { saveNotes(userId, course.id, updatedNotes, { immediate: true }) } catch {}
  };

  const handleDeleteNote = (id) => {
    const updatedNotes = notes.filter(n => n.id !== id);
    setNotes(updatedNotes);
    try { saveNotes(userId, course.id, updatedNotes, { immediate: true }) } catch {}
  };

  const toggleModule = (moduleId) => {
    setActiveModule(activeModule === moduleId ? null : moduleId);
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)(?:$|\/|\?)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return url;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 text-slate-100 flex flex-col animate-fade-in">
      {/* Header */}
      <div className="h-16 border-b border-slate-800 flex items-center justify-between px-6 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 shadow-lg">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition flex items-center gap-2"
          >
            <ChevronLeft size={20} />
            <span className="text-sm font-bold uppercase tracking-wider hidden md:inline">Voltar</span>
          </button>
          <div className="h-6 w-px bg-slate-800 mx-2 hidden md:block"></div>
          <h1 className="font-bold text-lg truncate max-w-xl text-white">{course.title}</h1>
        </div>
        
        {activeLesson && activeLesson.minTimeEnabled && (
           <div className="hidden md:flex items-center gap-3 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest text-right leading-tight">Tempo da Aula<br/>(requisito)</span>
              <LessonTimer 
                key={activeContent?.id}
                duration={activeLesson?.duration} 
                onComplete={() => {}} 
                className="w-12 h-12"
              />
           </div>
        )}

        <div className="flex items-center gap-6">
          <div className="hidden md:flex flex-col items-end">
             <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Seu Progresso</span>
             <span className="text-sm font-black text-teal-400">{progressPercent}%</span>
          </div>
          <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden hidden md:block">
             <div className="h-full bg-teal-500 transition-all duration-500 shadow-[0_0_10px_rgba(20,184,166,0.5)]" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="md:hidden text-slate-400 hover:text-white">
             {isSidebarOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content Area (Video/Player) */}
        <div className="flex-1 bg-slate-900 flex flex-col items-center justify-center relative overflow-hidden">
           {activeLesson ? (
             <div className="w-full h-full flex flex-col">
                <div className="flex-1 relative bg-slate-900 flex items-center justify-center w-full min-h-0 p-4 md:p-8 overflow-y-auto custom-scrollbar">
                   {activeContent?.type === 'video' || activeContent?.type === 'link' ? (
                     (activeContent.url ? (
                       <div className="player-wrapper w-full relative group">
                         <div className="player-status-pill">AULA ATUAL</div>
                         <VideoEmbed
                           key={activeContent.id}
                           url={activeContent.url}
                           title={activeContent.title}
                           storageKey={`progress-${course.id}-${activeContent.id}`}
                         userId={userId}
                         onEnded={() => markContentAsCompleted(activeContent.id)}
                         />
                       </div>
                     ) : (
                       <div className="text-center p-12 animate-pulse">
                          <Video size={64} className="mx-auto mb-6 text-slate-800" />
                          <h3 className="text-xl font-bold text-slate-600">URL não configurada</h3>
                       </div>
                     ))
                   ) : activeContent?.type === 'file' ? (
                     <div className="flex flex-col items-center justify-center text-center p-8 animate-fade-in">
                        <FileText size={64} className="mx-auto mb-6 text-teal-500" />
                        <h3 className="text-2xl font-bold text-white mb-2">{activeContent.title}</h3>
                        <p className="text-slate-400 mb-8 max-w-md">{activeContent.description || 'Este conteúdo é um arquivo disponível para download.'}</p>
                        <a 
                          href={activeContent.url} 
                          onClick={() => markContentAsCompleted(activeContent.id)}
                          download 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-teal-500 hover:bg-teal-400 text-slate-950 px-8 py-3 rounded-lg font-black flex items-center gap-3 transition shadow-lg shadow-teal-500/20 hover:shadow-teal-500/40"
                        >
                           <Download size={20} />
                           BAIXAR ARQUIVO
                        </a>
                     </div>
                   ) : (
                     <div className="text-center p-12 animate-pulse">
                        <Video size={64} className="mx-auto mb-6 text-slate-800" />
                        <h3 className="text-xl font-bold text-slate-600">Conteúdo não disponível</h3>
                        <p className="text-slate-700 mt-2 max-w-md mx-auto">Selecione um conteúdo na lista lateral.</p>
                     </div>
                   )}
                </div>
                <div className="p-8 bg-slate-900 w-full shrink-0 max-h-[40vh] overflow-y-auto custom-scrollbar">
                   <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className="text-xs font-bold text-teal-500 uppercase tracking-widest mb-1 block">Aula Atual</span>
                        <h2 className="text-2xl font-bold text-white mb-2">{activeLesson.title}</h2>
                      </div>
                      <div className="flex gap-3">
                        <button 
                          onClick={() => setShowNotesModal(true)}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-500 px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40"
                        >
                          <NotebookPen size={16} /> Minhas Anotações
                        </button>
                        {activeContent && !completedContents.includes(activeContent.id) && !activeLesson.minTimeEnabled && activeContent.type !== 'file' && (
                          <button 
                            onClick={() => markContentAsCompleted(activeContent.id)}
                            className="bg-teal-500 hover:bg-teal-600 text-white border border-teal-500 px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 shadow-lg"
                          >
                            <CheckCircle size={16} /> Concluir
                          </button>
                        )}
                        {role === 'admin' && (
                          <button className="bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 border border-teal-500/30 px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2">
                            <CheckCircle size={16} /> Marcar como Concluída
                          </button>
                        )}
                      </div>
                   </div>
                   <p className="text-slate-400 leading-relaxed max-w-3xl">{activeLesson.description || 'Sem descrição disponível para esta aula.'}</p>
                </div>
             </div>
           ) : (
             <div className="text-center p-12 text-slate-500">
               <BookOpen size={64} className="mx-auto mb-4 opacity-50" />
               <p className="text-lg font-medium">Selecione uma aula no menu lateral para começar.</p>
             </div>
           )}
        </div>

        {/* Sidebar (Curriculum) */}
        <div className={`fixed md:relative top-16 md:top-auto bottom-0 md:bottom-auto right-0 w-80 md:w-[500px] bg-slate-900 overflow-y-auto custom-scrollbar flex flex-col shadow-2xl z-40 transform transition-transform duration-300 md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
           {showNotesModal ? (
             <NotesPanel 
               onClose={() => setShowNotesModal(false)}
               notes={notes}
               onSave={handleSaveNote}
               onDelete={handleDeleteNote}
             />
           ) : (
             <>
               <div className="p-6 bg-slate-900 sticky top-0 z-10 border-b border-slate-800">
                  <h3 className="font-bold text-white mb-1 text-lg">Conteúdo do Curso</h3>
                  <p className="text-xs text-slate-400 font-medium">
                    {courseStats.modules} {courseStats.modules === 1 ? 'módulo' : 'módulos'} • {courseStats.lessons} {courseStats.lessons === 1 ? 'aula' : 'aulas'}
                    {courseStats.files > 0 && ` • ${courseStats.files} ${courseStats.files === 1 ? 'arquivo' : 'arquivos'}`}
                    {courseStats.quizzes > 0 && ` • ${courseStats.quizzes} ${courseStats.quizzes === 1 ? 'quiz' : 'quizes'}`}
                    {courseStats.assessments > 0 && ` • ${courseStats.assessments} ${courseStats.assessments === 1 ? 'prova' : 'provas'}`}
                  </p>
               </div>
               
               <div className="flex flex-col pb-20">
                  {course.modules?.map((module, index) => (
                    <div key={module.id} className="last:border-0">
                       <button 
                         onClick={() => toggleModule(module.id)}
                         className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition text-left group"
                       >
                         <div className="flex flex-col gap-1 pr-4">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider group-hover:text-slate-400 transition">Módulo {index + 1}</span>
                            <span className="font-bold text-slate-200 text-sm group-hover:text-white transition">{module.title}</span>
                         </div>
                         <ChevronDown size={16} className={`text-slate-500 transition-transform duration-300 ${activeModule === module.id ? 'rotate-180 text-teal-500' : ''}`} />
                       </button>
                       
                       {activeModule === module.id && (
                         <div className="bg-slate-950/30 flex flex-col animate-fade-in">
                            {module.lessons?.map((lesson, lIndex) => {
                               const isUnlocked = isContentUnlocked(lesson.contents?.[0]?.id || lesson.id);
                               return (
   <div key={lesson.id} className="flex flex-col">
       <button 
         onClick={() => isUnlocked && setActiveLesson(lesson)}
         disabled={!isUnlocked}
         className={`p-4 pl-6 flex gap-3 transition text-left border-l-2 relative overflow-hidden ${activeLesson?.id === lesson.id ? 'border-teal-500 bg-teal-500/5' : 'border-transparent hover:border-slate-700 hover:bg-slate-800/50'} ${!isUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
       >
          <div className="mt-0.5 z-10">
             {!isUnlocked ? (
               <Lock size={14} className="text-slate-500" />
             ) : (
               lesson.contentType === 'video' || lesson.contentType === 'link' ? 
               <Play size={14} className={activeLesson?.id === lesson.id ? 'text-teal-400 fill-teal-400/20' : 'text-slate-500'} /> : 
               <FileText size={14} className={activeLesson?.id === lesson.id ? 'text-teal-400' : 'text-slate-500'} />
             )}
          </div>
          <div className="flex flex-col gap-0.5 z-10">
             <span className={`text-sm font-medium transition ${activeLesson?.id === lesson.id ? 'text-teal-400' : 'text-slate-300'}`}>{lesson.title}</span>
             <span className="text-[10px] text-slate-500 font-mono">{lesson.duration || '5 min'}</span>
          </div>
          {activeLesson?.id === lesson.id && (
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 to-transparent pointer-events-none"></div>
          )}
       </button>
       {activeLesson?.id === lesson.id && lesson.contents && lesson.contents.length > 0 && (
          <div className="bg-slate-900/50 flex flex-col border-l-2 border-slate-800 ml-6 my-1">
             {lesson.contents.map((content) => {
                const isSubUnlocked = isContentUnlocked(content.id);
                return (
                <button 
                  key={content.id}
                  disabled={!isSubUnlocked}
                  onClick={(e) => { e.stopPropagation(); if(isSubUnlocked) setActiveContent(content); }}
                  className={`p-3 pl-4 flex gap-3 transition text-left relative ${activeContent?.id === content.id ? 'text-teal-400 bg-teal-500/5' : 'text-slate-400'} ${!isSubUnlocked ? 'opacity-50 cursor-not-allowed hover:bg-transparent' : 'hover:bg-slate-800/50'}`}
                >
                   <div className="mt-0.5">
                     {!isSubUnlocked ? <Lock size={12} /> : (content.type === 'video' ? <Play size={12} /> : <FileText size={12} />)}
                   </div>
                   <span className="text-xs font-medium">{content.title}</span>
                </button>
             )})}
          </div>
       )}
   </div>
)})}
                            {(!module.lessons || module.lessons.length === 0) && (
                              <div className="p-4 text-center text-xs text-slate-600 italic">Nenhuma aula neste módulo.</div>
                            )}
                         </div>
                       )}
                    </div>
                  ))}
    
                  {course.standaloneLessons?.length > 0 && (
                     <div className="">
                        <div className="p-4 bg-slate-900/50">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Aulas Extras</span>
                        </div>
                        {course.standaloneLessons.map(lesson => {
                           const isUnlocked = isContentUnlocked(lesson.contents?.[0]?.id || lesson.id);
                           return (
   <div key={lesson.id} className="flex flex-col">
       <button 
         onClick={() => isUnlocked && setActiveLesson(lesson)}
         disabled={!isUnlocked}
         className={`w-full p-4 pl-6 flex gap-3 transition text-left border-l-2 ${activeLesson?.id === lesson.id ? 'border-teal-500 bg-teal-500/5' : 'border-transparent'} ${!isUnlocked ? 'opacity-50 cursor-not-allowed hover:bg-transparent' : 'hover:bg-slate-800/50'}`}
       >
          {!isUnlocked ? (
             <Lock size={14} className="mt-0.5 text-slate-500" />
          ) : (
             <Play size={14} className={`mt-0.5 ${activeLesson?.id === lesson.id ? 'text-teal-400' : 'text-slate-500'}`} />
          )}
          <div className="flex flex-col gap-0.5">
             <span className={`text-sm font-medium ${activeLesson?.id === lesson.id ? 'text-teal-400' : 'text-slate-300'}`}>{lesson.title}</span>
          </div>
       </button>
       {activeLesson?.id === lesson.id && lesson.contents && lesson.contents.length > 0 && (
          <div className="bg-slate-900/50 flex flex-col border-l-2 border-slate-800 ml-6 my-1">
             {lesson.contents.map((content) => {
                const isSubUnlocked = isContentUnlocked(content.id);
                return (
                <button 
                  key={content.id}
                  disabled={!isSubUnlocked}
                  onClick={(e) => { e.stopPropagation(); if(isSubUnlocked) setActiveContent(content); }}
                  className={`p-3 pl-4 flex gap-3 transition text-left relative ${activeContent?.id === content.id ? 'text-teal-400 bg-teal-500/5' : 'text-slate-400'} ${!isSubUnlocked ? 'opacity-50 cursor-not-allowed hover:bg-transparent' : 'hover:bg-slate-800/50'}`}
                >
                   <div className="mt-0.5">
                     {!isSubUnlocked ? <Lock size={12} /> : (content.type === 'video' ? <Play size={12} /> : <FileText size={12} />)}
                   </div>
                   <span className="text-xs font-medium">{content.title}</span>
                </button>
             )})}
          </div>
       )}
   </div>
)})}
                     </div>
                  )}
               </div>
             </>
           )}
        </div>
      </div>
    </div>
  );
};

if (import.meta.env.DEV) {
  void MOCK_LIBRARY_ITEMS;
  void AdminCourses;
  void NotesPanel;
  void StudentCourseView;
}

const AdminEnrollments = ({ partner, users, courses, classes, partners, companies }) => {
  const auth = useAuth();
  const roleName = auth?.profileRole;
  const isAdminUser = (auth?.isAdmin === true) || String(roleName || '').toLowerCase() === 'admin';
  const enrollmentFiltersStorageKey = React.useMemo(
    () => `sofera:admin_enrollments:filters:${auth?.session?.user?.id || 'anon'}:${String(roleName || 'unknown')}`,
    [auth?.session?.user?.id, roleName]
  );
  const hasHydratedEnrollmentFilters = useRef(false);
  const isRestoringEnrollmentFilters = useRef(false);
  const pontoFocalCompanyIds = React.useMemo(() => {
    const ids = Array.isArray(auth?.profile?.companyIds) ? auth.profile.companyIds : [];
    const primaryCompanyId = auth?.profile?.companyId;
    return [...new Set([...ids, ...(primaryCompanyId ? [primaryCompanyId] : [])].filter(Boolean).map(String))];
  }, [auth?.profile?.companyId, auth?.profile?.companyIds]);
  const showActions = !(roleName === 'Ponto Focal' || isPartnerScopedRole(roleName));
  const canExport = (auth?.isAdmin === true) || roleName === 'Ponto Focal' || isPartnerScopedRole(roleName);
  const [showModal, setShowModal] = useState(false);
  const [enrollmentType, setEnrollmentType] = usePersistentTab('admin_enrollments_type', 'student');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingEnrollmentId, setDeletingEnrollmentId] = useState(null);
  const [pendingDeleteEnrollment, setPendingDeleteEnrollment] = useState(null);
  const [editingEnrollment, setEditingEnrollment] = useState(null); // Estado para edição
  
  const [form, setForm] = useState({
    studentId: '', // Changed from studentName to studentId for dropdown
    selectedStudentIds: [], // Múltiplos alunos na aba Turma
    linkClass: false,
    classId: '',
    companyId: '',
    selectedCourses: [],
    selectedClassIds: [],
    selectedCargos: [],
    selectedAreas: [],
    enrollmentMode: 'immediate', // 'immediate' | 'scheduled'
    scheduledDate: '', // YYYY-MM-DD for scheduled enrollment
    scheduledTime: '00:00', // HH:MM for scheduled enrollment
    status: 'Ativa',
    validityDays: '', // New field for validity
    startDate: '', // YYYY-MM-DD (input date format)
    endDate: '', // YYYY-MM-DD (input date format)
    file: null // For bulk upload
  });

  const isSaoPauloToday = (ymdDateStr) => {
    if (!ymdDateStr) return false;
    const todayPart = getSaoPauloDatePart(new Date());
    return String(ymdDateStr) === String(todayPart);
  };

  const [rawEnrollments, setRawEnrollments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ field: 'created_at', direction: 'desc' });
  const [enrollmentStudentSearch, setEnrollmentStudentSearch] = useState('');
  const [isEnrollmentStudentDropdownOpen, setIsEnrollmentStudentDropdownOpen] = useState(false);
  const [highlightedEnrollmentStudentIndex, setHighlightedEnrollmentStudentIndex] = useState(-1);
  const [scopedEnrollmentStudents, setScopedEnrollmentStudents] = useState([]);
  const [loadingEnrollmentStudentCandidates, setLoadingEnrollmentStudentCandidates] = useState(false);
  const [enrollmentClassSearch, setEnrollmentClassSearch] = useState('');
  const [isEnrollmentClassDropdownOpen, setIsEnrollmentClassDropdownOpen] = useState(false);
  const [classRequirementWarnings, setClassRequirementWarnings] = useState(null); // { student, classes: [{ id, name, violations: [{field, expected, actual}] }] }
  const [showClassRequirementConfirm, setShowClassRequirementConfirm] = useState(false);
  const [companyCargos, setCompanyCargos] = useState([]);
  const [companyAreas, setCompanyAreas] = useState([]);
  // Entidades do Core (Base Única) para busca integrada no modal de matrículas
  const [coreCompanies, setCoreCompanies] = useState([]);
  const [corePeople, setCorePeople] = useState([]);
  const [coreCompanyUsers, setCoreCompanyUsers] = useState([]);
  const [coreInvitations, setCoreInvitations] = useState([]);
  const [corePersonAuthLinks, setCorePersonAuthLinks] = useState([]);
  const [cargoSearch, setCargoSearch] = useState('');
  const [isCargoDropdownOpen, setIsCargoDropdownOpen] = useState(false);
  const [areaSearch, setAreaSearch] = useState('');
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const [enrollmentFilters, setEnrollmentFilters] = useState({
    student: '',
    course: '',
    courseIds: [],
    class: '',
    partner: '',
    partnerIds: [],
    company: '',
    companyIds: [],
    startDate: '',
    endDate: '',
    status: '',
    statuses: []
  });
  const buildDefaultEnrollmentFilters = React.useCallback(() => ({
    student: '',
    course: '',
    courseIds: [],
    class: '',
    partner: '',
    partnerIds: [],
    company: '',
    companyIds: [],
    startDate: '',
    endDate: '',
    status: '',
    statuses: []
  }), []);
  // All users can be enrolled
  const students = users;
  const enrollmentStudentInputRef = useRef(null);
  const enrollmentStudentListRef = useRef(null);
  const enrollmentClassInputRef = useRef(null);
  const enrollmentClassListRef = useRef(null);
  const [enrollmentStudentDropdownRect, setEnrollmentStudentDropdownRect] = useState(null);
  const [enrollmentClassDropdownRect, setEnrollmentClassDropdownRect] = useState(null);
  const getEnrollmentStudentLabel = React.useCallback(
    (student) => String(student?.name || student?.full_name || student?.email || '').trim(),
    []
  );
  // Calcula a posição (fixed) do dropdown com base no input, evitando recorte por overflow do modal.
  const getDropdownFixedRect = React.useCallback((inputEl) => {
    if (!inputEl) return null;
    const rect = inputEl.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    };
  }, []);
  // Busca cargos e áreas da empresa:
  // 1) Busca o central_company_id da empresa diretamente no banco.
  // 2) Se houver vínculo com o Core, lê do cache da Base Única
  //    (dataset.company_areas e dataset.company_positions filtrados por company_id).
  // 3) Caso contrário, faz merge do catálogo local (company_cargos/areas) com
  //    os valores distintos dos perfis.
  const fetchCompanyCargosAreas = React.useCallback(async (companyId) => {
    if (!companyId) {
      setCompanyCargos([]);
      setCompanyAreas([]);
      return;
    }
    try {
      // Determina o central_company_id:
      // - Empresa local: busca o central_company_id na tabela companies.
      // - Empresa do Core: o próprio companyId já é o central_company_id.
      let centralCompanyId = '';
      const { data: companyRow } = await supabase
        .from('companies')
        .select('central_company_id')
        .eq('id', companyId)
        .maybeSingle();

      if (companyRow) {
        centralCompanyId = companyRow.central_company_id || '';
      } else {
        // Empresa do Core (Base Única): o id recebido é o central_company_id.
        centralCompanyId = String(companyId);
      }

      if (centralCompanyId) {
        // Fonte: cache da Base Única (Core)
        const { data: cacheData, error: cacheError } = await supabase
          .from('core_base_cache')
          .select('dataset')
          .eq('id', 'current')
          .maybeSingle();

        if (!cacheError && cacheData?.dataset) {
          const ds = cacheData.dataset;
          const coreAreas = Array.isArray(ds.company_areas) ? ds.company_areas : [];
          const corePositions = Array.isArray(ds.company_positions) ? ds.company_positions : [];

          const areaNames = new Set();
          coreAreas.forEach(a => {
            if (a && String(a.company_id) === String(centralCompanyId) && a.name) {
              areaNames.add(String(a.name));
            }
          });
          const cargoNames = new Set();
          corePositions.forEach(p => {
            if (p && String(p.company_id) === String(centralCompanyId) && p.name) {
              cargoNames.add(String(p.name));
            }
          });

          setCompanyAreas(
            Array.from(areaNames).sort((a, b) => a.localeCompare(b, 'pt-BR')).map((name, i) => ({ id: `area-${i}`, name }))
          );
          setCompanyCargos(
            Array.from(cargoNames).sort((a, b) => a.localeCompare(b, 'pt-BR')).map((name, i) => ({ id: `cargo-${i}`, name }))
          );
          return;
        }
      }

      // Fallback: catálogo local + valores distintos dos perfis
      const [cargosRes, areasRes, profilesRes] = await Promise.all([
        supabase.from('company_cargos').select('id, name').eq('company_id', companyId).order('name'),
        supabase.from('company_areas').select('id, name').eq('company_id', companyId).order('name'),
        supabase.from('profiles').select('cargo, area').eq('company_id', companyId).not('cargo', 'is', null).not('area', 'is', null),
      ]);

      const cargoNames = new Set();
      (cargosRes.data || []).forEach(c => { if (c.name) cargoNames.add(c.name); });
      (profilesRes.data || []).forEach(p => { if (p.cargo) cargoNames.add(p.cargo); });
      setCompanyCargos(
        Array.from(cargoNames).sort((a, b) => a.localeCompare(b, 'pt-BR')).map((name, i) => ({ id: `cargo-${i}`, name }))
      );

      const areaNames = new Set();
      (areasRes.data || []).forEach(a => { if (a.name) areaNames.add(a.name); });
      (profilesRes.data || []).forEach(p => { if (p.area) areaNames.add(p.area); });
      setCompanyAreas(
        Array.from(areaNames).sort((a, b) => a.localeCompare(b, 'pt-BR')).map((name, i) => ({ id: `area-${i}`, name }))
      );
    } catch {
      setCompanyCargos([]);
      setCompanyAreas([]);
    }
  }, []);
  // Atualiza cargos/áreas quando a empresa selecionada mudar.
  React.useEffect(() => {
    if (enrollmentType === 'company' && form.companyId) {
      fetchCompanyCargosAreas(form.companyId);
    } else {
      setCompanyCargos([]);
      setCompanyAreas([]);
    }
  }, [enrollmentType, form.companyId, fetchCompanyCargosAreas]);

  // Busca empresas e pessoas do Core (Base Única) para busca integrada.
  const fetchCoreEntities = React.useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('core_base_cache')
        .select('dataset')
        .eq('id', 'current')
        .maybeSingle();
      if (error || !data?.dataset) return;
      const ds = data.dataset;
      setCoreCompanies(Array.isArray(ds.companies) ? ds.companies : []);
      setCorePeople(Array.isArray(ds.people) ? ds.people : []);
      setCoreCompanyUsers(Array.isArray(ds.company_users) ? ds.company_users : []);
      setCoreInvitations(Array.isArray(ds.invitations) ? ds.invitations : []);
      setCorePersonAuthLinks(Array.isArray(ds.person_auth_links) ? ds.person_auth_links : []);
    } catch {
      // Silencioso: fallback apenas para dados locais
    }
  }, []);

  // Carrega entidades do Core ao abrir o modal de matrículas.
  React.useEffect(() => {
    if (showModal) {
      fetchCoreEntities();
    }
  }, [showModal, fetchCoreEntities]);

  // Lista de empresas mesclada (local + Core) para o dropdown de empresa.
  const mergedCompanies = React.useMemo(() => {
    const localCentralIds = new Set(
      (Array.isArray(companies) ? companies : [])
        .map(c => String(c.central_company_id || ''))
        .filter(Boolean)
    );
    const merged = new Map();
    (Array.isArray(companies) ? companies : []).forEach(c => {
      merged.set(String(c.id), { ...c, fromCore: false });
    });
    (Array.isArray(coreCompanies) ? coreCompanies : []).forEach(c => {
      const cid = String(c.id || '');
      if (!cid || localCentralIds.has(cid)) return;
      merged.set(`core-${cid}`, {
        id: cid,
        name: c.legal_name || c.trade_name || 'Empresa Core',
        cnpj: c.cnpj || '',
        status: c.status === 'active' ? 'Ativo' : 'Inativo',
        central_company_id: cid,
        fromCore: true,
      });
    });
    return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [companies, coreCompanies]);

  // Lista de pessoas do Core para mesclar na busca de alunos.
  const corePeopleCandidates = React.useMemo(() => buildCorePeopleCandidates({
    corePeople,
    coreCompanies,
    coreCompanyUsers,
    coreInvitations,
    corePersonAuthLinks,
    localUsers: users,
  }), [corePeople, coreCompanies, coreCompanyUsers, coreInvitations, corePersonAuthLinks, users]);
  const toggleCargo = (name) => {
    setForm(prev => ({
      ...prev,
      selectedCargos: prev.selectedCargos.includes(name)
        ? prev.selectedCargos.filter(c => c !== name)
        : [...prev.selectedCargos, name]
    }));
  };
  const toggleArea = (name) => {
    setForm(prev => ({
      ...prev,
      selectedAreas: prev.selectedAreas.includes(name)
        ? prev.selectedAreas.filter(a => a !== name)
        : [...prev.selectedAreas, name]
    }));
  };
  // Valida se o aluno atende aos requisitos de uma turma (empresas, parceiros, áreas, cargos).
  // Retorna array de violações (vazio se tudo ok).
  const validateClassRequirements = React.useCallback((student, cls) => {
    const violations = [];
    if (!student || !cls) return violations;

    const studentCompanyIds = Array.isArray(student.companyIds)
      ? student.companyIds.map(String)
      : (student.company_id ? [String(student.company_id)] : []);
    const classCompanyIds = Array.isArray(cls.companyIds) ? cls.companyIds.map(String) : [];
    if (classCompanyIds.length > 0) {
      const match = studentCompanyIds.some(id => classCompanyIds.includes(id));
      if (!match) {
        violations.push({
          field: 'Empresa',
          expected: classCompanyIds,
          actual: studentCompanyIds,
          message: 'A turma é restrita a empresas às quais o aluno não está vinculado.'
        });
      }
    }

    const studentPartnerId = student.sas_partner_id ? String(student.sas_partner_id) : '';
    const classPartnerIds = Array.isArray(cls.partnerIds) ? cls.partnerIds.map(String) : [];
    if (classPartnerIds.length > 0) {
      const match = studentPartnerId && classPartnerIds.includes(studentPartnerId);
      if (!match) {
        violations.push({
          field: 'Parceiro SAS',
          expected: classPartnerIds,
          actual: studentPartnerId,
          message: 'A turma é restrita a um parceiro SAS diferente do aluno.'
        });
      }
    }

    const studentArea = String(student.area || '').trim();
    const classAreas = Array.isArray(cls.area) ? cls.area.map(a => String(a).trim()) : [];
    if (classAreas.length > 0) {
      const match = studentArea && classAreas.includes(studentArea);
      if (!match) {
        violations.push({
          field: 'Área',
          expected: classAreas,
          actual: studentArea,
          message: 'A área do aluno não está entre as áreas permitidas pela turma.'
        });
      }
    }

    const studentCargo = String(student.cargo || '').trim();
    const classCargos = Array.isArray(cls.cargo) ? cls.cargo.map(c => String(c).trim()) : [];
    if (classCargos.length > 0) {
      const match = studentCargo && classCargos.includes(studentCargo);
      if (!match) {
        violations.push({
          field: 'Cargo',
          expected: classCargos,
          actual: studentCargo,
          message: 'O cargo do aluno não está entre os cargos permitidos pela turma.'
        });
      }
    }

    return violations;
  }, []);
  const mapEnrollmentStudentRow = React.useCallback(
    (row) => ({
      id: row?.id || null,
      name: row?.name || row?.full_name || row?.email || '',
      full_name: row?.full_name || row?.name || '',
      email: row?.email || '',
      profile: row?.role || row?.profile || '',
      status: row?.status || '',
      company_id: row?.company_id || row?.companyId || null,
      companyIds: Array.isArray(row?.companyIds) ? row.companyIds : (row?.company_id ? [row.company_id] : []),
      sas_partner_id: row?.sas_partner_id || row?.partnerId || null,
      area: row?.area || '',
      cargo: row?.cargo || '',
      company_name: row?.companies?.name || row?.company_name || row?.company || '',
      partner_name: row?.sas_partners?.name || row?.partner_name || row?.partner || '',
    }),
    []
  );
  const normalizeEnrollmentStudentSearch = React.useCallback(
    (value) => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim(),
    []
  );
  const getEnrollmentStudentSearchIndex = React.useCallback(
    (student) => normalizeEnrollmentStudentSearch([
      student?.name,
      student?.full_name,
      student?.email,
      student?.company_name,
      student?.company,
      student?.partner_name,
    ].filter(Boolean).join(' ')),
    [normalizeEnrollmentStudentSearch]
  );
  const handleEnrollmentStudentSelect = React.useCallback(
    (student) => {
      const studentId = String(student?.id || '');
      if (!studentId) return;
      // Na aba Turma, selecionamos múltiplos alunos (adicionamos à lista).
      if (enrollmentType === 'class') {
        setForm((prev) => {
          const already = prev.selectedStudentIds.some((id) => String(id) === studentId);
          const nextIds = already
            ? prev.selectedStudentIds.filter((id) => String(id) !== studentId)
            : [...prev.selectedStudentIds, studentId];
          return { ...prev, selectedStudentIds: nextIds };
        });
        // Mantém o dropdown aberto para permitir múltiplas seleções.
        setEnrollmentStudentSearch('');
        return;
      }
      const nextLabel = getEnrollmentStudentLabel(student) || student?.email || '';
      setForm((prev) => ({ ...prev, studentId: studentId }));
      setEnrollmentStudentSearch(nextLabel);
      setIsEnrollmentStudentDropdownOpen(false);
    },
    [enrollmentType, getEnrollmentStudentLabel]
  );
  const enrollmentStudentCandidates = React.useMemo(() => {
    const merged = new Map();
    for (const student of Array.isArray(users) ? users : []) {
      const mappedStudent = mapEnrollmentStudentRow(student);
      const key = String(mappedStudent?.id || '');
      if (key && !merged.has(key)) merged.set(key, mappedStudent);
    }
    for (const student of Array.isArray(scopedEnrollmentStudents) ? scopedEnrollmentStudents : []) {
      const mappedStudent = mapEnrollmentStudentRow(student);
      const key = String(mappedStudent?.id || '');
      if (key && !merged.has(key)) merged.set(key, mappedStudent);
    }
    // Pessoas do Core (Base Única) que ainda não existem localmente.
    for (const student of Array.isArray(corePeopleCandidates) ? corePeopleCandidates : []) {
      const key = String(student?.id || '');
      if (key && !merged.has(key)) merged.set(key, { ...student, fromCore: true });
    }
    return Array.from(merged.values());
  }, [users, scopedEnrollmentStudents, mapEnrollmentStudentRow, corePeopleCandidates]);
  const selectableStudents = React.useMemo(() => {
    const term = normalizeEnrollmentStudentSearch(enrollmentStudentSearch);
    const baseStudents = (Array.isArray(enrollmentStudentCandidates) ? enrollmentStudentCandidates : [])
      .filter((student) => student?.status !== 'Inativo')
      .map((student) => ({
        raw: student,
        label: getEnrollmentStudentLabel(student),
        normalizedLabel: normalizeEnrollmentStudentSearch(getEnrollmentStudentLabel(student)),
        normalizedEmail: normalizeEnrollmentStudentSearch(student?.email),
        searchIndex: getEnrollmentStudentSearchIndex(student),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }));

    if (!term) return baseStudents.slice(0, 12).map((entry) => entry.raw);

    return baseStudents
      .filter((entry) => entry.searchIndex.includes(term))
      .sort((a, b) => {
        const aStartsWith = a.normalizedLabel.startsWith(term) || a.normalizedEmail.startsWith(term);
        const bStartsWith = b.normalizedLabel.startsWith(term) || b.normalizedEmail.startsWith(term);
        if (aStartsWith !== bStartsWith) return aStartsWith ? -1 : 1;
        return a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' });
      })
      .slice(0, 12)
      .map((entry) => entry.raw);
  }, [enrollmentStudentCandidates, enrollmentStudentSearch, getEnrollmentStudentLabel, getEnrollmentStudentSearchIndex, normalizeEnrollmentStudentSearch]);
  const selectedEnrollmentStudent = React.useMemo(
    () => enrollmentStudentCandidates.find((student) => String(student?.id || '') === String(form.studentId || '')) || null,
    [enrollmentStudentCandidates, form.studentId]
  );
  const selectedEnrollmentStudents = React.useMemo(() => {
    const ids = new Set((form.selectedStudentIds || []).map((id) => String(id)));
    if (ids.size === 0) return [];
    return (Array.isArray(enrollmentStudentCandidates) ? enrollmentStudentCandidates : [])
      .filter((student) => ids.has(String(student?.id || '')));
  }, [enrollmentStudentCandidates, form.selectedStudentIds]);
  const handleEnrollmentStudentSearchChange = React.useCallback(
    (value) => {
      const normalizedValue = normalizeEnrollmentStudentSearch(value);
      setEnrollmentStudentSearch(value);
      setIsEnrollmentStudentDropdownOpen(true);
      // Na aba Turma a seleção é múltipla e persistente; não limpamos ao digitar.
      if (enrollmentType === 'class') return;
      setForm((prev) => {
        if (!prev.studentId) return prev;
        const currentStudent = (Array.isArray(enrollmentStudentCandidates) ? enrollmentStudentCandidates : []).find(
          (student) => String(student?.id || '') === String(prev.studentId || '')
        );
        if (!currentStudent) return { ...prev, studentId: '' };
        const normalizedLabel = normalizeEnrollmentStudentSearch(getEnrollmentStudentLabel(currentStudent));
        const normalizedEmail = normalizeEnrollmentStudentSearch(currentStudent?.email);
        if (!normalizedValue || (normalizedValue !== normalizedLabel && normalizedValue !== normalizedEmail)) {
          return { ...prev, studentId: '' };
        }
        return prev;
      });
    },
    [enrollmentType, enrollmentStudentCandidates, normalizeEnrollmentStudentSearch, getEnrollmentStudentLabel]
  );
  // #region debug-point A:enrollments-user-search-config
  const __dbgEnrollUrl = 'http://127.0.0.1:7777/event';
  const __dbgEnrollSession = 'enrollment-user-search';
  const __dbgEnrollRun = 'pre-fix';
  const __dbgEnrollEnabled = false;
  const __dbgEnrollSend = React.useCallback((hypothesisId, msg, data) => {
    if (!__dbgEnrollEnabled) return;
    fetch(__dbgEnrollUrl, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: __dbgEnrollSession,
        runId: __dbgEnrollRun,
        hypothesisId,
        location: 'App.jsx:AdminEnrollments',
        msg: `[DEBUG] ${msg}`,
        data: data || {},
        ts: Date.now(),
      }),
    }).catch(() => {});
  }, [__dbgEnrollEnabled]);
  // #endregion
  useEffect(() => {
    if (!showModal) return;
    if (!(enrollmentType === 'student' || enrollmentType === 'edit')) return;

    let cancelled = false;

    const loadScopedEnrollmentStudents = async () => {
      setLoadingEnrollmentStudentCandidates(true);
      try {
        const focalScopeCompanyIds = [
          auth?.profile?.companyId,
          ...(Array.isArray(auth?.profile?.companyIds) ? auth.profile.companyIds : []),
        ]
          .filter(Boolean)
          .map(String);

        const uniqueFocalScopeCompanyIds = Array.from(new Set(focalScopeCompanyIds));
        const scopeParams = {
          p_company_id: null,
          p_partner_id: isPartnerScopedRole(roleName) ? (partner?.id || null) : null,
        };

        const applyRows = (rows) => {
          const mappedRows = (Array.isArray(rows) ? rows : []).map(mapEnrollmentStudentRow);
          const uniqueRows = Array.from(
            mappedRows.reduce((acc, row) => {
              const key = String(row?.id || '');
              if (key && !acc.has(key)) acc.set(key, row);
              return acc;
            }, new Map()).values()
          );
          if (!cancelled) setScopedEnrollmentStudents(uniqueRows);
        };

        const { data: scopedRows, error: scopedError } = await supabase.rpc('admin_list_users_by_scope', scopeParams);
        if (!scopedError) {
          applyRows(scopedRows);
          return;
        }

        if (isPartnerScopedRole(roleName) || roleName === 'Ponto Focal') {
          const { data: enrollmentRows, error: enrollmentError } = await supabase.rpc('admin_list_enrollments_by_scope', scopeParams);
          if (!enrollmentError) {
            const uniqueStudents = new Map();
            for (const row of (enrollmentRows || [])) {
              const sid = row?.student_id;
              if (!sid || uniqueStudents.has(String(sid))) continue;
              uniqueStudents.set(String(sid), {
                id: sid,
                email: row?.student_email || '',
                full_name: row?.student_full_name || row?.student_email || 'Aluno',
                role: 'Aluno',
                status: 'Ativo',
                company_name: '-',
                partner_name: partner?.name || '',
              });
            }
            applyRows(Array.from(uniqueStudents.values()));
            return;
          }
        }

        let query = supabase
          .from('profiles')
          .select('id, email, full_name, role, status, company_id, sas_partner_id, companies:companies!profiles_company_id_fkey(name), sas_partners(name)')
          .order('full_name', { ascending: true });

        if (isPartnerScopedRole(roleName) && partner?.id) {
          const [profilesScopeRes, classesScopeRes] = await Promise.all([
            supabase
              .from('profiles')
              .select('company_id')
              .eq('sas_partner_id', partner.id)
              .not('company_id', 'is', null),
            supabase
              .from('classes')
              .select('company_id')
              .eq('partner_id', partner.id)
              .not('company_id', 'is', null),
          ]);

          const companyIdsFromProfiles = (profilesScopeRes.data || []).map((row) => row.company_id).filter(Boolean);
          const companyIdsFromClasses = (classesScopeRes.data || []).map((row) => row.company_id).filter(Boolean);
          const allowedCompanyIds = Array.from(new Set([...companyIdsFromProfiles, ...companyIdsFromClasses]));

          if (allowedCompanyIds.length > 0) {
            query = query.or(`sas_partner_id.eq.${partner.id},company_id.in.(${allowedCompanyIds.join(',')})`);
          } else {
            query = query.eq('sas_partner_id', partner.id);
          }
        }

        if (roleName === 'Ponto Focal' && uniqueFocalScopeCompanyIds.length > 0) {
          query = query.in('company_id', uniqueFocalScopeCompanyIds);
        }

        const { data, error } = await query;
        if (!error) {
          applyRows(data);
        }
      } catch {
        if (!cancelled) setScopedEnrollmentStudents([]);
      } finally {
        if (!cancelled) setLoadingEnrollmentStudentCandidates(false);
      }
    };

    loadScopedEnrollmentStudents();
    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    enrollmentType,
    roleName,
    partner?.id,
    partner?.name,
    auth?.profile?.companyId,
    auth?.profile?.companyIds,
    mapEnrollmentStudentRow,
  ]);
  const handleEnrollmentStudentKeyDown = React.useCallback(
    (event) => {
      if (!isEnrollmentStudentDropdownOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        if (selectableStudents.length > 0) {
          event.preventDefault();
          setIsEnrollmentStudentDropdownOpen(true);
          setHighlightedEnrollmentStudentIndex(0);
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        if (selectableStudents.length === 0) return;
        event.preventDefault();
        setHighlightedEnrollmentStudentIndex((prev) => {
          const nextIndex = prev < 0 ? 0 : Math.min(prev + 1, selectableStudents.length - 1);
          return nextIndex;
        });
        return;
      }

      if (event.key === 'ArrowUp') {
        if (selectableStudents.length === 0) return;
        event.preventDefault();
        setHighlightedEnrollmentStudentIndex((prev) => {
          if (prev < 0) return 0;
          return Math.max(prev - 1, 0);
        });
        return;
      }

      if (event.key === 'Enter') {
        if (!isEnrollmentStudentDropdownOpen || highlightedEnrollmentStudentIndex < 0) return;
        const highlightedStudent = selectableStudents[highlightedEnrollmentStudentIndex];
        if (!highlightedStudent) return;
        event.preventDefault();
        handleEnrollmentStudentSelect(highlightedStudent);
        return;
      }

      if (event.key === 'Escape') {
        if (!isEnrollmentStudentDropdownOpen) return;
        event.preventDefault();
        setIsEnrollmentStudentDropdownOpen(false);
        setHighlightedEnrollmentStudentIndex(-1);
      }
    },
    [isEnrollmentStudentDropdownOpen, selectableStudents, highlightedEnrollmentStudentIndex, handleEnrollmentStudentSelect]
  );
  useEffect(() => {
    if (!isEnrollmentStudentDropdownOpen) {
      setHighlightedEnrollmentStudentIndex(-1);
      return;
    }
    if (selectableStudents.length === 0) {
      setHighlightedEnrollmentStudentIndex(-1);
      return;
    }
    const selectedIndex = selectableStudents.findIndex(
      (student) => String(student?.id || '') === String(form.studentId || '')
    );
    setHighlightedEnrollmentStudentIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [isEnrollmentStudentDropdownOpen, selectableStudents, form.studentId]);
  useEffect(() => {
    if (!isEnrollmentStudentDropdownOpen || highlightedEnrollmentStudentIndex < 0) return;
    const container = enrollmentStudentListRef.current;
    const item = container?.querySelector(`[data-student-option-index="${highlightedEnrollmentStudentIndex}"]`);
    if (item?.scrollIntoView) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [isEnrollmentStudentDropdownOpen, highlightedEnrollmentStudentIndex]);
  // #region debug-point B:enrollments-user-search-state
  useEffect(() => {
    if (!showModal) return;
    if (!(enrollmentType === 'student' || enrollmentType === 'edit')) return;
    const sample = enrollmentStudentCandidates.slice(0, 5).map((s) => ({
      id: s?.id || null,
      status: s?.status || null,
      name: s?.name || null,
      full_name: s?.full_name || null,
      email: s?.email || null,
    }));
    const filteredOutByStatus = enrollmentStudentCandidates.filter((s) => s?.status === 'Inativo').length;
    __dbgEnrollSend('B', 'student-search-snapshot', {
      enrollmentType,
      term: enrollmentStudentSearch,
      studentsCount: enrollmentStudentCandidates.length,
      inativoCount: filteredOutByStatus,
      selectableCount: Array.isArray(selectableStudents) ? selectableStudents.length : null,
      sample,
    });
  }, [showModal, enrollmentType, enrollmentStudentSearch, enrollmentStudentCandidates, selectableStudents, __dbgEnrollSend]);
  // #endregion
  const courseOptions = React.useMemo(() => {
    const activeCourses = (courses || []).filter(c => c.status === 'published' || c.status === 'Ativo');
    if (roleName === 'Ponto Focal' || isPartnerScopedRole(roleName)) {
      const ids = [...new Set(enrollments.map(e => e?.offering?.course_id).filter(Boolean))];
      return activeCourses.filter(c => ids.includes(c.id));
    }
    return activeCourses;
  }, [roleName, enrollments, courses]);
  const courseMultiSelectOptions = React.useMemo(() => (
    (courseOptions || [])
      .map((course) => ({ id: String(course?.id || ''), name: String(course?.title || course?.name || 'Curso sem nome') }))
      .filter((course) => course.id)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  ), [courseOptions]);
  const statusOptions = React.useMemo(() => ([
    { id: 'Programado', name: 'Programado' },
    { id: 'Pendente', name: 'Pendente' },
    { id: 'Ativa', name: 'Ativa' },
    { id: 'Inativada', name: 'Inativada' },
    { id: 'Cancelada', name: 'Cancelada' },
    { id: 'Expirada', name: 'Expirada' },
  ]), []);
  const classOptions = React.useMemo(() => {
    if (roleName === 'Ponto Focal' || isPartnerScopedRole(roleName)) {
      const ids = [...new Set(enrollments.map(e => e?.classId).filter(Boolean))];
      return classes.filter(c => ids.includes(c.id));
    }
    return classes;
  }, [roleName, enrollments, classes]);
  const companiesById = React.useMemo(() => {
    const map = new Map();
    (companies || []).forEach((company) => {
      const companyId = String(company?.id || '');
      if (!companyId) return;
      map.set(companyId, company?.name || '');
    });
    return map;
  }, [companies]);
  const usersById = React.useMemo(() => {
    const map = new Map();
    (users || []).forEach((user) => {
      const userId = String(user?.id || '');
      if (!userId) return;
      map.set(userId, user);
    });
    return map;
  }, [users]);
  // Lookup em memória para evitar N+1 queries na tabela classes.
  // Chave: `${studentId}::${courseId}` -> { id, name } da turma.
  const classByStudentCourse = React.useMemo(() => {
    const map = new Map();
    for (const cls of (Array.isArray(classes) ? classes : [])) {
      const studentIds = Array.isArray(cls?.students) ? cls.students : [];
      const courseIds = Array.isArray(cls?.courses) ? cls.courses : [];
      if (studentIds.length === 0 || courseIds.length === 0) continue;
      for (const sid of studentIds) {
        const sidKey = String(sid || '');
        if (!sidKey) continue;
        for (const cid of courseIds) {
          const cidKey = String(cid || '');
          if (!cidKey) continue;
          const key = `${sidKey}::${cidKey}`;
          if (!map.has(key)) {
            map.set(key, { id: cls?.id || null, name: cls?.name || '-' });
          }
        }
      }
    }
    return map;
  }, [classes]);
  const companyOptions = React.useMemo(() => {
    const optionsMap = new Map();

    (companies || []).forEach((company) => {
      const companyId = String(company?.id || '');
      if (!companyId) return;
      optionsMap.set(companyId, company?.name || 'Empresa sem nome');
    });

    (enrollments || []).forEach((enrollment) => {
      const companyId = String(enrollment?.companyId || '');
      const companyName = String(enrollment?.companyName || '').trim();
      if (!companyId || !companyName) return;
      if (!optionsMap.has(companyId)) {
        optionsMap.set(companyId, companyName);
      }
    });

    return Array.from(optionsMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [companies, enrollments]);
  const partnerOptions = React.useMemo(() => (
    Array.isArray(partners)
      ? partners
        .filter((currentPartner) => String(currentPartner?.status || 'Ativo') === 'Ativo')
        .map((currentPartner) => ({ id: String(currentPartner?.id || ''), name: String(currentPartner?.name || '') }))
        .filter((currentPartner) => currentPartner.id && currentPartner.name)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      : []
  ), [partners]);
  const [scopedCompanyOptions, setScopedCompanyOptions] = useState([]);
  const visibleCompanyOptions = React.useMemo(() => {
    if (isPartnerScopedRole(roleName) || roleName === 'Ponto Focal') {
      return scopedCompanyOptions;
    }
    const activePartnerIds = Array.isArray(enrollmentFilters.partnerIds) && enrollmentFilters.partnerIds.length > 0
      ? enrollmentFilters.partnerIds.map(String).filter(Boolean)
      : (enrollmentFilters.partner ? [String(enrollmentFilters.partner)] : []);
    if (isAdminUser && activePartnerIds.length > 0) {
      const selectedPartnerSet = new Set(activePartnerIds);
      const allowedCompanyIds = new Set(
        (Array.isArray(users) ? users : [])
          .filter((user) => selectedPartnerSet.has(String(user?.sas_partner_id || '')))
          .map((user) => String(user?.company_id || ''))
          .filter(Boolean)
      );
      return companyOptions.filter((company) => allowedCompanyIds.has(String(company.id)));
    }
    return companyOptions;
  }, [roleName, scopedCompanyOptions, companyOptions, isAdminUser, enrollmentFilters.partnerIds, enrollmentFilters.partner, users]);
  useEffect(() => {
    hasHydratedEnrollmentFilters.current = false;
    isRestoringEnrollmentFilters.current = false;
  }, [enrollmentFiltersStorageKey]);

  useEffect(() => {
    if (hasHydratedEnrollmentFilters.current) return;

    const defaults = buildDefaultEnrollmentFilters();
    let restored = defaults;

    try {
      const raw = window.localStorage.getItem(enrollmentFiltersStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          restored = {
            ...defaults,
            ...parsed,
            companyIds: Array.isArray(parsed.companyIds)
              ? parsed.companyIds.map(String)
              : (parsed.company ? [String(parsed.company)] : []),
            courseIds: Array.isArray(parsed.courseIds)
              ? parsed.courseIds.map(String)
              : (parsed.course ? [String(parsed.course)] : []),
            partnerIds: (isAdminUser && Array.isArray(parsed.partnerIds))
              ? parsed.partnerIds.map(String)
              : ((isAdminUser && parsed.partner) ? [String(parsed.partner)] : []),
            statuses: Array.isArray(parsed.statuses)
              ? parsed.statuses.map(String)
              : (parsed.status ? [String(parsed.status)] : []),
          };
          restored.company = restored.companyIds[0] || '';
          restored.course = restored.courseIds[0] || '';
          restored.partner = restored.partnerIds[0] || '';
          restored.status = restored.statuses[0] || '';
          if (!isAdminUser) {
            restored.partnerIds = [];
            restored.partner = '';
          }
        }
      }
    } catch {
      restored = defaults;
    }

    if (roleName === 'Ponto Focal') {
      const normalizedCompanyIds = Array.isArray(restored.companyIds)
        ? restored.companyIds.map(String).filter(Boolean)
        : [];
      restored.companyIds = normalizedCompanyIds.filter((id) => pontoFocalCompanyIds.includes(id));
      restored.company = restored.companyIds[0] || '';
    }

    isRestoringEnrollmentFilters.current = true;
    setEnrollmentFilters(restored);
    hasHydratedEnrollmentFilters.current = true;
  }, [buildDefaultEnrollmentFilters, enrollmentFiltersStorageKey, isAdminUser, roleName, pontoFocalCompanyIds]);

  useEffect(() => {
    if (!hasHydratedEnrollmentFilters.current) return;
    if (isRestoringEnrollmentFilters.current) {
      isRestoringEnrollmentFilters.current = false;
      return;
    }

    const normalizedFilters = {
      ...enrollmentFilters,
      partner: isAdminUser ? String(enrollmentFilters.partner || '') : '',
      partnerIds: isAdminUser && Array.isArray(enrollmentFilters.partnerIds)
        ? enrollmentFilters.partnerIds.map(String)
        : [],
      companyIds: Array.isArray(enrollmentFilters.companyIds)
        ? enrollmentFilters.companyIds.map(String)
        : [],
      courseIds: Array.isArray(enrollmentFilters.courseIds)
        ? enrollmentFilters.courseIds.map(String)
        : [],
      statuses: Array.isArray(enrollmentFilters.statuses)
        ? enrollmentFilters.statuses.map(String)
        : [],
      company: String(enrollmentFilters.company || ''),
      course: String(enrollmentFilters.course || ''),
      status: String(enrollmentFilters.status || ''),
    };

    try {
      window.localStorage.setItem(enrollmentFiltersStorageKey, JSON.stringify(normalizedFilters));
    } catch {
      // ignore localStorage errors
    }
  }, [enrollmentFilters, enrollmentFiltersStorageKey, isAdminUser]);
  const sortedEnrollments = React.useMemo(() => {
    const normalizedSortField = String(sortConfig?.field || 'created_at');
    const direction = sortConfig?.direction === 'asc' ? 1 : -1;
    return (Array.isArray(enrollments) ? enrollments : [])
      .map((enrollment, index) => ({ enrollment, index }))
      .sort((a, b) => {
        if (normalizedSortField === 'student' || normalizedSortField === 'company') {
          const aValue = String(
            normalizedSortField === 'student'
              ? (a.enrollment?.student?.full_name || a.enrollment?.student?.email || '')
              : (a.enrollment?.companyName || '')
          ).trim();
          const bValue = String(
            normalizedSortField === 'student'
              ? (b.enrollment?.student?.full_name || b.enrollment?.student?.email || '')
              : (b.enrollment?.companyName || '')
          ).trim();
          const compare = aValue.localeCompare(bValue, 'pt-BR', { sensitivity: 'base' });
          if (compare !== 0) return compare * direction;
          return a.index - b.index;
        }

        const dateField = normalizedSortField === 'expires_at' ? 'expires_at' : 'created_at';
        const aTime = a.enrollment?.[dateField] ? new Date(a.enrollment[dateField]).getTime() : Number.NaN;
        const bTime = b.enrollment?.[dateField] ? new Date(b.enrollment[dateField]).getTime() : Number.NaN;
        const aValid = Number.isFinite(aTime);
        const bValid = Number.isFinite(bTime);

        if (aValid && bValid && aTime !== bTime) return (aTime - bTime) * direction;
        if (aValid !== bValid) return aValid ? -1 : 1;
        return a.index - b.index;
      })
      .map(({ enrollment }) => enrollment);
  }, [enrollments, sortConfig]);
  const enrollmentTotalPages = Math.max(1, Math.ceil(sortedEnrollments.length / itemsPerPage));
  const paginatedEnrollments = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedEnrollments.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedEnrollments, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [enrollmentFilters, itemsPerPage, sortConfig]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, enrollmentTotalPages));
  }, [enrollmentTotalPages]);

  const toggleEnrollmentSort = (field, defaultDirection = 'asc') => {
    setSortConfig((current) => {
      if (current?.field === field) {
        return {
          field,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        field,
        direction: defaultDirection,
      };
    });
  };

  const getEnrollmentSortButtonLabel = (field) => {
    if (field === 'created_at' || field === 'expires_at') {
      return sortConfig?.direction === 'desc' ? 'Recentes' : 'Antigas';
    }
    return sortConfig?.direction === 'asc' ? 'A/Z' : 'Z/A';
  };

  const getEnrollmentSortButtonTitle = (field) => {
    if (field === 'student') {
      return `Classificar por usuário (${sortConfig?.field === 'student' && sortConfig?.direction === 'desc' ? 'Z para A' : 'A para Z'})`;
    }
    if (field === 'company') {
      return `Classificar por empresa (${sortConfig?.field === 'company' && sortConfig?.direction === 'desc' ? 'Z para A' : 'A para Z'})`;
    }
    if (field === 'expires_at') {
      return `Classificar por validade (${sortConfig?.field === 'expires_at' && sortConfig?.direction === 'asc' ? 'mais antigas primeiro' : 'mais recentes primeiro'})`;
    }
    return `Classificar por data de matrícula (${sortConfig?.field === 'created_at' && sortConfig?.direction === 'asc' ? 'mais antigas primeiro' : 'mais recentes primeiro'})`;
  };

  const getEnrollmentSortButtonClassName = (field) => {
    const isActive = sortConfig?.field === field;
    return `inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold normal-case transition ${
      isActive
        ? 'border-teal-500/60 bg-teal-500/10 text-teal-300 shadow-sm shadow-teal-950/40'
        : 'border-slate-700 bg-slate-900/80 text-slate-300 hover:border-slate-500 hover:text-white'
    }`;
  };
  const attachDisplayedProgramadoDates = React.useCallback(async (rows) => {
    const normalizedRows = Array.isArray(rows) ? rows : [];
    const programadoStudentIds = Array.from(new Set(
      normalizedRows
        .filter((row) => String(row?.status || '') === 'Programado')
        .map((row) => String(row?.student_id || ''))
        .filter(Boolean)
    ));

    if (programadoStudentIds.length === 0) return normalizedRows;

    const scheduleMetaByStudentId = new Map();
    const chunkSize = 100;

    for (let offset = 0; offset < programadoStudentIds.length; offset += chunkSize) {
      const chunk = programadoStudentIds.slice(offset, offset + chunkSize);
      const { data, error } = await supabase
        .from('communication_queue')
        .select('recipient_id, scheduled_for, payload, status, processed_at, created_at')
        .in('recipient_id', chunk);

      if (error) throw error;

      for (const job of (data || [])) {
        if (String(job?.payload?.type || '') !== 'user_invite_schedule') continue;
        const recipientId = String(job?.recipient_id || '').trim();
        if (!recipientId) continue;

        const candidateDate =
          job?.payload?.original_scheduled_for ||
          job?.payload?.current_scheduled_for ||
          job?.scheduled_for ||
          job?.processed_at ||
          job?.created_at ||
          null;

        if (!candidateDate) continue;

        const previous = scheduleMetaByStudentId.get(recipientId);
        const previousTime = previous?.effectiveDate ? new Date(previous.effectiveDate).getTime() : Number.NaN;
        const candidateTime = new Date(candidateDate).getTime();
        if (Number.isNaN(candidateTime)) continue;
        if (!previous || Number.isNaN(previousTime) || candidateTime > previousTime) {
          scheduleMetaByStudentId.set(recipientId, {
            effectiveDate: candidateDate,
            scheduledFor: job?.scheduled_for || null,
            originalScheduledFor: job?.payload?.original_scheduled_for || null,
            processedAt: job?.processed_at || null,
          });
        }
      }
    }

    return normalizedRows.map((row) => {
      if (String(row?.status || '') !== 'Programado') return row;
      if (row?.confirmed_at) {
        return {
          ...row,
          created_at: getAdminEnrollmentStartAt(row),
        };
      }

      const matchedUser = usersById.get(String(row?.student_id || ''));
      const scheduledMeta = scheduleMetaByStudentId.get(String(row?.student_id || ''));
      const effectiveCreatedAt =
        scheduledMeta?.originalScheduledFor ||
        scheduledMeta?.scheduledFor ||
        matchedUser?.scheduled_invite_original_for ||
        matchedUser?.scheduled_invite_for ||
        matchedUser?.last_invite_sent_at ||
        scheduledMeta?.processedAt ||
        scheduledMeta?.effectiveDate ||
        row?.created_at ||
        null;

      return {
        ...row,
        created_at: effectiveCreatedAt,
      };
    });
  }, [usersById]);

  useEffect(() => {
    let cancelled = false;
    const loadScopedCompanyOptions = async () => {
      if (!(isPartnerScopedRole(roleName) || roleName === 'Ponto Focal')) {
        setScopedCompanyOptions([]);
        return;
      }
      try {
        const { data, error } = await supabase.rpc('admin_list_companies_by_scope', {
          p_company_id: null,
          p_partner_id: isPartnerScopedRole(roleName) ? (auth?.profile?.sasPartnerId || partner?.id || null) : null,
        });
        if (error) throw error;
        if (cancelled) return;
        setScopedCompanyOptions(
          (Array.isArray(data) ? data : [])
            .map((row) => ({ id: String(row?.id || ''), name: String(row?.name || 'Empresa sem nome') }))
            .filter((row) => row.id)
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        );
      } catch {
        if (!cancelled) setScopedCompanyOptions([]);
      }
    };
    loadScopedCompanyOptions();
    return () => { cancelled = true; };
  }, [roleName, auth?.profile?.sasPartnerId, partner?.id]);

  useEffect(() => {
    const availableIds = new Set(visibleCompanyOptions.map((company) => String(company.id)));
    const normalized = Array.isArray(enrollmentFilters.companyIds)
      ? enrollmentFilters.companyIds.map(String).filter((id) => availableIds.has(id))
      : [];
    const normalizedCompany = normalized[0] || '';
    if (
      normalized.length !== (Array.isArray(enrollmentFilters.companyIds) ? enrollmentFilters.companyIds.length : 0) ||
      normalizedCompany !== String(enrollmentFilters.company || '')
    ) {
      setEnrollmentFilters((prev) => ({
        ...prev,
        companyIds: normalized,
        company: normalizedCompany,
      }));
    }
  }, [visibleCompanyOptions, enrollmentFilters.companyIds, enrollmentFilters.company]);
  useEffect(() => {
    const rawPartner = isAdminUser ? String(enrollmentFilters.partner || '') : '';
    const rawPartnerIds = isAdminUser && Array.isArray(enrollmentFilters.partnerIds)
      ? enrollmentFilters.partnerIds.map(String).filter(Boolean)
      : [];
    const normalizedPartnerIds = rawPartnerIds.length > 0 ? rawPartnerIds : (rawPartner ? [rawPartner] : []);
    const allPartnersExist = normalizedPartnerIds.every((pid) =>
      partnerOptions.some((currentPartner) => currentPartner.id === pid)
    );
    const normalizedSinglePartner = normalizedPartnerIds[0] || '';
    const sameSinglePartner = normalizedSinglePartner === String(enrollmentFilters.partner || '');
    const sameIdsCount = normalizedPartnerIds.length === rawPartnerIds.length;
    if (sameSinglePartner && sameIdsCount && allPartnersExist) return;
    setEnrollmentFilters((prev) => ({
      ...prev,
      partner: allPartnersExist ? normalizedSinglePartner : '',
      partnerIds: allPartnersExist ? normalizedPartnerIds : [],
      companyIds: [],
      company: '',
    }));
  }, [isAdminUser, enrollmentFilters.partner, enrollmentFilters.partnerIds, partnerOptions]);

  const fetchEnrollments = async () => {
    setLoadingEnrollments(true);
    try {
      const roleName = auth?.profileRole;
      let scopedUserStatusMap = new Map();
      if (usersById.size > 0) {
        usersById.forEach((user, userId) => {
          scopedUserStatusMap.set(String(userId), String(user?.status || '').trim());
        });
      } else if (roleName === 'Ponto Focal' || isPartnerScopedRole(roleName)) {
        scopedUserStatusMap = await fetchScopedUserStatusMap({
          profileRole: roleName,
          profile: auth?.profile,
          companyId: null,
          partnerId: isPartnerScopedRole(roleName) ? (auth?.profile?.sasPartnerId || partner?.id || null) : null,
        }).catch(() => new Map());
      }
      let rpcData = null;
      if (roleName === 'Ponto Focal') {
        const { data, error } = await supabase.rpc('admin_list_enrollments_by_scope', { p_company_id: null, p_partner_id: null });
        if (error) throw error;
        rpcData = (data || []).filter((row) => {
          if (pontoFocalCompanyIds.length === 0) return true;
          const studentCompanyId = String(row?.company_id || row?.student_company_id || '');
          return studentCompanyId ? pontoFocalCompanyIds.includes(studentCompanyId) : true;
        });
      } else if (isPartnerScopedRole(roleName)) {
        const partnerId = auth?.profile?.sasPartnerId || partner?.id || null;
        const { data, error } = await supabase.rpc('admin_list_enrollments_by_scope', { p_company_id: null, p_partner_id: partnerId });
        if (error) throw error;
        rpcData = data || [];
      }
      let enrollmentsWithClass;
      if (rpcData) {
        const enrollmentIds = (rpcData || []).map((row) => row?.id).filter(Boolean);
        let confirmedAtByEnrollmentId = new Map();
        if (enrollmentIds.length > 0) {
          const { data: enrollmentMetaRows, error: enrollmentMetaError } = await supabase
            .from('enrollments')
            .select('id, confirmed_at')
            .in('id', enrollmentIds);
          if (enrollmentMetaError) throw enrollmentMetaError;
          confirmedAtByEnrollmentId = new Map(
            (enrollmentMetaRows || []).map((row) => [String(row?.id || ''), row?.confirmed_at || null])
          );
        }

        // Map result set to UI shape
        enrollmentsWithClass = (rpcData || []).map(r => {
          const matchedUser = usersById.get(String(r?.student_id || ''));
          const scopedLifecycle = scopedUserStatusMap.get(String(r?.student_id || ''));
          const enrollmentStatus = normalizeEnrollmentStatus(r.status, r.expires_at);
          const confirmedAt = confirmedAtByEnrollmentId.get(String(r?.id || '')) || null;
          const effectiveStartAt = getAdminEnrollmentStartAt({
            created_at: r.created_at,
            confirmed_at: confirmedAt,
          });
          const studentProfileStatus = String(
            matchedUser?.status ||
            scopedLifecycle?.status ||
            ''
          ).trim();
          const displayStatus = getEnrollmentDisplayStatus(
            r.status,
            studentProfileStatus,
            r.expires_at,
            effectiveStartAt,
          ) || enrollmentStatus;
          const companyId = String(
            r?.company_id ||
            r?.student_company_id ||
            matchedUser?.company_id ||
            ''
          );
          const companyName =
            String(
              r?.company_name ||
              r?.student_company_name ||
              matchedUser?.company_name ||
              ''
            ).trim() ||
            companiesById.get(companyId) ||
            '';

          return {
            id: r.id,
            status: displayStatus,
            enrollmentStatus,
            studentProfileStatus,
            confirmed_at: confirmedAt,
            created_at: effectiveStartAt,
            expires_at: r.expires_at,
            offering_id: r.offering_id,
            student_id: r.student_id,
            student: { full_name: r.student_full_name, email: r.student_email },
            offering: { id: r.offering_id, course_id: r.course_id, course: { name: r.course_name } },
            className: r.class_name || '-',
            classId: r.class_id || null,
            partnerId: String(matchedUser?.sas_partner_id || ''),
            partnerName: String(matchedUser?.partner_name || ''),
            companyId,
            companyName
          };
        });
      } else {
        let query = supabase
          .from('enrollments')
          .select(`
            id,
            status,
            created_at,
            confirmed_at,
            expires_at,
            offering_id,
            student_id,
            student:student_id (full_name, email, company_id, status),
            offering:offering_id (
              id,
              course_id,
              course:course_id (name)
            )
          `)
          .order('updated_at', { ascending: false });

        if (enrollmentFilters.startDate) query = query.gte('updated_at', `${enrollmentFilters.startDate}T00:00:00`);
        if (enrollmentFilters.endDate) query = query.lte('updated_at', `${enrollmentFilters.endDate}T23:59:59`);

        const { data, error } = await query;
        if (error) throw error;

        enrollmentsWithClass = (data || []).map((enrol) => {
            const classKey = `${String(enrol?.student_id || '')}::${String(enrol?.offering?.course_id || '')}`;
            const matchedClass = classByStudentCourse.get(classKey) || null;
            const effectiveStartAt = getAdminEnrollmentStartAt(enrol);
            const matchedUser = usersById.get(String(enrol?.student_id || ''));

            return {
                ...enrol,
                created_at: effectiveStartAt,
                status: getEnrollmentDisplayStatus(
                  enrol?.status,
                  enrol?.student?.status,
                  enrol?.expires_at,
                  effectiveStartAt,
                ),
                enrollmentStatus: normalizeEnrollmentStatus(enrol?.status, enrol?.expires_at),
                studentProfileStatus: String(enrol?.student?.status || '').trim(),
                className: matchedClass?.name || '-',
                classId: matchedClass?.id || null,
                partnerId: String(matchedUser?.sas_partner_id || ''),
                partnerName: String(matchedUser?.partner_name || ''),
                companyId: String(enrol?.student?.company_id || ''),
                companyName:
                  matchedUser?.company_name ||
                  companiesById.get(String(enrol?.student?.company_id || '')) ||
                  ''
            };
        });
      }

      enrollmentsWithClass = await attachDisplayedProgramadoDates(enrollmentsWithClass);

      // Os filtros client-side (nome, curso, turma, parceiro, empresa, status)
      // são aplicados via useMemo em `enrollments` derivado de `rawEnrollments`.
      setRawEnrollments(enrollmentsWithClass);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      setNotification({ message: 'Erro ao carregar matrículas.', type: 'error' });
    } finally {
      setLoadingEnrollments(false);
    }
  };

  useEffect(() => {
    fetchEnrollments();
  }, [enrollmentFilters.startDate, enrollmentFilters.endDate, pontoFocalCompanyIds, usersById, companiesById, attachDisplayedProgramadoDates, partner?.id, classByStudentCourse]);

  // Debounce do termo de busca por aluno para evitar re-renders excessivos durante a digitação.
  const [debouncedStudentFilter, setDebouncedStudentFilter] = useState(enrollmentFilters.student);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStudentFilter(enrollmentFilters.student), 250);
    return () => clearTimeout(timer);
  }, [enrollmentFilters.student]);

  // Aplicação de todos os filtros client-side sobre o dataset já carregado.
  // Isso elimina re-fetches desnecessários a cada mudança de filtro.
  const filteredEnrollments = React.useMemo(() => {
    const rows = Array.isArray(rawEnrollments) ? rawEnrollments : [];

    // Filtro por aluno (nome ou e-mail)
    const studentTerm = String(debouncedStudentFilter || '').toLowerCase().trim();
    let result = studentTerm
      ? rows.filter((e) =>
          String(e?.student?.full_name || '').toLowerCase().includes(studentTerm) ||
          String(e?.student?.email || '').toLowerCase().includes(studentTerm)
        )
      : rows;

    // Filtro por curso
    const selectedCourseIds = Array.isArray(enrollmentFilters.courseIds) && enrollmentFilters.courseIds.length > 0
      ? enrollmentFilters.courseIds.map(String)
      : (enrollmentFilters.course ? [String(enrollmentFilters.course)] : []);
    if (selectedCourseIds.length > 0) {
      const selected = new Set(selectedCourseIds);
      result = result.filter((e) => selected.has(String(e?.offering?.course_id || '')));
    }

    // Filtro por turma
    if (enrollmentFilters.class) {
      result = result.filter((e) => String(e?.classId || '') === String(enrollmentFilters.class));
    }

    // Filtro por parceiro
    const selectedPartnerIds = isAdminUser && Array.isArray(enrollmentFilters.partnerIds) && enrollmentFilters.partnerIds.length > 0
      ? enrollmentFilters.partnerIds.map(String)
      : ((isAdminUser && enrollmentFilters.partner) ? [String(enrollmentFilters.partner)] : []);
    if (selectedPartnerIds.length > 0) {
      const selected = new Set(selectedPartnerIds);
      result = result.filter((e) => selected.has(String(e?.partnerId || '')));
    }

    // Filtro por empresa
    const selectedCompanyIds = Array.isArray(enrollmentFilters.companyIds) && enrollmentFilters.companyIds.length > 0
      ? enrollmentFilters.companyIds.map(String)
      : (enrollmentFilters.company ? [String(enrollmentFilters.company)] : []);
    if (selectedCompanyIds.length > 0) {
      const selected = new Set(selectedCompanyIds);
      result = result.filter((e) => selected.has(String(e?.companyId || '')));
    }

    // Filtro por data (created_at = data efetiva de início)
    if (enrollmentFilters.startDate) {
      result = result.filter((e) => {
        if (!e?.created_at) return false;
        const key = new Intl.DateTimeFormat('sv-SE', {
          timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(new Date(e.created_at));
        return key >= enrollmentFilters.startDate;
      });
    }
    if (enrollmentFilters.endDate) {
      result = result.filter((e) => {
        if (!e?.created_at) return false;
        const key = new Intl.DateTimeFormat('sv-SE', {
          timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(new Date(e.created_at));
        return key <= enrollmentFilters.endDate;
      });
    }

    // Filtro por status
    const selectedStatuses = Array.isArray(enrollmentFilters.statuses) && enrollmentFilters.statuses.length > 0
      ? enrollmentFilters.statuses.map(String)
      : (enrollmentFilters.status ? [String(enrollmentFilters.status)] : []);
    if (selectedStatuses.length > 0) {
      const selected = new Set(selectedStatuses);
      result = result.filter((e) => selected.has(String(e?.status || '')));
    }

    return result;
  }, [
    rawEnrollments,
    debouncedStudentFilter,
    enrollmentFilters.courseIds,
    enrollmentFilters.course,
    enrollmentFilters.class,
    isAdminUser,
    enrollmentFilters.partnerIds,
    enrollmentFilters.partner,
    enrollmentFilters.companyIds,
    enrollmentFilters.company,
    enrollmentFilters.startDate,
    enrollmentFilters.endDate,
    enrollmentFilters.statuses,
    enrollmentFilters.status,
  ]);

  // Sincroniza o estado `enrollments` (usado por sorting/paginação/exportação) com o resultado filtrado.
  useEffect(() => {
    setEnrollments(filteredEnrollments);
  }, [filteredEnrollments]);

  const handleNewEnrollment = () => {
    setEditingEnrollment(null);
    setEnrollmentType('student');
    setEnrollmentStudentSearch('');
    setIsEnrollmentStudentDropdownOpen(false);
    setForm({
        studentId: '',
        selectedStudentIds: [],
        linkClass: false,
        classId: '',
        companyId: '',
        selectedCourses: [],
        selectedClassIds: [],
        selectedCargos: [],
        selectedAreas: [],
        enrollmentMode: 'immediate',
        scheduledDate: '',
        scheduledTime: '00:00',
        status: 'Ativa',
        validityDays: '',
        startDate: '',
        endDate: '',
        file: null
    });
    setShowModal(true);
  };

  const handleEdit = (enrollment) => {
    setEditingEnrollment(enrollment);
    setEnrollmentStudentSearch('');
    setIsEnrollmentStudentDropdownOpen(false);
    const formState = buildEnrollmentEditFormState(enrollment);

    setForm({
        studentId: enrollment.student?.full_name || 'Aluno', // Apenas visual
        selectedStudentIds: [],
        linkClass: !!enrollment.classId,
        classId: enrollment.classId || '',
        companyId: '',
        selectedCourses: [enrollment.offering?.course_id], // Pré-selecionar curso atual
        selectedCargos: [],
        selectedAreas: [],
        enrollmentMode: 'immediate',
        scheduledDate: '',
        scheduledTime: '00:00',
        status: normalizeEnrollmentStatus(enrollment.enrollmentStatus || enrollment.status, enrollment.expires_at) || 'Ativa',
        validityDays: formState.validityDays || '',
        startDate: formState.startDate,
        endDate: formState.endDate,
        file: null
    });
    setEnrollmentType('edit'); // Modo edição
    setShowModal(true);
  };

  const handleDeleteEnrollment = async (enrollment) => {
    if (!isAdminUser || !enrollment?.id) return;
    setPendingDeleteEnrollment(enrollment);
  };

  const confirmDeleteEnrollment = async () => {
    if (!pendingDeleteEnrollment?.id) return;

    setDeletingEnrollmentId(pendingDeleteEnrollment.id);
    try {
      const { error } = await supabase
        .from('enrollments')
        .delete()
        .eq('id', pendingDeleteEnrollment.id);

      if (error) throw error;

      setNotification({
        message: 'Matrícula excluída com sucesso!',
        type: 'success'
      });
      setPendingDeleteEnrollment(null);
      await fetchEnrollments();
    } catch (err) {
      console.error('Delete enrollment error:', err);
      setNotification({
        message: `Erro ao excluir matrícula: ${err?.message || 'erro desconhecido'}`,
        type: 'error'
      });
    } finally {
      setDeletingEnrollmentId(null);
    }
  };

  const handleToggleCourse = (courseId) => {
    setForm(prev => {
      const isSelected = prev.selectedCourses.includes(courseId);
      if (enrollmentType === 'edit') {
        return {
          ...prev,
          selectedCourses: isSelected ? [] : [courseId]
        };
      }
      return {
        ...prev,
        selectedCourses: isSelected 
          ? prev.selectedCourses.filter(id => id !== courseId)
          : [...prev.selectedCourses, courseId]
      };
    });
  };

  const handleToggleClass = (classId) => {
    setForm(prev => {
      const isSelected = prev.selectedClassIds.includes(classId);
      return {
        ...prev,
        selectedClassIds: isSelected
          ? prev.selectedClassIds.filter(id => id !== classId)
          : [...prev.selectedClassIds, classId]
      };
    });
  };

  const handleStartDateChange = (nextYmd) => {
    setForm((prev) => {
      const next = { ...prev, startDate: String(nextYmd || '') };
      if (next.startDate && prev.validityDays) {
        const newEnd = addDaysToYmd(next.startDate, prev.validityDays);
        if (newEnd) next.endDate = newEnd;
      } else if (!next.startDate) {
        // manter endDate como está
      }
      return next;
    });
  };

  const handleValidityDaysChange = (nextDays) => {
    setForm((prev) => {
      const next = { ...prev, validityDays: nextDays };
      if (prev.startDate && nextDays) {
        const newEnd = addDaysToYmd(prev.startDate, nextDays);
        if (newEnd) next.endDate = newEnd;
      }
      return next;
    });
  };

  const handleEndDateChange = (nextYmd) => {
    setForm((prev) => {
      const next = { ...prev, endDate: String(nextYmd || '') };
      if (prev.startDate && next.endDate) {
        const newDays = computeDaysBetween(prev.startDate, next.endDate);
        if (newDays !== '') next.validityDays = newDays;
      }
      return next;
    });
  };

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
          setForm(prev => ({ ...prev, file }));
      }
  };

  // Executa a matrícula do aluno em todas as turmas selecionadas (sem validação de requisitos).
  const executeClassEnrollment = async () => {
    setLoading(true);
    try {
      const selectedClasses = classOptions.filter(c => form.selectedClassIds.includes(c.id));
      // Determina a lista de alunos a matricular (múltiplos na aba Turma, único nas demais).
      const studentIdsToEnroll = enrollmentType === 'class'
        ? (form.selectedStudentIds || []).map((id) => String(id)).filter(Boolean)
        : form.studentId ? [String(form.studentId)] : [];

      if (studentIdsToEnroll.length === 0) {
        throw new Error(enrollmentType === 'class' ? 'Marque ao menos um aluno.' : 'Selecione um aluno.');
      }

      let totalSuccess = 0;
      let totalFail = 0;
      const errors = [];

      for (const rawStudentId of studentIdsToEnroll) {
        // Se o aluno é do Core (Base Única), espelha localmente antes da matrícula.
        let studentId = rawStudentId;
        const selectedStudent = enrollmentStudentCandidates.find(
          (s) => String(s?.id || '') === rawStudentId
        );
        if (selectedStudent?.fromCore && selectedStudent?.central_person_id) {
          const { data: mirroredId, error: mirrorError } = await supabase.rpc('core_mirror_person', {
            p_central_person_id: String(selectedStudent.central_person_id),
          });
          if (mirrorError) {
            errors.push(`Aluno "${getEnrollmentStudentLabel(selectedStudent) || selectedStudent?.email || rawStudentId}": falha ao espelhar do Core: ${mirrorError.message}`);
            totalFail += 1;
            continue;
          }
          if (!mirroredId) {
            errors.push(`Aluno "${getEnrollmentStudentLabel(selectedStudent) || selectedStudent?.email || rawStudentId}": não foi possível criar a partir da Base Core.`);
            totalFail += 1;
            continue;
          }
          studentId = mirroredId;
        }

        for (const cls of selectedClasses) {
          const courseIds = Array.isArray(cls.courses) ? cls.courses.filter(Boolean) : [];
          const durationDays = cls.durationDays ? parseInt(cls.durationDays, 10) : null;

          if (courseIds.length === 0) {
            errors.push(`A turma "${cls.name}" não possui cursos vinculados.`);
            continue;
          }

          const { data, error } = await supabase.rpc('admin_enroll_student', {
            p_student_id: studentId,
            p_course_ids: courseIds,
            p_class_id: cls.id,
            p_duration_days: durationDays,
            p_start_date: null
          });

          if (error) {
            errors.push(`Turma "${cls.name}" / ${getEnrollmentStudentLabel(selectedStudent) || selectedStudent?.email || rawStudentId}: ${error.message}`);
            totalFail += courseIds.length;
            continue;
          }

          totalSuccess += (data?.success_count || 0);
          totalFail += (data?.fail_count || 0);
          if (data?.results) {
            data.results.filter(r => r.status === 'error').forEach(r => {
              errors.push(`Turma "${cls.name}" / ${getEnrollmentStudentLabel(selectedStudent) || selectedStudent?.email || rawStudentId}: ${r.message}`);
            });
          }
        }
      }

      if (totalFail > 0 || errors.length > 0) {
        const summary = `${totalSuccess} matrícula(s) realizada(s) com sucesso. ${totalFail} falha(s).`;
        throw new Error(`${summary}${errors.length ? ' Detalhes: ' + errors.join(' | ') : ''}`);
      }

      setNotification({ message: "Matrícula(s) realizada(s) com sucesso!", type: 'success' });
      setShowModal(false);
      setShowClassRequirementConfirm(false);
      setClassRequirementWarnings(null);
      setEnrollmentStudentSearch('');
      setIsEnrollmentStudentDropdownOpen(false);
      setForm({
        studentId: '',
        selectedStudentIds: [],
        linkClass: false,
        classId: '',
        companyId: '',
        selectedCourses: [],
        selectedClassIds: [],
        selectedCargos: [],
        selectedAreas: [],
        enrollmentMode: 'immediate',
        scheduledDate: '',
        scheduledTime: '00:00',
        status: 'Ativa',
        validityDays: '',
        startDate: '',
        endDate: '',
        file: null
      });
      await fetchEnrollments();
    } catch (err) {
      console.error("Enrollment error:", err);
      alert("Erro ao realizar matrícula: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
        let payload = {};
        let rpcName = '';

        if (enrollmentType === 'edit') {
            // Lógica de Edição de Matrícula (Update)
            const nextStatus = form.status || normalizeEnrollmentStatus(editingEnrollment.enrollmentStatus || editingEnrollment.status, editingEnrollment.expires_at) || 'Ativa';
            const requiresValidity = nextStatus !== 'Cancelada';

            if (requiresValidity) {
              if (!form.startDate) throw new Error("Informe a data de início.");
              if (!form.endDate) throw new Error("Informe a data final ou a validade em dias.");
              if (String(form.startDate) > String(form.endDate)) throw new Error("A data de início não pode ser posterior à data final.");
            }

            const currentUserLifecycleStatus = String(
              editingEnrollment?.studentProfileStatus || editingEnrollment?.status || ''
            ).trim();
            const currentDateInSaoPaulo = getSaoPauloDatePart(new Date());
            const nextUserLifecycleStatus = resolveUserLifecycleStatusForEnrollmentPeriod({
              currentStatus: currentUserLifecycleStatus,
              startDate: form.startDate,
              todayDate: currentDateInSaoPaulo,
            });

            const newCourseId = form.selectedCourses[0]; // Assume 1 curso na edição
            if (!newCourseId) throw new Error("Selecione um curso.");

            // 1. Calcular novas datas (ISO)
            const newConfirmedAtIso = requiresValidity ? buildIsoFromYmdStartOfDay(form.startDate) : null;
            const newExpiresAtIso = requiresValidity ? buildIsoFromYmdEndOfDay(form.endDate) : editingEnrollment.expires_at || null;

            // Status de enrollment para persistir no banco
            let persistedNextStatus;
            persistedNextStatus = getPersistedEnrollmentStatus(nextStatus, newExpiresAtIso);

            const enrollmentUpdatePayload = {
              status: persistedNextStatus,
              updated_at: new Date()
            };
            if (requiresValidity && newConfirmedAtIso) {
              enrollmentUpdatePayload.confirmed_at = newConfirmedAtIso;
            }
            if (requiresValidity && newExpiresAtIso) {
              enrollmentUpdatePayload.expires_at = newExpiresAtIso;
            }
            
            // 2. Verificar se curso mudou
            const currentCourseId = editingEnrollment.offering?.course_id;
            
            if (newCourseId !== currentCourseId) {
                 // Curso mudou: precisamos encontrar/criar offering do novo curso e atualizar enrollment
                 // Isso é complexo pois envolve mudar a chave estrangeira offering_id
                 // Vamos usar uma RPC simplificada ou fazer em 2 passos se permitido
                 
                 // Passo 1: Encontrar offering do novo curso
                 const { data: offeringData, error: offError } = await supabase
                    .from('course_offerings')
                    .select('id')
                    .eq('course_id', newCourseId)
                    .eq('status', 'Aberta')
                    .limit(1)
                    .single();
                 
                 let newOfferingId = offeringData?.id;
                 
                 if (!newOfferingId) {
                     // Criar offering se não existir (igual na criação)
                     const { data: newOff, error: createOffErr } = await supabase
                        .from('course_offerings')
                        .insert({
                            course_id: newCourseId,
                            code: `AUTO-${new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0,14)}`,
                            name: 'Oferta Automática (Edição)',
                            status: 'Aberta',
                            capacity: 999999,
                            start_date: new Date()
                        })
                        .select()
                        .single();
                     if (createOffErr) throw createOffErr;
                     newOfferingId = newOff.id;
                 }
                 
                 // Atualizar enrollment
                 const { error: updateErr } = await supabase
                    .from('enrollments')
                    .update({
                        ...enrollmentUpdatePayload,
                        offering_id: newOfferingId,
                    })
                    .eq('id', editingEnrollment.id);
                 
                 if (updateErr) throw updateErr;

            } else {
                // Apenas atualizar validade
                const { error: updateErr } = await supabase
                    .from('enrollments')
                    .update(enrollmentUpdatePayload)
                    .eq('id', editingEnrollment.id);
                
                if (updateErr) throw updateErr;
            }

            const shouldSyncAwaitingInviteLifecycle = ['Programado', 'Pendente'].includes(currentUserLifecycleStatus);
            if (shouldSyncAwaitingInviteLifecycle) {
                const { data: profileSnapshot, error: profileSnapshotError } = await supabase
                    .from('profiles')
                    .select('id, email, full_name, cpf, whatsapp, role, status, company_id, sas_partner_id')
                    .eq('id', editingEnrollment.student_id)
                    .maybeSingle();

                if (profileSnapshotError) throw profileSnapshotError;
                if (!profileSnapshot?.id) {
                    throw new Error('Não foi possível localizar o perfil do aluno para sincronizar o status do convite.');
                }

                const syncPayload = {
                    id: profileSnapshot.id,
                    email: String(profileSnapshot.email || editingEnrollment.student?.email || '').trim().toLowerCase(),
                    full_name: profileSnapshot.full_name || editingEnrollment.student?.full_name || 'Aluno',
                    cpf: profileSnapshot.cpf || null,
                    whatsapp: profileSnapshot.whatsapp || null,
                    role: profileSnapshot.role || 'Aluno',
                    status: nextUserLifecycleStatus || currentUserLifecycleStatus || profileSnapshot.status || 'Pendente',
                    company_id: profileSnapshot.company_id || null,
                    sas_partner_id: profileSnapshot.sas_partner_id || null,
                    invite_start_mode: nextUserLifecycleStatus === 'Programado' ? 'scheduled' : undefined,
                    invite_start_at: nextUserLifecycleStatus === 'Programado'
                      ? buildIsoFromYmdStartOfDay(form.startDate)
                      : undefined,
                };

                const { error: manageUserError } = await invokeAdminManageUser({
                    action: 'update',
                    payload: syncPayload,
                });

                if (manageUserError) {
                    throw new Error(manageUserError?.message || 'Falha ao sincronizar o status do convite do aluno.');
                }
            }

            // Atualizar vínculo com turma
            // Se form.linkClass for true e tiver classId, garantir que o aluno esteja na turma
            // Se for false, remover da turma? Talvez melhor não remover automaticamente para não perder histórico,
            // mas o requisito é "Permita edição da turma".
            // Vamos assumir:
            // 1. Se linkClass=true e classId diferente: mudar turma (remover da antiga, adicionar na nova)
            // 2. Se linkClass=false: remover da turma atual vinculada a este curso (se houver)

            const currentClassId = editingEnrollment.classId;
            const newClassId = (form.linkClass && form.classId) ? form.classId : null;

            if (newClassId !== currentClassId) {
                // Remover da turma anterior se existia
                if (currentClassId) {
                    await supabase
                        .from('class_students')
                        .delete()
                        .eq('class_id', currentClassId)
                        .eq('student_id', editingEnrollment.student_id);
                    
                    // Também remover o vínculo do curso com a turma anterior se não tiver mais alunos?
                    // Não, a relação class_courses é estrutural.
                }

                // Adicionar na nova turma se selecionada
                if (newClassId) {
                    const { error: linkClassError } = await supabase
                        .from('class_students')
                        .insert({
                            class_id: newClassId,
                            student_id: editingEnrollment.student_id
                        });

                    if (linkClassError) {
                        setNotification({ message: 'Não foi possível vincular o aluno à turma: ' + linkClassError.message, type: 'error' });
                        return;
                    }

                    // Garantir que o curso esteja na nova turma
                    const courseId = form.selectedCourses[0];
                    if (courseId) {
                        await supabase
                            .from('class_courses')
                            .insert({
                                class_id: newClassId,
                                course_id: courseId
                            })
                            .onConflict('class_id, course_id') // ignorar se já existe
                            .ignore();
                    }
                }
            }
            
            setNotification({ message: "Matrícula atualizada com sucesso!", type: 'success' });
            setShowModal(false);
            setEditingEnrollment(null);
            setEnrollmentStudentSearch('');
            setIsEnrollmentStudentDropdownOpen(false);
            await fetchEnrollments();
            return; // Fim do fluxo de edição
        }

        if (enrollmentType === 'student') {
            if (!form.studentId) throw new Error("Selecione um aluno.");
            if (!form.validityDays || parseInt(form.validityDays) <= 0) throw new Error("Informe a validade da matrícula em dias.");
            if (form.selectedCourses.length === 0) throw new Error("Selecione pelo menos um curso.");

            // Se o aluno é do Core (Base Única), espelha localmente antes da matrícula.
            let studentId = form.studentId;
            const selectedStudent = enrollmentStudentCandidates.find(
              (s) => String(s?.id || '') === String(form.studentId || '')
            );
            if (selectedStudent?.fromCore && selectedStudent?.central_person_id) {
              const { data: mirroredId, error: mirrorError } = await supabase.rpc('core_mirror_person', {
                p_central_person_id: String(selectedStudent.central_person_id),
              });
              if (mirrorError) throw new Error(`Falha ao espelhar aluno do Core: ${mirrorError.message}`);
              if (!mirroredId) throw new Error('Não foi possível criar o aluno a partir da Base Core.');
              studentId = mirroredId;
            }

            // Assuming we process one student at a time for now, or use a loop
            // Ideally we'd have a bulk enrollment RPC even for single student to reuse logic
            // For now, let's just enroll the student in the selected courses
            
            const enrollmentsToCreate = form.selectedCourses.map(courseId => ({
                student_id: studentId,
                course_id: courseId, // Note: Schema uses offering_id, we need to find open offering
                // If direct course enrollment is simplified in frontend, we need to find the offering backend side or here.
                // Assuming 'courses' passed here are actually course objects. 
                // We need to find an open offering for each course.
            }));
            
            // Simplified: we'll call a hypothetical RPC or insert directly
            // But wait, the schema requires offering_id. 
            // Let's assume we create a default offering if none exists or pick the first open one.
            // For this task, let's create a specific RPC for this administrative action.
            
            rpcName = 'admin_enroll_student';
            payload = {
                p_student_id: studentId,
                p_course_ids: form.selectedCourses,
                p_class_id: (form.linkClass && form.classId) ? form.classId : null,
                p_duration_days: form.validityDays ? parseInt(form.validityDays) : null,
                p_start_date: form.startDate ? new Date(`${form.startDate}T${form.scheduledTime || '00:00'}:00`).toISOString() : null
            };

        } else if (enrollmentType === 'company') {
             if (!form.companyId) throw new Error("Selecione uma empresa.");
             if (!form.validityDays || parseInt(form.validityDays) <= 0) throw new Error("Informe a validade da matrícula em dias.");
             if (form.selectedCourses.length === 0) throw new Error("Selecione pelo menos um curso.");

             // Se a empresa é do Core (Base Única), espelha localmente antes da matrícula.
             let companyId = form.companyId;
             const selectedCompany = mergedCompanies.find(
               (c) => String(c?.id || '') === String(form.companyId || '')
             );
             if (selectedCompany?.fromCore && selectedCompany?.central_company_id) {
               const { data: mirroredId, error: mirrorError } = await supabase.rpc('core_mirror_company', {
                 p_central_company_id: String(selectedCompany.central_company_id),
               });
               if (mirrorError) throw new Error(`Falha ao espelhar empresa do Core: ${mirrorError.message}`);
               if (!mirroredId) throw new Error('Não foi possível criar a empresa a partir da Base Core.');
               companyId = mirroredId;
             }

             // Valida data de início
             let startDateIso = null;
             if (form.startDate) {
               const dt = new Date(`${form.startDate}T${form.scheduledTime || '00:00'}:00`);
               if (!isNaN(dt.getTime())) startDateIso = dt.toISOString();
             }

             rpcName = 'admin_enroll_company';
             payload = {
                 p_company_id: companyId,
                 p_course_ids: form.selectedCourses,
                 p_class_id: (form.linkClass && form.classId) ? form.classId : null,
                 p_duration_days: form.validityDays ? parseInt(form.validityDays) : null,
                 p_areas: form.selectedAreas.length > 0 ? form.selectedAreas : null,
                 p_cargos: form.selectedCargos.length > 0 ? form.selectedCargos : null,
                 p_start_date: startDateIso
             };

        } else if (enrollmentType === 'bulk') {
            if (!form.file) throw new Error("Selecione um arquivo.");
            // Bulk upload logic would typically involve uploading file to storage then triggering a function
            // Or parsing locally (csv) and sending data.
            // For simplicity, let's assume we parse CSV here.
            alert("Importação em massa via arquivo ainda não implementada no backend.");
            setLoading(false);
            return;

        } else if (enrollmentType === 'class') {
            const studentIdsToCheck = (form.selectedStudentIds || []).map((id) => String(id)).filter(Boolean);
            if (studentIdsToCheck.length === 0) throw new Error("Marque ao menos um aluno.");
            if (form.selectedClassIds.length === 0) throw new Error("Selecione pelo menos uma turma.");

            const selectedClasses = classOptions.filter(c => form.selectedClassIds.includes(c.id));
            const studentsToCheck = selectedEnrollmentStudents;

            // Valida requisitos de cada turma para cada aluno marcado.
            const classesWithViolations = [];
            for (const cls of selectedClasses) {
              const classViolations = [];
              for (const student of studentsToCheck) {
                const violations = validateClassRequirements(student, cls);
                if (violations.length > 0) {
                  const studentName = getEnrollmentStudentLabel(student) || student?.email || 'Aluno';
                  violations.forEach((v) => classViolations.push({ ...v, studentName }));
                }
              }
              if (classViolations.length > 0) {
                classesWithViolations.push({ id: cls.id, name: cls.name, violations: classViolations });
              }
            }

            // Se houver violações, exibe modal de confirmação (override) e interrompe.
            if (classesWithViolations.length > 0) {
              setClassRequirementWarnings({ students: studentsToCheck, classes: classesWithViolations });
              setShowClassRequirementConfirm(true);
              setLoading(false);
              return;
            }

            // Sem violações: executa a matrícula diretamente.
            await executeClassEnrollment();
            return;
        }

        if (rpcName) {
            const { data, error } = await supabase.rpc(rpcName, payload);
            if (error) throw error;
            
            if (data && data.fail_count > 0) {
                const firstError = data.results?.find(r => r.status === 'error')?.message || 'Erro desconhecido';
                throw new Error(`Falha na matrícula: ${firstError}`);
            }

            const processed = data?.students_processed;
            const successMsg = enrollmentType === 'company' && typeof processed === 'number'
              ? `Matrícula realizada com sucesso! ${processed} aluno(s) processado(s).`
              : "Matrícula realizada com sucesso!";
            setNotification({ message: successMsg, type: 'success' });
            setShowModal(false);
            setEnrollmentStudentSearch('');
            setIsEnrollmentStudentDropdownOpen(false);
            setForm({
                studentId: '',
                linkClass: false,
                classId: '',
                companyId: '',
                selectedCourses: [],
                selectedClassIds: [],
                selectedCargos: [],
                selectedAreas: [],
                enrollmentMode: 'immediate',
                scheduledDate: '',
                scheduledTime: '00:00',
                status: 'Ativa',
                validityDays: '',
                startDate: '',
                endDate: '',
                file: null
            });
            await fetchEnrollments();
        }

    } catch (err) {
        console.error("Enrollment error:", err);
        alert("Erro ao realizar matrícula: " + err.message);
    } finally {
        setLoading(false);
    }
  };

  const exportEnrollmentsToCsv = () => {
    try {
      const headers = [
        'Usuário',
        'E-mail',
        'Empresa',
        'Curso',
        'Turma',
        'Data Matrícula',
        'Validade',
        'Status'
      ];
      const sanitize = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = (enrollments || []).map(e => [
        sanitize(e?.student?.full_name || ''),
        sanitize(e?.student?.email || ''),
        sanitize(e?.companyName || '-'),
        sanitize(e?.offering?.course?.name || ''),
        sanitize(e?.className || '-'),
        sanitize(e?.created_at ? new Date(e.created_at).toLocaleDateString('pt-BR') : ''),
        sanitize(e?.expires_at ? formatEnrollmentExpiresAt(e.expires_at) : 'Ilimitado'),
        sanitize(e?.status || '')
      ].join(';'));
      const csvContent = "\uFEFF" + [headers.join(';'), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `matriculas_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export CSV error:', err);
      alert('Erro ao exportar planilha.');
    }
  };

  const enrollmentStatusTotals = React.useMemo(() => {
    return (enrollments || []).reduce((acc, enrollment) => {
      const status = String(enrollment?.status || 'Outros');
      acc.total += 1;
      if (status === 'Programado') acc.programado += 1;
      else if (status === 'Ativa') acc.ativa += 1;
      else if (status === 'Expirada') acc.expirada += 1;
      else if (status === 'Inativada') acc.inativada += 1;
      else if (status === 'Cancelada') acc.cancelada += 1;
      else acc.outros += 1;
      return acc;
    }, {
      total: 0,
      programado: 0,
      ativa: 0,
      expirada: 0,
      inativada: 0,
      cancelada: 0,
      outros: 0,
    });
  }, [enrollments]);

  const enrollmentSummaryCards = [
    {
      id: 'total',
      label: 'Total de Matrículas',
      value: enrollmentStatusTotals.total,
      helper: 'Registros no recorte atual',
      icon: Users,
      valueClass: 'text-white',
      iconClass: 'text-primary',
      borderClass: 'hover:border-primary/40 hover:shadow-[0_0_22px_rgba(249,115,22,0.18)]',
    },
    {
      id: 'programado',
      label: 'Programadas',
      value: enrollmentStatusTotals.programado,
      helper: 'Alunos com convite agendado',
      icon: PauseCircle,
      valueClass: 'text-amber-300',
      iconClass: 'text-amber-300',
      borderClass: 'hover:border-amber-400/40 hover:shadow-[0_0_22px_rgba(251,191,36,0.16)]',
    },
    {
      id: 'ativa',
      label: 'Ativas',
      value: enrollmentStatusTotals.ativa,
      helper: 'Matrículas liberadas para acesso',
      icon: CheckCircle,
      valueClass: 'text-teal-400',
      iconClass: 'text-teal-400',
      borderClass: 'hover:border-teal-400/40 hover:shadow-[0_0_22px_rgba(45,212,191,0.16)]',
    },
    {
      id: 'expirada',
      label: 'Expiradas',
      value: enrollmentStatusTotals.expirada,
      helper: 'Vigência já ultrapassada',
      icon: Clock,
      valueClass: 'text-amber-300',
      iconClass: 'text-amber-300',
      borderClass: 'hover:border-amber-400/40 hover:shadow-[0_0_22px_rgba(251,191,36,0.16)]',
    },
    {
      id: 'inativada',
      label: 'Inativadas',
      value: enrollmentStatusTotals.inativada,
      helper: 'Visíveis, porém bloqueadas',
      icon: Lock,
      valueClass: 'text-slate-300',
      iconClass: 'text-slate-300',
      borderClass: 'hover:border-slate-500/40 hover:shadow-[0_0_22px_rgba(148,163,184,0.12)]',
    },
    {
      id: 'cancelada',
      label: 'Canceladas',
      value: enrollmentStatusTotals.cancelada,
      helper: 'Registros cancelados',
      icon: AlertCircle,
      valueClass: 'text-rose-400',
      iconClass: 'text-rose-400',
      borderClass: 'hover:border-rose-400/40 hover:shadow-[0_0_22px_rgba(251,113,133,0.16)]',
    },
  ];

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <ConfirmModal
        isOpen={Boolean(pendingDeleteEnrollment)}
        title="Excluir matrícula?"
        message={
          pendingDeleteEnrollment
            ? `Deseja excluir a matrícula de ${pendingDeleteEnrollment?.student?.full_name || 'este usuário'} no curso "${pendingDeleteEnrollment?.offering?.course?.name || 'este curso'}"? Após excluir, o usuário não receberá novas comunicações automáticas sobre este curso.`
            : ''
        }
        onCancel={() => setPendingDeleteEnrollment(null)}
        onConfirm={confirmDeleteEnrollment}
      />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Matrículas</h1>
          <p className="text-slate-400">Gerencie as matrículas de alunos em turmas e cursos.</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <button
              onClick={exportEnrollmentsToCsv}
              className="bg-slate-800 text-slate-200 px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 border border-slate-700 hover:bg-slate-700 transition"
              title="Exportar Excel"
            >
              <FileSpreadsheet size={18} /> Exportar Excel
            </button>
          )}
          {showActions && (
            <button 
              onClick={handleNewEnrollment} 
              className="bg-orange-500 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition"
            >
              <UserPlus size={20} /> Matricular
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-6">
        {enrollmentSummaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              className={`min-w-0 bg-slate-900 border border-slate-800 rounded-lg p-3 shadow-lg transition-all duration-300 ${card.borderClass}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-[9px] uppercase tracking-[0.16em] text-slate-500 font-bold truncate pr-2">{card.label}</p>
                <Icon size={14} className={`${card.iconClass} shrink-0`} />
              </div>
              <p className={`text-2xl md:text-[28px] font-black mt-1 leading-none ${card.valueClass}`}>{card.value}</p>
              <p className="text-[10px] text-slate-500 mt-1 truncate">{card.helper}</p>
            </div>
          );
        })}
      </div>

      <div className={`bg-slate-900 border border-slate-800 rounded-xl p-4 mb-6 grid grid-cols-1 ${isAdminUser ? 'md:grid-cols-3 xl:grid-cols-4' : 'md:grid-cols-3 xl:grid-cols-3'} gap-4`}>
         <div className="flex flex-col gap-1">
           <label className="text-[10px] uppercase font-bold text-slate-500">Usuário</label>
           <div className="relative">
             <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
             <input 
               type="text" 
               placeholder="Buscar por nome ou e-mail"
               value={enrollmentFilters.student}
               onChange={e => setEnrollmentFilters({...enrollmentFilters, student: e.target.value})}
               className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-10 pr-4 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] placeholder:text-slate-600 hover:border-slate-700"
             />
           </div>
         </div>
         <div className="flex flex-col gap-1">
           <label className="text-[10px] uppercase font-bold text-slate-500">Curso</label>
           <ReportMultiSelect
             options={courseMultiSelectOptions}
             value={Array.isArray(enrollmentFilters.courseIds) ? enrollmentFilters.courseIds : []}
             onChange={(next) => setEnrollmentFilters({ ...enrollmentFilters, courseIds: next, course: next[0] || '' })}
             placeholder="Todos os cursos"
             allLabel="Todos os Cursos"
           />
         </div>
         {isAdminUser && (
           <div className="flex flex-col gap-1">
             <label className="text-[10px] uppercase font-bold text-slate-500">Parceiro SAS</label>
             <ReportMultiSelect
               options={partnerOptions}
               value={Array.isArray(enrollmentFilters.partnerIds) ? enrollmentFilters.partnerIds : []}
               onChange={(next) => setEnrollmentFilters({
                 ...enrollmentFilters,
                 partnerIds: next,
                 partner: next[0] || '',
                 companyIds: [],
                 company: '',
               })}
               placeholder="Todos os parceiros"
               allLabel="Todos os Parceiros"
             />
           </div>
         )}
         <div className="flex flex-col gap-1">
           <label className="text-[10px] uppercase font-bold text-slate-500">Empresa</label>
           <ReportCompaniesMultiSelect
             options={visibleCompanyOptions}
             value={Array.isArray(enrollmentFilters.companyIds) ? enrollmentFilters.companyIds : []}
             onChange={(next) => setEnrollmentFilters({ ...enrollmentFilters, companyIds: next, company: next[0] || '' })}
             placeholder="Todas as empresas"
           />
         </div>
         <div className="flex flex-col gap-1">
           <label className="text-[10px] uppercase font-bold text-slate-500">Início</label>
           <input 
             type="date" 
             value={enrollmentFilters.startDate}
             onChange={e => setEnrollmentFilters({...enrollmentFilters, startDate: e.target.value})}
             className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] [color-scheme:dark] hover:border-slate-700"
           />
         </div>
         <div className="flex flex-col gap-1">
           <label className="text-[10px] uppercase font-bold text-slate-500">Fim</label>
           <input 
             type="date" 
             value={enrollmentFilters.endDate}
             onChange={e => setEnrollmentFilters({...enrollmentFilters, endDate: e.target.value})}
             className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] [color-scheme:dark] hover:border-slate-700"
           />
         </div>
         <div className="flex flex-col gap-1">
           <label className="text-[10px] uppercase font-bold text-slate-500">Status</label>
           <ReportMultiSelect
             options={statusOptions}
             value={Array.isArray(enrollmentFilters.statuses) ? enrollmentFilters.statuses : []}
             onChange={(next) => setEnrollmentFilters({ ...enrollmentFilters, statuses: next, status: next[0] || '' })}
             placeholder="Todos os status"
             allLabel="Todos os Status"
           />
         </div>
      </div>

      {loadingEnrollments ? (
        <div className="flex justify-center p-12 text-slate-500">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : enrollments.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 md:p-12 flex flex-col items-center justify-center text-slate-500 border-dashed">
          <Users size={48} className="mb-4 opacity-50" />
          <p className="text-lg font-medium">Gestão de matrículas independente.</p>
          <p className="text-sm">Utilize o botão "Matricular" para iniciar um novo registro.</p>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
              <tr>
                <th className="p-5">
                  <div className="flex items-center gap-2">
                    <span>Usuário</span>
                    <button
                      type="button"
                      onClick={() => toggleEnrollmentSort('student', 'asc')}
                      className={getEnrollmentSortButtonClassName('student')}
                      title={getEnrollmentSortButtonTitle('student')}
                    >
                      <ArrowUpDown size={12} />
                      {sortConfig?.field === 'student' ? getEnrollmentSortButtonLabel('student') : 'A/Z'}
                    </button>
                  </div>
                </th>
                <th className="p-5">
                  <div className="flex items-center gap-2">
                    <span>Empresa</span>
                    <button
                      type="button"
                      onClick={() => toggleEnrollmentSort('company', 'asc')}
                      className={getEnrollmentSortButtonClassName('company')}
                      title={getEnrollmentSortButtonTitle('company')}
                    >
                      <ArrowUpDown size={12} />
                      {sortConfig?.field === 'company' ? getEnrollmentSortButtonLabel('company') : 'A/Z'}
                    </button>
                  </div>
                </th>
                <th className="p-5">Curso</th>
                <th className="p-5">
                  <div className="flex items-center gap-2">
                    <span>Data Matrícula</span>
                    <button
                      type="button"
                      onClick={() => toggleEnrollmentSort('created_at', 'desc')}
                      className={getEnrollmentSortButtonClassName('created_at')}
                      title={getEnrollmentSortButtonTitle('created_at')}
                    >
                      <ArrowUpDown size={12} />
                      {sortConfig?.field === 'created_at' ? getEnrollmentSortButtonLabel('created_at') : 'Recentes'}
                    </button>
                  </div>
                </th>
                <th className="p-5">
                  <div className="flex items-center gap-2">
                    <span>Validade</span>
                    <button
                      type="button"
                      onClick={() => toggleEnrollmentSort('expires_at', 'desc')}
                      className={getEnrollmentSortButtonClassName('expires_at')}
                      title={getEnrollmentSortButtonTitle('expires_at')}
                    >
                      <ArrowUpDown size={12} />
                      {sortConfig?.field === 'expires_at' ? getEnrollmentSortButtonLabel('expires_at') : 'Recentes'}
                    </button>
                  </div>
                </th>
                <th className="p-5">Status</th>
                {showActions && <th className="p-5 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {paginatedEnrollments.map((enrollment) => (
                <tr key={enrollment.id} className="hover:bg-slate-800/50 transition text-slate-300">
                  <td className="p-5">
                    <div className="font-bold text-white">{enrollment.student?.full_name || 'Desconhecido'}</div>
                    <div className="text-xs opacity-50">{enrollment.student?.email}</div>
                  </td>
                  <td className="p-5">
                    <span className="text-slate-300">{enrollment.companyName || '-'}</span>
                  </td>
                  <td className="p-5">
                    {enrollment.offering?.course?.name || 'Curso não encontrado'}
                  </td>
                  <td className="p-5">
                    {new Date(enrollment.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="p-5">
                    {enrollment.expires_at 
                      ? (
                        <div className="leading-tight">
                          <div>{formatEnrollmentExpiresAt(enrollment.expires_at, { includeTime: false })}</div>
                          <div className="text-xs text-slate-400">
                            {formatEnrollmentExpiresAtTime(enrollment.expires_at)} SP-BR
                          </div>
                        </div>
                      )
                      : <span className="text-slate-500">Ilimitado</span>}
                  </td>
                  <td className="p-5">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getEnrollmentStatusBadgeClass(enrollment.status)}`}>
                      {enrollment.status}
                    </span>
                  </td>
                  {showActions && (
                    <td className="p-5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(enrollment)}
                          className="text-slate-500 hover:text-white transition"
                          title="Editar Matrícula"
                        >
                          <Edit2 size={18} />
                        </button>
                        {isAdminUser && (
                          <button
                            onClick={() => handleDeleteEnrollment(enrollment)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-rose-500/30 text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 transition disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Excluir Matrícula"
                            disabled={deletingEnrollmentId === enrollment.id}
                          >
                            {deletingEnrollmentId === enrollment.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 pb-6">
            <DataTablePagination
              totalItems={enrollments.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onItemsPerPageChange={setItemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-950/80 backdrop-blur-sm p-4 py-6 animate-fade-in overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[calc(100vh-3rem)]">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-800 shrink-0">
              <h3 className="text-xl font-bold text-white">{editingEnrollment ? 'Editar Matrícula' : 'Nova Matrícula'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white transition"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar">
              {/* Seletor de Tipo de Matrícula (Oculto em Edição) */}
              {!editingEnrollment && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <button 
                  onClick={() => { setEnrollmentType('student'); setForm((p) => ({ ...p, studentId: '', selectedStudentIds: [] })); setEnrollmentStudentSearch(''); }}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition h-20 ${enrollmentType === 'student' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-slate-950 border-slate-800 text-slate-400 hover:bg-slate-800 hover:border-slate-700'}`}
                >
                  <User size={24} />
                  <span className="text-xs font-bold uppercase tracking-wide">Usuário</span>
                </button>
                
                <button 
                  onClick={() => { setEnrollmentType('company'); setForm((p) => ({ ...p, studentId: '', selectedStudentIds: [] })); setEnrollmentStudentSearch(''); }}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition h-20 ${enrollmentType === 'company' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-slate-950 border-slate-800 text-slate-400 hover:bg-slate-800 hover:border-slate-700'}`}
                >
                  <Building size={24} />
                  <span className="text-xs font-bold uppercase tracking-wide">Empresa</span>
                </button>

                <button 
                  onClick={() => { setEnrollmentType('class'); setForm((p) => ({ ...p, studentId: '', selectedStudentIds: [] })); setEnrollmentStudentSearch(''); }}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition h-20 ${enrollmentType === 'class' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-slate-950 border-slate-800 text-slate-400 hover:bg-slate-800 hover:border-slate-700'}`}
                >
                  <GraduationCap size={24} />
                  <span className="text-xs font-bold uppercase tracking-wide">Turma</span>
                </button>
                
                <button 
                  onClick={() => { setEnrollmentType('bulk'); setForm((p) => ({ ...p, studentId: '', selectedStudentIds: [] })); setEnrollmentStudentSearch(''); }}
                  className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition h-20 ${enrollmentType === 'bulk' ? 'bg-orange-500/10 border-orange-500 text-orange-400' : 'bg-slate-950 border-slate-800 text-slate-400 hover:bg-slate-800 hover:border-slate-700'}`}
                >
                  <UploadCloud size={24} />
                  <span className="text-xs font-bold uppercase tracking-wide">Em Massa</span>
                </button>
              </div>
              )}

              <form id="enrollmentForm" onSubmit={handleSave} className="flex flex-col gap-6 animate-fade-in">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="flex flex-col gap-4">
                    {/* Campos Específicos por Tipo */}
                    {(enrollmentType === 'student' || enrollmentType === 'class' || enrollmentType === 'edit') && (
                        <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Usuário</label>
                        {enrollmentType === 'edit' ? (
                            <input 
                                disabled
                                value={form.studentId}
                                className="bg-slate-900 border border-slate-800 p-3 rounded-lg text-slate-400 cursor-not-allowed"
                            />
                        ) : (
                            <>
                              <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                  ref={enrollmentStudentInputRef}
                                  type="text"
                                  value={enrollmentStudentSearch}
                                  onFocus={() => {
                                    setEnrollmentStudentDropdownRect(getDropdownFixedRect(enrollmentStudentInputRef.current));
                                    setIsEnrollmentStudentDropdownOpen(true);
                                  }}
                                  onBlur={() => {
                                    window.setTimeout(() => {
                                      setIsEnrollmentStudentDropdownOpen(false);
                                    }, 120);
                                  }}
                                  onChange={e => {
                                    const next = e.target.value;
                                    handleEnrollmentStudentSearchChange(next);
                                    setEnrollmentStudentDropdownRect(getDropdownFixedRect(enrollmentStudentInputRef.current));
                                    // #region debug-point C:enrollments-user-search-input
                                    __dbgEnrollSend('C', 'student-search-input-change', { term: next });
                                    // #endregion
                                  }}
                                  onKeyDown={handleEnrollmentStudentKeyDown}
                                  placeholder="Buscar usuário por nome ou e-mail"
                                  className="bg-slate-950 border border-slate-800 pl-10 pr-3 py-3 rounded-lg text-white outline-none focus:border-white transition"
                                  autoComplete="off"
                                />
                                {isEnrollmentStudentDropdownOpen && enrollmentStudentDropdownRect && createPortal(
                                  <div
                                    ref={enrollmentStudentListRef}
                                    className="fixed z-[9999] max-h-56 overflow-auto rounded-lg border border-slate-800 bg-slate-950 shadow-2xl"
                                    style={{
                                      top: enrollmentStudentDropdownRect.top,
                                      left: enrollmentStudentDropdownRect.left,
                                      width: enrollmentStudentDropdownRect.width,
                                    }}
                                  >
                                    {loadingEnrollmentStudentCandidates ? (
                                      <div className="px-3 py-3 text-sm text-slate-500">Buscando usuários...</div>
                                    ) : selectableStudents.length > 0 ? (
                                      selectableStudents.map((student, index) => {
                                        const isSingleSelected = String(form.studentId || '') === String(student.id || '');
                                        const isMultiSelected = form.selectedStudentIds.some((id) => String(id) === String(student.id || ''));
                                        const isSelected = enrollmentType === 'class' ? isMultiSelected : isSingleSelected;
                                        const isHighlighted = index === highlightedEnrollmentStudentIndex;
                                        return (
                                          <button
                                            key={student.id}
                                            type="button"
                                            data-student-option-index={index}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => handleEnrollmentStudentSelect(student)}
                                            className={`w-full border-b border-slate-800 px-3 py-2 text-left transition last:border-b-0 ${
                                              isHighlighted || isSelected
                                                ? 'bg-orange-500/10 text-orange-300'
                                                : 'text-slate-200 hover:bg-slate-900'
                                            }`}
                                          >
                                            <div className="flex items-center justify-between gap-3">
                                              <div className="flex items-center gap-2 min-w-0">
                                                {enrollmentType === 'class' && (
                                                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-600'}`}>
                                                    {isSelected && <Check size={12} className="text-white" />}
                                                  </div>
                                                )}
                                                <div className="min-w-0">
                                                  <div className="font-medium truncate">{getEnrollmentStudentLabel(student) || 'Usuário sem nome'}</div>
                                                  <div className="text-xs text-slate-500 truncate">{student.email || 'Sem e-mail'}</div>
                                                </div>
                                              </div>
                                              {(isSelected || isHighlighted) && <Check size={14} className="shrink-0 text-orange-300" />}
                                            </div>
                                          </button>
                                        );
                                      })
                                    ) : (
                                      <div className="px-3 py-3 text-sm text-slate-500">Nenhum usuário encontrado.</div>
                                    )}
                                  </div>,
                                  document.body
                                )}
                              </div>
                              {/* Lista de alunos marcados (apenas na aba Turma) */}
                              {enrollmentType === 'class' && selectedEnrollmentStudents.length > 0 && (
                                <div className="flex flex-col gap-2 rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold uppercase tracking-widest text-orange-300">
                                      Alunos marcados ({selectedEnrollmentStudents.length})
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setForm((prev) => ({ ...prev, selectedStudentIds: [] }));
                                      }}
                                      className="text-xs font-bold uppercase tracking-wide text-orange-300/70 hover:text-orange-200"
                                    >
                                      Limpar todos
                                    </button>
                                  </div>
                                  <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto custom-scrollbar">
                                    {selectedEnrollmentStudents.map((student) => (
                                      <div
                                        key={String(student?.id || '')}
                                        className="flex items-center justify-between gap-3 rounded-md bg-slate-950/60 border border-slate-800 px-3 py-2"
                                      >
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium text-white truncate">
                                            {getEnrollmentStudentLabel(student) || 'Usuário sem nome'}
                                          </div>
                                          <div className="text-xs text-slate-500 truncate">{student.email || 'Sem e-mail'}</div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const sid = String(student?.id || '');
                                            setForm((prev) => ({
                                              ...prev,
                                              selectedStudentIds: prev.selectedStudentIds.filter((id) => String(id) !== sid),
                                            }));
                                          }}
                                          className="text-slate-500 hover:text-rose-400 transition shrink-0"
                                          title="Remover da lista"
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {selectedEnrollmentStudent && enrollmentType !== 'class' && (
                                <div className="flex items-center justify-between rounded-lg border border-orange-500/20 bg-orange-500/10 px-3 py-2 text-sm text-orange-200">
                                  <div>
                                    <div className="font-medium">{getEnrollmentStudentLabel(selectedEnrollmentStudent) || 'Usuário sem nome'}</div>
                                    <div className="text-xs text-orange-200/80">{selectedEnrollmentStudent.email || 'Sem e-mail'}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setForm((prev) => ({ ...prev, studentId: '' }));
                                      setEnrollmentStudentSearch('');
                                      setIsEnrollmentStudentDropdownOpen(false);
                                    }}
                                    className="text-xs font-bold uppercase tracking-wide text-orange-200/80 hover:text-orange-100"
                                  >
                                    Limpar
                                  </button>
                                </div>
                              )}
                              <div className="text-[11px] text-slate-500">
                                {loadingEnrollmentStudentCandidates
                                  ? 'Buscando usuários disponíveis para matrícula'
                                  : enrollmentType === 'class'
                                  ? selectedEnrollmentStudents.length > 0
                                    ? `${selectedEnrollmentStudents.length} aluno(s) marcado(s) para matrícula.`
                                    : 'Busque e marque os alunos para matricular na(s) turma(s).'
                                  : enrollmentStudentSearch.trim()
                                  ? `${selectableStudents.length} usuário(s) encontrado(s) para "${enrollmentStudentSearch.trim()}"`
                                  : 'Digite para buscar e selecione o usuário da matrícula'}
                              </div>
                            </>
                        )}
                        </div>
                    )}

                    {enrollmentType === 'company' && (
                        <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Selecione a Empresa</label>
                        <select
                            required
                            value={form.companyId}
                            onChange={e => setForm({...form, companyId: e.target.value, selectedCargos: [], selectedAreas: []})}
                            className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                        >
                            <option value="">Selecione...</option>
                            {mergedCompanies.map(c => (
                            <option key={c.id} value={c.id}>{c.name}{c.fromCore ? '  (Base Core)' : ''}</option>
                            ))}
                        </select>
                        </div>

                        {/* Cargo - múltipla seleção com autocomplete */}
                        <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cargo (opcional)</label>
                        <div className="relative">
                          {form.selectedCargos.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                              {form.selectedCargos.map(cargo => (
                                <span key={cargo} className="inline-flex items-center gap-1.5 bg-orange-500/15 text-orange-300 text-xs font-medium px-2.5 py-1 rounded border border-orange-500/30">
                                  <span className="truncate max-w-[200px]">{cargo}</span>
                                  <button type="button" onClick={() => toggleCargo(cargo)} className="text-orange-300/70 hover:text-orange-200 transition shrink-0">
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                              type="text"
                              value={cargoSearch}
                              onFocus={() => setIsCargoDropdownOpen(true)}
                              onBlur={() => { window.setTimeout(() => setIsCargoDropdownOpen(false), 120); }}
                              onChange={e => { setCargoSearch(e.target.value); setIsCargoDropdownOpen(true); }}
                              placeholder={form.companyId ? "Buscar cargo..." : "Selecione uma empresa primeiro"}
                              disabled={!form.companyId}
                              className="bg-slate-950 border border-slate-800 pl-10 pr-3 py-3 rounded-lg text-white outline-none focus:border-white transition w-full disabled:opacity-50 disabled:cursor-not-allowed"
                              autoComplete="off"
                            />
                          </div>
                          {isCargoDropdownOpen && form.companyId && (
                            <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-48 overflow-auto rounded-lg border border-slate-800 bg-slate-950 shadow-2xl">
                              {(() => {
                                const term = cargoSearch.trim().toLowerCase();
                                const filtered = term
                                  ? companyCargos.filter(c => String(c.name || '').toLowerCase().includes(term))
                                  : companyCargos;
                                if (filtered.length === 0) {
                                  return <div className="px-3 py-3 text-sm text-slate-500">Nenhum cargo cadastrado para a empresa.</div>;
                                }
                                return filtered.map(c => {
                                  const isSelected = form.selectedCargos.includes(c.name);
                                  return (
                                    <button
                                      key={c.id}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => toggleCargo(c.name)}
                                      className={`w-full border-b border-slate-800 px-3 py-2 text-left transition last:border-b-0 ${isSelected ? 'bg-orange-500/10 text-orange-300' : 'text-slate-200 hover:bg-slate-900'}`}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-600'}`}>
                                            {isSelected && <Check size={12} className="text-white" />}
                                          </div>
                                          <span className="font-medium truncate">{c.name}</span>
                                        </div>
                                        {isSelected && <Check size={14} className="shrink-0 text-orange-300" />}
                                      </div>
                                    </button>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500">
                          {form.selectedCargos.length > 0
                            ? `${form.selectedCargos.length} cargo(s) selecionado(s) — apenas usuários com estes cargos serão matriculados.`
                            : 'Todos os cargos serão matriculados.'}
                        </p>
                        </div>

                        {/* Área - múltipla seleção com autocomplete */}
                        <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Área (opcional)</label>
                        <div className="relative">
                          {form.selectedAreas.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                              {form.selectedAreas.map(area => (
                                <span key={area} className="inline-flex items-center gap-1.5 bg-orange-500/15 text-orange-300 text-xs font-medium px-2.5 py-1 rounded border border-orange-500/30">
                                  <span className="truncate max-w-[200px]">{area}</span>
                                  <button type="button" onClick={() => toggleArea(area)} className="text-orange-300/70 hover:text-orange-200 transition shrink-0">
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                              type="text"
                              value={areaSearch}
                              onFocus={() => setIsAreaDropdownOpen(true)}
                              onBlur={() => { window.setTimeout(() => setIsAreaDropdownOpen(false), 120); }}
                              onChange={e => { setAreaSearch(e.target.value); setIsAreaDropdownOpen(true); }}
                              placeholder={form.companyId ? "Buscar área..." : "Selecione uma empresa primeiro"}
                              disabled={!form.companyId}
                              className="bg-slate-950 border border-slate-800 pl-10 pr-3 py-3 rounded-lg text-white outline-none focus:border-white transition w-full disabled:opacity-50 disabled:cursor-not-allowed"
                              autoComplete="off"
                            />
                          </div>
                          {isAreaDropdownOpen && form.companyId && (
                            <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-48 overflow-auto rounded-lg border border-slate-800 bg-slate-950 shadow-2xl">
                              {(() => {
                                const term = areaSearch.trim().toLowerCase();
                                const filtered = term
                                  ? companyAreas.filter(a => String(a.name || '').toLowerCase().includes(term))
                                  : companyAreas;
                                if (filtered.length === 0) {
                                  return <div className="px-3 py-3 text-sm text-slate-500">Nenhuma área cadastrada para a empresa.</div>;
                                }
                                return filtered.map(a => {
                                  const isSelected = form.selectedAreas.includes(a.name);
                                  return (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => toggleArea(a.name)}
                                      className={`w-full border-b border-slate-800 px-3 py-2 text-left transition last:border-b-0 ${isSelected ? 'bg-orange-500/10 text-orange-300' : 'text-slate-200 hover:bg-slate-900'}`}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-600'}`}>
                                            {isSelected && <Check size={12} className="text-white" />}
                                          </div>
                                          <span className="font-medium truncate">{a.name}</span>
                                        </div>
                                        {isSelected && <Check size={14} className="shrink-0 text-orange-300" />}
                                      </div>
                                    </button>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500">
                          {form.selectedAreas.length > 0
                            ? `${form.selectedAreas.length} área(s) selecionada(s) — apenas usuários destas áreas serão matriculados.`
                            : 'Todas as áreas serão matriculadas.'}
                        </p>
                        </div>

                        {/* Matrícula Imediata vs Programada REMOVIDA — a data/hora de início agora é definida na seção de Vigência */}
                        </div>
                    )}

                    {enrollmentType === 'bulk' && (
                        <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Arquivo de Importação</label>
                        <div className="border-2 border-dashed border-slate-800 rounded-lg p-8 flex flex-col items-center justify-center text-slate-500 hover:border-orange-500/50 hover:bg-slate-900/50 transition cursor-pointer group relative">
                            <input 
                                type="file" 
                                accept=".csv, .xlsx" 
                                onChange={handleFileChange}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <UploadCloud size={32} className="mb-2 group-hover:text-orange-400 transition" />
                            <p className="text-sm font-medium">{form.file ? form.file.name : 'Clique para selecionar ou arraste o arquivo'}</p>
                            <p className="text-xs text-slate-600">.CSV ou .XLSX</p>
                        </div>
                        </div>
                    )}

                    {enrollmentType === 'class' && (
                        <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Selecione a(s) Turma(s)</label>
                        <div className="relative">
                          {/* Chips das turmas selecionadas */}
                          {form.selectedClassIds.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                              {form.selectedClassIds.map(classId => {
                                const cls = classOptions.find(c => String(c.id) === String(classId));
                                if (!cls) return null;
                                return (
                                  <span
                                    key={classId}
                                    className="inline-flex items-center gap-1.5 bg-orange-500/15 text-orange-300 text-xs font-medium px-2.5 py-1 rounded border border-orange-500/30"
                                  >
                                    <span className="truncate max-w-[200px]">{cls.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleToggleClass(classId)}
                                      className="text-orange-300/70 hover:text-orange-200 transition shrink-0"
                                    >
                                      <X size={12} />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {/* Campo de busca */}
                          <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                              ref={enrollmentClassInputRef}
                              type="text"
                              value={enrollmentClassSearch}
                              onFocus={() => {
                                setEnrollmentClassDropdownRect(getDropdownFixedRect(enrollmentClassInputRef.current));
                                setIsEnrollmentClassDropdownOpen(true);
                              }}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setIsEnrollmentClassDropdownOpen(false);
                                }, 120);
                              }}
                              onChange={e => {
                                setEnrollmentClassSearch(e.target.value);
                                setEnrollmentClassDropdownRect(getDropdownFixedRect(enrollmentClassInputRef.current));
                                setIsEnrollmentClassDropdownOpen(true);
                              }}
                              placeholder="Buscar turma por nome..."
                              className="bg-slate-950 border border-slate-800 pl-10 pr-3 py-3 rounded-lg text-white outline-none focus:border-white transition w-full"
                              autoComplete="off"
                            />
                          </div>
                          {/* Dropdown de opções (renderizado via portal com position fixed para não ser coberto pelo rodapé) */}
                          {isEnrollmentClassDropdownOpen && enrollmentClassDropdownRect && createPortal(
                            <div
                              ref={enrollmentClassListRef}
                              className="fixed z-[9999] max-h-64 overflow-auto rounded-lg border border-slate-800 bg-slate-950 shadow-2xl"
                              style={{
                                top: enrollmentClassDropdownRect.top,
                                left: enrollmentClassDropdownRect.left,
                                width: enrollmentClassDropdownRect.width,
                              }}
                            >
                              {(() => {
                                const term = enrollmentClassSearch.trim().toLowerCase();
                                const filtered = term
                                  ? classOptions.filter(c => String(c.name || '').toLowerCase().includes(term))
                                  : classOptions;
                                if (filtered.length === 0) {
                                  return <div className="px-3 py-3 text-sm text-slate-500">Nenhuma turma encontrada.</div>;
                                }
                                return filtered.map(cls => {
                                  const isSelected = form.selectedClassIds.includes(cls.id);
                                  const courseCount = Array.isArray(cls.courses) ? cls.courses.length : 0;
                                  const validity = cls.durationDays;
                                  return (
                                    <button
                                      key={cls.id}
                                      type="button"
                                      onMouseDown={(event) => event.preventDefault()}
                                      onClick={() => handleToggleClass(cls.id)}
                                      className={`w-full border-b border-slate-800 px-3 py-2 text-left transition last:border-b-0 ${
                                        isSelected ? 'bg-orange-500/10 text-orange-300' : 'text-slate-200 hover:bg-slate-900'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition shrink-0 ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-slate-600'}`}>
                                            {isSelected && <Check size={12} className="text-white" />}
                                          </div>
                                          <div className="min-w-0">
                                            <div className="font-medium truncate">{cls.name}</div>
                                            <div className="text-xs text-slate-500 truncate">
                                              {courseCount} curso(s){validity ? ` • ${validity} dia(s)` : ' • sem validade'}
                                            </div>
                                          </div>
                                        </div>
                                        {isSelected && <Check size={14} className="shrink-0 text-orange-300" />}
                                      </div>
                                    </button>
                                  );
                                });
                              })()}
                            </div>,
                            document.body
                          )}
                        </div>
                        {form.selectedClassIds.length > 0 && (
                            <p className="text-xs text-orange-400 font-bold">{form.selectedClassIds.length} turma(s) selecionada(s)</p>
                        )}
                        </div>
                    )}

                    {/* Campos Comuns - Vincular Turma (oculto na aba Empresa) */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Em modo de edição, ocultamos a opção de turma para simplificar, já que o foco é curso/validade. No modo Turma, a turma e a validade são derivadas da seleção. Na aba Empresa, a vinculação de turma não se aplica. */}
                        {!editingEnrollment && enrollmentType !== 'class' && enrollmentType !== 'company' && (
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Vincular Turma?</label>
                                <div className="flex gap-4 items-center h-[46px]">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                    type="radio" 
                                    name="linkClass" 
                                    checked={form.linkClass === true} 
                                    onChange={() => setForm({...form, linkClass: true})}
                                    className="accent-orange-500 w-4 h-4"
                                    />
                                    <span className="text-white">Sim</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                    type="radio" 
                                    name="linkClass" 
                                    checked={form.linkClass === false} 
                                    onChange={() => setForm({...form, linkClass: false, classId: ''})}
                                    className="accent-orange-500 w-4 h-4"
                                    />
                                    <span className="text-white">Não</span>
                                </label>
                                </div>
                            </div>
                        )}

                       {editingEnrollment && (
                         <div className="flex flex-col gap-2 col-span-2">
                           <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Período da Matrícula</label>
                           <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                             <div className="flex flex-col gap-1">
                               <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Data Início</label>
                               <input
                                 type="date"
                                 value={form.startDate}
                                 onChange={(e) => handleStartDateChange(e.target.value)}
                                 className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition [color-scheme:dark]"
                               />
                             </div>
                             <div className="flex flex-col gap-1">
                               <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Validade (dias) {form.status !== 'Cancelada' && <span className="text-red-500">*</span>}</label>
                               <input
                                 type="number"
                                 min="1"
                                 required={form.status !== 'Cancelada'}
                                 placeholder="Ex: 30"
                                 value={form.validityDays}
                                 onChange={(e) => handleValidityDaysChange(e.target.value)}
                                 className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                               />
                             </div>
                             <div className="flex flex-col gap-1">
                               <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Data Final</label>
                               <input
                                 type="date"
                                 value={form.endDate}
                                 onChange={(e) => handleEndDateChange(e.target.value)}
                                 className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition [color-scheme:dark]"
                               />
                             </div>
                           </div>
                           {form.startDate && form.endDate && (
                             <p className="text-xs text-orange-400 font-bold mt-1">
                               Expira em: {formatEnrollmentExpiresAt(buildIsoFromYmdEndOfDay(form.endDate))}
                             </p>
                           )}
                           {editingEnrollment?.status === 'Programado' && form.startDate && !isSaoPauloToday(form.startDate) && (
                             <p className="text-xs text-amber-300/90 mt-1">
                               Status Programado será preservado pois a data de início não é hoje.
                             </p>
                           )}
                           {editingEnrollment?.status === 'Programado' && form.startDate && isSaoPauloToday(form.startDate) && (
                             <p className="text-xs text-teal-300/90 mt-1">
                               Data de início alterada para hoje: matrícula passará a ter acesso imediato.
                             </p>
                           )}
                         </div>
                       )}

                       {!editingEnrollment && enrollmentType !== 'class' && (
                         <div className="flex flex-col gap-3 col-span-2">
                           <div className="grid grid-cols-2 gap-3">
                             {/* VIGÊNCIA (substitui Validade; usada para calcular a CONCLUSÃO) */}
                             <div className="flex flex-col gap-1">
                               <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Vigência (dias) {form.status !== 'Cancelada' && <span className="text-red-500">*</span>}</label>
                               <input
                                   type="number"
                                   min="1"
                                   required={form.status !== 'Cancelada'}
                                   placeholder="Ex: 365"
                                   value={form.validityDays}
                                   onChange={e => setForm({...form, validityDays: e.target.value})}
                                   className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                               />
                             </div>
                             {/* INÍCIO (+ Hora quando a data for futura) */}
                             <div className="flex flex-col gap-1">
                               <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Início</label>
                               <div className="flex gap-2">
                                 <input
                                     type="date"
                                     value={form.startDate}
                                     onChange={e => setForm({...form, startDate: e.target.value})}
                                     className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition flex-1 [color-scheme:dark]"
                                 />
                                 {form.startDate && form.startDate > getSaoPauloDatePart(new Date()) && (
                                   <input
                                       type="time"
                                       value={form.scheduledTime}
                                       onChange={e => setForm({...form, scheduledTime: e.target.value})}
                                       className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition w-32 [color-scheme:dark]"
                                   />
                                 )}
                               </div>
                             </div>
                             {/* CONCLUSÃO (calculada: Início + Vigência) */}
                             <div className="flex flex-col gap-1 col-span-2">
                               <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Conclusão</label>
                               <input
                                   type="date"
                                   value={(form.startDate && form.validityDays) ? addDaysToYmd(form.startDate, form.validityDays) : ''}
                                   readOnly
                                   placeholder="Calculado automaticamente a partir do Início + Vigência"
                                   className="bg-slate-900 border border-slate-800 p-3 rounded-lg text-slate-400 outline-none cursor-not-allowed"
                               />
                             </div>
                           </div>
                         </div>
                       )}
                   </div>

                   {editingEnrollment && (
                       <div className="flex flex-col gap-2">
                           <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status da Matrícula</label>
                           <select
                               value={form.status}
                               onChange={e => setForm({ ...form, status: e.target.value })}
                               className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                           >
                               <option value="Ativa">Ativa</option>
                               <option value="Inativada">Inativada</option>
                               <option value="Cancelada">Cancelada</option>
                           </select>
                           <p className="text-xs text-slate-400 leading-relaxed">
                               Cancelada remove o curso da area do aluno. Inativada mantem o curso visivel no login, mas bloqueia a continuidade. Expirada e aplicada automaticamente quando a vigencia termina.
                           </p>
                       </div>
                   )}

                   {/* Vincular Turma em Edição */}
                   {editingEnrollment && (
                       <div className="flex flex-col gap-2 mt-4 border-t border-slate-800 pt-4">
                           <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Alterar Turma</label>
                           <div className="flex items-center gap-4">
                               <label className="flex items-center gap-2 cursor-pointer">
                                   <input 
                                       type="checkbox" 
                                       checked={form.linkClass} 
                                       onChange={(e) => setForm({...form, linkClass: e.target.checked})}
                                       className="accent-orange-500 w-4 h-4"
                                   />
                                   <span className="text-white text-sm">Vincular a uma turma</span>
                               </label>
                           </div>
                           
                           {form.linkClass && (
                               <select 
                                   value={form.classId} 
                                   onChange={e => setForm({...form, classId: e.target.value})} 
                                   className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition mt-2"
                               >
                                   <option value="">Selecione uma turma...</option>
                                   {classes.map(c => (
                                       <option key={c.id} value={c.id}>{c.name}</option>
                                   ))}
                               </select>
                           )}
                       </div>
                   )}
                     {!editingEnrollment && form.validityDays && parseInt(form.validityDays) > 0 && (
                        <p className="text-xs text-orange-400 font-bold -mt-2">
                           Expira em: {formatEnrollmentExpiresAt(buildEnrollmentExpirationAtIso(form.validityDays, form.startDate ? new Date(`${form.startDate}T${form.scheduledTime || '00:00'}:00`) : new Date()))}
                        </p>
                    )}
                    {!editingEnrollment && !form.validityDays && form.status !== 'Cancelada' && enrollmentType !== 'class' && <p className="text-xs text-slate-600 -mt-2">Informe a duração da matrícula em dias.</p>}


                    {!editingEnrollment && form.linkClass && (
                        <div className="flex flex-col gap-2 animate-fade-in">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Selecione a Turma</label>
                        <select 
                            required 
                            value={form.classId} 
                            onChange={e => setForm({...form, classId: e.target.value})} 
                            className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                        >
                            <option value="">Selecione...</option>
                            {classes.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        </div>
                    )}
                 </div>

                 {/* Coluna Direita - Seleção de Cursos / Detalhes da Turma */}
                 <div className="flex flex-col gap-2">
                    {enrollmentType === 'class' ? (
                      <>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Detalhes da(s) Turma(s)</label>
                        <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-y-auto custom-scrollbar max-h-[520px]">
                          {(() => {
                            const selectedClasses = classOptions.filter(c => form.selectedClassIds.includes(c.id));
                            if (selectedClasses.length === 0) {
                              return <p className="text-sm text-slate-500">Selecione uma ou mais turmas para visualizar todos os dados vinculados.</p>;
                            }

                            const resolveCompanyNames = (ids) => {
                              if (!Array.isArray(ids) || ids.length === 0) return [];
                              return ids
                                .map(id => companies.find(cp => String(cp.id) === String(id))?.name)
                                .filter(Boolean);
                            };
                            const resolvePartnerNames = (ids) => {
                              if (!Array.isArray(ids) || ids.length === 0) return [];
                              return ids
                                .map(id => partners.find(p => String(p.id) === String(id))?.name)
                                .filter(Boolean);
                            };

                            return (
                              <div className="flex flex-col gap-5">
                                {selectedClasses.map(cls => {
                                  const classCourseIds = Array.isArray(cls.courses) ? cls.courses : [];
                                  const classCourses = courseOptions.filter(c => classCourseIds.includes(c.id));
                                  const validity = cls.durationDays;
                                  const startDate = cls.startDate || '';
                                  const endDateYmd = (cls.settings?.startDate && cls.settings?.durationDays)
                                    ? addDaysToYmd(cls.settings.startDate, cls.settings.durationDays)
                                    : (cls.settings?.endDate || '');
                                  const endDate = ymdToBrDate ? ymdToBrDate(endDateYmd) : endDateYmd;
                                  const companyNames = resolveCompanyNames(cls.companyIds);
                                  const partnerNames = resolvePartnerNames(cls.partnerIds);
                                  const areas = Array.isArray(cls.area) ? cls.area : [];
                                  const cargos = Array.isArray(cls.cargo) ? cls.cargo : [];
                                  const studentCount = Array.isArray(cls.students) ? cls.students.length : 0;

                                  const renderEmpty = (label) => (
                                    <span className="text-xs text-slate-600 italic">{label}</span>
                                  );

                                  return (
                                    <div key={cls.id} className="flex flex-col gap-3 border border-slate-800 rounded-lg p-3 bg-slate-900/40">
                                      {/* Cabeçalho: nome + status */}
                                      <div className="flex items-start justify-between gap-2 pb-2 border-b border-slate-800">
                                        <div className="flex flex-col min-w-0">
                                          <span className="text-base font-bold text-orange-300 truncate">{cls.name}</span>
                                          <span className="text-[11px] text-slate-500">ID: {String(cls.id).slice(0, 8)}...</span>
                                        </div>
                                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded shrink-0 ${
                                          cls.status === 'Ativo' || cls.status === 'Em andamento'
                                            ? 'bg-teal-500/15 text-teal-400 border border-teal-500/30'
                                            : cls.status === 'Encerrada'
                                            ? 'bg-slate-700/40 text-slate-400 border border-slate-600'
                                            : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                        }`}>{cls.status || 'Ativo'}</span>
                                      </div>

                                      {/* Grid de dados gerais */}
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                        {/* Vigência */}
                                        <div className="flex flex-col gap-1">
                                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vigência</span>
                                          <span className="text-sm text-slate-200">
                                            {startDate || endDate ? (
                                              <>
                                                <span className="text-slate-300">{startDate || '—'}</span>
                                                <span className="text-slate-600 mx-1">→</span>
                                                <span className="text-slate-300">{endDate || '—'}</span>
                                              </>
                                            ) : renderEmpty('Não definida')}
                                          </span>
                                        </div>

                                        {/* Duração */}
                                        <div className="flex flex-col gap-1">
                                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Duração</span>
                                          <span className="text-sm text-slate-200">
                                            {validity ? `${validity} dia(s)` : renderEmpty('Não definida')}
                                          </span>
                                        </div>

                                        {/* Vagas */}
                                        <div className="flex flex-col gap-1">
                                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vagas</span>
                                          <span className="text-sm text-slate-200">
                                            {cls.vacancies ? String(cls.vacancies) : renderEmpty('Ilimitadas')}
                                          </span>
                                        </div>

                                        {/* Alunos */}
                                        <div className="flex flex-col gap-1">
                                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Alunos Vinculados</span>
                                          <span className="text-sm text-slate-200">{studentCount} aluno(s)</span>
                                        </div>
                                      </div>

                                      {/* Cursos Vinculados */}
                                      <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cursos Vinculados ({classCourses.length})</span>
                                        {classCourses.length === 0 ? (
                                          renderEmpty('Nenhum curso vinculado')
                                        ) : (
                                          <div className="flex flex-col gap-1 mt-1">
                                            {classCourses.map(course => (
                                              <div key={course.id} className="flex items-center gap-2 p-2 rounded bg-slate-950/60 border border-slate-800/60">
                                                <Check size={14} className="text-teal-400 shrink-0" />
                                                <div className="flex flex-col min-w-0">
                                                  <span className="text-sm text-slate-200 truncate">{course.title}</span>
                                                  <span className="text-[11px] text-slate-500">{formatCourseWorkloadHHMM(course.workload_hours || course.workload) || '00:00'} • {course.category || 'Geral'}</span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Empresas Vinculadas */}
                                      <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Empresas Vinculadas ({companyNames.length})</span>
                                        {companyNames.length === 0 ? (
                                          renderEmpty('Nenhuma empresa vinculada')
                                        ) : (
                                          <div className="flex flex-wrap gap-1.5 mt-1">
                                            {companyNames.map((name, i) => (
                                              <span key={i} className="text-[11px] bg-slate-800/70 text-slate-300 px-2 py-1 rounded border border-slate-700">{name}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Parceiros SAS */}
                                      <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Parceiro(s) SAS ({partnerNames.length})</span>
                                        {partnerNames.length === 0 ? (
                                          renderEmpty('Sem parceiro vinculado')
                                        ) : (
                                          <div className="flex flex-wrap gap-1.5 mt-1">
                                            {partnerNames.map((name, i) => (
                                              <span key={i} className="text-[11px] bg-orange-500/10 text-orange-300 px-2 py-1 rounded border border-orange-500/20">{name}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Áreas */}
                                      <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Áreas ({areas.length})</span>
                                        {areas.length === 0 ? (
                                          renderEmpty('Todas as áreas')
                                        ) : (
                                          <div className="flex flex-wrap gap-1.5 mt-1">
                                            {areas.map((a, i) => (
                                              <span key={i} className="text-[11px] bg-blue-500/10 text-blue-300 px-2 py-1 rounded border border-blue-500/20">{a}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      {/* Cargos */}
                                      <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cargos ({cargos.length})</span>
                                        {cargos.length === 0 ? (
                                          renderEmpty('Todos os cargos')
                                        ) : (
                                          <div className="flex flex-wrap gap-1.5 mt-1">
                                            {cargos.map((c, i) => (
                                              <span key={i} className="text-[11px] bg-purple-500/10 text-purple-300 px-2 py-1 rounded border border-purple-500/20">{c}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                        {form.selectedClassIds.length > 0 && (
                          <p className="text-xs text-orange-400 font-bold">
                            {(() => {
                              const selectedClasses = classOptions.filter(c => form.selectedClassIds.includes(c.id));
                              const totalCourses = selectedClasses.reduce((acc, c) => acc + (Array.isArray(c.courses) ? c.courses.length : 0), 0);
                              const studentCount = enrollmentType === 'class'
                                ? (form.selectedStudentIds || []).length
                                : (form.studentId ? 1 : 0);
                              const totalEnrollments = totalCourses * Math.max(studentCount, 0);
                              if (enrollmentType === 'class') {
                                return `${totalEnrollments} matrícula(s) serão criadas para ${studentCount} aluno(s) em ${selectedClasses.length} turma(s)`;
                              }
                              return `${totalCourses} matrícula(s) serão criadas em ${selectedClasses.length} turma(s)`;
                            })()}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Seleção de Cursos</label>
                        <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-y-auto custom-scrollbar max-h-[540px]">
                        <div className="grid grid-cols-1 gap-2">
                            {courseOptions.map(course => (
                            <label key={course.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition border ${form.selectedCourses.includes(course.id) ? 'bg-orange-500/10 border-orange-500/30' : 'border-transparent hover:bg-slate-900'}`}>
                                <div className={`w-5 h-5 rounded border flex items-center justify-center transition ${form.selectedCourses.includes(course.id) ? 'bg-orange-500 border-orange-500' : 'border-slate-600'}`}>
                                {form.selectedCourses.includes(course.id) && <Check size={14} className="text-white" />}
                                </div>
                                <input 
                                type="checkbox" 
                                className="hidden" 
                                checked={form.selectedCourses.includes(course.id)} 
                                onChange={() => handleToggleCourse(course.id)} 
                                />
                                <div className="flex flex-col">
                                <span className={`text-sm font-medium ${form.selectedCourses.includes(course.id) ? 'text-white' : 'text-slate-300'}`}>{course.title}</span>
                                <span className="text-xs text-slate-500">{formatCourseWorkloadHHMM(course.workload_hours || course.workload) || '00:00'} • {course.category || 'Geral'}</span>
                                </div>
                            </label>
                            ))}
                        </div>
                        </div>
                        {form.selectedCourses.length > 0 && (
                        <p className="text-xs text-orange-400 font-bold mt-1">{form.selectedCourses.length} curso(s) selecionado(s)</p>
                        )}
                      </>
                    )}
                 </div>
              </div>

            </form>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800 shrink-0">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
              <button type="submit" form="enrollmentForm" disabled={loading} className={`bg-orange-500 hover:bg-orange-600 px-8 py-2 rounded-lg text-white font-bold shadow-lg transition ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {loading ? 'Processando...' : getEnrollmentEditSubmitLabel(Boolean(editingEnrollment))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Requisitos da Turma (override) */}
      {showClassRequirementConfirm && classRequirementWarnings && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl flex flex-col max-h-[85vh]">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-800 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <AlertTriangle size={20} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Requisitos da Turma não atendidos</h3>
                  <p className="text-xs text-slate-500">
                    {(classRequirementWarnings.students?.length || 0) > 1
                      ? 'Alguns alunos marcados não cumprem todos os requisitos'
                      : 'O aluno selecionado não cumpre todos os requisitos'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setShowClassRequirementConfirm(false); setClassRequirementWarnings(null); }}
                className="text-slate-500 hover:text-white transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 custom-scrollbar">
              <div className="mb-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <p className="text-sm text-amber-200">
                  <span className="font-bold">
                    {(classRequirementWarnings.students?.length || 0) > 1 ? 'Alunos marcados:' : 'Aluno:'}
                  </span>{' '}
                  {Array.isArray(classRequirementWarnings.students) && classRequirementWarnings.students.length > 0
                    ? classRequirementWarnings.students
                        .map((s) => getEnrollmentStudentLabel(s) || s?.email || '—')
                        .join(', ')
                    : classRequirementWarnings.student?.name || classRequirementWarnings.student?.email || '—'}
                </p>
              </div>

              <div className="flex flex-col gap-4">
                {classRequirementWarnings.classes.map((cls) => (
                  <div key={cls.id} className="border border-slate-800 rounded-lg p-4 bg-slate-950/50">
                    <div className="flex items-center gap-2 mb-3">
                      <GraduationCap size={16} className="text-orange-400" />
                      <span className="font-bold text-white">{cls.name}</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {cls.violations.map((v, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <X size={14} className="text-red-400 shrink-0 mt-0.5" />
                          <div className="flex flex-col">
                            <span className="text-slate-300">
                              {v.studentName ? `${v.studentName} — ` : ''}{v.message}
                            </span>
                            <span className="text-xs text-slate-500">
                              <span className="text-slate-400">{v.field}:</span> esperado {Array.isArray(v.expected) ? v.expected.join(', ') : v.expected || '—'}
                              {' • '}
                              <span className="text-slate-400">atual:</span> {Array.isArray(v.actual) ? (v.actual.length ? v.actual.join(', ') : 'não definido') : (v.actual || 'não definido')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-5 text-xs text-slate-500">
                Você pode prosseguir com a matrícula mesmo assim. Esta ação sobrescreve os requisitos de acesso da turma.
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800 shrink-0">
              <button
                type="button"
                onClick={() => { setShowClassRequirementConfirm(false); setClassRequirementWarnings(null); }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => executeClassEnrollment()}
                disabled={loading}
                className={`bg-amber-500 hover:bg-amber-600 px-6 py-2 rounded-lg text-slate-950 font-bold shadow-lg transition ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Processando...' : 'Confirmar matrícula mesmo assim'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Menu de ações (três pontos na vertical) por aluno na tela de detalhes da turma.
const StudentActionsMenu = ({ student, onStandardEmail, onCustomEmail, onViewDetails, onRemoveFromClass }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const close = () => setOpen(false);

  const items = [
    { key: 'details', label: 'Ver Detalhes', title: 'Detalhes', icon: Eye, iconClass: 'text-teal-400' },
    { key: 'standard', label: 'Enviar e-mail padrão', icon: Mail, iconClass: 'text-blue-400' },
    { key: 'custom', label: 'Enviar e-mail personalizado', icon: Send, iconClass: 'text-teal-400' },
    // Só aparece em turma manual (vínculo por matrícula explícita).
    ...(onRemoveFromClass
      ? [{ key: 'remove', label: 'Remover da turma', title: 'Remover da turma', icon: Trash2, iconClass: 'text-rose-400' }]
      : []),
  ];

  const menuStyle = () => {
    const el = triggerRef.current;
    if (!el) return {};
    const rect = el.getBoundingClientRect();
    const menuWidth = 240;
    const menuHeight = 44 + items.length * 44;
    const margin = 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const style = {};
    let left = rect.right - menuWidth;
    left = Math.min(Math.max(8, left), Math.max(8, vw - menuWidth - 8));
    style.left = `${left}px`;
    if (rect.bottom + menuHeight + margin > vh && rect.top - menuHeight - margin > 0) {
      style.bottom = `${vh - rect.top + margin}px`;
    } else {
      style.top = `${rect.bottom + margin}px`;
    }
    return style;
  };

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        title="Ações"
        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80 border border-transparent hover:border-slate-700 transition"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 cursor-default"
            onClick={(e) => { e.stopPropagation(); close(); }}
            onMouseDown={(e) => { e.preventDefault(); close(); }}
            aria-hidden="true"
          />
          <div
            className="fixed min-w-[240px] overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl shadow-black/60 z-[9999] ring-1 ring-black/60 py-1.5 animate-fade-in"
            style={menuStyle()}
          >
            <div className="px-3 py-2 border-b border-slate-800/70 mb-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Detalhes</div>
              <div className="mt-0.5 text-xs font-semibold text-white truncate">{student?.name || 'Aluno'}</div>
            </div>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  title={item.title || item.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                    if (item.key === 'details') onViewDetails?.(student);
                    else if (item.key === 'standard') onStandardEmail?.(student);
                    else if (item.key === 'custom') onCustomEmail?.(student);
                    else if (item.key === 'remove') onRemoveFromClass?.(student);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-200 hover:bg-slate-800/70 hover:text-white transition"
                >
                  <Icon size={16} className={item.iconClass} />
                  <span className="text-sm font-semibold leading-tight">{item.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Menu de ações (três pontos na vertical) por turma na tela de Turmas.
const ClassActionsMenu = ({ turma, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const close = () => setOpen(false);

  const items = [
    { key: 'edit', label: 'Editar', icon: Edit2, iconClass: 'text-blue-400', onClick: () => onEdit?.(turma) },
    { key: 'delete', label: 'Excluir', icon: Trash2, iconClass: 'text-rose-400', destructive: true, onClick: () => onDelete?.(turma.id) },
  ];

  const menuStyle = () => {
    const el = triggerRef.current;
    if (!el) return {};
    const rect = el.getBoundingClientRect();
    const menuWidth = 240;
    const menuHeight = 44 + items.length * 44;
    const margin = 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const style = {};
    let left = rect.right - menuWidth;
    left = Math.min(Math.max(8, left), Math.max(8, vw - menuWidth - 8));
    style.left = `${left}px`;
    if (rect.bottom + menuHeight + margin > vh && rect.top - menuHeight - margin > 0) {
      style.bottom = `${vh - rect.top + margin}px`;
    } else {
      style.top = `${rect.bottom + margin}px`;
    }
    return style;
  };

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        title="Ações"
        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80 border border-transparent hover:border-slate-700 transition"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 cursor-default"
            onClick={(e) => { e.stopPropagation(); close(); }}
            onMouseDown={(e) => { e.preventDefault(); close(); }}
            aria-hidden="true"
          />
          <div
            className="fixed min-w-[240px] overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl shadow-black/60 z-[9999] ring-1 ring-black/60 py-1.5 animate-fade-in"
            style={menuStyle()}
          >
            <div className="px-3 py-2 border-b border-slate-800/70 mb-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Ações da Turma</div>
              <div className="mt-0.5 text-xs font-semibold text-white truncate">{turma?.name || 'Turma'}</div>
            </div>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); close(); item.onClick?.(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition ${item.destructive ? 'text-slate-200 hover:bg-rose-500/10 hover:text-rose-200' : 'text-slate-200 hover:bg-slate-800/70 hover:text-white'}`}
                >
                  <Icon size={16} className={item.iconClass} />
                  <span className="text-sm font-semibold leading-tight">{item.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const escapeHtmlText = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Tokens de variáveis de sistema reconhecidos na pré-visualização das comunicações
// (E-mail/WhatsApp). Os valores são apenas exemplos, usados para mostrar a mensagem
// final preenchida antes do disparo real.
const COMMUNICATION_PREVIEW_TOKENS = [
  { tokens: ['{nomeUsuario}', '{primeiro_nome}', '{{nome_usuario}}', '{{aluno}}'], sample: 'João' },
  { tokens: ['{{nome_completo}}', '{{nome_usuario_completo}}'], sample: 'João Silva' },
  { tokens: ['{email}'], sample: 'joao.silva@empresa.com.br' },
  { tokens: ['{cpf}'], sample: '123.456.789-00' },
  { tokens: ['{nomeEmpresa}', '{{empresa}}'], sample: 'Empresa Parceira' },
  { tokens: ['{parceiro}', '{partnerName}'], sample: 'Parceiro SAS' },
  { tokens: ['{linkConvite}', '{{link_convite}}'], sample: 'https://acesso.a2care.com.br/invite?code=AB12CD' },
  { tokens: ['{{link_reset}}', '{linkRedefinicaoSenha}'], sample: 'https://acesso.a2care.com.br/reset-password?token=exemplo' },
  { tokens: ['{linkPlataforma}'], sample: 'https://acesso.a2care.com.br' },
  { tokens: ['{linkCertificado}', '{{url_certificado}}'], sample: 'https://acesso.a2care.com.br/certificado/123' },
  { tokens: ['{{curso}}', '{{CURSO}}'], sample: 'Curso de Segurança do Trabalho' },
  { tokens: ['{{progresso}}'], sample: '75' },
  { tokens: ['{{prazo_conclusao}}'], sample: '31/12/2026' },
  { tokens: ['{{url_curso}}'], sample: 'https://acesso.a2care.com.br/courses/123' },
  { tokens: ['{{url_acesso_curso}}'], sample: 'https://acesso.a2care.com.br/courses/123?turma=456' },
  { tokens: ['{{numero_matricula}}'], sample: '4821' },
  { tokens: ['{{codigo_verificacao}}'], sample: 'A1B2C3D4' },
  { tokens: ['{{carga_horaria}}'], sample: '08:00' },
  { tokens: ['{{data_conclusao}}'], sample: '15/01/2026' },
  { tokens: ['{{ano}}'], sample: String(new Date().getFullYear()) },
  { tokens: ['{{email_suporte}}'], sample: 'suporte@sofera.com.br' },
  { tokens: ['{{whatsapp_suporte}}'], sample: '11 5141-0749' },
  { tokens: ['{{whatsapp_suporte_link}}'], sample: 'https://wa.me/551151410749' },
  { tokens: ['{{horario_atendimento}}'], sample: 'Seg a Sex, das 08:00 às 18:00' },
  { tokens: ['{{dias_atendimento}}'], sample: 'Seg a Sex' },
  { tokens: ['{{hr_inicio}}'], sample: '08:00' },
  { tokens: ['{{hr_fim}}'], sample: '18:00' },
];

const resolveCommunicationPreview = (value) => {
  let out = String(value ?? '');
  COMMUNICATION_PREVIEW_TOKENS.forEach(({ tokens, sample }) => {
    tokens.forEach((tok) => { out = out.split(tok).join(sample); });
  });
  return out;
};

// Modal "Enviar e-mail padrão": lista os templates de e-mail prontos do banco
// e envia o escolhido exatamente como está (mesmo assunto e conteúdo).
const StandardEmailModal = ({ student, templates, onClose, onSend, sending }) => {
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');

  const available = (templates || []).filter(
    (t) => t.channel === 'E-mail' && t.is_active !== false && !(t.meta && t.meta.base)
  );
  const term = search.trim().toLowerCase();
  const filtered = term
    ? available.filter((t) =>
        (t.name || '').toLowerCase().includes(term) || (t.subject || '').toLowerCase().includes(term)
      )
    : available;
  const selected = available.find((t) => t.id === selectedId);

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-xl shadow-2xl flex flex-col max-h-[85vh]">
        <div className="flex justify-between items-start gap-4 p-6 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
              <Mail size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Enviar e-mail padrão</h3>
              <p className="text-sm text-slate-400">
                Escolha um modelo para enviar a <span className="text-slate-200 font-semibold">{student?.name || 'aluno'}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition"><X size={20} /></button>
        </div>

        <div className="p-4 border-b border-slate-800">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Buscar modelo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:border-slate-600 outline-none transition"
            />
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex flex-col gap-2">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">
              Nenhum modelo de e-mail ativo encontrado.
            </div>
          )}
          {filtered.map((t) => {
            const isSelected = t.id === selectedId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left p-3 rounded-lg border transition ${
                  isSelected
                    ? 'bg-teal-500/10 border-teal-500'
                    : 'bg-slate-950 border-slate-800 hover:border-slate-600'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{t.name}</p>
                    <p className="text-xs text-slate-400 truncate">Assunto: {t.subject || '—'}</p>
                  </div>
                  {isSelected && <Check size={18} className="text-teal-400 shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-between items-center gap-3 p-6 border-t border-slate-800">
          <span className="text-xs text-slate-500 truncate">
            {selected ? `Assunto: ${selected.subject || '—'}` : 'Selecione um modelo'}
          </span>
          <div className="flex gap-3 shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
            <button
              disabled={!selectedId || sending}
              onClick={() => onSend(selected)}
              className={`px-6 py-2 rounded-lg font-bold shadow-lg transition inline-flex items-center gap-2 ${
                !selectedId || sending
                  ? 'opacity-50 cursor-not-allowed bg-slate-700 text-white'
                  : 'bg-teal-500 text-slate-950 hover:bg-teal-400'
              }`}
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal "Enviar e-mail personalizado": usa um template HTML existente como casca
// ({body}) e envia o texto digitado pelo admin dentro dele.
const CustomEmailModal = ({ student, baseTemplate, onClose, onSend, sending }) => {
  const [subject, setSubject] = useState(baseTemplate?.subject || 'Mensagem');
  const [body, setBody] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const previewHtml = (() => {
    const shell = baseTemplate?.content || '';
    if (!shell) return '';
    const safeBody = escapeHtmlText(body).replace(/\n/g, '<br>');
    return shell
      .replace(/\{\{body\}\}/g, safeBody)
      .replace(/\{body\}/g, safeBody)
      .replace(/\{\{nome_usuario\}\}/g, (student?.name || '').split(' ')[0])
      .replace(/\{\{nome_completo\}\}/g, student?.name || '')
      .replace(/\{\{ano\}\}/g, String(new Date().getFullYear()))
      .replace(/\{\{email_suporte\}\}/g, 'suporte@sofera.com.br');
  })();

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-start gap-4 p-6 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
              <Send size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Enviar e-mail personalizado</h3>
              <p className="text-sm text-slate-400">
                Mensagem para <span className="text-slate-200 font-semibold">{student?.name || 'aluno'}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition"><X size={20} /></button>
        </div>

        <div className="p-6 flex flex-col gap-5 overflow-y-auto">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Assunto</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto do e-mail"
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-sm text-white focus:border-slate-600 outline-none transition"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block">Mensagem</label>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white transition"
              >
                <Eye size={14} />
                {showPreview ? 'Editar' : 'Pré-visualizar'}
              </button>
            </div>

            {showPreview ? (
              <div className="rounded-lg border border-slate-800 bg-white overflow-hidden">
                <iframe
                  title="Pré-visualização do e-mail"
                  className="w-full h-[360px] bg-white"
                  srcDoc={previewHtml}
                />
              </div>
            ) : (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={9}
                placeholder="Digite aqui o texto que será enviado ao aluno..."
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-white focus:border-slate-600 outline-none transition resize-y leading-relaxed"
              />
            )}
            {!baseTemplate && (
              <p className="mt-2 text-xs text-rose-400 flex items-center gap-1.5">
                <AlertCircle size={14} /> Nenhum template base disponível para montar o e-mail.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-800">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
          <button
            disabled={!baseTemplate || !subject.trim() || !body.trim() || sending}
            onClick={() => onSend({ subject: subject.trim(), body })}
            className={`px-6 py-2 rounded-lg font-bold shadow-lg transition inline-flex items-center gap-2 ${
              !baseTemplate || !subject.trim() || !body.trim() || sending
                ? 'opacity-50 cursor-not-allowed bg-slate-700 text-white'
                : 'bg-teal-500 text-slate-950 hover:bg-teal-400'
            }`}
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminClassDetails = ({ classData, onBack, users, courses, templates, companies, sasPartners }) => {
  const auth = useAuth();
  const profileRole = auth?.profileRole;
  const partnerScoped = isPartnerScopedRole(profileRole);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  const [studentsData, setStudentsData] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);

  // Abas da tela de detalhes: visão geral (padrão) x convite link.
  const [detailsTab, setDetailsTab] = usePersistentTab('admin_class_details_tabs', 'geral');
  const activeDetailsTab = detailsTab === 'invite' && classData?.inviteMode === 'link' ? 'invite' : 'geral';
  
  // Estado para persistir configurações de comunicação
  const [savedCommunications, setSavedCommunications] = useState({
      whatsapp: { status: ['Todos'], templateId: '' },
      email: { status: ['Todos'], templateId: '' }
  });

  const fetchReportsData = React.useCallback(async () => {
    if (!classData?.id) return;
    setLoadingProgress(true);
    try {
      const [{ data, error }, statusMap] = await Promise.all([
        supabase.rpc('get_class_reports_data', { p_class_id: classData.id }),
        fetchScopedUserStatusMap({ profileRole, profile: auth.profile }).catch(() => new Map()),
      ]);
      if (error) throw error;
      const rows = Array.isArray(data?.students) ? data.students : [];
      // Mesma normalização aplicada na tela de Relatórios.
      const normalized = rows.map((student) => {
        const lifecycle = statusMap.get(String(student?.student_id || '')) || null;
        const lifecycleStatus = lifecycle?.status || '';
        const hasApprovedResult = student.result === 'Aprovado' && student.score !== '-' && student.score != null;
        const normalizedProgress = hasApprovedResult
          ? 100
          : Number(student.report_course_progress ?? (student.progress_avg || 0));
        const baseStatus = hasApprovedResult
          ? 'Concluído'
          : (student.status || 'Não iniciado');
        return {
          ...student,
          last_access: resolveEffectiveReportLastAccess(student, lifecycle),
          report_course_progress: normalizedProgress,
          progress_avg: Math.max(Number(student.progress_avg || 0), normalizedProgress),
          status: getReportDisplayStatus(baseStatus, lifecycleStatus),
          enrollment_status_display: getEnrollmentDisplayStatus(
            student.enrollment_status,
            lifecycleStatus,
            student.enrollment_deadline,
          ),
        };
      });
      setStudentsData(normalized);
    } catch (err) {
      console.error('Error fetching class reports data:', err);
      setStudentsData([]);
    } finally {
      setLoadingProgress(false);
    }
  }, [classData?.id, profileRole, auth.profile?.sasPartnerId]);

  useEffect(() => {
    fetchReportsData();
  }, [fetchReportsData]);

  const [communicationModal, setCommunicationModal] = useState({ isOpen: false, type: '' });
  const [sendingCommunication, setSendingCommunication] = useState(false);

  // Estado dos modais de e-mail por aluno (menu de ações)
  const [standardEmailStudent, setStandardEmailStudent] = useState(null);
  const [customEmailStudent, setCustomEmailStudent] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null); // { type: 'success' | 'error', message }
  const [communicationResult, setCommunicationResult] = useState(null); // { variant: 'success' | 'partial' | 'error', title, description }

  useEffect(() => {
    if (!emailFeedback) return;
    const timer = setTimeout(() => setEmailFeedback(null), 4500);
    return () => clearTimeout(timer);
  }, [emailFeedback]);

  // Matrícula manual: estado do modal de inclusão, confirmação e feedback da ação.
  const [enrollModal, setEnrollModal] = useState({ isOpen: false, term: '', studentId: '' });
  const [savingEnrollment, setSavingEnrollment] = useState(false);
  const [enrollmentFeedback, setEnrollmentFeedback] = useState(null); // { type: 'success' | 'error', message }
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    if (!enrollmentFeedback) return;
    const timer = setTimeout(() => setEnrollmentFeedback(null), 4500);
    return () => clearTimeout(timer);
  }, [enrollmentFeedback]);

  // Contato (e-mail/WhatsApp) por aluno, para enriquecer cada linha aluno+curso.
  const userById = React.useMemo(
    () => new Map((users || []).map((u) => [String(u.id), u])),
    [users],
  );

  // Uma linha por aluno+curso, igual à tela de Relatórios.
  const reportRows = studentsData.map((row) => {
    const user = userById.get(String(row.student_id)) || null;
    return {
      ...row,
      name: row.student,
      email: user?.email || '',
      whatsapp: user?.whatsapp || '',
    };
  });

  // Lista única de alunos da turma (sem duplicar por curso) para seleção individual em comunicações.
  const communicationStudents = (() => {
    const map = new Map();
    reportRows.forEach((row) => {
      const sid = String(row.student_id || row.id || '');
      if (!sid) return;
      const courseId = String(row.report_course_id || '');
      const existing = map.get(sid);
      if (existing) {
        if (courseId && !existing.courseIds.includes(courseId)) existing.courseIds.push(courseId);
        return;
      }
      map.set(sid, {
        id: sid,
        name: row.student || 'Aluno',
        email: row.email || '',
        whatsapp: row.whatsapp || '',
        status: row.status || 'Não iniciado',
        courseIds: courseId ? [courseId] : [],
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
  })();

  // Cursos da turma para o filtro de envio de comunicação (quando houver mais de um).
  const classCourseOptions = React.useMemo(() => {
    const ids = Array.isArray(classData?.courses) ? classData.courses.map(String) : [];
    return Array.from(new Set(ids)).map((id) => {
      const course = (courses || []).find((c) => String(c.id) === id);
      return { id, name: course?.name || 'Curso' };
    });
  }, [classData?.courses, courses]);

  // Filter rows based on search term and filters
  const filteredStudents = reportRows.filter((row) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term
      || (row.student || '').toLowerCase().includes(term)
      || (row.email || '').toLowerCase().includes(term);

    const matchesStatus = statusFilter ? row.status === statusFilter : true;
    const matchesResult = resultFilter ? row.result === resultFilter : true;

    return matchesSearch && matchesStatus && matchesResult;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when search term or filters changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, resultFilter]);

  // Turma manual: vínculo só por matrícula explícita.
  const isManualClass = classData?.enrollmentMode === 'manual';

  // Alunos que já estão na turma (não aparecem no autocomplete de inclusão).
  const enrolledStudentIds = React.useMemo(() => {
    const ids = new Set((classData?.students || []).map(String));
    studentsData.forEach((row) => { if (row.student_id) ids.add(String(row.student_id)); });
    return ids;
  }, [classData?.students, studentsData]);

  // Pessoas da Base Única (Core) ainda sem perfil local, para não limitar a
  // busca do autocomplete aos perfis já espelhados na SOFERA.
  const [coreEnrollEntities, setCoreEnrollEntities] = useState({
    people: [], companies: [], companyUsers: [], invitations: [], authLinks: [],
  });

  const fetchCoreEnrollEntities = React.useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('core_base_cache')
        .select('dataset')
        .eq('id', 'current')
        .maybeSingle();
      if (error || !data?.dataset) return;
      const ds = data.dataset;
      setCoreEnrollEntities({
        people: Array.isArray(ds.people) ? ds.people : [],
        companies: Array.isArray(ds.companies) ? ds.companies : [],
        companyUsers: Array.isArray(ds.company_users) ? ds.company_users : [],
        invitations: Array.isArray(ds.invitations) ? ds.invitations : [],
        authLinks: Array.isArray(ds.person_auth_links) ? ds.person_auth_links : [],
      });
    } catch {
      // Silencioso: mantém apenas os perfis locais como fallback.
    }
  }, []);

  useEffect(() => {
    if (isManualClass) fetchCoreEnrollEntities();
  }, [isManualClass, fetchCoreEnrollEntities]);

  const enrollableStudentOptions = React.useMemo(() => {
    const localOptions = (users || [])
      .filter((u) => u && u.status !== 'Inativo' && !enrolledStudentIds.has(String(u.id)))
      .map((u) => ({
        id: String(u.id),
        name: u.full_name || u.name || u.email || 'Aluno',
        fromCore: false,
      }));

    const coreOptions = buildCorePeopleCandidates({
      corePeople: coreEnrollEntities.people,
      coreCompanies: coreEnrollEntities.companies,
      coreCompanyUsers: coreEnrollEntities.companyUsers,
      coreInvitations: coreEnrollEntities.invitations,
      corePersonAuthLinks: coreEnrollEntities.authLinks,
      localUsers: users,
    })
      .filter((p) => p.status !== 'Inativo')
      .map((p) => ({
        id: `core-${p.central_person_id}`,
        name: p.full_name || p.email || 'Usuário Core',
        fromCore: true,
        centralPersonId: String(p.central_person_id),
      }));

    return [...localOptions, ...coreOptions]
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
  }, [users, enrolledStudentIds, coreEnrollEntities]);

  // O autocomplete entrega o nome selecionado; resolve o id correspondente.
  const handleEnrollSearchChange = (value) => {
    const exact = enrollableStudentOptions.find((option) => option.name === value);
    setEnrollModal((prev) => ({ ...prev, term: value, studentId: exact ? exact.id : '' }));
  };

  const handleConfirmManualEnroll = async () => {
    if (!classData?.id) return;
    const selected = enrollableStudentOptions.find((option) => String(option.id) === String(enrollModal.studentId));
    if (!selected) {
      setEnrollmentFeedback({ type: 'error', message: 'Selecione um aluno da lista para matricular.' });
      return;
    }
    setSavingEnrollment(true);
    try {
      // Aluno vindo da Base Única: espelha o perfil local antes de matricular.
      let studentId = selected.id;
      if (selected.fromCore && selected.centralPersonId) {
        const { data: mirroredId, error: mirrorError } = await supabase.rpc('core_mirror_person', {
          p_central_person_id: selected.centralPersonId,
        });
        if (mirrorError) throw new Error(`Falha ao espelhar aluno da Base Core: ${mirrorError.message}`);
        if (!mirroredId) throw new Error('Não foi possível criar o aluno a partir da Base Core.');
        studentId = mirroredId;
      }

      const { error } = await supabase.rpc('admin_add_student_to_class', {
        p_class_id: classData.id,
        p_student_id: studentId,
      });
      if (error) throw error;
      setEnrollModal({ isOpen: false, term: '', studentId: '' });
      await fetchReportsData();
      setEnrollmentFeedback({ type: 'success', message: `${selected.name} matriculado(a) na turma.` });
    } catch (err) {
      console.error('Error adding student to class:', err);
      setEnrollmentFeedback({ type: 'error', message: String(err?.message || err || 'Falha ao matricular o aluno.') });
    } finally {
      setSavingEnrollment(false);
    }
  };

  const handleManualRemove = (row) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remover da turma',
      message: `Remover ${row.student} desta turma? O histórico e o progresso do aluno no curso serão mantidos.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        try {
          const { error } = await supabase.rpc('admin_remove_student_from_class', {
            p_class_id: classData.id,
            p_student_id: row.student_id,
          });
          if (error) throw error;
          await fetchReportsData();
          setEnrollmentFeedback({ type: 'success', message: `${row.student} removido(a) da turma.` });
        } catch (err) {
          console.error('Error removing student from class:', err);
          setEnrollmentFeedback({ type: 'error', message: String(err?.message || err || 'Falha ao remover o aluno.') });
        }
      },
    });
  };

  // Carrega o SheetJS sob demanda (mesmo padrão usado nas demais exportações .xlsx).
  const ensureXLSX = () => new Promise((resolve, reject) => {
    if (window.XLSX) return resolve();
    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.0/package/dist/xlsx.full.min.js';
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

  // Exporta para Excel (.xlsx) exatamente as linhas filtradas na tela.
  const handleExportExcel = async () => {
    try {
      await ensureXLSX();
      const headers = ['Aluno', 'E-mail', 'WhatsApp', 'Empresa', 'Curso'];
      if (!partnerScoped) headers.push('Parceiro');
      headers.push('Progresso (%)', 'Status', 'Parecer', 'Nota', 'Acesso Curso');

      const rows = filteredStudents.map((row) => {
        const progress = Math.round(Number(row.report_course_progress ?? (row.progress_avg || 0)));
        const line = [
          row.student || '',
          row.email || '',
          row.whatsapp || '',
          formatCompanyLabel(row.company) || '',
          row.report_course_name || row.courses_enrolled || '',
        ];
        if (!partnerScoped) line.push(shortOrgLabel(row.partner) || '');
        line.push(
          `${progress}%`,
          row.status || 'Indefinido',
          row.result || '-',
          row.score ?? '-',
          row.last_access ? formatShortDateTime(row.last_access) : 'Nunca acessou',
        );
        return line;
      });

      const data = [headers, ...rows];
      const ws = window.XLSX.utils.aoa_to_sheet(data);
      ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
      ws['!autofilter'] = { ref: `A1:${window.XLSX.utils.encode_col(headers.length - 1)}${Math.max(1, data.length)}` };

      headers.forEach((_, idx) => {
        const cellRef = window.XLSX.utils.encode_cell({ r: 0, c: idx });
        if (!ws[cellRef]) return;
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          fill: { fgColor: { rgb: 'FF0F172A' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      });

      ws['!cols'] = headers.map((header, colIndex) => {
        const maxLen = data.reduce((max, line) => {
          const value = line[colIndex] == null ? '' : String(line[colIndex]);
          return Math.max(max, value.length);
        }, header.length);
        return { wch: Math.min(60, Math.max(12, maxLen + 2)) };
      });

      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'Alunos');
      const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const slug = String(classData?.name || 'turma').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'turma';
      const a = document.createElement('a');
      a.href = url;
      a.download = `alunos_turma_${slug}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar alunos da turma:', err);
      alert('Não foi possível exportar os alunos para Excel.');
    }
  };

  // Calculate class progress (average across all student+course rows)
  const classProgress = reportRows.length > 0
    ? Math.round(reportRows.reduce((acc, row) => acc + Number(row.report_course_progress || 0), 0) / reportRows.length)
    : 0;

  // Indicadores da turma: consideram todas as matrículas (aluno × curso) da turma.
  const classStudentIds = new Set(reportRows.map((row) => String(row.student_id || row.student || '')));
  const classStatusCount = (status) => reportRows.filter((row) => (row.status || 'Indefinido') === status).length;
  const classApproved = reportRows.filter((row) => row.result === 'Aprovado').length;
  const classReproved = reportRows.filter((row) => row.result === 'Reprovado').length;
  const classEvaluated = classApproved + classReproved;
  const classApprovalRate = classEvaluated > 0 ? Math.round((classApproved / classEvaluated) * 100) : 0;

  const classSummaryCards = [
    {
      id: 'students',
      label: 'Alunos Matriculados',
      value: classStudentIds.size,
      helper: `${reportRows.length} matrícula${reportRows.length === 1 ? '' : 's'} aluno × curso`,
      icon: Users,
      valueClass: 'text-white',
      iconClass: 'text-teal-400',
      borderClass: 'hover:border-teal-400/40 hover:shadow-[0_0_22px_rgba(45,212,191,0.18)]',
    },
    {
      id: 'approved',
      label: 'Aprovados',
      value: classApproved,
      helper: classEvaluated > 0 ? `${classApprovalRate}% dos avaliados` : 'Nenhuma avaliação lançada',
      icon: Award,
      valueClass: 'text-teal-400',
      iconClass: 'text-teal-400',
      borderClass: 'hover:border-teal-400/40 hover:shadow-[0_0_22px_rgba(45,212,191,0.16)]',
    },
    {
      id: 'reproved',
      label: 'Reprovados',
      value: classReproved,
      helper: 'Parecer reprovado',
      icon: X,
      valueClass: 'text-rose-400',
      iconClass: 'text-rose-400',
      borderClass: 'hover:border-rose-400/40 hover:shadow-[0_0_22px_rgba(251,113,133,0.16)]',
    },
    {
      id: 'completed',
      label: 'Concluídos',
      value: classStatusCount('Concluído'),
      helper: 'Alunos com curso concluído',
      icon: CheckCircle,
      valueClass: 'text-teal-400',
      iconClass: 'text-teal-400',
      borderClass: 'hover:border-teal-400/40 hover:shadow-[0_0_22px_rgba(45,212,191,0.16)]',
    },
    {
      id: 'ongoing',
      label: 'Em Andamento',
      value: classStatusCount('Em andamento'),
      helper: 'Alunos com curso iniciado',
      icon: Clock,
      valueClass: 'text-orange-300',
      iconClass: 'text-orange-300',
      borderClass: 'hover:border-orange-400/40 hover:shadow-[0_0_22px_rgba(251,146,60,0.16)]',
    },
    {
      id: 'not-started',
      label: 'Não Iniciados',
      value: classStatusCount('Não iniciado'),
      helper: 'Alunos sem início de curso',
      icon: Minus,
      valueClass: 'text-slate-300',
      iconClass: 'text-slate-300',
      borderClass: 'hover:border-slate-500/40 hover:shadow-[0_0_22px_rgba(148,163,184,0.12)]',
    },
    {
      id: 'pending',
      label: 'Pendentes',
      value: classStatusCount('Pendente'),
      helper: 'Alunos com pendência de acesso',
      icon: AlertCircle,
      valueClass: 'text-rose-400',
      iconClass: 'text-rose-400',
      borderClass: 'hover:border-rose-400/40 hover:shadow-[0_0_22px_rgba(251,113,133,0.16)]',
    },
  ];

  // Período da turma: início + duração definidos no formulário da turma.
  const classStartYmd = String(classData.settings?.startDate || '');
  const classDurationDays = Number.parseInt(classData.settings?.durationDays, 10) || 0;
  const classEndYmd = classStartYmd && classDurationDays > 0
    ? addDaysToYmd(classStartYmd, classDurationDays)
    : '';

  // Régua regressiva: enche no início e vai diminuindo conforme os dias restantes.
  const classTimeline = (() => {
    if (!classStartYmd || !classEndYmd) return null;
    const dayMs = 24 * 60 * 60 * 1000;
    const startMs = new Date(`${classStartYmd}T00:00:00-03:00`).getTime();
    const endMs = new Date(`${classEndYmd}T23:59:59-03:00`).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
    const nowMs = Date.now();
    const totalMs = Math.max(endMs - startMs, dayMs);
    const remainingMs = endMs - nowMs;
    return {
      ended: remainingMs <= 0,
      notStarted: nowMs < startMs,
      remainingRatio: Math.max(0, Math.min(1, remainingMs / totalMs)),
      remainingDays: Math.max(0, Math.ceil(remainingMs / dayMs)),
      startsInDays: Math.max(0, Math.ceil((startMs - nowMs) / dayMs)),
    };
  })();

  const classTimelineTone = !classTimeline
    ? null
    : classTimeline.ended
      ? { label: 'Encerrada', rightLabel: 'Prazo encerrado', bar: 'bg-rose-500/70', text: 'text-rose-400', badge: 'border-rose-500/20 bg-rose-500/10 text-rose-400' }
      : classTimeline.notStarted
        ? { label: 'Programada', rightLabel: `Inicia em ${classTimeline.startsInDays} ${classTimeline.startsInDays === 1 ? 'dia' : 'dias'}`, bar: 'bg-sky-500/70', text: 'text-sky-400', badge: 'border-amber-500/20 bg-amber-500/10 text-amber-400' }
        : classTimeline.remainingDays <= 7
          ? { label: 'Em andamento', rightLabel: `${classTimeline.remainingDays} ${classTimeline.remainingDays === 1 ? 'dia restante' : 'dias restantes'}`, bar: 'bg-amber-500/70', text: 'text-amber-400', badge: 'border-amber-500/20 bg-amber-500/10 text-amber-400' }
          : { label: 'Em andamento', rightLabel: `${classTimeline.remainingDays} dias restantes`, bar: 'bg-teal-500', text: 'text-teal-400', badge: 'border-teal-500/20 bg-teal-500/10 text-teal-400' };

  // Pontos focais vinculados às empresas da turma (mesma regra do modal de criação/edição).
  const selectedCompanyIds = (classData.companyIds || []).map(String);
  const companyNameById = new Map((companies || []).map(c => [String(c.id), c.name]));
  const focalPoints = selectedCompanyIds.length === 0
    ? []
    : (users || [])
        .filter(u => u.profile === 'Ponto Focal')
        .map(u => {
          const linkedNames = (u.companyIds || [])
            .map(String)
            .filter(id => selectedCompanyIds.includes(id))
            .map(id => companyNameById.get(id))
            .filter(Boolean);
          return linkedNames.length ? { id: u.id, name: u.name, companyNames: linkedNames } : null;
        })
        .filter(Boolean);

  // Template base (casca HTML com {body}) usado no e-mail personalizado
  const baseEmailTemplate = (templates || []).find(
    (t) => t.channel === 'E-mail' && t.is_active !== false && (t.meta?.base === true || (t.content || '').includes('{body}'))
  );

  const enqueueEmail = async (student, templateId, payload) => {
    // Em cada linha do relatório, `id` é o id da MATRÍCULA e `student_id` é o id do PROFILE.
    // A fila de comunicação referencia profiles(id), então o destinatário deve ser o student_id.
    const recipientId = student.student_id || student.id;
    if (!recipientId) throw new Error('Aluno sem identificador de perfil.');
    const { data, error } = await supabase.rpc('insert_single_communication_job', {
      p_recipient_id: recipientId,
      p_template_id: templateId,
      p_payload: payload,
    });
    if (error) throw error;
    // A RPC retorna o id do job na fila, usado para consultar o status real do envio.
    return data;
  };

  // Aciona a fila de comunicação e acompanha o status real dos jobs criados.
  // Retorna o mapa { jobId: { status, last_error } } ao final do processamento.
  const processCommunicationJobs = async (jobIds) => {
    const ids = (jobIds || []).filter(Boolean).map(String);
    const statusById = new Map();
    if (ids.length === 0) return statusById;

    const readStatuses = async () => {
      const { data, error } = await supabase
        .from('communication_queue')
        .select('id, status, last_error')
        .in('id', ids);
      if (error) throw error;
      (data || []).forEach((row) => statusById.set(String(row.id), row));
      return statusById;
    };

    await readStatuses();

    // O worker processa até 5 jobs por execução; repetimos até finalizar ou esgotar as rodadas.
    const maxRounds = Math.min(20, Math.max(1, Math.ceil(ids.length / 5) + 1));
    for (let round = 0; round < maxRounds; round += 1) {
      const hasOpen = ids.some((id) => {
        const row = statusById.get(id);
        return !row || row.status === 'pending' || row.status === 'processing';
      });
      if (!hasOpen) break;
      try {
        await supabase.functions.invoke('communication-worker', { body: {} });
      } catch (err) {
        console.warn('Worker de comunicação indisponível:', err?.message || err);
      }
      await readStatuses();
    }

    return statusById;
  };

  const handleSendStandardEmail = async (template) => {
    if (!standardEmailStudent || !template) return;
    setSendingEmail(true);
    try {
      // Envia o template exatamente como está (mesmo assunto e conteúdo)
      await enqueueEmail(standardEmailStudent, template.id, { type: 'manual_email_standard' });
      setEmailFeedback({ type: 'success', message: `E-mail "${template.name}" enviado para ${standardEmailStudent.name}.` });
      setStandardEmailStudent(null);
    } catch (err) {
      console.error('Erro ao enviar e-mail padrão:', err);
      setEmailFeedback({ type: 'error', message: 'Não foi possível enviar o e-mail. ' + (err?.message || '') });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendCustomEmail = async ({ subject, body }) => {
    if (!customEmailStudent || !baseEmailTemplate) return;
    setSendingEmail(true);
    try {
      // O worker injeta payload.message no placeholder {body} da casca HTML
      await enqueueEmail(customEmailStudent, baseEmailTemplate.id, {
        type: 'manual_email_custom',
        subject,
        message: escapeHtmlText(body).replace(/\r?\n/g, '<br>'),
      });
      setEmailFeedback({ type: 'success', message: `E-mail personalizado enviado para ${customEmailStudent.name}.` });
      setCustomEmailStudent(null);
    } catch (err) {
      console.error('Erro ao enviar e-mail personalizado:', err);
      setEmailFeedback({ type: 'error', message: 'Não foi possível enviar o e-mail. ' + (err?.message || '') });
    } finally {
      setSendingEmail(false);
    }
  };

  // Envio em lote (por status ou seleção individual) para os alunos da turma.
  const handleSendCommunication = async ({ mode, status, studentIds, templateId, courseId }) => {
    const template = (templates || []).find((t) => String(t.id) === String(templateId));
    if (!template) {
      setCommunicationResult({ variant: 'error', title: 'Modelo não selecionado', description: 'Selecione um modelo válido para realizar o envio.' });
      return;
    }

    const classCourseIds = (classCourseOptions || []).map((c) => String(c.id));
    const courseNameById = new Map((classCourseOptions || []).map((c) => [String(c.id), c.name]));
    const isSpecificCourse = !!courseId && courseId !== 'all';

    const baseStudents = mode === 'individual'
      ? communicationStudents.filter((s) => studentIds.includes(s.id))
      : (() => {
          const isAll = !status || status.length === 0 || status.includes('Todos');
          return communicationStudents.filter((s) => isAll || status.includes(s.status));
        })();

    // Um destino por aluno e por curso. Quando a turma tem mais de um curso e a
    // opção "Todos os cursos" é escolhida, replica o envio por curso para que
    // cada mensagem carregue as informações (nome, link e código) do curso certo.
    const targets = [];
    if (isSpecificCourse) {
      const selected = String(courseId);
      baseStudents
        .filter((s) => (s.courseIds || []).includes(selected))
        .forEach((s) => targets.push({ student: s, courseId: selected }));
    } else if (classCourseIds.length > 1) {
      classCourseIds.forEach((cid) => {
        baseStudents
          .filter((s) => (s.courseIds || []).includes(cid))
          .forEach((s) => targets.push({ student: s, courseId: cid }));
      });
    } else {
      const onlyCourseId = classCourseIds[0] || null;
      baseStudents.forEach((s) => targets.push({ student: s, courseId: onlyCourseId }));
    }

    const channelLabel = communicationModal.type === 'whatsapp' ? 'WhatsApp' : 'E-mail';

    if (targets.length === 0) {
      setCommunicationResult({ variant: 'error', title: 'Nenhum destinatário', description: 'Nenhum aluno corresponde aos destinatários selecionados.' });
      return;
    }

    setSendingCommunication(true);
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const isLocalOrigin = /^https?:\/\/(localhost|127\.0\.0\.1|(\d{1,3}\.){3}\d{1,3})(:\d+)?$/i.test(origin);
      const basePayload = {
        type: communicationModal.type === 'whatsapp' ? 'manual_whatsapp' : 'manual_email_standard',
        ...(isLocalOrigin ? { frontend_base_url: origin } : {}),
        ...(classData?.id ? { class_id: classData.id } : {}),
      };
      const enqueued = await Promise.allSettled(
        targets.map(({ student, courseId: cid }) => enqueueEmail(student, template.id, {
          ...basePayload,
          ...(cid ? { course_id: cid, course_title: courseNameById.get(String(cid)) || '' } : {}),
        }).then((jobId) => ({ jobId, student }))),
      );
      const jobs = enqueued.filter((r) => r.status === 'fulfilled' && r.value?.jobId).map((r) => r.value);
      const enqueueFailures = enqueued.filter((r) => r.status !== 'fulfilled' || !r.value?.jobId);

      if (jobs.length === 0) {
        setCommunicationResult({
          variant: 'error',
          title: 'Falha no envio',
          description: `Não foi possível inserir nenhuma mensagem na fila de ${channelLabel}. ${enqueueFailures[0]?.reason?.message || 'Tente novamente.'}`,
        });
        setCommunicationModal({ isOpen: false, type: '' });
        return;
      }

      // Processa a fila e consulta o status real de cada job antes de confirmar.
      const statusById = await processCommunicationJobs(jobs.map((j) => j.jobId));
      const jobStatus = (jobId) => statusById.get(String(jobId))?.status || 'pending';
      const sent = jobs.filter((j) => jobStatus(j.jobId) === 'completed').length;
      const failedJobs = jobs.filter((j) => jobStatus(j.jobId) === 'failed');
      const pending = jobs.filter((j) => {
        const st = jobStatus(j.jobId);
        return st !== 'completed' && st !== 'failed';
      });
      const totalFailed = failedJobs.length + enqueueFailures.length + pending.length;
      const firstError = failedJobs
        .map((j) => statusById.get(String(j.jobId))?.last_error)
        .find(Boolean) || enqueueFailures[0]?.reason?.message || '';

      setSavedCommunications((prev) => ({
        ...prev,
        [communicationModal.type]: mode === 'individual'
          ? { mode, status: ['Seleção individual'], templateId, studentIds, courseId }
          : { mode, status, templateId, courseId },
      }));

      const total = targets.length;
      if (totalFailed === 0) {
        setCommunicationResult({
          variant: 'success',
          title: 'Mensagem enviada',
          description: `"${template.name}" enviado com sucesso para ${sent} aluno(s) via ${channelLabel}.`,
        });
      } else if (sent === 0) {
        setCommunicationResult({
          variant: 'error',
          title: 'Falha no envio',
          description: `O envio via ${channelLabel} falhou para os ${total} aluno(s). ${firstError || 'Verifique a fila de comunicação.'}`,
        });
      } else {
        setCommunicationResult({
          variant: 'partial',
          title: 'Envio parcial',
          description: `Enviado para ${sent} de ${total} aluno(s) via ${channelLabel}. ${totalFailed} falha(s)${pending.length ? ` (${pending.length} ainda em processamento)` : ''}.`,
        });
      }
      setCommunicationModal({ isOpen: false, type: '' });
    } catch (err) {
      console.error('Erro ao enviar comunicação:', err);
      setCommunicationResult({
        variant: 'error',
        title: 'Falha no envio',
        description: `Não foi possível enviar via ${channelLabel}. ${err?.message || 'Tente novamente.'}`,
      });
    } finally {
      setSendingCommunication(false);
    }
  };

  const CommunicationModal = ({ isOpen, type, initialConfig, students, courseOptions, sending, onClose, onSend }) => {
     const [recipientMode, setRecipientMode] = useState('status'); // 'status' | 'individual'
     const [selectedStatus, setSelectedStatus] = useState(['Todos']);
     const [selectedTemplateId, setSelectedTemplateId] = useState('');
     const [selectedStudentIds, setSelectedStudentIds] = useState([]);
     const [selectedCourseId, setSelectedCourseId] = useState('all');
     const [studentSearch, setStudentSearch] = useState('');
     const options = ['Todos', 'Não iniciado', 'Em andamento', 'Concluído'];

     useEffect(() => {
        if (isOpen) {
            setRecipientMode(initialConfig?.mode || 'status');
            setSelectedStatus(initialConfig?.status || ['Todos']);
            setSelectedTemplateId(initialConfig?.templateId || '');
            setSelectedStudentIds(initialConfig?.studentIds || []);
            setSelectedCourseId(initialConfig?.courseId || 'all');
            setStudentSearch('');
        }
     }, [isOpen, initialConfig]);

     const availableTemplates = templates.filter(t => t.channel === (type === 'whatsapp' ? 'WhatsApp' : 'E-mail') && t.is_active !== false);

     const toggleOption = (option) => {
        if (option === 'Todos') {
           setSelectedStatus(['Todos']);
        } else {
           let newSelected = selectedStatus.filter(s => s !== 'Todos');
           if (selectedStatus.includes(option)) {
              newSelected = newSelected.filter(s => s !== option);
           } else {
              newSelected = [...newSelected, option];
           }
           if (newSelected.length === 0) newSelected = ['Todos'];
           setSelectedStatus(newSelected);
        }
     };

     const toggleStudent = (id) => {
        setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
     };

     const term = studentSearch.trim().toLowerCase();
     const matchesCourse = (s) => selectedCourseId === 'all' || (s.courseIds || []).includes(String(selectedCourseId));
     const visibleStudents = (students || []).filter(s => matchesCourse(s) && (!term
        || (s.name || '').toLowerCase().includes(term)
        || (s.email || '').toLowerCase().includes(term)
        || (s.whatsapp || '').toLowerCase().includes(term)));
     const allVisibleSelected = visibleStudents.length > 0 && visibleStudents.every(s => selectedStudentIds.includes(s.id));
     const toggleAllVisible = () => {
        if (allVisibleSelected) {
           const visibleIds = new Set(visibleStudents.map(s => s.id));
           setSelectedStudentIds(prev => prev.filter(id => !visibleIds.has(id)));
        } else {
           setSelectedStudentIds(prev => Array.from(new Set([...prev, ...visibleStudents.map(s => s.id)])));
        }
     };

     const canSend = !!selectedTemplateId && (recipientMode === 'status' || selectedStudentIds.length > 0);

     const selectedTemplate = availableTemplates.find(t => String(t.id) === String(selectedTemplateId)) || null;
     const previewSource = selectedTemplate ? (selectedTemplate.meta?.textoHtml || selectedTemplate.content || '') : '';
     const previewSubject = selectedTemplate ? (selectedTemplate.subject || '') : '';
     const previewHeaderUrl = selectedTemplate?.meta?.headerUrl || '';
     const previewFooterUrl = selectedTemplate?.meta?.rodapeUrl || '';
     const previewButton = selectedTemplate?.meta?.botao && (selectedTemplate.meta.botao.titulo || selectedTemplate.meta.botao.link) ? selectedTemplate.meta.botao : null;

     const whatsappPreviewHtml = (() => {
        if (!selectedTemplate) return '';
        return escapeHtmlText(resolveCommunicationPreview(previewSource))
           .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
           .replace(/_(.*?)_/g, '<em>$1</em>')
           .replace(/\n/g, '<br />');
     })();

     const emailPreviewHtml = (() => {
        if (!selectedTemplate) return '';
        const resolved = resolveCommunicationPreview(previewSource);
        const looksHtml = /<[a-z][\s\S]*>/i.test(previewSource) || selectedTemplate.is_html === true || selectedTemplate.meta?.usarHtml === true;
        return looksHtml
           ? resolved
           : `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#111827;padding:20px;line-height:1.6;">${escapeHtmlText(resolved).replace(/\n/g, '<br />')}</div>`;
     })();

     if (!isOpen) return null;

     return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
           <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[90vh]">
              <div className="flex justify-between items-center px-6 py-4 border-b border-slate-800 shrink-0">
                 <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    {type === 'whatsapp' ? <MessageCircle size={22} className="text-green-500" /> : <Mail size={22} className="text-blue-500" />}
                    Enviar {type === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
                 </h3>
                 <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={20} /></button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden flex flex-col lg:flex-row custom-scrollbar">
                 <div className="px-6 py-5 flex flex-col gap-5 lg:w-[460px] lg:shrink-0 lg:overflow-y-auto custom-scrollbar">
                 <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Selecione o Modelo</label>
                    <select
                       value={selectedTemplateId}
                       onChange={(e) => setSelectedTemplateId(e.target.value)}
                       className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white outline-none focus:border-white transition"
                    >
                       <option value="">Selecione...</option>
                       {availableTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    {availableTemplates.length === 0 && (
                       <p className="mt-2 text-xs text-slate-500">Nenhum modelo ativo disponível para este canal.</p>
                    )}
                 </div>

                 {courseOptions && courseOptions.length > 1 && (
                 <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Curso</label>
                    <select
                       value={selectedCourseId}
                       onChange={(e) => setSelectedCourseId(e.target.value)}
                       className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white outline-none focus:border-white transition"
                    >
                       <option value="all">Todos os cursos</option>
                       {courseOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                 </div>
                 )}

                 <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Destinatários</label>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                       <button
                          type="button"
                          onClick={() => setRecipientMode('status')}
                          className={`p-2.5 rounded-lg border text-xs font-bold flex items-center justify-center gap-2 transition ${recipientMode === 'status' ? 'bg-slate-800 border-teal-500 text-white' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'}`}
                       >
                          <Filter size={14} /> Por Status
                       </button>
                       <button
                          type="button"
                          onClick={() => setRecipientMode('individual')}
                          className={`p-2.5 rounded-lg border text-xs font-bold flex items-center justify-center gap-2 transition ${recipientMode === 'individual' ? 'bg-slate-800 border-teal-500 text-white' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'}`}
                       >
                          <UserCheck size={14} /> Alunos Específicos
                       </button>
                    </div>

                    {recipientMode === 'status' ? (
                       <div className="flex flex-col gap-2">
                          {options.map(option => (
                             <div key={option} onClick={() => toggleOption(option)} className={`p-3 rounded-lg border cursor-pointer flex items-center justify-between transition ${selectedStatus.includes(option) ? 'bg-slate-800 border-teal-500 text-white' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'}`}>
                                <span className="text-sm font-medium">{option}</span>
                                {selectedStatus.includes(option) && <Check size={16} className="text-teal-500" />}
                             </div>
                          ))}
                       </div>
                    ) : (
                       <div className="flex flex-col gap-2">
                          <div className="relative">
                             <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                             <input
                                type="text"
                                value={studentSearch}
                                onChange={(e) => setStudentSearch(e.target.value)}
                                placeholder="Buscar aluno..."
                                className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-slate-600 transition"
                             />
                          </div>
                          <div className="flex items-center justify-between px-1">
                             <span className="text-xs text-slate-500">{selectedStudentIds.length} selecionado(s)</span>
                             <button type="button" onClick={toggleAllVisible} className="text-xs font-bold text-teal-400 hover:text-teal-300 transition">
                                {allVisibleSelected ? 'Limpar seleção' : 'Selecionar todos'}
                             </button>
                          </div>
                          <div className="max-h-64 overflow-y-auto flex flex-col gap-1.5 rounded-lg border border-slate-800 bg-slate-950/50 p-2 custom-scrollbar">
                             {visibleStudents.length === 0 && (
                                <p className="p-4 text-center text-xs text-slate-500">Nenhum aluno encontrado.</p>
                             )}
                             {visibleStudents.map(s => {
                                const isSelected = selectedStudentIds.includes(s.id);
                                const contact = type === 'whatsapp' ? s.whatsapp : s.email;
                                return (
                                   <div
                                      key={s.id}
                                      onClick={() => toggleStudent(s.id)}
                                      className={`p-2.5 rounded-lg border cursor-pointer flex items-center gap-3 transition ${isSelected ? 'bg-slate-800 border-teal-500' : 'bg-slate-950 border-slate-800 hover:border-slate-600'}`}
                                   >
                                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-slate-600'}`}>
                                         {isSelected && <Check size={12} className="text-slate-950" />}
                                      </span>
                                      <div className="min-w-0 flex-1">
                                         <p className="text-sm text-white font-medium truncate">{s.name}</p>
                                         <p className="text-[11px] text-slate-500 truncate">{contact || (type === 'whatsapp' ? 'Sem WhatsApp' : 'Sem e-mail')}</p>
                                      </div>
                                   </div>
                                );
                             })}
                          </div>
                       </div>
                    )}
                 </div>
                 </div>

                 <div className="lg:flex-1 lg:min-h-0 border-t lg:border-t-0 lg:border-l border-slate-800 bg-slate-950/40 p-5 flex flex-col gap-3 lg:overflow-y-auto custom-scrollbar">
                    <div className="flex items-center gap-2 text-slate-400 shrink-0">
                       {type === 'whatsapp' ? <MessageCircle size={16} className="text-green-500" /> : <Mail size={16} className="text-blue-500" />}
                       <span className="text-xs font-bold uppercase tracking-widest">Pré-visualização</span>
                    </div>

                    {!selectedTemplate ? (
                       <div className="flex-1 flex items-center justify-center text-center text-sm text-slate-500 p-8">
                          Selecione um modelo para visualizar a mensagem.
                       </div>
                    ) : type === 'whatsapp' ? (
                       <div className="rounded-2xl bg-[#0b141a] p-4 flex-1">
                          <div className="max-w-sm mx-auto rounded-xl bg-[#005c4b] text-white px-4 py-3 shadow-lg">
                             {previewHeaderUrl && (
                                <img src={previewHeaderUrl} alt="Cabeçalho" className="w-full rounded-lg mb-2 object-cover max-h-40" />
                             )}
                             {resolveCommunicationPreview(previewSource)
                                ? <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: whatsappPreviewHtml }} />
                                : <div className="text-sm text-white/60 italic">Mensagem sem conteúdo.</div>}
                             {previewFooterUrl && (
                                <img src={previewFooterUrl} alt="Rodapé" className="w-full rounded-lg mt-2 object-cover max-h-28" />
                             )}
                             {previewButton && (
                                <div className="mt-3 border-t border-white/20 pt-2 text-center">
                                   <span className="text-sm font-semibold text-teal-200">{resolveCommunicationPreview(previewButton.titulo) || 'Acessar'}</span>
                                </div>
                             )}
                          </div>
                       </div>
                    ) : (
                       <>
                          <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs shrink-0">
                             <span className="text-slate-500 font-bold uppercase tracking-widest mr-2">Assunto:</span>
                             <span className="text-slate-200">{resolveCommunicationPreview(previewSubject) || '—'}</span>
                          </div>
                          <div className="rounded-lg border border-slate-800 bg-white overflow-hidden flex-1 min-h-[420px]">
                             <iframe title="Pré-visualização da mensagem" className="w-full h-full min-h-[420px] bg-white" srcDoc={emailPreviewHtml} />
                          </div>
                       </>
                    )}
                 </div>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800 shrink-0">
                 <button onClick={onClose} disabled={sending} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition disabled:opacity-50">Cancelar</button>
                 <button
                    disabled={!canSend || sending}
                    onClick={() => onSend(
                       recipientMode === 'individual'
                          ? { mode: 'individual', studentIds: selectedStudentIds, templateId: selectedTemplateId, courseId: selectedCourseId }
                          : { mode: 'status', status: selectedStatus, templateId: selectedTemplateId, courseId: selectedCourseId }
                    )}
                    className={`px-6 py-2 rounded-lg text-white font-bold shadow-lg transition inline-flex items-center gap-2 ${(!canSend || sending) ? 'opacity-50 cursor-not-allowed bg-slate-700' : (type === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700')}`}
                 >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Enviar
                 </button>
              </div>
           </div>
        </div>
     );
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <CommunicationModal 
        isOpen={communicationModal.isOpen} 
        type={communicationModal.type} 
        initialConfig={savedCommunications[communicationModal.type]}
        students={communicationStudents}
        courseOptions={classCourseOptions}
        sending={sendingCommunication}
        onClose={() => { if (!sendingCommunication) setCommunicationModal({ isOpen: false, type: '' }); }} 
        onSend={handleSendCommunication} 
      />

      {communicationResult && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-sm shadow-2xl p-6 text-center">
            <div className={`mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center ${
              communicationResult.variant === 'success'
                ? 'bg-teal-500/15 text-teal-400'
                : communicationResult.variant === 'partial'
                  ? 'bg-amber-500/15 text-amber-400'
                  : 'bg-rose-500/15 text-rose-400'
            }`}>
              {communicationResult.variant === 'success'
                ? <CheckCircle size={30} />
                : <AlertCircle size={30} />}
            </div>
            <h3 className="text-lg font-bold text-white mb-1">{communicationResult.title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{communicationResult.description}</p>
            <button
              onClick={() => setCommunicationResult(null)}
              className={`mt-6 w-full px-4 py-2 rounded-lg font-bold text-white transition ${
                communicationResult.variant === 'error' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {standardEmailStudent && (
        <StandardEmailModal
          student={standardEmailStudent}
          templates={templates}
          sending={sendingEmail}
          onClose={() => { if (!sendingEmail) setStandardEmailStudent(null); }}
          onSend={handleSendStandardEmail}
        />
      )}

      {customEmailStudent && (
        <CustomEmailModal
          student={customEmailStudent}
          baseTemplate={baseEmailTemplate}
          sending={sendingEmail}
          onClose={() => { if (!sendingEmail) setCustomEmailStudent(null); }}
          onSend={handleSendCustomEmail}
        />
      )}

      {emailFeedback && (
        <div className="fixed top-6 right-6 z-[400] animate-fade-in">
          <div className={`flex items-start gap-3 max-w-sm px-4 py-3 rounded-xl border shadow-2xl backdrop-blur-sm ${
            emailFeedback.type === 'error'
              ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'
              : 'bg-slate-900 border-slate-700 text-slate-100'
          }`}>
            <div className={`mt-0.5 shrink-0 ${emailFeedback.type === 'error' ? 'text-rose-400' : 'text-teal-400'}`}>
              {emailFeedback.type === 'error' ? <AlertCircle size={18} /> : <Check size={18} />}
            </div>
            <p className="text-sm font-medium leading-snug flex-1">{emailFeedback.message}</p>
            <button
              onClick={() => setEmailFeedback(null)}
              className="shrink-0 text-slate-500 hover:text-white transition"
              aria-label="Fechar aviso"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <StudentDetailsModal
        isOpen={!!selectedStudentDetails}
        student={selectedStudentDetails}
        onClose={() => setSelectedStudentDetails(null)}
      />

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition">
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Detalhes da Turma</h1>
            <p className="text-slate-400">Visualizando informações e alunos matriculados.</p>
          </div>
        </div>
        {activeDetailsTab === 'geral' && (
        <div className="flex items-center gap-3">
          {isManualClass && (
            <button
              onClick={() => setEnrollModal({ isOpen: true, term: '', studentId: '' })}
              className="flex items-center gap-2 bg-teal-500 text-slate-950 px-4 py-2 rounded-lg text-sm font-bold shadow-lg hover:bg-teal-400 transition"
            >
              <UserPlus size={16} /> Matricular aluno
            </button>
          )}
          <button
            onClick={handleExportExcel}
            disabled={loadingProgress || filteredStudents.length === 0}
            title="Exportar alunos listados para Excel (respeita os filtros aplicados)"
            className="flex items-center gap-2 bg-emerald-600/10 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-600/20 hover:border-emerald-500/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet size={16} /> Exportar Excel
          </button>
        </div>
        )}
      </div>

      {/* Abas da turma: visão geral (padrão) x convite link. */}
      <div className="flex items-center gap-6 border-b border-slate-800 mb-8">
        <button
          onClick={() => setDetailsTab('geral')}
          className={`pb-3 px-1 text-sm font-bold uppercase tracking-wide transition border-b-2 ${activeDetailsTab === 'geral' ? 'text-white border-teal-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
        >
          Visão Geral
        </button>
        {classData.inviteMode === 'link' && (
          <button
            onClick={() => setDetailsTab('invite')}
            className={`pb-3 px-1 text-sm font-bold uppercase tracking-wide transition border-b-2 inline-flex items-center gap-2 ${activeDetailsTab === 'invite' ? 'text-white border-teal-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
          >
            <KeyRound size={14} /> Convite Link
          </button>
        )}
      </div>

      {activeDetailsTab === 'geral' && (
      <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-lg">
          <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">Informações da Turma</h3>
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-slate-500 text-xs">Nome</p>
              <p className="text-white font-bold text-lg">{classData.name}</p>
            </div>
            <div>
               <p className="text-slate-500 text-xs">Empresa</p>
               <p className="text-white font-bold text-sm">
                  {(classData.companyIds || []).map(id => (companies || []).find(c => String(c.id) === String(id))?.name).filter(Boolean).join(', ') || '-'}
               </p>
            </div>
            <div>
               <p className="text-slate-500 text-xs">Parceiro SAS</p>
               <p className="text-white font-bold text-sm">
                  {(classData.partnerIds || []).map(id => (sasPartners || []).find(p => String(p.id) === String(id))?.name).filter(Boolean).join(', ') || '-'}
               </p>
            </div>
            <div>
               <p className="text-slate-500 text-xs mb-1">Progresso da Turma</p>
               <div className="flex items-center gap-3">
                   <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${classProgress}%` }}></div>
                   </div>
                   <span className="text-xs font-bold text-teal-400 w-8 text-right">{classProgress}%</span>
               </div>
            </div>
            <div>
               <p className="text-slate-500 text-xs">Status</p>
               <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${classData.status === 'Ativo' ? 'border-teal-500/20 bg-teal-500/10 text-teal-400' : classData.status === 'Programada' ? 'border-amber-500/20 bg-amber-500/10 text-amber-400' : classData.status === 'Encerrada' ? 'border-slate-600/40 bg-slate-600/10 text-slate-400' : 'border-rose-500/20 bg-rose-500/10 text-rose-400'}`}>{classData.status}</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 p-6 rounded-xl shadow-lg md:col-span-2 relative flex flex-col">
           <div className="flex justify-between items-start mb-4">
              <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest">Cursos Vinculados ({classData.courses?.length || 0})</h3>
           </div>
           
           <div className="absolute top-6 right-6 flex flex-col items-end gap-1">
                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1">Comunicação</span>
                <div className="flex gap-2">
                   <button onClick={() => setCommunicationModal({ isOpen: true, type: 'email' })} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition shadow-lg flex items-center gap-2 text-xs font-bold" title="E-mail">
                      <Mail size={14} /> E-mail
                   </button>
                   <button onClick={() => setCommunicationModal({ isOpen: true, type: 'whatsapp' })} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition shadow-lg flex items-center gap-2 text-xs font-bold" title="WhatsApp">
                      <MessageCircle size={14} /> WhatsApp
                   </button>
                </div>
          </div>

           <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {courses.filter(c => classData.courses.includes(c.id)).map(course => (
                 <div key={course.id} className="flex items-center gap-3 bg-slate-950/50 p-3 rounded-lg border border-slate-800/50">
                    <div className="w-10 h-10 rounded bg-slate-800 overflow-hidden shrink-0">
                       <img src={course.thumb} alt="" className="w-full h-full object-cover opacity-80" />
                    </div>
                    <div>
                       <p className="text-white text-sm font-medium line-clamp-1">{course.title}</p>
                       <p className="text-slate-500 text-xs">{formatCourseWorkloadHHMM(course.workload_hours || course.workload) || '00:00'}</p>
                    </div>
                 </div>
              ))}
              {(!classData.courses || classData.courses.length === 0) && <p className="text-slate-500 text-sm italic">Nenhum curso vinculado.</p>}
           </div>

           {classTimeline && (
              <div className="mt-5 pt-5 border-t border-slate-800">
                 <div className="flex items-center justify-between gap-3 mb-4">
                    <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest">Período da Turma</h3>
                    <span className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${classTimelineTone.badge}`}>
                       <Clock size={12} /> {classTimelineTone.label}
                    </span>
                 </div>

                 <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                       <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1">Duração</p>
                       <p className="text-white font-bold text-sm">{classDurationDays} {classDurationDays === 1 ? 'dia' : 'dias'}</p>
                    </div>
                    <div>
                       <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1">Início</p>
                       <p className="text-white font-bold text-sm">{ymdToBrDate(classStartYmd) || '-'}</p>
                    </div>
                    <div>
                       <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1">Fim</p>
                       <p className="text-white font-bold text-sm">{ymdToBrDate(classEndYmd) || '-'}</p>
                    </div>
                 </div>

                 <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                       <div
                          className={`h-full rounded-full transition-all duration-500 ${classTimelineTone.bar}`}
                          style={{ width: `${Math.round(classTimeline.remainingRatio * 100)}%` }}
                       ></div>
                    </div>
                    <span className={`text-xs font-bold whitespace-nowrap ${classTimelineTone.text}`}>{classTimelineTone.rightLabel}</span>
                 </div>
                 <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-500 font-medium">
                    <span>{ymdToBrDate(classStartYmd)}</span>
                    <span>{ymdToBrDate(classEndYmd)}</span>
                 </div>
              </div>
           )}

           <div className="mt-5 pt-5 border-t border-slate-800 flex flex-col gap-2">
              <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest">Pontos Focais ({focalPoints.length})</h3>
              {focalPoints.length === 0 ? (
                 <p className="text-slate-500 text-sm italic">Nenhum ponto focal vinculado às empresas da turma.</p>
              ) : (
                 <div className="flex flex-wrap gap-2">
                    {focalPoints.map(fp => (
                       <span key={fp.id} className="inline-flex items-center gap-1.5 rounded-md border border-teal-500/20 bg-teal-500/10 px-2.5 py-1 text-xs font-bold text-teal-300">
                          {fp.name}
                          <span className="font-medium text-slate-400">· {fp.companyNames.join(', ')}</span>
                       </span>
                    ))}
                 </div>
              )}
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {classSummaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              className={`min-w-0 bg-slate-900 border border-slate-800 rounded-lg p-4 shadow-lg transition-all duration-300 ${card.borderClass}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold truncate">{card.label}</p>
                <Icon size={16} className={`${card.iconClass} shrink-0`} />
              </div>
              <p className={`text-[28px] font-black mt-2 leading-none ${card.valueClass}`}>{card.value}</p>
              <p className="text-[11px] text-slate-500 mt-1.5 truncate">{card.helper}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
        <div className="p-5 border-b border-slate-800 bg-slate-950/30 flex flex-col md:flex-row justify-between items-center gap-4">
           <h3 className="font-bold text-white flex items-center gap-2"><Users size={18} className="text-slate-400" /> Alunos Matriculados ({filteredStudents.length})</h3>
           
           <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
             <div className="relative">
                <select 
                   value={statusFilter} 
                   onChange={(e) => setStatusFilter(e.target.value)}
                   className="w-full sm:w-40 bg-slate-950 border border-slate-800 rounded-lg pl-3 pr-8 py-2 text-sm text-slate-400 outline-none focus:border-slate-600 appearance-none cursor-pointer"
                >
                   <option value="">Status: Todos</option>
                   <option value="Concluído">Concluído</option>
                   <option value="Em andamento">Em andamento</option>
                   <option value="Programado">Programado</option>
                   <option value="Não iniciado">Não iniciado</option>
                   <option value="Pendente">Pendente</option>
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
             </div>

             <div className="relative">
                <select 
                   value={resultFilter} 
                   onChange={(e) => setResultFilter(e.target.value)}
                   className="w-full sm:w-40 bg-slate-950 border border-slate-800 rounded-lg pl-3 pr-8 py-2 text-sm text-slate-400 outline-none focus:border-slate-600 appearance-none cursor-pointer"
                >
                   <option value="">Parecer: Todos</option>
                   <option value="Aprovado">Aprovado</option>
                   <option value="Reprovado">Reprovado</option>
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
             </div>

             <div className="relative w-full sm:w-64">
               <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
               <input 
                 type="text" 
                 placeholder="Buscar aluno..." 
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:border-slate-600 outline-none transition"
               />
             </div>
           </div>
        </div>
        <table className="w-full table-fixed text-left">
          <thead className="bg-slate-950/90 text-slate-200 text-xs uppercase font-semibold shadow-[inset_0_-1px_0_rgba(51,65,85,0.55)]">
            <tr>
              <th className="px-3 py-3 w-[170px]">Aluno</th>
              <th className="px-3 py-3 w-[150px]">Contato</th>
              <th className="px-3 py-3 w-[140px]">Empresa</th>
              <th className="px-3 py-3 w-[185px]">Curso</th>
              {!partnerScoped && <th className="px-3 py-3 w-[110px]">Parceiro</th>}
              <th className="px-3 py-3 w-[115px]">Progr.</th>
              <th className="px-3 py-3 w-[70px]">Status</th>
              <th className="px-3 py-3 w-[88px] text-center">Parecer</th>
              <th className="px-3 py-3 w-[55px] text-center">Nota</th>
              <th className="px-3 py-3 w-[120px]">Acesso Curso</th>
              <th className="px-3 py-3 w-[60px] text-right">Ações</th>
            </tr>
           </thead>
          <tbody className="divide-y divide-slate-800/50 text-sm">
            {loadingProgress ? (
              <tr>
                <td colSpan={partnerScoped ? 10 : 11} className="p-8 text-center text-slate-500">Carregando dados...</td>
              </tr>
            ) : currentStudents.map(row => {
              const progress = Math.round(Number(row.report_course_progress ?? (row.progress_avg || 0)));
              const status = row.status || 'Indefinido';
              const statusClass =
                status === 'Concluído'
                  ? 'text-teal-400'
                  : status === 'Em andamento'
                    ? 'text-orange-300'
                    : status === 'Programado'
                      ? 'text-amber-300'
                      : status === 'Não iniciado'
                        ? 'text-slate-400'
                        : status === 'Pendente'
                          ? 'text-rose-400'
                          : 'text-slate-300';
              const statusIcon = (() => {
                const iconProps = { size: 16, className: `${statusClass} transition` };
                if (status === 'Concluído') return <CheckCircle {...iconProps} />;
                if (status === 'Em andamento') return <Clock {...iconProps} />;
                if (status === 'Programado') return <PauseCircle {...iconProps} />;
                if (status === 'Não iniciado') return <Minus {...iconProps} />;
                if (status === 'Pendente') return <AlertCircle {...iconProps} />;
                return <HelpCircle {...iconProps} />;
              })();

              return (
                <tr key={`${row.student_id}-${row.report_course_id}`} className="hover:bg-slate-800/50 transition text-slate-300">
                  <td className="px-3 py-3">
                     <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-400 shrink-0">
                           {(row.student || '?').substring(0,2).toUpperCase()}
                        </div>
                        <span className="font-medium text-white truncate" title={row.student || '-'}>{row.student}</span>
                     </div>
                  </td>
                  <td className="px-3 py-3">
                     <div className="flex flex-col gap-0.5">
                        <span className="text-slate-300 truncate" title={row.email || '-'}>{row.email || '-'}</span>
                        <span className="text-slate-500 text-xs truncate">{row.whatsapp || ''}</span>
                     </div>
                  </td>
                  <td className="px-3 py-3 text-slate-400">
                     <span className="truncate block max-w-[140px]" title={row.company || '-'}>{formatCompanyLabel(row.company)}</span>
                  </td>
                  <td className="px-3 py-3 text-slate-300">
                     <span className="truncate block max-w-[185px]" title={row.report_course_name || '-'}>{row.report_course_name || row.courses_enrolled || '-'}</span>
                  </td>
                  {!partnerScoped && (
                    <td className="px-3 py-3 text-slate-400">
                       <span className="truncate block max-w-[110px]" title={row.partner || '-'}>{shortOrgLabel(row.partner)}</span>
                    </td>
                  )}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                       <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: `${progress}%` }}></div>
                       </div>
                       <span className="text-xs font-bold text-teal-400">{progress}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                     <span className="inline-flex items-center cursor-help" title={status} aria-label={status}>
                        {statusIcon}
                     </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                      <span className={`text-xs font-bold ${row.result === 'Aprovado' ? 'text-teal-400' : 'text-slate-500'}`}>
                         {row.result}
                      </span>
                   </td>
                   <td className="px-3 py-3 text-white font-bold text-center">{row.score}</td>
                   <td className="px-3 py-3 text-slate-400 text-xs">
                      {row.last_access ? (
                         <span className="block leading-tight font-mono text-slate-300" title={new Date(row.last_access).toLocaleString('pt-BR')}>
                           {formatShortDateTime(row.last_access)}
                         </span>
                      ) : (
                         <span className="text-rose-400 bg-rose-500/10 px-2 py-1 rounded border border-rose-500/20 text-[10px] uppercase font-bold">Nunca acessou</span>
                      )}
                   </td>
                   <td className="px-3 py-3 text-right">
                      <div className="flex justify-end">
                         <StudentActionsMenu
                            student={row}
                            onStandardEmail={(s) => setStandardEmailStudent(s)}
                            onCustomEmail={(s) => setCustomEmailStudent(s)}
                            onViewDetails={(s) => setSelectedStudentDetails(s)}
                            onRemoveFromClass={isManualClass ? handleManualRemove : undefined}
                         />
                      </div>
                   </td>
                </tr>
              );
            })}
            {!loadingProgress && currentStudents.length === 0 && (
              <tr>
                <td colSpan={partnerScoped ? 10 : 11} className="p-8 text-center text-slate-500">
                  {searchTerm ? 'Nenhum aluno encontrado para sua busca.' : 'Nenhum aluno matriculado nesta turma.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-800 flex justify-between items-center bg-slate-950/30">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-slate-400">
              Página <span className="text-white font-bold">{currentPage}</span> de {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
      </>
      )}

      {activeDetailsTab === 'invite' && (
        <ClassInviteLinkPanel classId={classData.id} collapsible={false} showExport />
      )}

      {enrollmentFeedback && (
        <Notification
          message={enrollmentFeedback.message}
          type={enrollmentFeedback.type}
          onClose={() => setEnrollmentFeedback(null)}
        />
      )}

      {/* Modal de matrícula manual: lista apenas quem ainda não está na turma. */}
      {isManualClass && enrollModal.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-1">Matricular aluno</h3>
            <p className="text-slate-400 text-sm mb-5">
              Turma <span className="text-white font-semibold">{classData?.name}</span> · o aluno entra nos cursos vinculados e recebe o comunicado de início.
            </p>
            <ReportStudentAutocomplete
              value={enrollModal.term}
              onChange={handleEnrollSearchChange}
              options={enrollableStudentOptions}
              placeholder="Buscar aluno por nome"
              emptyLabel="Nenhum aluno disponível encontrado"
              clearAriaLabel="Limpar aluno selecionado"
            />
            {enrollModal.term && !enrollModal.studentId && (
              <p className="mt-2 text-xs text-amber-400">Selecione um aluno da lista para confirmar.</p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { if (!savingEnrollment) setEnrollModal({ isOpen: false, term: '', studentId: '' }); }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmManualEnroll}
                disabled={!enrollModal.studentId || savingEnrollment}
                className={`px-6 py-2 rounded-lg font-bold shadow-lg transition inline-flex items-center gap-2 ${
                  !enrollModal.studentId || savingEnrollment
                    ? 'opacity-50 cursor-not-allowed bg-slate-700 text-white'
                    : 'bg-teal-500 text-slate-950 hover:bg-teal-400'
                }`}
              >
                {savingEnrollment ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                Matricular
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
};

// Helpers de data digitável (dd/mm/aaaa) para o modal de turma.
const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const maskBrDate = (value) => {
  const digits = onlyDigits(value).slice(0, 8);
  const parts = [digits.slice(0, 2)];
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.filter((part, index) => index === 0 || part.length > 0).join('/');
};

const brDateToYmd = (brDate) => {
  const digits = onlyDigits(brDate);
  if (digits.length !== 8) return '';
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return '';
  return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
};

const ymdToBrDate = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

// Data dd/mm/aaaa -> dd/mm/aa (compacto, usado em listagens).
const brDateShortYear = (brDate) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(brDate || ''));
  return match ? `${match[1]}/${match[2]}/${match[3].slice(2)}` : '';
};

// Data de hoje (fuso local) no formato aaaa-mm-dd, para comparação com a data de início.
const todayYmd = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Status da turma é automático:
//   - validade (fim = início + duração) já vencida => 'Encerrada';
//   - data de início futura => 'Programada';
//   - caso contrário mantém 'Inativo' quando definido, senão 'Ativo'.
const deriveClassStatus = (storedStatus, startYmd, endYmd) => {
  if (endYmd && endYmd < todayYmd()) return 'Encerrada';
  if (startYmd && startYmd > todayYmd()) return 'Programada';
  if (storedStatus === 'Inativo') return 'Inativo';
  return 'Ativo';
};

// Normaliza o par de colunas (lista nova ou coluna única legada) para array de ids em string.
const toIdArray = (listValue, scalarValue) => {
  const source = Array.isArray(listValue) ? listValue : (scalarValue ? [scalarValue] : []);
  return source.filter(Boolean).map(String);
};

// Normalização única da linha de turma vinda do banco. É a fonte de verdade do
// frontend: qualquer loader (admin, aluno, detalhes) deve usar esta função para
// que 'settings' (modo de matrícula, modo de convite, período, vagas) seja
// lido sempre igual e não se perca entre telas/recargas.
const normalizeClassRow = (c) => {
  if (!c) return c;
  const companyIds = toIdArray(c.company_ids, c.company_id);
  const partnerIds = toIdArray(c.partner_ids, c.partner_id);
  const startDate = ymdToBrDate(c.settings?.startDate);
  // Fim da turma: início + duração (mesma regra do formulário); se não houver, usa o endDate gravado.
  const endDateYmd = (c.settings?.startDate && c.settings?.durationDays)
    ? addDaysToYmd(c.settings.startDate, c.settings.durationDays)
    : (c.settings?.endDate || '');
  return {
    ...c,
    companyId: companyIds[0] ?? c.company_id ?? null,
    partnerId: partnerIds[0] ?? c.partner_id ?? null,
    companyIds,
    partnerIds,
    courses: (c.class_courses || []).map(cc => cc.course_id),
    students: (c.class_students || []).map(cs => cs.student_id),
    area: Array.isArray(c.settings?.area) ? c.settings.area : (c.settings?.area ? [c.settings.area] : []),
    cargo: Array.isArray(c.settings?.cargo) ? c.settings.cargo : (c.settings?.cargo ? [c.settings.cargo] : []),
    startDate,
    startTime: c.settings?.startTime || '00:00',
    endDate: ymdToBrDate(endDateYmd),
    durationDays: c.settings?.durationDays ?? '',
    vacancies: c.settings?.vacancies ?? '',
    enrollmentMode: c.settings?.enrollmentMode === 'manual' ? 'manual' : 'auto',
    inviteMode: c.settings?.inviteMode === 'link' ? 'link' : 'system',
    notifyEmail: c.settings?.notifyEmail === true,
    notifyWhatsapp: c.settings?.notifyWhatsapp === true,
    status: deriveClassStatus(c.status, brDateToYmd(startDate), endDateYmd),
  };
};

// Painel "Convite Link": links diretos por curso + códigos de 4 dígitos por
// aluno x curso (pivot), com botões de copiar. Usado no modal da turma e nos
// detalhes da turma.
const ClassInviteLinkPanel = ({ classId, defaultOpen = true, title = 'Convite Link', collapsible = true, showExport = false, showStudentCodes = true, showCourseLinks = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [exporting, setExporting] = useState(false);

  const load = React.useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const { data: res, error } = await supabase.rpc('get_class_invite_codes', { p_class_id: classId });
      if (error) throw error;
      setData(res && typeof res === 'object' ? res : { links: [], codes: [] });
    } catch (err) {
      console.error('Error loading class invite codes:', err);
      setData({ links: [], codes: [] });
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => { load(); }, [load]);

  const copyText = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((cur) => (cur === key ? '' : cur)), 1800);
  };

  const links = data?.links || [];
  const codeRows = data?.codes || [];
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const isExpanded = collapsible ? isOpen : true;

  // Status do código: Ativo (já resgatado) > Expirado (passou da validade) > Pendente.
  const statusOf = (row) => {
    if (!row) return 'Pendente';
    if (row.redeemed_at) return 'Ativo';
    const expiresAt = row.expires_at ? new Date(row.expires_at).getTime() : null;
    if (expiresAt && expiresAt < Date.now()) return 'Expirado';
    return 'Pendente';
  };
  const statusClass = {
    Pendente: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
    Ativo: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    Expirado: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  };

  const coursesById = new Map();
  const studentsById = new Map();
  codeRows.forEach((row) => {
    if (!coursesById.has(row.course_id)) coursesById.set(row.course_id, { id: row.course_id, title: row.course_title });
    if (!studentsById.has(row.student_id)) studentsById.set(row.student_id, { id: row.student_id, name: row.student_name, email: row.student_email });
  });
  const pivotCourses = Array.from(coursesById.values());
  const pivotStudents = Array.from(studentsById.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }));
  const linkPathByCourse = new Map(links.map((l) => [l.course_id, l.path || '']));
  // Lista plana (uma linha por aluno × curso), no mesmo padrão da tela de Relatórios.
  const sortedCodeRows = [...codeRows].sort((a, b) => {
    const byStudent = String(a.student_name || '').localeCompare(String(b.student_name || ''), 'pt-BR', { sensitivity: 'base' });
    if (byStudent !== 0) return byStudent;
    return String(a.course_title || '').localeCompare(String(b.course_title || ''), 'pt-BR', { sensitivity: 'base' });
  });
  const formatExpiry = (value, withTime = false) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('pt-BR', withTime
        ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '-'; }
  };
  // A validade do código é a mesma da matrícula: data e hora de encerramento da turma.
  const expiryLabel = (() => {
    const withDate = codeRows.find((r) => r.expires_at);
    if (!withDate) return 'Sem validade definida';
    return formatExpiry(withDate.expires_at, true);
  })();

  const handleExport = async () => {
    if (codeRows.length === 0) return;
    setExporting(true);
    try {
      await exportRowsToExcel({
        fileName: `convite-link-${classId}.xlsx`,
        sheetName: 'Convite Link',
        headers: ['Aluno', 'E-mail', 'Curso', 'Código', 'Status', 'Expira em', 'Link do curso'],
        rows: codeRows.map((row) => [
          row.student_name || 'Aluno',
          row.student_email || '',
          row.course_title || '',
          row.code,
          statusOf(row),
          formatExpiry(row.expires_at, true),
          origin + (linkPathByCourse.get(row.course_id) || ''),
        ]),
      });
    } catch (err) {
      console.error('Error exporting class invite codes:', err);
      alert('Não foi possível exportar os códigos. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={`rounded-lg border border-teal-500/30 bg-slate-950/60 overflow-hidden ${collapsible ? 'mt-3' : ''}`}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-900/60 transition"
        >
          <span className="flex items-center gap-2 text-sm font-bold text-white">
            <KeyRound size={16} className="text-teal-400" /> {title}
            <span className="ml-1 rounded-full border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {pivotStudents.length} aluno(s) · {pivotCourses.length} curso(s)
            </span>
          </span>
          <span className="flex items-center gap-2 text-[11px] font-medium text-slate-400">
            {loading ? 'Carregando…' : 'Válido até ' + expiryLabel}
            <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </span>
        </button>
      ) : (
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-2 text-sm font-bold text-white">
              <KeyRound size={16} className="text-teal-400" /> {title}
              <span className="ml-1 rounded-full border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {pivotStudents.length} aluno(s) · {pivotCourses.length} curso(s)
              </span>
            </span>
            <span className="text-[11px] font-medium text-slate-400">
              {loading ? 'Carregando…' : 'Validade dos códigos: ' + expiryLabel}
            </span>
          </div>
          {showExport && (
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading || codeRows.length === 0}
              title="Exportar a relação de códigos para Excel"
              className="shrink-0 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-600/10 px-4 py-2 text-sm font-bold text-emerald-400 transition hover:border-emerald-500/50 hover:bg-emerald-600/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileSpreadsheet size={16} /> {exporting ? 'Exportando…' : 'Exportar Excel'}
            </button>
          )}
        </div>
      )}

      {isExpanded && (
        <div className={`p-4 flex flex-col gap-4 ${collapsible ? 'border-t border-slate-800' : ''}`}>
          {/* Links diretos por curso */}
          {showCourseLinks && (
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Links por curso</span>
            {links.length === 0 ? (
              <span className="text-sm text-slate-500 italic">Nenhum curso vinculado à turma.</span>
            ) : (
              links.map((link) => {
                const fullUrl = origin + (link.path || '');
                const key = `link-${link.course_id}`;
                return (
                  <div key={link.course_id} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-teal-300 truncate">{link.course_title}</p>
                      <p className="text-[11px] text-slate-400 truncate">{fullUrl}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyText(fullUrl, key)}
                      className="shrink-0 flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-[11px] font-bold text-slate-300 hover:text-white hover:border-slate-500 transition"
                    >
                      {copiedKey === key ? <Check size={13} className="text-teal-400" /> : <Copy size={13} />}
                      {copiedKey === key ? 'Copiado' : 'Copiar'}
                    </button>
                    <a
                      href={fullUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir o link do curso em uma nova aba"
                      className="shrink-0 flex items-center gap-1.5 rounded-md border border-teal-500/40 bg-teal-500/10 px-2.5 py-1.5 text-[11px] font-bold text-teal-300 hover:text-white hover:border-teal-400 transition"
                    >
                      <ExternalLink size={13} /> Abrir
                    </a>
                  </div>
                );
              })
            )}
          </div>
          )}

          {/* Códigos: lista plana aluno × curso (padrão da tela de Relatórios) */}
          {showStudentCodes && (
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Alunos, códigos e status</span>
            {codeRows.length === 0 ? (
              <span className="text-sm text-slate-500 italic">Nenhum aluno vinculado à turma.</span>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <table className="w-full table-fixed text-left">
                  <thead className="bg-slate-950/90 text-slate-200 text-xs uppercase font-semibold shadow-[inset_0_-1px_0_rgba(51,65,85,0.55)]">
                    <tr>
                      <th className="px-3 py-3 w-[220px]">Aluno</th>
                      <th className="px-3 py-3 w-[220px]">Curso</th>
                      <th className="px-3 py-3 w-[150px] text-center">Código</th>
                      <th className="px-3 py-3 w-[110px] text-center">Status</th>
                      <th className="px-3 py-3 w-[140px] text-center">Expira em</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50 text-sm">
                    {sortedCodeRows.map((row) => {
                      const key = `code-${row.student_id}-${row.course_id}`;
                      const status = statusOf(row);
                      return (
                        <tr key={key} className="hover:bg-slate-800/50 transition">
                          <td className="px-3 py-3 text-white font-medium">
                            <span className="truncate block max-w-[220px]" title={row.student_name || 'Aluno'}>
                              {row.student_name || 'Aluno'}
                            </span>
                            {row.student_email && (
                              <span className="truncate block max-w-[220px] text-[11px] text-slate-500" title={row.student_email}>
                                {row.student_email}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-slate-300">
                            <span className="truncate block max-w-[220px]" title={row.course_title || '-'}>
                              {row.course_title || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => copyText(row.code, key)}
                              title="Copiar código"
                              className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 font-mono text-sm font-bold tracking-[0.2em] transition ${status === 'Pendente' ? 'border-slate-700 bg-slate-950 text-white hover:border-slate-500' : 'border-slate-800 bg-slate-900/70 text-slate-400 hover:border-slate-600'}`}
                            >
                              {row.code}
                              {copiedKey === key ? <Check size={12} className="text-teal-400" /> : <Copy size={12} className="text-slate-500" />}
                            </button>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusClass[status]}`}>
                              {status}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-xs whitespace-nowrap text-center">
                            <span className="font-mono text-slate-300">{formatExpiry(row.expires_at, true)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-500">
              <span>O aluno abre o link do curso e digita o código para liberar o acesso. A validade do código é a mesma da matrícula.</span>
              <span className="flex items-center gap-2">
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statusClass.Pendente}`}>Pendente</span>
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statusClass.Ativo}`}>Ativo</span>
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statusClass.Expirado}`}>Expirado</span>
              </span>
            </div>
          </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-[11px] font-bold text-slate-300 hover:text-white hover:border-slate-500 transition disabled:opacity-50"
            >
              <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} /> Atualizar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminClasses = ({ partner, courses, classes, setClasses, companies, partners, users, onViewClass }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', courses: [], status: 'Ativo', companyIds: [], partnerIds: [], area: [], cargo: [], startDate: '', startTime: '00:00', durationDays: '', vacancies: '', enrollmentMode: 'auto', inviteMode: 'system', notifyEmail: false, notifyWhatsapp: false });
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', content: '' });

  // Progresso médio por turma (coluna PROGRESSO) e filtros da listagem.
  const [classProgressById, setClassProgressById] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [companyFilters, setCompanyFilters] = useState([]);
  const [partnerFilters, setPartnerFilters] = useState([]);
  const [courseFilters, setCourseFilters] = useState([]);

  // Catálogos de Cargo/Área das empresas selecionadas: são os critérios de vínculo da turma.
  const [cargoCatalogOptions, setCargoCatalogOptions] = useState([]);
  const [areaCatalogOptions, setAreaCatalogOptions] = useState([]);

  // Empresas da Base Única (Core) que ainda não existem localmente: entram no
  // seletor de empresa do modal e são espelhadas ao salvar a turma.
  const [coreCompanies, setCoreCompanies] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('core_base_cache')
          .select('dataset')
          .eq('id', 'current')
          .maybeSingle();
        if (cancelled) return;
        const ds = data?.dataset;
        setCoreCompanies(Array.isArray(ds?.companies) ? ds.companies : []);
      } catch {
        // Silencioso: segue apenas com as empresas locais.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadClassProgress = async () => {
    try {
      const { data, error } = await supabase.rpc('get_classes_reports_progress');
      if (error) throw error;
      const progressMap = {};
      (data || []).forEach(row => {
        progressMap[String(row.class_id)] = Math.round(Number(row.avg_progress || 0));
      });
      setClassProgressById(progressMap);
    } catch (err) {
      console.error('Error loading class progress:', err);
    }
  };

  useEffect(() => {
    loadClassProgress();
  }, []);

  // Atualiza um campo do formulário, seja no modo criação ou edição.
  const setField = (key, value) => {
    if (editing) {
      setEditing((prev) => ({ ...prev, [key]: value }));
    } else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  // Cargo/Área são critérios da turma: o catálogo vem das empresas selecionadas.
  const formCompanyIds = ((editing || form).companyIds || []).map(String).sort();
  const formCompanyIdsKey = formCompanyIds.join(',');
  useEffect(() => {
    let cancelled = false;
    const loadCatalogOptions = async () => {
      if (formCompanyIds.length === 0) {
        setCargoCatalogOptions([]);
        setAreaCatalogOptions([]);
        return;
      }
      try {
        // O catálogo de áreas/cargos de empresas vindas do Core vive no snapshot
        // da Base Única (dataset.company_areas / dataset.company_positions).
        // O catálogo local (company_cargos/company_areas) é mantido como complemento.
        // O central_company_id é lido direto do banco: o estado global 'companies'
        // pode ter sido sobrescrito pela aba de Empresas sem esse campo.
        // Empresas do Core entram no seletor com o prefixo 'core-': não são UUID
        // local, então ficam fora das consultas por id local.
        const localCompanyIds = formCompanyIds.filter(id => !id.startsWith('core-'));
        const coreCompanyIds = formCompanyIds.filter(id => id.startsWith('core-')).map(id => id.slice(5));
        const [cargosRes, areasRes, companiesRes, cacheRes] = await Promise.all([
          localCompanyIds.length
            ? supabase.from('company_cargos').select('name').in('company_id', localCompanyIds)
            : Promise.resolve({ data: [] }),
          localCompanyIds.length
            ? supabase.from('company_areas').select('name').in('company_id', localCompanyIds)
            : Promise.resolve({ data: [] }),
          localCompanyIds.length
            ? supabase.from('companies').select('id, central_company_id').in('id', localCompanyIds)
            : Promise.resolve({ data: [] }),
          supabase.from('core_base_cache').select('dataset').eq('id', 'current').maybeSingle()
        ]);
        if (cancelled) return;

        const cargoNames = new Set();
        const areaNames = new Set();
        (cargosRes.data || []).forEach(row => {
          const name = String(row?.name ?? '').trim();
          if (name) cargoNames.add(name);
        });
        (areasRes.data || []).forEach(row => {
          const name = String(row?.name ?? '').trim();
          if (name) areaNames.add(name);
        });

        const ds = cacheRes?.data?.dataset;
        if (ds && typeof ds === 'object') {
          const coreAreas = Array.isArray(ds.company_areas) ? ds.company_areas : [];
          const corePositions = Array.isArray(ds.company_positions) ? ds.company_positions : [];
          const centralIdByCompanyId = new Map(
            (companiesRes.data || []).map(c => [String(c.id), String(c.central_company_id || '')])
          );
          // Empresa local usa central_company_id; empresa do Core já é o próprio id.
          const centralIds = new Set(
            localCompanyIds
              .map(id => centralIdByCompanyId.get(String(id)) || String(id))
              .filter(Boolean)
          );
          coreCompanyIds.forEach(cid => { if (cid) centralIds.add(cid); });
          coreAreas.forEach(a => {
            const name = String(a?.name ?? '').trim();
            if (name && centralIds.has(String(a?.company_id))) areaNames.add(name);
          });
          corePositions.forEach(p => {
            const name = String(p?.name ?? '').trim();
            if (name && centralIds.has(String(p?.company_id))) cargoNames.add(name);
          });
        }

        const toOptions = (names) =>
          Array.from(names)
            .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
            .map(name => ({ id: name, name }));
        setCargoCatalogOptions(toOptions(cargoNames));
        setAreaCatalogOptions(toOptions(areaNames));
      } catch (err) {
        console.error('Error loading cargo/area catalogs:', err);
      }
    };
    loadCatalogOptions();
    return () => { cancelled = true; };
  }, [formCompanyIdsKey]);

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          class_courses(course_id),
          class_students(student_id)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const formatted = (data || []).map(normalizeClassRow);
      setClasses(formatted);
    } catch (err) {
      console.error('Error loading classes:', err);
    }
  };

  const handleView = (c) => {
      if (onViewClass) {
          onViewClass(c);
      } else {
          setInfoModal({
            isOpen: true,
            title: `Visualizando Turma: ${c.name}`,
            content: `Alunos: ${c.students?.length || 0}`
          });
      }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const model = editing || form;
    
    // Empresas/parceiros como listas; o status é derivado da data de início.
    const startYmd = brDateToYmd(model.startDate);
    const endYmd = startYmd && model.durationDays ? addDaysToYmd(startYmd, model.durationDays) : '';
    const derivedStatus = deriveClassStatus(model.status, startYmd, endYmd);

    const payload = {
        id: model.id,
        name: model.name,
        companyIds: Array.isArray(model.companyIds) ? model.companyIds : [],
        partnerIds: Array.isArray(model.partnerIds) ? model.partnerIds : [],
        status: derivedStatus,
        courses: model.courses,
        settings: {
            area: Array.isArray(model.area) ? model.area : [],
            cargo: Array.isArray(model.cargo) ? model.cargo : [],
            startDate: startYmd,
            startTime: model.startTime || '00:00',
            durationDays: model.durationDays || '',
            endDate: endYmd,
            vacancies: model.vacancies || '',
            enrollmentMode: model.enrollmentMode === 'manual' ? 'manual' : 'auto',
            inviteMode: model.inviteMode === 'link' ? 'link' : 'system',
            notifyEmail: model.notifyEmail === true,
            notifyWhatsapp: model.notifyWhatsapp === true
        }
    };

    try {
        // Empresas que só existem na Base Única (id prefixado 'core-') precisam ser
        // espelhadas localmente antes de salvar: a turma grava o id local da empresa.
        const resolvedCompanyIds = [];
        for (const companyId of payload.companyIds.map(String)) {
            if (!companyId.startsWith('core-')) {
                resolvedCompanyIds.push(companyId);
                continue;
            }
            const option = companyFormOptions.find(o => String(o.id) === companyId);
            const centralCompanyId = option?.central_company_id || companyId.slice(5);
            const { data: mirroredId, error: mirrorError } = await supabase.rpc('core_mirror_company', {
                p_central_company_id: String(centralCompanyId),
            });
            if (mirrorError) throw new Error(`Falha ao espelhar empresa do Core: ${mirrorError.message}`);
            if (!mirroredId) throw new Error('Não foi possível criar a empresa a partir da Base Core.');
            resolvedCompanyIds.push(String(mirroredId));
        }
        payload.companyIds = resolvedCompanyIds;

        console.log('Saving class with payload:', payload);

        const { data, error } = await supabase.rpc('save_class_full', {
            p_class_data: payload
        });
        
        if (error) throw error;
        
        await fetchClasses();
        await loadClassProgress();
        setShowModal(false);
        setEditing(null);
        setForm({ name: '', courses: [], status: 'Ativo', companyIds: [], partnerIds: [], area: [], cargo: [], startDate: '', startTime: '00:00', durationDays: '', vacancies: '', enrollmentMode: 'auto', inviteMode: 'system', notifyEmail: false, notifyWhatsapp: false });

        // Aviso de lotação: vagas definidas e total de alunos atingiu o limite.
        const savedVacancies = Number(data?.vacancies) || 0;
        const savedStudents = Number(data?.student_count) || 0;
        if (savedVacancies > 0 && savedStudents >= savedVacancies) {
            alert(`Turma salva. Atenção: limite de vagas atingido — ${savedStudents} de ${savedVacancies} vaga(s) preenchida(s). Colaboradores elegíveis além do limite não foram incluídos.`);
        } else {
            setNotification({ message: 'Turma salva com sucesso!', type: 'success' });
        }
    } catch (err) {
        console.error('Error saving class:', err);
        alert('Erro ao salvar turma: ' + err.message);
    }
  };

  const handleDelete = (id) => {
      setConfirmModal({
        isOpen: true,
        title: 'Excluir Turma',
        message: 'Tem certeza que deseja excluir esta turma?',
        onConfirm: async () => {
          try {
              const { error } = await supabase.from('classes').delete().eq('id', id);
              if (error) throw error;
              setClasses(classes.filter(c => c.id !== id));
              setNotification({ message: 'Turma excluída com sucesso!', type: 'success' });
          } catch (err) {
              alert('Erro ao excluir: ' + err.message);
          }
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      });
  };

  const activeModel = editing || form;
  // Modo gravado da turma em edição: usado para avisar quando o modelo está mudando.
  const originalEnrollmentMode = editing
    ? ((classes || []).find(c => String(c.id) === String(editing.id))?.enrollmentMode || 'auto')
    : null;
  const originalInviteMode = editing
    ? ((classes || []).find(c => String(c.id) === String(editing.id))?.inviteMode || 'system')
    : null;
  const startYmd = brDateToYmd(activeModel.startDate);
  const computedEndYmd = startYmd && activeModel.durationDays ? addDaysToYmd(startYmd, activeModel.durationDays) : '';
  const courseOptions = (courses || []).map(c => ({ id: String(c.id), name: c.title || c.name || 'Curso sem nome' }));
  // Filtro da listagem: apenas empresas locais (a turma grava o id local).
  const companyOptions = (companies || []).map(c => ({ id: String(c.id), name: c.name }));
  // Seletor do modal: mescla local + Base Única. Empresa que só existe no Core
  // entra com o id prefixado 'core-' e é espelhada ao salvar a turma.
  const companyFormOptions = React.useMemo(() => {
    const localCentralIds = new Set(
      (Array.isArray(companies) ? companies : [])
        .map(c => String(c.central_company_id || ''))
        .filter(Boolean)
    );
    // Fallback: parte do estado global pode não carregar central_company_id
    // (ex.: escopo de Ponto Focal). Nesses casos o nome evita duplicar a empresa.
    const localNames = new Set(
      (Array.isArray(companies) ? companies : [])
        .map(c => String(c.name || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const merged = new Map();
    (Array.isArray(companies) ? companies : []).forEach(c => {
      merged.set(String(c.id), { id: String(c.id), name: c.name, fromCore: false });
    });
    (Array.isArray(coreCompanies) ? coreCompanies : []).forEach(c => {
      const cid = String(c.id || '');
      if (!cid || localCentralIds.has(cid)) return;
      const name = c.legal_name || c.trade_name || 'Empresa Core';
      if (localNames.has(String(name).trim().toLowerCase())) return;
      merged.set(`core-${cid}`, {
        id: `core-${cid}`,
        name,
        fromCore: true,
        central_company_id: cid,
      });
    });
    return Array.from(merged.values()).sort((a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR'));
  }, [companies, coreCompanies]);
  const partnerOptions = (partners || []).map(p => ({ id: String(p.id), name: p.name }));
  // Garante que valores já gravados (mesmo fora do catálogo atual) continuem visíveis/selecionáveis.
  const ensureSelectedOptions = (options, values) => {
    const byId = new Map(options.map(o => [String(o.id), o]));
    (values || []).forEach(v => {
      const id = String(v);
      if (id && !byId.has(id)) byId.set(id, { id, name: id });
    });
    return Array.from(byId.values());
  };
  const areaSelectOptions = ensureSelectedOptions(areaCatalogOptions, activeModel.area);
  const cargoSelectOptions = ensureSelectedOptions(cargoCatalogOptions, activeModel.cargo);
  const activeStatus = deriveClassStatus(activeModel.status, startYmd, computedEndYmd);

  // Pontos focais vinculados às empresas selecionadas (carregamento automático).
  const selectedCompanyIds = (activeModel.companyIds || []).map(String);
  const companyNameById = new Map((companies || []).map(c => [String(c.id), c.name]));
  const focalPoints = selectedCompanyIds.length === 0
    ? []
    : (users || [])
        .filter(u => u.profile === 'Ponto Focal')
        .map(u => {
          const linkedNames = (u.companyIds || [])
            .map(String)
            .filter(id => selectedCompanyIds.includes(id))
            .map(id => companyNameById.get(id))
            .filter(Boolean);
          return linkedNames.length ? { id: u.id, name: u.name, companyNames: linkedNames } : null;
        })
        .filter(Boolean);

  // Opções e filtragem da listagem (padrão dos Relatórios: autocomplete + multi-seleção).
  const statusFilterOptions = [
    { id: 'Ativo', name: 'Ativo' },
    { id: 'Programada', name: 'Programada' },
    { id: 'Inativo', name: 'Inativo' },
    { id: 'Encerrada', name: 'Encerrada' },
  ];
  const classAutocompleteOptions = (() => {
    const unique = new Map();
    (classes || []).forEach(c => {
      const rawName = String(c?.name ?? '').trim();
      const normalizedName = normalizeAdminReportStudentName(rawName);
      if (!rawName || !normalizedName || unique.has(normalizedName)) return;
      unique.set(normalizedName, rawName);
    });
    return Array.from(unique.values())
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
      .map(name => ({ id: name, name }));
  })();
  const normalizedSearchTerm = normalizeAdminReportStudentName(searchTerm);
  const normalizedCompanyFilters = companyFilters.map(String);
  const normalizedPartnerFilters = partnerFilters.map(String);
  const normalizedCourseFilters = courseFilters.map(String);
  const filteredClasses = (classes || []).filter((c) => {
    if (normalizedSearchTerm && !normalizeAdminReportStudentName(c?.name).includes(normalizedSearchTerm)) return false;
    if (statusFilters.length > 0 && !statusFilters.includes(c.status)) return false;
    if (normalizedCompanyFilters.length > 0 && !(c.companyIds || []).some(id => normalizedCompanyFilters.includes(String(id)))) return false;
    if (normalizedPartnerFilters.length > 0 && !(c.partnerIds || []).some(id => normalizedPartnerFilters.includes(String(id)))) return false;
    if (normalizedCourseFilters.length > 0 && !(c.courses || []).some(id => normalizedCourseFilters.includes(String(id)))) return false;
    return true;
  });
  const hasActiveClassFilters = Boolean(normalizedSearchTerm)
    || statusFilters.length > 0
    || companyFilters.length > 0
    || partnerFilters.length > 0
    || courseFilters.length > 0;
  const clearClassFilters = () => {
    setSearchTerm('');
    setStatusFilters([]);
    setCompanyFilters([]);
    setPartnerFilters([]);
    setCourseFilters([]);
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Turmas</h1>
          <p className="text-slate-400">Gerencie as turmas e cursos vinculados.</p>
        </div>
        <button onClick={() => { setForm({ name: '', courses: [], status: 'Ativo', companyIds: [], partnerIds: [], area: [], cargo: [], startDate: '', startTime: '00:00', durationDays: '', vacancies: '', enrollmentMode: 'auto', inviteMode: 'system', notifyEmail: false, notifyWhatsapp: false }); setShowModal(true); }} style={{ backgroundColor: partner.color }} className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition">
          <Plus size={20} /> Nova Turma
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Filtros e Busca</p>
          {hasActiveClassFilters && (
            <button
              type="button"
              onClick={clearClassFilters}
              className="text-xs font-bold text-slate-400 hover:text-white transition flex items-center gap-1.5"
            >
              <X size={14} /> Limpar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Turma</p>
            <ReportStudentAutocomplete
              value={searchTerm}
              onChange={setSearchTerm}
              options={classAutocompleteOptions}
              placeholder="Buscar turma por nome"
              emptyLabel="Nenhuma turma encontrada"
              clearAriaLabel="Limpar busca de turma"
            />
          </div>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Status</p>
            <ReportMultiSelect
              options={statusFilterOptions}
              value={statusFilters}
              onChange={setStatusFilters}
              placeholder="Todos os Status"
              allLabel="Todos os Status"
            />
          </div>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Empresa</p>
            <ReportCompaniesMultiSelect
              options={companyOptions}
              value={companyFilters}
              onChange={setCompanyFilters}
              placeholder="Todas as Empresas"
            />
          </div>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Parceiro</p>
            <ReportMultiSelect
              options={partnerOptions}
              value={partnerFilters}
              onChange={setPartnerFilters}
              placeholder="Todos os Parceiros"
              allLabel="Todos os Parceiros"
            />
          </div>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Curso</p>
            <ReportMultiSelect
              options={courseOptions}
              value={courseFilters}
              onChange={setCourseFilters}
              placeholder="Todos os Cursos"
              allLabel="Todos os Cursos"
            />
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
            <tr>
              <th className="p-5">Nome</th>
              <th className="p-5">Contexto</th>
              <th className="p-5">Alunos/Cursos</th>
              <th className="p-5">Período</th>
              <th className="p-5">Status</th>
              <th className="p-5">Progresso</th>
              <th className="p-5 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {filteredClasses.map(c => {
               const classCompanyNames = (c.companyIds || []).map(id => companies.find(comp => String(comp.id) === String(id))?.name).filter(Boolean);
               const classPartnerNames = (c.partnerIds || []).map(id => partners.find(p => String(p.id) === String(id))?.name).filter(Boolean);
               const classNameCompany = classCompanyNames.length ? classCompanyNames.join(', ') : '-';
               const classNamePartner = classPartnerNames.length ? classPartnerNames.join(', ') : '-';
               const progress = classProgressById[String(c.id)] ?? 0;
               return (
              <tr
                key={c.id}
                onClick={() => handleView(c)}
                className="hover:bg-slate-800/50 transition text-slate-300 cursor-pointer"
              >
                <td className="p-5">
                   <div className="font-bold text-white">{c.name}</div>
                   {/* Modo de matrícula da turma: Automática (ciano) x Manual (âmbar). */}
                   <span
                     className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold border uppercase"
                     style={c.enrollmentMode === 'manual'
                       ? { color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)', backgroundColor: 'rgba(245,158,11,0.1)' }
                       : { color: '#05BEB7', borderColor: 'rgba(5,190,183,0.35)', backgroundColor: 'rgba(5,190,183,0.12)' }}
                   >
                     {c.enrollmentMode === 'manual' ? 'Manual' : 'Automática'}
                   </span>
                   {/* Modo de convite da turma: Convite Sistema (padrão) x Convite Link. */}
                   {c.inviteMode === 'link' && (
                     <span
                       className="inline-block mt-1 ml-1 px-2 py-0.5 rounded text-[10px] font-bold border uppercase"
                       style={{ color: '#38bdf8', borderColor: 'rgba(56,189,248,0.35)', backgroundColor: 'rgba(56,189,248,0.12)' }}
                     >
                       Convite Link
                     </span>
                   )}
                </td>
                <td className="p-5 max-w-[170px]">
                   <div className="text-xs text-slate-400 truncate" title={classNameCompany}>Emp: <span className="text-white">{classNameCompany}</span></div>
                   <div className="text-xs text-slate-400 truncate" title={classNamePartner}>Par: <span className="text-white">{classNamePartner}</span></div>
                </td>
                <td className="p-5 font-mono text-xs">
                   <div>{c.students?.length || 0} alunos{Number(c.vacancies) > 0 ? ` / ${c.vacancies} vagas` : ''}
                      {Number(c.vacancies) > 0 && (c.students?.length || 0) >= Number(c.vacancies) && (
                        <span className="text-amber-400 font-bold"> · Lotada</span>
                      )}
                   </div>
                   <div>{c.courses?.length || 0} cursos</div>
                </td>
                <td className="p-5 text-xs whitespace-nowrap text-slate-300">
                   {[brDateShortYear(c.startDate), brDateShortYear(c.endDate)].filter(Boolean).join(' - ') || '-'}
                </td>
                <td className="p-5"><span className={`px-2 py-1 rounded text-[10px] font-bold border uppercase ${c.status === 'Ativo' ? 'border-teal-500/20 bg-teal-500/10 text-teal-400' : c.status === 'Programada' ? 'border-amber-500/20 bg-amber-500/10 text-amber-400' : c.status === 'Encerrada' ? 'border-slate-600/40 bg-slate-600/10 text-slate-400' : 'border-rose-500/20 bg-rose-500/10 text-rose-400'}`}>{c.status}</span></td>
                <td className="p-5">
                  <div className="flex items-center gap-2">
                     <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full" style={{ width: `${progress}%` }}></div>
                     </div>
                     <span className="text-xs font-bold text-teal-400">{progress}%</span>
                  </div>
                </td>
                <td className="p-5 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end">
                    <ClassActionsMenu
                      turma={c}
                      onEdit={(turma) => { setEditing(turma); setShowModal(true); }}
                      onDelete={handleDelete}
                    />
                  </div>
                </td>
              </tr>
            )})}
             {filteredClasses.length === 0 && (
              <tr>
                <td colSpan="7" className="p-8 text-center text-slate-500">
                  {hasActiveClassFilters ? 'Nenhuma turma encontrada com os filtros aplicados.' : 'Nenhuma matrícula cadastrada.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(showModal || editing) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl shadow-2xl p-5 my-6 max-h-[92vh] flex flex-col">
            <h3 className="text-lg font-bold text-white mb-3">{editing ? 'Editar Turma' : 'Nova Turma'}</h3>
            <form onSubmit={handleSave} className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1 -mr-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                 <div className="flex flex-col gap-1.5 md:col-span-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nome da Turma</label>
                    <input type="text" required value={editing ? editing.name : form.name} onChange={e => editing ? setEditing({...editing, name: e.target.value}) : setForm({...form, name: e.target.value})} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none" placeholder="Ex: Turma 2024.1" />
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status</label>
                    <select
                       value={activeStatus}
                       disabled
                       title="Definido automaticamente pelas datas da turma"
                       className="bg-slate-950/60 border border-slate-800 p-3 rounded-lg text-slate-300 outline-none cursor-not-allowed min-h-[48px]"
                    >
                        <option value="Ativo">Ativo</option>
                        <option value="Inativo">Inativo</option>
                        <option value="Programada">Programada</option>
                        <option value="Encerrada">Encerrada</option>
                    </select>
                    <span className="text-[11px] text-slate-500">
                       {activeStatus === 'Encerrada'
                          ? 'Automático: validade da turma encerrada.'
                          : activeStatus === 'Programada'
                            ? 'Automático: data de início futura.'
                            : 'Automático pelas datas da turma.'}
                    </span>
                 </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                 <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Matrícula</label>
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                       type="button"
                       onClick={() => setField('enrollmentMode', 'auto')}
                       className={`text-left rounded-lg border px-4 py-3 transition ${activeModel.enrollmentMode === 'manual' ? 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600' : 'bg-teal-500/10 border-teal-500/40 text-white'}`}
                    >
                       <span className="flex items-center gap-2 text-sm font-bold">
                          <UserCheck size={16} /> Matrícula Automática
                       </span>
                       <span className="mt-1 block text-[11px] text-slate-400">Entram automaticamente os usuários que cumprirem os critérios da turma.</span>
                    </button>
                    <button
                       type="button"
                       onClick={() => setField('enrollmentMode', 'manual')}
                       className={`text-left rounded-lg border px-4 py-3 transition ${activeModel.enrollmentMode === 'manual' ? 'bg-teal-500/10 border-teal-500/40 text-white' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'}`}
                    >
                       <span className="flex items-center gap-2 text-sm font-bold">
                          <UserPlus size={16} /> Matrícula Manual
                       </span>
                       <span className="mt-1 block text-[11px] text-slate-400">Os alunos precisam ser matriculados manualmente na turma.</span>
                    </button>
                 </div>
                 {/* Troca de modelo em turma existente: o passado não é afetado. */}
                 {editing && originalEnrollmentMode !== (activeModel.enrollmentMode || 'auto') && (
                    <span className="mt-2 block text-[11px] font-medium text-amber-400">
                       Alteração de modelo: os alunos já matriculados permanecem na turma. A nova regra passa a valer a partir de agora.
                    </span>
                 )}
              </div>

              <div className="flex flex-col gap-1.5">
                 <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Convite</label>
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                       type="button"
                       onClick={() => setField('inviteMode', 'system')}
                       className={`text-left rounded-lg border px-4 py-3 transition ${activeModel.inviteMode === 'link' ? 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600' : 'bg-teal-500/10 border-teal-500/40 text-white'}`}
                    >
                       <span className="flex items-center gap-2 text-sm font-bold">
                          <Mail size={16} /> Convite Sistema
                       </span>
                       <span className="mt-1 block text-[11px] text-slate-400">Fluxo atual: o sistema envia o convite/comunicado por e-mail.</span>
                    </button>
                    <button
                       type="button"
                       onClick={() => setField('inviteMode', 'link')}
                       className={`text-left rounded-lg border px-4 py-3 transition ${activeModel.inviteMode === 'link' ? 'bg-teal-500/10 border-teal-500/40 text-white' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'}`}
                    >
                       <span className="flex items-center gap-2 text-sm font-bold">
                          <LinkIcon size={16} /> Convite Link
                       </span>
                       <span className="mt-1 block text-[11px] text-slate-400">Gera um link por curso e um código de 4 dígitos por aluno e por curso. O convite pode ser enviado por WhatsApp e/ou e-mail (opt-in abaixo).</span>
                    </button>
                 </div>
                 {activeModel.inviteMode === 'link' && (
                    <>
                       {editing && originalInviteMode !== 'link' && (
                          <span className="mt-2 block text-[11px] font-medium text-amber-400">
                             Ao salvar, os links e códigos serão gerados e o comunicado de início deixa de ser enviado por e-mail para esta turma.
                          </span>
                       )}
                    </>
                 )}
              </div>
              </div>

              <div className="flex flex-col gap-1.5">
                 <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Comunicação automática</label>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3">
                       <div className="flex items-start gap-2">
                          <MessageCircle size={16} className="mt-0.5 shrink-0 text-slate-400" />
                          <div className="flex flex-col">
                             <span className="text-sm font-bold text-slate-200">Comunicação automática - WhatsApp</span>
                             <span className="text-[11px] text-slate-500">Envia o convite de acesso (link + código) por WhatsApp nas turmas de Convite Link.</span>
                          </div>
                       </div>
                       <ToggleSwitch checked={!!activeModel.notifyWhatsapp} onChange={() => setField('notifyWhatsapp', !activeModel.notifyWhatsapp)} ariaLabel="Comunicação automática - WhatsApp" />
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3">
                       <div className="flex items-start gap-2">
                          <Mail size={16} className="mt-0.5 shrink-0 text-slate-400" />
                          <div className="flex flex-col">
                             <span className="text-sm font-bold text-slate-200">Comunicação automática - E-mail</span>
                             <span className="text-[11px] text-slate-500">Envia o comunicado de início da turma por e-mail. No modelo Convite Link, envia o convite de acesso (link + código) por e-mail.</span>
                          </div>
                       </div>
                       <ToggleSwitch checked={!!activeModel.notifyEmail} onChange={() => setField('notifyEmail', !activeModel.notifyEmail)} ariaLabel="Comunicação automática - E-mail" />
                    </div>
                 </div>
                 {activeModel.inviteMode === 'link' && (
                    <span className="text-[11px] text-slate-500">No modelo Convite Link, os convites por WhatsApp e por e-mail são opcionais e controlados pelos toggles acima.</span>
                 )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Empresa</label>
                    <ReportMultiSelect
                       options={companyFormOptions}
                       value={activeModel.companyIds || []}
                       onChange={(next) => setField('companyIds', next)}
                       placeholder="Selecione as empresas..."
                       allLabel="Todas as Empresas"
                    />
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Parceiro SAS</label>
                    <ReportMultiSelect
                       options={partnerOptions}
                       value={activeModel.partnerIds || []}
                       onChange={(next) => setField('partnerIds', next)}
                       placeholder="Selecione os parceiros..."
                       allLabel="Todos os Parceiros"
                    />
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Vagas na Turma</label>
                    <input
                       type="number"
                       min="1"
                       placeholder="Ex: 50"
                       value={activeModel.vacancies || ''}
                       onChange={e => setField('vacancies', e.target.value)}
                       className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition min-h-[48px]"
                    />
                 </div>
              </div>

              <div className="flex flex-col gap-1.5">
                 <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Ponto Focal</label>
                 <div className="bg-slate-950/60 border border-slate-800 rounded-lg px-4 py-3 min-h-[48px] flex flex-wrap items-center gap-2">
                    {selectedCompanyIds.length === 0 ? (
                       <span className="text-sm text-slate-500">Selecione as empresas para carregar os pontos focais vinculados.</span>
                    ) : focalPoints.length === 0 ? (
                       <span className="text-sm text-slate-500">Nenhum ponto focal vinculado às empresas selecionadas.</span>
                    ) : (
                       focalPoints.map(fp => (
                          <span key={fp.id} className="inline-flex items-center gap-1.5 rounded-md border border-teal-500/20 bg-teal-500/10 px-2.5 py-1 text-xs font-bold text-teal-300">
                             {fp.name}
                             <span className="font-medium text-slate-400">· {fp.companyNames.join(', ')}</span>
                          </span>
                       ))
                    )}
                 </div>
                 <span className="text-[11px] text-slate-500">Carregado automaticamente a partir das empresas selecionadas.</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                 {activeModel.enrollmentMode === 'manual' ? (
                    <div className="flex flex-col justify-center gap-1 rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3 md:col-span-2">
                       <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Critérios de entrada</span>
                       <span className="text-sm text-slate-400">Matrícula manual: Área e Cargo não são usados. Matricule os alunos individualmente na turma.</span>
                    </div>
                 ) : (
                    <>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Área</label>
                    <ReportMultiSelect
                       options={areaSelectOptions}
                       value={activeModel.area || []}
                       onChange={(next) => setField('area', next)}
                       placeholder="Selecione as áreas..."
                       allLabel="Todas as Áreas"
                    />
                    <span className="text-[11px] text-slate-500">Critério da turma: o aluno entra ao casar com a Área.</span>
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cargo</label>
                    <ReportMultiSelect
                       options={cargoSelectOptions}
                       value={activeModel.cargo || []}
                       onChange={(next) => setField('cargo', next)}
                       placeholder="Selecione os cargos..."
                       allLabel="Todos os Cargos"
                    />
                    <span className="text-[11px] text-slate-500">Critério da turma: o aluno entra ao casar com o Cargo.</span>
                 </div>
                    </>
                 )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Data de Início</label>
                    <input
                       type="text"
                       inputMode="numeric"
                       placeholder="dd/mm/aaaa"
                       value={activeModel.startDate || ''}
                       onChange={e => setField('startDate', maskBrDate(e.target.value))}
                       className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                    />
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Horário de Início</label>
                    <input
                       type="time"
                       value={activeModel.startTime || '00:00'}
                       onChange={e => setField('startTime', e.target.value)}
                       className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                    />
                    <span className="text-[11px] text-slate-500">Fuso de São Paulo (SP).</span>
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Duração (dias)</label>
                    <input
                       type="number"
                       min="1"
                       placeholder="Ex: 30"
                       value={activeModel.durationDays || ''}
                       onChange={e => setField('durationDays', e.target.value)}
                       className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                    />
                 </div>
                 <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Data Fim</label>
                    <input
                       type="text"
                       readOnly
                       tabIndex={-1}
                       placeholder="Calculada automaticamente"
                       value={ymdToBrDate(computedEndYmd) ? `${ymdToBrDate(computedEndYmd)} 23:59` : ''}
                       className="bg-slate-950/60 border border-slate-800 p-3 rounded-lg text-slate-400 outline-none cursor-default"
                    />
                    <span className="text-[11px] text-slate-500">Encerra sempre às 23:59 (SP) do último dia vigente.</span>
                 </div>
              </div>

              <div className="flex flex-col gap-1.5">
                 <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cursos ({activeModel.courses.length})</label>
                 <ReportMultiSelect
                    options={courseOptions}
                    value={activeModel.courses || []}
                    onChange={(next) => setField('courses', next)}
                    placeholder="Selecione os cursos..."
                    allLabel="Todos os Cursos"
                 />
              </div>

              <div className="flex justify-end gap-3 mt-1">
                <button type="button" onClick={() => {setShowModal(false); setEditing(null);}} className="px-4 py-2 text-slate-400 font-bold">Cancelar</button>
                <button type="submit" style={{ backgroundColor: partner.color }} className="px-8 py-2 rounded-lg text-white font-bold shadow-lg">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};



const AdminAssessments = ({ partner, courses, assessments, setAssessments }) => {
  const replaceBankInputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    type: 'Prova',
    methodology: 'Múltipla Escolha',
    linkedCourses: [],
    questionBank: null
  });

  const [fileStats, setFileStats] = useState(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [showFileDetails, setShowFileDetails] = useState(false);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [viewingAssessment, setViewingAssessment] = useState(null);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isEditingAssessment, setIsEditingAssessment] = useState(false);
  const [assessmentCourseLabelsMap, setAssessmentCourseLabelsMap] = useState({});
  const [filters, setFilters] = useState({
    type: '',
    courseId: '',
    startDate: '',
    endDate: ''
  });
  const [assessmentPage, setAssessmentPage] = useState(1);
  const assessmentsPerPage = 6;
  const isMultipleChoiceMethodology = form.methodology === 'Múltipla Escolha';

  const createEmptyQuestion = (order) => ({
    order,
    statement: '',
    alternatives: [
      { id: 'a', text: '' },
      { id: 'b', text: '' }
    ],
    correctAnswer: { id: 'a' },
    explanation: ''
  });

  const getQuestionCorrectIds = (question) => {
    const raw = Array.isArray(question?.correctAnswer)
      ? question.correctAnswer
      : question?.correctAnswer
        ? [question.correctAnswer]
        : [];

    return raw
      .map((item) => String(item?.id || '').trim().toLowerCase())
      .filter(Boolean);
  };

  const normalizeQuestionsForSave = (questions, methodology) => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return { errors: [], questions: [] };
    }

    const errors = [];
    const normalizedQuestions = questions.map((question, index) => {
      const statement = String(question?.statement || '').trim();
      const explanation = String(question?.explanation || '').trim();
      const alternatives = (Array.isArray(question?.alternatives) ? question.alternatives : [])
        .map((alternative) => String(alternative?.text || '').trim())
        .filter(Boolean)
        .slice(0, 5)
        .map((text, alternativeIndex) => ({
          id: String.fromCharCode(97 + alternativeIndex),
          text
        }));

      if (!statement) {
        errors.push(`Questão ${index + 1}: informe o enunciado.`);
      }

      if (alternatives.length < 2) {
        errors.push(`Questão ${index + 1}: mantenha pelo menos 2 alternativas preenchidas.`);
      }

      const validAlternativeIds = new Set(alternatives.map((alternative) => alternative.id));
      const correctIds = getQuestionCorrectIds(question).filter((id) => validAlternativeIds.has(id));
      const uniqueCorrectIds = [...new Set(correctIds)];
      const isMultiple = methodology === 'Múltipla Escolha';

      if (isMultiple) {
        if (uniqueCorrectIds.length === 0) {
          errors.push(`Questão ${index + 1}: selecione ao menos uma alternativa correta.`);
        }
      } else if (uniqueCorrectIds.length !== 1) {
        errors.push(`Questão ${index + 1}: selecione exatamente uma alternativa correta.`);
      }

      return {
        order: index + 1,
        statement,
        alternatives,
        correctAnswer: isMultiple
          ? uniqueCorrectIds.map((id) => ({ id }))
          : (uniqueCorrectIds[0] ? { id: uniqueCorrectIds[0] } : null),
        explanation: explanation || null
      };
    });

    return { errors, questions: normalizedQuestions };
  };

  useEffect(() => {
    const loadAssessmentCourseLabels = async () => {
      const list = Array.isArray(assessments) ? assessments : [];
      const assessmentIds = list.map((item) => item?.id).filter(Boolean);

      if (assessmentIds.length === 0) {
        setAssessmentCourseLabelsMap({});
        return;
      }

      try {
        const { data, error } = await supabase
          .from('courses')
          .select('id, name, assessment_id')
          .in('assessment_id', assessmentIds);

        if (error) throw error;

        const nextMap = {};
        for (const row of data || []) {
          const key = String(row?.assessment_id || '');
          if (!key) continue;
          const label = row?.name || '';
          if (!label) continue;
          if (!Array.isArray(nextMap[key])) nextMap[key] = [];
          if (!nextMap[key].includes(label)) nextMap[key].push(label);
        }
        setAssessmentCourseLabelsMap(nextMap);
      } catch (err) {
        console.error('Error loading assessment-course labels:', err);
      }
    };

    loadAssessmentCourseLabels();
  }, [assessments]);

  const getLinkedCourseIds = (assessment) => {
    const assessmentId = String(assessment?.id || '');
    if (!assessmentId || !Array.isArray(courses)) return [];

    return courses
      .filter((course) => {
        const finalAssessmentMatch = String(course?.assessment_id || course?.assessmentId || '') === assessmentId;
        if (finalAssessmentMatch) return true;

        const modules = Array.isArray(course?.modules) ? course.modules : [];
        const lessonsFromModules = modules.reduce((acc, module) => {
          const lessons = Array.isArray(module?.lessons) ? module.lessons : [];
          return acc.concat(lessons);
        }, []);
        const standaloneLessons = Array.isArray(course?.standaloneLessons) ? course.standaloneLessons : [];
        const allLessons = [...lessonsFromModules, ...standaloneLessons];

        return allLessons.some((lesson) =>
          (Array.isArray(lesson?.contents) ? lesson.contents : []).some((content) =>
            content?.type === 'quiz' &&
            String(content?.assessmentId || content?.assessment_id || '') === assessmentId
          )
        );
      })
      .map((course) => String(course?.id || ''))
      .filter(Boolean);
  };

  const getLinkedCourseLabels = (assessment) => {
    const assessmentId = String(assessment?.id || '');
    const linkedCourseIds = getLinkedCourseIds(assessment);
    const labelsByEffectiveSource = (Array.isArray(courses) ? courses : [])
      .filter((course) => linkedCourseIds.includes(String(course?.id || '')))
      .map((course) => course?.title || course?.name || 'Curso sem nome')
      .filter(Boolean);

    if (labelsByEffectiveSource.length > 0) return [...new Set(labelsByEffectiveSource)];

    const byMap = assessmentCourseLabelsMap[assessmentId];
    return Array.isArray(byMap) ? byMap : [];
  };

  const courseFilterOptions = (Array.isArray(courses) ? courses : [])
    .map((course) => ({
      id: String(course?.id || ''),
      name: course?.title || course?.name || ''
    }))
    .filter((item) => item.id && item.name)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const filteredAssessments = (Array.isArray(assessments) ? assessments : []).filter((assessment) => {
    if (filters.type) {
      const normalized = String(assessment?.type || '').toLowerCase();
      if (filters.type === 'exam' && normalized !== 'exam') return false;
      if (filters.type === 'quiz' && normalized !== 'quiz') return false;
    }

    if (filters.courseId) {
      const linkedIds = getLinkedCourseIds(assessment);
      if (!linkedIds.includes(String(filters.courseId))) return false;
    }

    const createdAt = assessment?.created_at ? new Date(assessment.created_at) : null;
    if (filters.startDate) {
      const start = new Date(`${filters.startDate}T00:00:00`);
      if (!createdAt || createdAt < start) return false;
    }
    if (filters.endDate) {
      const end = new Date(`${filters.endDate}T23:59:59`);
      if (!createdAt || createdAt > end) return false;
    }

    return true;
  });

  const filteredCounters = filteredAssessments.reduce(
    (acc, assessment) => {
      const normalizedType = String(assessment?.type || '').toLowerCase();
      if (normalizedType === 'exam') acc.exams += 1;
      if (normalizedType === 'quiz') acc.quizzes += 1;

      const linkedIds = getLinkedCourseIds(assessment);
      linkedIds.forEach((id) => acc.courseIds.add(String(id)));

      return acc;
    },
    { exams: 0, quizzes: 0, courseIds: new Set() }
  );

  const totalResults = filteredAssessments.length;
  const totalCoursesInResults = filteredCounters.courseIds.size;
  const assessmentTotalPages = Math.max(1, Math.ceil(filteredAssessments.length / assessmentsPerPage));
  const safeAssessmentPage = Math.min(assessmentPage, assessmentTotalPages);
  const paginatedAssessments = filteredAssessments.slice(
    (safeAssessmentPage - 1) * assessmentsPerPage,
    safeAssessmentPage * assessmentsPerPage
  );

  const fetchAssessments = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('assessments')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      if (setAssessments) setAssessments(data || []);
    } catch (err) {
      console.error('Error fetching assessments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  useEffect(() => {
    setAssessmentPage(1);
  }, [filters.type, filters.courseId, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (assessmentPage > assessmentTotalPages) {
      setAssessmentPage(assessmentTotalPages);
    }
  }, [assessmentPage, assessmentTotalPages]);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação? Todas as questões vinculadas também serão removidas.')) return;
    
    try {
      const { error } = await supabase.from('assessments').delete().eq('id', id);
      if (error) throw error;
      if (setAssessments) setAssessments(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert('Erro ao excluir avaliação: ' + err.message);
    }
  };

  const handleUpdateQuestion = (index, field, value) => {
    const updated = [...parsedQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setParsedQuestions(updated);
  };

  const handleUpdateAlternative = (qIndex, altIndex, value) => {
    const updated = [...parsedQuestions];
    updated[qIndex].alternatives[altIndex].text = value;
    setParsedQuestions(updated);
  };

  const handleToggleCorrect = (qIndex, altId) => {
    const updated = [...parsedQuestions];
    const q = updated[qIndex];
    const isMultiple = isMultipleChoiceMethodology;
    
    if (isMultiple) {
      let currentCorrect = Array.isArray(q.correctAnswer) ? q.correctAnswer : (q.correctAnswer ? [q.correctAnswer] : []);
      if (currentCorrect.some(c => c.id === altId)) {
        currentCorrect = currentCorrect.filter(c => c.id !== altId);
      } else {
        currentCorrect.push({ id: altId });
      }
      updated[qIndex].correctAnswer = currentCorrect;
    } else {
      updated[qIndex].correctAnswer = { id: altId };
    }
    setParsedQuestions(updated);
  };
  
  const handleAddAlternative = (qIndex) => {
     const updated = [...parsedQuestions];
     const q = updated[qIndex];
     if ((q.alternatives || []).length >= 5) return;
     const nextId = String.fromCharCode(97 + q.alternatives.length);
     q.alternatives.push({ id: nextId, text: '' });
     setParsedQuestions(updated);
  };

  const handleRemoveAlternative = (qIndex, altIndex) => {
     const updated = [...parsedQuestions];
     const question = updated[qIndex];
     if ((question?.alternatives || []).length <= 2) return;
     question.alternatives.splice(altIndex, 1);
     question.alternatives = question.alternatives.map((alt, idx) => ({
        ...alt,
        id: String.fromCharCode(97 + idx)
     }));
     const availableIds = new Set(question.alternatives.map((alt) => alt.id));
     if (isMultipleChoiceMethodology) {
      const filteredCorrect = getQuestionCorrectIds(question)
        .filter((id) => availableIds.has(id))
        .map((id) => ({ id }));
      question.correctAnswer = filteredCorrect.length > 0 ? filteredCorrect : [{ id: question.alternatives[0].id }];
     } else {
      const nextCorrectId = getQuestionCorrectIds(question).find((id) => availableIds.has(id)) || question.alternatives[0].id;
      question.correctAnswer = { id: nextCorrectId };
     }
     setParsedQuestions(updated);
  };

  const handleAddQuestion = () => {
    setParsedQuestions([
      ...parsedQuestions,
      createEmptyQuestion(parsedQuestions.length + 1)
    ]);
    setShowFileDetails(true);
  };

  const handleRemoveQuestion = (index) => {
     const updated = parsedQuestions.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i + 1 }));
     setParsedQuestions(updated);
  };

  const handleEdit = async (assessment) => {
    const linkedCourseIds = Array.isArray(courses)
      ? courses
          .filter((course) => {
            const finalAssessmentMatch = String(course?.assessment_id || course?.assessmentId || '') === String(assessment?.id || '');
            if (finalAssessmentMatch) return true;

            const modules = Array.isArray(course?.modules) ? course.modules : [];
            const lessonsFromModules = modules.reduce((acc, module) => {
              const lessons = Array.isArray(module?.lessons) ? module.lessons : [];
              return acc.concat(lessons);
            }, []);
            const standaloneLessons = Array.isArray(course?.standaloneLessons) ? course.standaloneLessons : [];
            const allLessons = [...lessonsFromModules, ...standaloneLessons];

            return allLessons.some((lesson) =>
              (Array.isArray(lesson?.contents) ? lesson.contents : []).some((content) =>
                content?.type === 'quiz' &&
                String(content?.assessmentId || content?.assessment_id || '') === String(assessment?.id || '')
              )
            );
          })
          .map((course) => String(course?.id || ''))
          .filter(Boolean)
      : [];

    setForm({
      id: assessment.id,
      title: assessment.title,
      type: assessment.type === 'exam' ? 'Prova' : 'Quizz',
      methodology: assessment.methodology,
      linkedCourses: [...new Set(linkedCourseIds)],
      questionBank: null
    });
    
    setViewingAssessment(null);
    setShowModal(true);
    setShowFileDetails(true);
    setFileStats(null);
    setIsEditingAssessment(true);
    setIsLoadingQuestions(true);

    try {
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .eq('assessment_id', assessment.id)
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      
      // Load questions for editing
      setAssessmentQuestions(data || []);
      
      // Also populate parsedQuestions just in case, though we will primarily use assessmentQuestions for the UI
      const formattedQuestions = (data || []).map(q => ({
        order: q.order_index,
        statement: q.statement,
        alternatives: q.alternatives,
        correctAnswer: q.correct_answer,
        explanation: q.explanation
      }));
      setParsedQuestions(formattedQuestions);
      
    } catch (err) {
      console.error('Error loading questions for edit:', err);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleView = async (assessment) => {
    setViewingAssessment(assessment);
    setIsLoadingQuestions(true);
    try {
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .eq('assessment_id', assessment.id)
        .order('order_index', { ascending: true });
      if (error) throw error;
      setAssessmentQuestions(data || []);
    } catch (err) {
      console.error('Error fetching questions:', err);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setForm({ title: '', type: 'Prova', methodology: 'Múltipla Escolha', linkedCourses: [], questionBank: null });
    setFileStats(null);
    setShowFileDetails(false);
    setParsedQuestions([]);
    setIsSaving(false);
    setAssessmentQuestions([]);
    setViewingAssessment(null);
    setIsEditingAssessment(false);
  };

  const handleSave = async (manualQuestions = null) => {
    if (!form.title) {
      alert('Por favor, informe o título da avaliação.');
      return;
    }

    const isEditing = !!form.id;
    const questionsToSave = Array.isArray(manualQuestions) ? manualQuestions : parsedQuestions;
    const { errors: validationErrors, questions: normalizedQuestions } = normalizeQuestionsForSave(questionsToSave, form.methodology);

    if (validationErrors.length > 0) {
      alert(`Revise o banco de questões antes de salvar:\n\n${validationErrors.join('\n')}`);
      return;
    }

    setIsSaving(true);

    try {
      const assessmentType = form.type === 'Prova' ? 'exam' : 'quiz';
      let assessmentId = form.id;

      if (isEditing) {
        // 1. Update Assessment
        const { error: updateError } = await supabase
          .from('assessments')
          .update({
            title: form.title,
            type: assessmentType,
            methodology: form.methodology,
            updated_at: new Date().toISOString()
          })
          .eq('id', form.id);
        
        if (updateError) throw updateError;

        // Se houver novas questões carregadas (via upload na edição) OU se estivermos editando manualmente
        if (normalizedQuestions.length > 0 && (form.questionBank || isEditingAssessment)) {
            // Delete old questions
            await supabase.from('questions').delete().eq('assessment_id', form.id);
            
            // Insert new ones
            let defaultQuestionType = 'single_choice';
            if (form.methodology === 'Múltipla Escolha') defaultQuestionType = 'multiple_choice';
            
            for (const q of normalizedQuestions) {
              let formattedCorrectAnswer = q.correctAnswer;
              if (defaultQuestionType === 'multiple_choice' && !Array.isArray(formattedCorrectAnswer)) {
                 formattedCorrectAnswer = [formattedCorrectAnswer];
              } else if (defaultQuestionType === 'single_choice' && Array.isArray(formattedCorrectAnswer)) {
                 formattedCorrectAnswer = formattedCorrectAnswer[0];
              }

              const { error: qError } = await supabase.rpc('create_question', {
                p_assessment_id: assessmentId,
                p_type: defaultQuestionType,
                p_statement: q.statement,
                p_alternatives: q.alternatives,
                p_correct_answer: formattedCorrectAnswer,
                p_explanation: q.explanation,
                p_order_index: q.order
              });

              if (qError) throw new Error(`Erro ao salvar questão ${q.order}: ${qError.message}`);
            }
        }

        // Update course links
        await supabase.from('course_assessments').delete().eq('assessment_id', form.id);
      } else {
         // 1. Create Assessment
         const { data: assessment, error: assessError } = await supabase
           .from('assessments')
           .insert({
             title: form.title,
             type: assessmentType,
             methodology: form.methodology,
             description: `Importado via upload em ${new Date().toLocaleDateString()}`
           })
           .select()
           .single();

         if (assessError) throw assessError;
         assessmentId = assessment.id;

         // 2. Insert Questions via RPC
         let defaultQuestionType = 'single_choice';
         if (form.methodology === 'Múltipla Escolha') defaultQuestionType = 'multiple_choice';
         
         for (const q of normalizedQuestions) {
           let formattedCorrectAnswer = q.correctAnswer;
           if (defaultQuestionType === 'multiple_choice' && !Array.isArray(formattedCorrectAnswer)) {
              formattedCorrectAnswer = [formattedCorrectAnswer];
           } else if (defaultQuestionType === 'single_choice' && Array.isArray(formattedCorrectAnswer)) {
              formattedCorrectAnswer = formattedCorrectAnswer[0];
           }

           const { error: qError } = await supabase.rpc('create_question', {
             p_assessment_id: assessmentId,
             p_type: defaultQuestionType,
             p_statement: q.statement,
             p_alternatives: q.alternatives,
             p_correct_answer: formattedCorrectAnswer,
             p_explanation: q.explanation,
             p_order_index: q.order
           });

           if (qError) throw new Error(`Erro ao salvar questão ${q.order}: ${qError.message}`);
         }
       }

      // 3. Link Courses (Common for both new and edit)
      if (form.linkedCourses && form.linkedCourses.length > 0) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const validLinks = form.linkedCourses
            .filter(id => uuidRegex.test(id))
            .map(courseId => ({
              course_id: courseId,
              assessment_id: assessmentId
            }));

        if (validLinks.length > 0) {
            const { error: linkError } = await supabase.from('course_assessments').insert(validLinks);
            if (linkError) console.error('Error linking courses:', linkError);
        }
      }

      await fetchAssessments(); // Refresh list from DB
      setShowSuccessModal(true);
      setShowModal(false);
      setViewingAssessment(null);
      setIsEditingAssessment(false);

    } catch (error) {
      console.error('Save failed:', error);
      alert(`Falha ao salvar avaliação: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (form.id && parsedQuestions.length > 0) {
      const shouldReplace = window.confirm('Ao importar uma nova planilha, o banco atual desta avaliação será substituído quando você salvar. Deseja continuar?');
      if (!shouldReplace) {
        e.target.value = '';
        return;
      }
    }

    setForm({ ...form, questionBank: file });
    setIsProcessingFile(true);
    setFileStats(null);
    setShowFileDetails(false);
    setParsedQuestions([]);

    try {
      const rows = await readXlsxFile(file);
      
      if (rows.length < 2) {
        setFileStats({ total: 0, errors: [{ row: 1, message: 'Arquivo vazio ou sem dados além do cabeçalho.' }], valid: false });
        setIsProcessingFile(false);
        return;
      }

      // 1. Identify Columns by Header (Row 1, index 0 in rows)
      const headerRow = rows[0].map(cell => String(cell || '').trim().toLowerCase());
      
      // Heuristic to find columns
      const getColIndex = (keywords) => headerRow.findIndex(h => keywords.some(k => h.includes(k)));
      
      // Correct Answer: Look for "correta", "gabarito", "resposta", "resp"
      let correctColIdx = getColIndex(['correta', 'gabarito', 'resposta', 'resp']);
      // Fallback: Check typical position L (index 11) if header fails or is ambiguous, but prioritize header match
      if (correctColIdx === -1 && rows[0].length > 11) correctColIdx = 11;

      // Statement: Look for "enunciado", "pergunta", "questão", "statement", "texto"
      let statementColIdx = getColIndex(['enunciado', 'pergunta', 'questão', 'statement', 'texto']);
      // If not found, use a placeholder logic (e.g. "Questão N") as per user template analysis

      // Alternatives: Look for columns starting with "alternativa", "opção", "escolha", "letra"
      // We will scan all columns to find alternatives
      const altColIndices = [];
      headerRow.forEach((h, idx) => {
          const lowerH = h.toLowerCase();
          if ((lowerH.startsWith('alternativa') || lowerH.startsWith('opção') || lowerH.startsWith('opcao') || lowerH.startsWith('escolha') || lowerH.startsWith('letra')) && !lowerH.includes('correta')) {
              altColIndices.push(idx);
          }
      });
      
      // If no alternatives found by header, assume B..I (1..8) if standard template
      if (altColIndices.length === 0) {
          for (let i = 1; i <= 8; i++) altColIndices.push(i);
      }

      const dataRows = rows.slice(1);
      const errors = [];
      const parsed = [];
      let validCount = 0;

      dataRows.forEach((row, index) => {
        const rowIndex = index + 2; 
        const isEmpty = row.every(cell => cell === null || cell === undefined || String(cell).trim() === '');
        
        if (isEmpty) return;

        // Extract Data based on detected columns
        const questionNumber = index + 1; // Auto-generate
        
        // Statement Logic:
        // If we found a column, use it. If not, use placeholder.
        const rawStatement = statementColIdx !== -1 ? row[statementColIdx] : null;
        let statement = rawStatement || `Questão ${questionNumber} (Enunciado não identificado)`;
        if (!statement || String(statement).trim() === '') statement = `Questão ${questionNumber} (Enunciado vazio)`;

        const correctRaw = correctColIdx !== -1 ? row[correctColIdx] : null;
        const explanation = null; 

        // Process Alternatives
        // We iterate through identified alternative columns.
        // Important: Map visual column index to logical ID (a, b, c, d...)
        // "Alternativa 1" -> 'a', "Alternativa 2" -> 'b', ...
        
        const alternatives = [];
        const validAltIds = []; // Track which IDs ('a', 'b'...) are valid (non-empty)

        altColIndices.forEach((colIdx, arrIdx) => {
            const cellValue = row[colIdx];
            // Determine ID: try to parse "Alternativa X" from header, else use array index
            let id = String.fromCharCode(97 + arrIdx); // default a, b, c...
            
            const headerText = headerRow[colIdx] || '';
            const match = headerText.match(/alternativa\s*(\d+)/);
            if (match) {
                const altNum = parseInt(match[1], 10);
                if (altNum > 0) id = String.fromCharCode(97 + (altNum - 1));
            }

            if (cellValue !== null && cellValue !== undefined && String(cellValue).trim() !== '') {
                alternatives.push({
                    id: id,
                    text: String(cellValue).trim()
                });
                validAltIds.push(id);
            }
        });

        // Extra check for "phantom" rows that passed isEmpty but have no content in mapped columns
        if ((!rawStatement || String(rawStatement).trim() === '') && alternatives.length === 0 && !correctRaw) {
             return;
        }

        if (alternatives.length < 2) {
            errors.push({ row: rowIndex, message: `Mínimo de 2 alternativas preenchidas necessárias. Encontradas: ${alternatives.length}.` });
            return;
        }

        // Process Correct Answer
        if (!correctRaw) {
            errors.push({ row: rowIndex, message: 'Resposta correta ausente.' });
            return;
        }

        const correctStr = String(correctRaw).trim();
        const parts = correctStr.split(/[\s,]+/); 
        const correctIds = [];
        let hasError = false;

        for (const part of parts) {
            const cleanPart = part.toLowerCase();
            if (cleanPart === '') continue;

            // Numeric (1, 2, 3...)
            if (!isNaN(Number(cleanPart))) {
                const num = Number(cleanPart);
                // Convert number to letter ID
                // 1 -> 'a', 2 -> 'b'
                const charCode = 97 + (num - 1);
                const char = String.fromCharCode(charCode);
                
                // Check if this ID corresponds to a valid (non-empty) alternative?
                // Or check if it is within valid range (1..8)
                const isValidRange = num >= 1 && num <= 8; // Assuming max 8 alts as per template
                
                if (isValidRange) {
                     // Check if this specific alternative is actually filled in this row
                     const targetAlt = alternatives.find(a => a.id === char);
                     if (targetAlt) {
                        correctIds.push(char);
                        console.log(`[Importação L${rowIndex}] Convertido número ${num} para letra '${char}'`);
                     } else {
                        errors.push({ row: rowIndex, message: `Resposta correta indica alternativa ${num} (${char}), mas ela está vazia nesta linha.` });
                        hasError = true;
                        break;
                     }
                } else {
                     errors.push({ row: rowIndex, message: `Número da resposta inválido: "${num}". Deve ser entre 1 e 8.` });
                     hasError = true;
                     break;
                }
            } 
            // Letter (A, B, C...)
            else if (cleanPart.length === 1 && cleanPart >= 'a' && cleanPart <= 'z') {
                // Validate if this letter exists in non-empty alternatives
                const targetAlt = alternatives.find(a => a.id === cleanPart);
                if (targetAlt) {
                    correctIds.push(cleanPart);
                } else {
                    errors.push({ row: rowIndex, message: `Resposta correta indica letra '${cleanPart.toUpperCase()}', mas essa alternativa está vazia ou não existe.` });
                    hasError = true;
                    break;
                }
            } else {
                errors.push({ row: rowIndex, message: `Formato da resposta inválido: "${part}". Use números (1-8) ou letras (A-H).` });
                hasError = true;
                break;
            }
        }
        
        if (hasError) return;
        
        if (correctIds.length === 0) {
             errors.push({ row: rowIndex, message: `Nenhuma resposta válida encontrada em: "${correctRaw}".` });
             return;
        }

        // Construct Correct Answer Object
        const correctAnswerObj = correctIds.map(id => ({ id }));

        validCount++;
        parsed.push({
            order: questionNumber,
            statement,
            alternatives,
            correctAnswer: correctIds.length === 1 ? correctAnswerObj[0] : correctAnswerObj,
            explanation: explanation || null
        });
      });

      setFileStats({
        total: validCount,
        errors: errors,
        valid: errors.length === 0
      });
      
      if (errors.length === 0) {
          setParsedQuestions(parsed);
          setShowFileDetails(true);
      }

    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setFileStats({
        total: 0,
        errors: [{ row: 0, message: 'Erro ao ler o arquivo Excel. Verifique o formato.' }],
        valid: false
      });
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleDownloadAssessmentTemplate = () => {
    downloadAssessmentQuestionTemplate();
  };

  const handleReplaceQuestionBank = () => {
    if (replaceBankInputRef.current) {
      replaceBankInputRef.current.value = '';
      replaceBankInputRef.current.click();
    }
  };

  const handleExportDraftQuestions = () => {
    if (parsedQuestions.length === 0) return;
    exportAssessmentQuestionsToExcel(
      form.title || viewingAssessment?.title || 'avaliacao',
      parsedQuestions.map((question) => ({
        statement: question.statement,
        alternatives: question.alternatives,
        correct_answer: question.correctAnswer,
        explanation: question.explanation
      }))
    );
  };

  const handleExportAssessmentQuestions = () => {
    if (!viewingAssessment || assessmentQuestions.length === 0) return;
    exportAssessmentQuestionsToExcel(viewingAssessment.title, assessmentQuestions);
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tighter mb-2">Provas & Avaliações</h1>
          <p className="text-slate-500 font-medium">Gerencie provas, testes e avaliações para os alunos.</p>
        </div>
        <button onClick={() => { handleClose(); setShowModal(true); }} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white px-6 py-3 rounded-lg font-bold shadow-lg shadow-orange-500/20 transition flex items-center gap-2">
          <Plus size={20} /> Nova Avaliação
        </button>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Tipo</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-40"
            >
              <option value="">Todos</option>
              <option value="exam">Prova</option>
              <option value="quiz">Quizz</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Curso</label>
            <select
              value={filters.courseId}
              onChange={(e) => setFilters({ ...filters, courseId: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-72"
            >
              <option value="">Todos os Cursos</option>
              {courseFilterOptions.map((course) => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Período (Início)</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Período (Fim)</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px]"
            />
          </div>

          <div className="flex items-end pb-0.5">
            <button
              onClick={() => setFilters({ type: '', courseId: '', startDate: '', endDate: '' })}
              className="text-slate-400 hover:text-white text-sm font-medium px-3 py-2 transition"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Total</p>
          <p className="text-2xl font-black text-white">{totalResults}</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Provas</p>
          <p className="text-2xl font-black text-orange-400">{filteredCounters.exams}</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Quizzes</p>
          <p className="text-2xl font-black text-purple-400">{filteredCounters.quizzes}</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Cursos</p>
          <p className="text-2xl font-black text-teal-400">{totalCoursesInResults}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <RefreshCcw size={48} className="text-orange-500 animate-spin mb-4" />
          <p className="text-slate-400 font-medium">Carregando avaliações...</p>
        </div>
      ) : filteredAssessments.length > 0 ? (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedAssessments.map((assessment) => {
            const linkedCourseLabels = getLinkedCourseLabels(assessment);

            return (
              <div key={assessment.id} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition group">
                <div className="flex justify-between items-start mb-4">
                  <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${assessment.type === 'exam' ? 'bg-orange-500/10 text-orange-400' : 'bg-purple-500/10 text-purple-400'}`}>
                    {assessment.type === 'exam' ? 'Prova' : 'Quizz'}
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => handleEdit(assessment)} className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition" title="Editar">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(assessment.id)} className="p-2 bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 rounded-lg transition" title="Excluir">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{assessment.title}</h3>
                <p className="text-slate-500 text-sm mb-3 line-clamp-2">{assessment.methodology || 'Metodologia não informada'}</p>

                {linkedCourseLabels.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {linkedCourseLabels.map((label) => (
                      <span key={`${assessment.id}-${label}`} className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-500/10 border border-blue-500/20 text-blue-400">
                        {label}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800/50">
                  <div className="flex items-center gap-2 text-slate-500 text-xs font-medium">
                    <Clock size={14} />
                    {new Date(assessment.created_at).toLocaleDateString()}
                  </div>
                  <button 
                    onClick={() => handleView(assessment)}
                    className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-sm font-bold transition"
                  >
                    Visualizar <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/70 px-2 py-1.5">
            <button
              onClick={() => setAssessmentPage(prev => Math.max(prev - 1, 1))}
              disabled={safeAssessmentPage === 1}
              className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-slate-800 text-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Página anterior"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="px-2 text-xs font-semibold text-slate-300">
              Página <span className="text-white">{safeAssessmentPage}</span> de <span className="text-white">{assessmentTotalPages}</span>
            </span>
            <button
              onClick={() => setAssessmentPage(prev => Math.min(prev + 1, assessmentTotalPages))}
              disabled={safeAssessmentPage === assessmentTotalPages}
              className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-slate-800 text-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Próxima página"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
        </>
      ) : (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 md:p-12 text-center">
          <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-slate-600" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Nenhuma avaliação encontrada</h3>
          <p className="text-slate-500 max-w-md mx-auto">Comece criando uma nova avaliação para testar o conhecimento dos alunos.</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10">
              <h3 className="text-xl font-bold text-white">{form.id ? 'Editar Avaliação' : 'Nova Avaliação'}</h3>
              <button onClick={handleClose} className="text-slate-500 hover:text-white transition"><X size={24} /></button>
            </div>
            
            <div className="p-4 md:p-8 overflow-y-auto custom-scrollbar">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-slate-400 mb-2">Título da Avaliação</label>
                  <input 
                    type="text" 
                    value={form.title} 
                    onChange={e => setForm({...form, title: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 transition outline-none" 
                    placeholder="Ex: Prova Final de Vendas" 
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-400 mb-2">Tipo</label>
                    <select 
                      value={form.type} 
                      onChange={e => setForm({...form, type: e.target.value})}
                      className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 transition outline-none"
                    >
                      <option value="Prova">Prova</option>
                      <option value="Quizz">Quizz</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-400 mb-2">Metodologia</label>
                    <select 
                      value={form.methodology} 
                      onChange={e => setForm({...form, methodology: e.target.value})}
                      className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 transition outline-none"
                    >
                      <option value="Múltipla Escolha">Múltipla Escolha</option>
                      <option value="Verdadeiro ou Falso">Verdadeiro ou Falso</option>
                      <option value="Escolha Única">Escolha Única</option>
                      <option value="Subjetiva">Subjetiva</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-8">
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="block text-sm font-bold text-slate-400">Banco de Questões (Excel)</label>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleDownloadAssessmentTemplate}
                          className="inline-flex items-center gap-2 text-xs text-orange-500 hover:text-orange-400 font-bold underline"
                        >
                          <Download size={14} />
                          Baixar Planilha Modelo
                        </button>
                        <button
                          type="button"
                          onClick={handleReplaceQuestionBank}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20 transition text-xs font-bold"
                        >
                          <FileUp size={14} />
                          Substituir Banco
                        </button>
                        <button
                          type="button"
                          onClick={handleExportDraftQuestions}
                          disabled={parsedQuestions.length === 0}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 transition text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Download size={14} />
                          Exportar Atual
                        </button>
                      </div>
                    </div>
                    <input
                      ref={replaceBankInputRef}
                      type="file"
                      accept=".xlsx, .xls, .csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <p className="text-xs text-slate-500 mb-3">
                      O upload de uma nova planilha substitui o banco atual desta avaliação quando você salvar. Você também pode editar manualmente abaixo.
                    </p>
                    <label className={`block w-full h-64 border border-dashed rounded-lg p-8 transition cursor-pointer group text-center flex flex-col items-center justify-center ${fileStats && !fileStats.valid ? 'border-rose-500/50 bg-rose-500/5' : 'border-slate-700 hover:border-orange-500/50 hover:bg-slate-900/50'}`}>
                      <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} className="hidden" />
                      {isProcessingFile ? (
                        <div className="flex flex-col items-center animate-pulse">
                           <RefreshCcw size={48} className="text-orange-500 mb-4 animate-spin" />
                           <span className="text-white font-bold text-lg">Processando arquivo...</span>
                        </div>
                      ) : (form.questionBank || form.id) ? (
                        <div className="flex flex-col items-center w-full">
                          <FileSpreadsheet size={48} className={`${fileStats && !fileStats.valid ? 'text-rose-500' : 'text-green-500'} mb-4`} />
                          <span className="text-white font-bold text-lg">{form.questionBank ? form.questionBank.name : 'Questões Carregadas do Banco'}</span>
                          <span className="text-sm text-slate-500 mt-2">Clique para {form.id ? 'substituir a planilha de questões' : 'trocar o arquivo'}</span>
                          
                          {fileStats && (
                            <div className="mt-6 w-full max-w-lg bg-slate-950/50 rounded-xl p-4 border border-slate-800">
                               <div className="flex justify-between items-center mb-2">
                                  <span className="text-slate-400 font-bold text-sm uppercase">Resumo da Importação</span>
                                  {fileStats.valid ? 
                                    <span className="text-green-400 text-xs font-bold flex items-center gap-1"><CheckCircle size={14} /> Válido</span> : 
                                    <span className="text-rose-400 text-xs font-bold flex items-center gap-1"><AlertCircle size={14} /> Atenção</span>
                                  }
                               </div>
                               <div className="flex items-center justify-between text-sm border-b border-slate-800 pb-3 mb-3">
                                  <span className="text-slate-300">Linhas Processadas:</span>
                                  <span className="text-white font-mono font-bold">{fileStats.total}</span>
                               </div>
                               {fileStats.errors.length > 0 && (
                                 <div className="mb-4">
                                   <div className="flex items-center justify-between text-sm mb-2">
                                      <span className="text-rose-400 font-bold">Erros Encontrados:</span>
                                      <span className="text-rose-400 font-mono font-bold">{fileStats.errors.length}</span>
                                   </div>
                                   <div className="max-h-32 overflow-y-auto custom-scrollbar space-y-1 pr-2">
                                      {fileStats.errors.map((err, eidx) => (
                                        <div key={eidx} className="text-[10px] text-rose-400 bg-rose-500/5 p-1.5 rounded border border-rose-500/20">
                                          <span className="font-bold mr-1">Linha {err.row}:</span> {err.message}
                                        </div>
                                      ))}
                                   </div>
                                 </div>
                               )}
                               
                               {parsedQuestions.length > 0 && (
                                 <button 
                                   onClick={(e) => { e.preventDefault(); setShowFileDetails(!showFileDetails); }}
                                   className="w-full mt-2 py-2 bg-slate-900 hover:bg-slate-800 text-slate-400 text-xs font-bold rounded border border-slate-800 transition flex items-center justify-center gap-2"
                                 >
                                   {showFileDetails ? 'Ocultar Banco de Questões' : 'Visualizar Banco de Questões'} <ChevronDown size={14} className={`transition ${showFileDetails ? 'rotate-180' : ''}`} />
                                 </button>
                               )}
                               
                               {showFileDetails && (
                                 <div className="mt-4 space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar text-left pr-2">
                                   {parsedQuestions.map((q, idx) => (
                                     <div key={idx} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4">
                                       <div className="flex gap-3 mb-3">
                                         <span className="shrink-0 w-6 h-6 bg-slate-800 text-slate-400 rounded flex items-center justify-center text-[10px] font-bold">{q.order}</span>
                                         <p className="text-white text-sm font-medium">{q.statement}</p>
                                       </div>
                                       
                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-9">
                                         {q.alternatives?.map((alt) => {
                                           const isCorrect = Array.isArray(q.correctAnswer) 
                                             ? q.correctAnswer.some(ca => ca.id === alt.id)
                                             : q.correctAnswer?.id === alt.id;
                                           
                                           return (
                                             <div key={alt.id} className={`p-2 rounded border text-[11px] flex items-center justify-between ${isCorrect ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-slate-950/50 border-slate-800 text-slate-500'}`}>
                                               <span><span className="font-bold uppercase mr-1">{alt.id})</span> {alt.text}</span>
                                               {isCorrect && <Check size={12} />}
                                             </div>
                                           );
                                         })}
                                       </div>
                                       {q.explanation && (
                                         <div className="mt-3 ml-9 p-2 bg-orange-500/5 border-l border-orange-500/30 text-[10px] text-slate-400 italic">
                                           {q.explanation}
                                         </div>
                                       )}
                                     </div>
                                   ))}
                                 </div>
                               )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <UploadCloud size={48} className="text-slate-600 group-hover:text-orange-500 transition mb-4" />
                          <span className="text-slate-400 group-hover:text-slate-300 transition font-medium text-lg">Clique para fazer upload</span>
                          <span className="text-sm text-slate-600 mt-2">Formatos aceitos: .xlsx, .xls, .csv</span>
                        </div>
                      )}
                    </label>
                  </div>

                  <div className="bg-slate-950/40 border border-slate-800 rounded-2xl p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                      <div>
                        <h4 className="text-white font-bold text-lg">Editor de Questões</h4>
                        <p className="text-slate-500 text-sm">
                          Edite enunciados, alternativas, explicações e {isMultipleChoiceMethodology ? 'as respostas corretas' : 'a resposta correta'} diretamente por aqui.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white font-bold text-sm transition shadow-lg shadow-orange-500/10"
                      >
                        <Plus size={16} />
                        Adicionar Questão
                      </button>
                    </div>

                    {parsedQuestions.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-8 text-center">
                        <p className="text-white font-semibold mb-2">Nenhuma questão carregada no editor</p>
                        <p className="text-slate-500 text-sm mb-4">
                          Use a planilha modelo para substituir o banco atual ou adicione questões manualmente.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                          <button
                            type="button"
                            onClick={handleReplaceQuestionBank}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20 transition text-sm font-bold"
                          >
                            <FileUp size={16} />
                            Substituir Banco
                          </button>
                          <button
                            type="button"
                            onClick={handleAddQuestion}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 transition text-sm font-bold"
                          >
                            <Plus size={16} />
                            Criar Manualmente
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {parsedQuestions.map((q, idx) => {
                          const correctIds = getQuestionCorrectIds(q);
                          return (
                            <div key={`${idx}-${q.order}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 md:p-5">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-3">
                                  <span className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm font-black flex items-center justify-center">
                                    {idx + 1}
                                  </span>
                                  <div>
                                    <p className="text-white font-bold">Questão {idx + 1}</p>
                                    <p className="text-slate-500 text-xs">
                                      {isMultipleChoiceMethodology ? 'Marque uma ou mais corretas.' : 'Marque apenas uma alternativa correta.'}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveQuestion(idx)}
                                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 hover:bg-rose-500/20 transition text-xs font-bold"
                                >
                                  <Trash2 size={14} />
                                  Remover Questão
                                </button>
                              </div>

                              <div className="space-y-4">
                                <div>
                                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Enunciado</label>
                                  <textarea
                                    value={q.statement}
                                    onChange={(e) => handleUpdateQuestion(idx, 'statement', e.target.value)}
                                    rows={3}
                                    className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 transition outline-none resize-y"
                                    placeholder="Digite o enunciado da questão"
                                  />
                                </div>

                                <div>
                                  <div className="flex items-center justify-between gap-3 mb-2">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest">Alternativas</label>
                                    <button
                                      type="button"
                                      onClick={() => handleAddAlternative(idx)}
                                      disabled={(q.alternatives || []).length >= 5}
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 transition text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <PlusIcon size={14} />
                                      Adicionar Alternativa
                                    </button>
                                  </div>
                                  <div className="space-y-3">
                                    {(q.alternatives || []).map((alt, altIdx) => {
                                      const isCorrect = correctIds.includes(String(alt.id || '').toLowerCase());
                                      return (
                                        <div key={`${alt.id}-${altIdx}`} className={`grid grid-cols-1 md:grid-cols-[auto,1fr,auto] gap-3 rounded-xl border p-3 ${isCorrect ? 'border-green-500/30 bg-green-500/5' : 'border-slate-800 bg-slate-950/50'}`}>
                                          <button
                                            type="button"
                                            onClick={() => handleToggleCorrect(idx, alt.id)}
                                            className={`min-w-[112px] px-3 py-2 rounded-lg text-xs font-bold transition ${isCorrect ? 'bg-green-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                                          >
                                            {isCorrect ? 'Correta' : (isMultipleChoiceMethodology ? 'Marcar Correta' : 'Definir Correta')}
                                          </button>
                                          <div className="flex items-center gap-2">
                                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black uppercase ${isCorrect ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
                                              {alt.id}
                                            </span>
                                            <input
                                              type="text"
                                              value={alt.text}
                                              onChange={(e) => handleUpdateAlternative(idx, altIdx, e.target.value)}
                                              className="w-full bg-transparent text-white placeholder:text-slate-600 focus:outline-none"
                                              placeholder={`Texto da alternativa ${String(alt.id || '').toUpperCase()}`}
                                            />
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveAlternative(idx, altIdx)}
                                            disabled={(q.alternatives || []).length <= 2}
                                            className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-rose-500/20 hover:text-rose-300 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                            title="Remover alternativa"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Explicação / Gabarito Comentado</label>
                                  <textarea
                                    value={q.explanation || ''}
                                    onChange={(e) => handleUpdateQuestion(idx, 'explanation', e.target.value)}
                                    rows={3}
                                    className="w-full bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-orange-500 transition outline-none resize-y"
                                    placeholder="Opcional: explique por que a alternativa correta está certa"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-800 flex items-center justify-between gap-3 bg-slate-900">
              <div className="flex flex-wrap gap-2">
                {(form.linkedCourses && form.linkedCourses.length > 0 && courses)
                  ? courses
                      .filter(c => form.linkedCourses.includes(c.id))
                      .map(c => (
                        <span key={c.id} className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-800 border border-slate-700 text-slate-300">{c.title}</span>
                      ))
                  : <span className="text-slate-500 text-sm">Sem cursos vinculados</span>
                }
              </div>
              <div className="flex gap-3">
                <button onClick={handleClose} className="px-6 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition font-bold">Cancelar</button>
                <button 
                  onClick={handleSave} 
                  disabled={isSaving}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white px-8 py-2 rounded-lg font-bold shadow-lg shadow-orange-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving && <RefreshCcw size={20} className="animate-spin" />}
                  {isSaving ? 'Salvando...' : 'Salvar Avaliação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewingAssessment && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10">
              <div>
                <h3 className="text-xl font-bold text-white">{viewingAssessment.title}</h3>
                <p className="text-slate-500 text-xs mt-1">
                  {viewingAssessment.type === 'exam' ? 'Prova' : 'Quizz'} • {viewingAssessment.methodology}
                </p>
              </div>
              <button onClick={() => setViewingAssessment(null)} className="text-slate-500 hover:text-white transition"><X size={24} /></button>
            </div>
            
            <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar bg-slate-950/20">
              {isLoadingQuestions ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <RefreshCcw size={40} className="text-orange-500 animate-spin mb-4" />
                  <p className="text-slate-400 font-medium">Carregando questões...</p>
                </div>
              ) : assessmentQuestions.length > 0 ? (
                <div className="space-y-6">
                  {assessmentQuestions.map((q, idx) => (
                    <div key={q.id} className="bg-slate-900/50 border border-slate-800/80 rounded-xl p-6 hover:border-slate-700 transition shadow-sm">
                      <div className="flex gap-4 mb-5">
                        <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-slate-800 to-slate-900 text-slate-400 rounded-lg flex items-center justify-center font-black text-sm border border-slate-700 shadow-inner">
                          {idx + 1}
                        </span>
                        <p className="text-white font-bold leading-relaxed pt-1">{q.statement}</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-12">
                        {q.alternatives?.map((alt) => {
                          const isCorrect = Array.isArray(q.correct_answer) 
                            ? q.correct_answer.some(ca => ca.id === alt.id)
                            : q.correct_answer?.id === alt.id;
                          
                          return (
                            <div key={alt.id} className={`p-3 rounded-lg border text-sm transition-all duration-300 flex items-center justify-between group ${isCorrect ? 'bg-green-500/10 border-green-500/30 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.05)]' : 'bg-slate-950/30 border-slate-800 text-slate-400 hover:border-slate-700'}`}>
                              <div className="flex items-center gap-3">
                                <span className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-black uppercase ${isCorrect ? 'bg-green-500 text-slate-950' : 'bg-slate-800 text-slate-500'}`}>
                                  {alt.id}
                                </span>
                                <span>{alt.text}</span>
                              </div>
                              {isCorrect && <Check size={16} className="text-green-500" />}
                            </div>
                          );
                        })}
                      </div>
                      
                      {q.explanation && (
                        <div className="mt-5 ml-12 p-4 bg-orange-500/5 border-l-2 border-orange-500/30 rounded-r-lg relative overflow-hidden group">
                          <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:opacity-10 transition-opacity">
                            <Info size={40} className="text-orange-500" />
                          </div>
                          <p className="text-[10px] font-black text-orange-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <Info size={12} /> Explicação do Gabarito
                          </p>
                          <p className="text-sm text-slate-400 italic leading-relaxed">{q.explanation}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-24 bg-slate-900/20 rounded-2xl border border-dashed border-slate-800">
                  <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-600">
                    <FileText size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Nenhuma questão vinculada</h3>
                  <p className="text-slate-500 max-w-xs mx-auto">Esta avaliação ainda não possui questões cadastradas em seu banco.</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-800 flex items-center justify-between gap-3 bg-slate-900">
              <button
                type="button"
                onClick={handleExportAssessmentQuestions}
                disabled={isLoadingQuestions || assessmentQuestions.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-bold transition shadow-lg border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                Exportar Questoes (Excel)
              </button>
              <button 
                onClick={() => setViewingAssessment(null)} 
                className="px-8 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-bold transition shadow-lg border border-slate-700"
              >
                Fechar Visualização
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminLibrary = ({ partner, courses }) => {
  const fileTypes = ['PDF', 'Word', 'PPT', 'HTML', 'Áudio', 'Vídeo', 'Excel', 'Link', 'Outro'];

  const ensureValidCategoryId = async (categoryId) => {
    const cid = String(categoryId || '').trim();
    if (!cid) return null;

    try {
      const { data, error } = await supabase.from('course_categories').select('id').eq('id', cid).limit(1);
      if (!error && Array.isArray(data) && data[0]?.id) return data[0].id;
    } catch {}

    throw new Error('Categoria selecionada não existe mais. Selecione novamente.');
  };

  const getIcon = (type) => {
    switch (type) {
      case 'PDF': return <FileText size={24} />;
      case 'Word': return <FileText size={24} />;
      case 'PPT': return <FileText size={24} />;
      case 'HTML': return <FileCode size={24} />;
      case 'Excel': return <FileSpreadsheet size={24} />;
      case 'Áudio': return <Headphones size={24} />;
      case 'Vídeo': return <Video size={24} />;
      case 'Link': return <LinkIcon size={24} />;
      default: return <File size={24} />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'PDF': return 'bg-red-500/10 text-red-400';
      case 'Word': return 'bg-blue-500/10 text-blue-400';
      case 'PPT': return 'bg-orange-500/10 text-orange-400';
      case 'HTML': return 'bg-emerald-500/10 text-emerald-400';
      case 'Excel': return 'bg-green-500/10 text-green-400';
      case 'Áudio': return 'bg-purple-500/10 text-purple-400';
      case 'Vídeo': return 'bg-pink-500/10 text-pink-400';
      case 'Link': return 'bg-cyan-500/10 text-cyan-400';
      default: return 'bg-slate-500/10 text-slate-400';
    }
  };

  const formatBytes = (bytes) => {
    const b = Number(bytes || 0);
    if (!b) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const idx = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
    const val = b / Math.pow(1024, idx);
    return `${val.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
  };

  const sanitizeFileName = (name) => {
    const src = String(name || '').trim() || 'arquivo';
    const normalized = src.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    const dotIdx = normalized.lastIndexOf('.');
    const baseRaw = dotIdx > 0 ? normalized.slice(0, dotIdx) : normalized;
    const extRaw = dotIdx > 0 ? normalized.slice(dotIdx + 1) : '';
    const base = baseRaw
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/[^a-zA-Z0-9._ -]+/g, '-')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^\.+/, '')
      .replace(/\.+$/, '')
      .slice(0, 90) || 'arquivo';
    const ext = extRaw
      .replace(/[^a-zA-Z0-9]+/g, '')
      .slice(0, 12)
      .toLowerCase();
    return ext ? `${base}.${ext}` : base;
  };

  const generateUuid = () => {
    try {
      const fn = (globalThis.crypto && globalThis.crypto.randomUUID) ? globalThis.crypto.randomUUID.bind(globalThis.crypto) : null;
      if (fn) return fn();
    } catch {}
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  };

  const fetchAdminFlag = async () => {
    try {
      const { data, error } = await supabase.rpc('get_my_authorization');
      if (error) throw error;
      const row = Array.isArray(data) ? (data[0] || null) : data;
      return isAdminRole(row?.role, row?.status);
    } catch {
      try {
        const { data } = await supabase.auth.getSession();
        const u = data?.session?.user;
        // user_metadata e gravavel pelo proprio usuario: nunca usar como papel.
        const legacy = u?.app_metadata?.role || null;
        return String(legacy || '') === 'admin';
      } catch {
        return false;
      }
    }
  };

  const [appNotification, setAppNotification] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const [isFileDragging, setIsFileDragging] = useState(false);

  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authForm, setAuthForm] = useState({ email: '', password: '' });
  const [authLoading, setAuthLoading] = useState(false);

  const [categories, setCategories] = useState([]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveOpRef = useRef({ token: 0 });

  const [form, setForm] = useState({
    title: '',
    type: 'PDF',
    url: '',
    htmlContent: '',
    allowPdfDownload: false,
    description: '',
    categoryId: '',
    status: 'Ativo',
    linkedCourses: [],
    file: null,
    files: []
  });
  const [filters, setFilters] = useState({
    title: '',
    type: '',
    categoryId: '',
    courseId: '',
    status: '',
    startDate: '',
    endDate: '',
    order: 'newest'
  });
  const [libraryPage, setLibraryPage] = useState(1);
  const libraryItemsPerPage = 6;

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data?.session || null);
        setIsAdmin(await fetchAdminFlag());
      } catch {
        setSession(null);
        setIsAdmin(false);
      }
    };
    run();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession || null);
      fetchAdminFlag().then(setIsAdmin).catch(() => setIsAdmin(false));
    });
    return () => {
      try { sub?.subscription?.unsubscribe?.(); } catch {}
    };
  }, []);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('course_categories')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      const mapped = (data || []).map(r => ({
        id: r.id,
        name: r.name,
        description: r.description || '',
        status: (typeof r.status === 'boolean') ? r.status : String(r.status || 'Ativo').toLowerCase() === 'ativo',
        domain: r.domain || null,
      }));
      const active = mapped.filter(c => c.status === true);
      const hasDomain = active.some(c => typeof c.domain === 'string');
      setCategories(hasDomain ? active.filter(c => c.domain === 'biblioteca') : active);
    } catch (e) {
      const { data, error } = await supabase
        .from('course_categories')
        .select('id, name, description, status')
        .order('name', { ascending: true });
      if (error) throw error;
      setCategories((data || []).filter(c => ((typeof c.status === 'boolean') ? c.status : String(c.status || 'Ativo').toLowerCase() === 'ativo') === true));
    }
  };

  const loadContents = async (activeFilters) => {
    setLoading(true);
    try {
      let q = supabase
        .from('library_contents')
        .select('id, title, type, status, description, url, meta, category_id, published_on, linked_course_ids, file_path, original_filename, mime_type, size_bytes, created_at, category:course_categories(id, name)')
        .order('published_on', { ascending: activeFilters.order === 'oldest' })
        .order('created_at', { ascending: activeFilters.order === 'oldest' });

      const t = String(activeFilters.title || '').trim();
      if (t) q = q.ilike('title', `%${t}%`);
      if (activeFilters.type) q = q.eq('type', activeFilters.type);
      if (activeFilters.status) q = q.eq('status', activeFilters.status);
      if (activeFilters.categoryId) q = q.eq('category_id', activeFilters.categoryId);
      if (activeFilters.courseId) {
        if (activeFilters.courseId === 'unlinked') {
          q = q.or('linked_course_ids.is.null,linked_course_ids.eq.{}');
        } else {
          q = q.contains('linked_course_ids', [activeFilters.courseId]);
        }
      }
      if (activeFilters.startDate) q = q.gte('published_on', activeFilters.startDate);
      if (activeFilters.endDate) q = q.lte('published_on', activeFilters.endDate);

      const { data, error } = await q;
      if (error) throw error;
      setItems(data || []);
    } catch (e) {
      const msg = String(e?.message || e || 'Falha ao carregar conteúdos.')
        .replace('permission denied', 'Permissão insuficiente');
      setAppNotification({ message: msg, type: 'error' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      try {
        await loadCategories();
      } catch (e) {
        setAppNotification({ message: String(e?.message || e || 'Falha ao carregar categorias.'), type: 'error' });
      }
    };
    boot();
  }, []);

  useEffect(() => {
    const f = { ...filters };
    const timer = setTimeout(() => {
      loadContents(f);
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.title, filters.type, filters.status, filters.categoryId, filters.courseId, filters.startDate, filters.endDate, filters.order]);

  useEffect(() => {
    setLibraryPage(1);
  }, [filters.title, filters.type, filters.status, filters.categoryId, filters.courseId, filters.startDate, filters.endDate, filters.order]);

  const requireAdmin = () => {
    if (isAdmin) return true;
    setAuthModalOpen(true);
    return false;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const email = String(authForm.email || '').trim();
      const password = String(authForm.password || '');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const isAdminNext = await fetchAdminFlag();
      if (!isAdminNext) {
        try { await supabase.auth.signOut(); } catch {}
        throw new Error("Seu usuário não tem role='Admin' na tabela Profiles.");
      }
      setAuthModalOpen(false);
      setAppNotification({ message: 'Login realizado.', type: 'success' });
      await loadCategories();
      await loadContents({ ...filters });
    } catch (err) {
      setAppNotification({ message: String(err?.message || err || 'Falha no login.'), type: 'error' });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setAppNotification({ message: 'Sessão encerrada.', type: 'success' });
    } catch (err) {
      setAppNotification({ message: String(err?.message || err || 'Falha ao sair.'), type: 'error' });
    }
  };

  const toggleCourseLink = (courseId) => {
    const src = editing ? (editing.linkedCourses || []) : (form.linkedCourses || []);
    const next = src.includes(courseId) ? src.filter(id => id !== courseId) : [...src, courseId];
    if (editing) setEditing({ ...editing, linkedCourses: next });
    else setForm({ ...form, linkedCourses: next });
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', type: 'PDF', url: '', htmlContent: '', allowPdfDownload: false, description: '', categoryId: '', status: 'Ativo', linkedCourses: [], file: null, files: [] });
    setShowModal(true);
  };

  const openEdit = async (item) => {
    const baseEditing = {
      id: item.id,
      title: item.title || '',
      type: item.type || 'PDF',
      status: item.status || 'Ativo',
      description: item.description || '',
      url: item.url || '',
      htmlContent: '',
      allowPdfDownload: Boolean(item?.meta?.allowPdfDownload ?? item?.meta?.allow_pdf_download),
      meta: (item?.meta && typeof item.meta === 'object') ? item.meta : {},
      categoryId: item.category_id || item.category?.id || '',
      linkedCourses: Array.isArray(item.linked_course_ids)
        ? item.linked_course_ids.map(v => String(v)).filter(Boolean)
        : (Array.isArray(item.linkedCourses) ? item.linkedCourses.map(v => String(v)).filter(Boolean) : [])
    };

    setEditing(baseEditing);
    setForm({ title: '', type: 'PDF', url: '', htmlContent: '', allowPdfDownload: false, description: '', categoryId: '', status: 'Ativo', linkedCourses: [], file: null, files: [] });
    setShowModal(true);

    if (baseEditing.type !== 'HTML') return;

    try {
      let htmlText = '';

      if (item.file_path) {
        const { data, error } = await supabase.storage
          .from('library-contents')
          .createSignedUrl(item.file_path, 300);
        if (error) throw error;
        const signedUrl = String(data?.signedUrl || '').trim();
        if (signedUrl) {
          const res = await fetch(signedUrl);
          if (!res.ok) throw new Error(`Falha ao carregar HTML (HTTP ${res.status}).`);
          htmlText = await res.text();
        }
      } else if (item.url) {
        const sourceUrl = String(item.url || '').trim();
        if (sourceUrl) {
          const res = await fetch(sourceUrl);
          if (!res.ok) throw new Error(`Falha ao carregar HTML (HTTP ${res.status}).`);
          htmlText = await res.text();
        }
      }

      setEditing((prev) => (prev?.id === item.id ? { ...prev, htmlContent: htmlText || '' } : prev));
    } catch (err) {
      setAppNotification({ message: String(err?.message || err || 'Falha ao carregar conteúdo HTML para edição.'), type: 'error' });
    }
  };

  const setSelectedFile = (file, opts = {}) => {
    const isEdit = !!editing;
    const list = (() => {
      if (!file) return [];
      if (Array.isArray(file)) return file.filter(Boolean);
      if (typeof file === 'object' && typeof file.length === 'number' && typeof file.item === 'function') return Array.from(file).filter(Boolean);
      return [file].filter(Boolean);
    })();
    if (!list.length) return;

    const t = isEdit ? (editing.type || 'PDF') : form.type;
    if (isEdit) {
      const f = list[0];
      const v = validateFileForType(t, f);
      if (!v.ok) {
        try { opts.clearInput && opts.clearInput(); } catch {}
        setAppNotification({ message: v.message, type: 'error' });
        setForm((cur) => ({ ...cur, file: null, files: [] }));
        return;
      }
      setForm((cur) => ({ ...cur, file: f, files: [] }));
      return;
    }

    for (const f of list) {
      const v = validateFileForType(t, f);
      if (!v.ok) {
        try { opts.clearInput && opts.clearInput(); } catch {}
        setAppNotification({ message: v.message, type: 'error' });
        setForm((cur) => ({ ...cur, file: null, files: [] }));
        return;
      }
    }
    setForm((cur) => ({ ...cur, file: null, files: list }));
  };

  const uploadFileForContent = async (contentId, file, opts = {}) => {
    const uploadSource = file?.data || file;
    const providedName = String(file?.name || '').trim();
    const cleanName = sanitizeFileName(providedName || 'arquivo');
    const originalName = providedName || cleanName;
    const prefix = String(opts.prefix || '').trim();
    const finalName = prefix ? `${prefix}-${cleanName}` : cleanName;
    const path = `contents/${contentId}/${finalName}`;
    const contentType = file?.contentType || uploadSource?.type || 'application/octet-stream';
    const { error } = await supabase.storage
      .from('library-contents')
      .upload(path, uploadSource, { upsert: true, contentType });
    if (error) throw error;
    const sizeBytes = Number(file?.sizeBytes ?? uploadSource?.size ?? 0);
    return { path, cleanName, originalName, contentType, sizeBytes };
  };

  const handleCancel = () => {
    saveOpRef.current.token += 1;
    setSaving(false);
    setShowModal(false);
    setEditing(null);
    setForm({ title: '', type: 'PDF', url: '', htmlContent: '', allowPdfDownload: false, description: '', categoryId: '', status: 'Ativo', linkedCourses: [], file: null, files: [] });
  };

  const handleDownload = async (item) => {
    try {
      if (item.type === 'Link') {
        const u = String(item.url || '').trim();
        if (!u) throw new Error('Este item não possui URL.');
        window.open(u, '_blank', 'noopener,noreferrer');
        return;
      }
      const path = item.file_path;
      if (!path) throw new Error('Este item não possui arquivo associado.');

      const { data, error } = await supabase.storage
        .from('library-contents')
        .createSignedUrl(path, 120);
      if (!error && data?.signedUrl) {
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const { data: blob, error: dErr } = await supabase.storage.from('library-contents').download(path);
      if (dErr) throw dErr;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.original_filename || 'download';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setAppNotification({ message: String(err?.message || err || 'Falha ao baixar.'), type: 'error' });
    }
  };

  const handleRemoveFile = async () => {
    if (!requireAdmin()) return;
    if (!editing?.id) return;
    setSaving(true);
    try {
      const { data: row, error } = await supabase
        .from('library_contents')
        .select('id, file_path')
        .eq('id', editing.id)
        .single();
      if (error) throw error;
      if (row?.file_path) {
        const { error: rErr } = await supabase.storage.from('library-contents').remove([row.file_path]);
        if (rErr) throw rErr;
      }
      const { error: uErr } = await supabase
        .from('library_contents')
        .update({ file_path: null, original_filename: null, mime_type: null, size_bytes: null })
        .eq('id', editing.id);
      if (uErr) throw uErr;
      setAppNotification({ message: 'Arquivo removido.', type: 'success' });
      setShowModal(false);
      setEditing(null);
      await loadContents({ ...filters });
    } catch (err) {
      setAppNotification({ message: String(err?.message || err || 'Falha ao remover arquivo.'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!requireAdmin()) return;
    setSaving(true);
    const opToken = saveOpRef.current.token + 1;
    saveOpRef.current.token = opToken;
    const ensureActive = () => {
      if (saveOpRef.current.token !== opToken) throw new Error('UPLOAD_CANCELLED');
    };
    const cleanupPaths = [];
    const cleanupRowIds = [];
    try {
      const isEdit = !!editing;
      const model = isEdit ? editing : form;

      const createFiles = Array.isArray(form.files) ? form.files : [];
      let fileCandidates = isEdit
        ? (form.file ? [form.file] : [])
        : (createFiles.length ? createFiles : (form.file ? [form.file] : []));
      const htmlSnippet = String(model.htmlContent || '').trim();
      if (model.type === 'HTML' && htmlSnippet) {
        const cleanBase = sanitizeFileName(String(model.title || 'conteudo').trim() || 'conteudo').replace(/\.[^.]+$/, '');
        const htmlBlob = new Blob([htmlSnippet], { type: 'text/html' });
        const htmlFile = {
          data: htmlBlob,
          name: `${cleanBase || 'conteudo'}.html`,
          contentType: 'text/html',
          sizeBytes: htmlBlob.size,
        };
        fileCandidates = [htmlFile];
      }
      const isMultiCreate = !isEdit && model.type !== 'Link' && model.type !== 'HTML' && fileCandidates.length > 1;
      if (model.type !== 'Link' && fileCandidates.length) {
        for (const f of fileCandidates) {
          const v = validateFileForType(model.type, f);
          if (!v.ok) throw new Error(v.message);
        }
      }

      const payloadBase = {
        title: String(model.title || '').trim(),
        type: model.type,
        status: model.status,
        description: String(model.description || '').trim() || null,
        url: model.type === 'Link' ? (String(model.url || '').trim() || null) : (String(model.url || '').trim() || null),
        meta: (() => {
          const baseMeta = (model.meta && typeof model.meta === 'object') ? { ...model.meta } : {};
          if (model.type === 'HTML') {
            const allow = Boolean(model.allowPdfDownload);
            baseMeta.allowPdfDownload = allow;
            baseMeta.allow_pdf_download = allow;
          }
          // `library_contents.meta` is NOT NULL in DB, so always persist an object.
          return baseMeta;
        })(),
        category_id: await ensureValidCategoryId(model.categoryId),
        linked_course_ids: (model.linkedCourses || []).map(v => String(v)).filter(Boolean)
      };

      const payload = (payloadBase.type === 'Link')
        ? { ...payloadBase, file_path: null, original_filename: null, mime_type: null, size_bytes: null }
        : payloadBase;

      if (!isMultiCreate && !payload.title) throw new Error('Título é obrigatório.');
      if (payload.type === 'Link' && !payload.url) throw new Error('URL é obrigatória para tipo Link.');
      if (payload.type === 'HTML' && !fileCandidates.length && !htmlSnippet) {
        throw new Error('Cole o código HTML ou selecione um arquivo .html.');
      }

      let row;
      const isUpload = payload.type !== 'Link' && fileCandidates.length > 0;

      if (isEdit) {
        if (isUpload) {
          ensureActive();
          const { data: prevRows, error: prevErr } = await supabase
            .from('library_contents')
            .select('id, file_path')
            .eq('id', editing.id)
            ;
          if (prevErr) throw prevErr;
          const prevRow = Array.isArray(prevRows) ? (prevRows[0] || null) : prevRows;
          if (!prevRow?.id) throw new Error('Conteúdo não encontrado ou sem permissão para editar.');
          const prevPath = prevRow?.file_path || null;

          const uploaded = await uploadFileForContent(editing.id, fileCandidates[0], { prefix: generateUuid() });
          cleanupPaths.push(uploaded.path);
          ensureActive();

          const { data, error } = await supabase
            .from('library_contents')
            .update({
              ...payload,
              file_bucket: 'library-contents',
              file_path: uploaded.path,
              original_filename: uploaded.originalName,
              mime_type: uploaded.contentType,
              size_bytes: uploaded.sizeBytes
            })
            .eq('id', editing.id)
            .select('id');
          if (error) {
            try { await supabase.storage.from('library-contents').remove([uploaded.path]); } catch {}
            cleanupPaths.length = 0;
            throw error;
          }
          if (!Array.isArray(data) || data.length === 0) throw new Error('Nenhuma linha foi atualizada. Verifique permissões e se o conteúdo ainda existe.');
          row = data[0];
          cleanupPaths.length = 0;
          if (prevPath && prevPath !== uploaded.path) {
            try { await supabase.storage.from('library-contents').remove([prevPath]); } catch {}
          }
        } else {
          ensureActive();
          const { data, error } = await supabase
            .from('library_contents')
            .update(payload)
            .eq('id', editing.id)
            .select('id');
          if (error) throw error;
          if (!Array.isArray(data) || data.length === 0) throw new Error('Nenhuma linha foi atualizada. Verifique permissões e se o conteúdo ainda existe.');
          row = data[0];
        }
      } else {
        if (isUpload) {
          const publishedOn = new Date().toISOString().slice(0, 10);
          if (isMultiCreate) {
            for (const f of fileCandidates) {
              ensureActive();
              const newId = generateUuid();
              cleanupRowIds.push(newId);
              const uploaded = await uploadFileForContent(newId, f);
              cleanupPaths.push(uploaded.path);
              ensureActive();

              const { data, error } = await supabase
                .from('library_contents')
                .insert({
                  ...payload,
                  title: String(f?.name || '').trim() || payload.title,
                  id: newId,
                  published_on: publishedOn,
                  file_bucket: 'library-contents',
                  file_path: uploaded.path,
                  original_filename: uploaded.originalName,
                  mime_type: uploaded.contentType,
                  size_bytes: uploaded.sizeBytes
                })
                .select('id');
              if (error) throw error;
              if (!Array.isArray(data) || data.length === 0) throw new Error('Falha ao criar: nenhum registro retornado.');
              row = data[0];
            }
          } else {
            ensureActive();
            const newId = generateUuid();
            cleanupRowIds.push(newId);
            const uploaded = await uploadFileForContent(newId, fileCandidates[0]);
            cleanupPaths.push(uploaded.path);
            ensureActive();

            const { data, error } = await supabase
              .from('library_contents')
              .insert({
                ...payload,
                id: newId,
                published_on: publishedOn,
                file_bucket: 'library-contents',
                file_path: uploaded.path,
                original_filename: uploaded.originalName,
                mime_type: uploaded.contentType,
                size_bytes: uploaded.sizeBytes
              })
              .select('id');
            if (error) throw error;
            if (!Array.isArray(data) || data.length === 0) throw new Error('Falha ao criar: nenhum registro retornado.');
            row = data[0];
          }
        } else {
          ensureActive();
          const newId = generateUuid();
          cleanupRowIds.push(newId);
          const { data, error } = await supabase
            .from('library_contents')
            .insert({ ...payload, id: newId, published_on: new Date().toISOString().slice(0, 10) })
            .select('id');
          if (error) throw error;
          if (!Array.isArray(data) || data.length === 0) throw new Error('Falha ao criar: nenhum registro retornado.');
          row = data[0];
        }
      }

      ensureActive();
      cleanupPaths.length = 0;
      cleanupRowIds.length = 0;
      setAppNotification({
        message: isEdit ? 'Conteúdo atualizado com sucesso!' : (isMultiCreate ? 'Conteúdos criados com sucesso!' : 'Conteúdo criado com sucesso!'),
        type: 'success'
      });
      setShowModal(false);
      setEditing(null);
      setForm({ title: '', type: 'PDF', url: '', htmlContent: '', allowPdfDownload: false, description: '', categoryId: '', status: 'Ativo', linkedCourses: [], file: null, files: [] });
      await loadContents({ ...filters });
    } catch (err) {
      if (saveOpRef.current.token !== opToken || String(err?.message || '') === 'UPLOAD_CANCELLED') {
        if (cleanupPaths.length) {
          try { await supabase.storage.from('library-contents').remove(cleanupPaths); } catch {}
        }
        for (const id of cleanupRowIds) {
          try { await supabase.from('library_contents').delete().eq('id', id); } catch {}
        }
        setAppNotification({ message: 'Upload cancelado.', type: 'error' });
        return;
      }
      if (cleanupPaths.length) {
        try { await supabase.storage.from('library-contents').remove(cleanupPaths); } catch {}
      }
      for (const id of cleanupRowIds) {
        try { await supabase.from('library_contents').delete().eq('id', id); } catch {}
      }
      const details = err && typeof err === 'object'
        ? [err.code, err.message, err.details, err.hint].filter(Boolean).join(' — ')
        : '';
      setAppNotification({ message: details || String(err?.message || err || 'Falha ao salvar.'), type: 'error' });
    } finally {
      if (saveOpRef.current.token === opToken) setSaving(false);
    }
  };

  const handleDelete = (item) => {
    setConfirmModal({
      isOpen: true,
      title: 'Excluir Conteúdo',
      message: 'Tem certeza que deseja excluir este item?',
      onConfirm: async () => {
        if (!requireAdmin()) return;
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        try {
          const path = item.file_path;
          if (path) {
            try { await supabase.storage.from('library-contents').remove([path]); } catch {}
          }
          const { error } = await supabase.from('library_contents').delete().eq('id', item.id);
          if (error) throw error;
          setAppNotification({ message: 'Conteúdo excluído com sucesso!', type: 'success' });
          await loadContents({ ...filters });
        } catch (err) {
          setAppNotification({ message: String(err?.message || err || 'Falha ao excluir.'), type: 'error' });
        }
      }
    });
  };

  /* categorias são gerenciadas em Gestão Admin */

  const visibleItems = items.map(i => {
    const originalFileName = String(i.original_filename || '').trim() || (i.file_path ? String(i.file_path).split('/').pop() : '');
    return {
      ...i,
      originalFileName,
      categoryName: i.category?.name || '-',
      date: i.published_on || i.created_at || ''
    };
  });

  const libraryCounters = visibleItems.reduce(
    (acc, item) => {
      const status = String(item?.status || '').toLowerCase();
      if (status === 'ativo') acc.active += 1;
      if (status === 'inativo') acc.inactive += 1;

      const linkedIds = Array.isArray(item?.linked_course_ids) ? item.linked_course_ids : [];
      linkedIds.forEach((id) => acc.courseIds.add(String(id)));

      return acc;
    },
    { active: 0, inactive: 0, courseIds: new Set() }
  );

  const libraryTotalResults = visibleItems.length;
  const libraryCoursesInResults = libraryCounters.courseIds.size;
  const libraryTotalPages = Math.max(1, Math.ceil(visibleItems.length / libraryItemsPerPage));
  const safeLibraryPage = Math.min(libraryPage, libraryTotalPages);
  const paginatedVisibleItems = visibleItems.slice(
    (safeLibraryPage - 1) * libraryItemsPerPage,
    safeLibraryPage * libraryItemsPerPage
  );

  useEffect(() => {
    if (libraryPage > libraryTotalPages) {
      setLibraryPage(libraryTotalPages);
    }
  }, [libraryPage, libraryTotalPages]);

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {appNotification && <Notification message={appNotification.message} type={appNotification.type} onClose={() => setAppNotification(null)} />}
      <ConfirmModal isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })} />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Biblioteca</h1>
          <p className="text-slate-400">Gerencie arquivos e links úteis para sua organização.</p>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={openCreate}
            style={{ backgroundColor: partner.color }}
            className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition"
          >
            <Plus size={20} /> Novo Conteúdo
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="ml-auto flex items-center gap-2">
            {!session && (
              <button onClick={() => setAuthModalOpen(true)} className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition font-bold text-sm flex items-center gap-2">
                <Lock size={16} /> Entrar (Admin)
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-4 bg-slate-900 p-4 rounded-xl border border-slate-800 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Título</label>
            <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
              <Search size={16} className="text-slate-500 mr-2" />
              <input
                type="text"
                placeholder="Buscar por título..."
                value={filters.title}
                onChange={e => setFilters({ ...filters, title: e.target.value })}
                className="bg-transparent text-white outline-none text-sm w-48"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Tipo</label>
            <select
              value={filters.type}
              onChange={e => setFilters({ ...filters, type: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-32"
            >
              <option value="">Todos</option>
              {fileTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Categoria</label>
            <select
              value={filters.categoryId}
              onChange={e => setFilters({ ...filters, categoryId: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-44"
            >
              <option value="">Todas</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Curso</label>
            <select
              value={filters.courseId}
              onChange={e => setFilters({ ...filters, courseId: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-56"
            >
              <option value="">Todos os Cursos</option>
              <option value="unlinked">Sem vínculo</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Status</label>
            <select
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-32"
            >
              <option value="">Todos</option>
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Período (Início)</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => setFilters({ ...filters, startDate: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Período (Fim)</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => setFilters({ ...filters, endDate: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase">Ordenação</label>
            <select
              value={filters.order}
              onChange={e => setFilters({ ...filters, order: e.target.value })}
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none h-[38px] w-36"
            >
              <option value="newest">Mais Novos</option>
              <option value="oldest">Mais Antigos</option>
            </select>
          </div>

          <div className="flex items-end pb-0.5">
            <button
              onClick={() => setFilters({ title: '', type: '', categoryId: '', courseId: '', status: '', startDate: '', endDate: '', order: 'newest' })}
              className="text-slate-400 hover:text-white text-sm font-medium px-3 py-2 transition"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Total</p>
          <p className="text-2xl font-black text-white">{libraryTotalResults}</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Ativos</p>
          <p className="text-2xl font-black text-teal-400">{libraryCounters.active}</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Inativos</p>
          <p className="text-2xl font-black text-rose-400">{libraryCounters.inactive}</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Cursos</p>
          <p className="text-2xl font-black text-blue-400">{libraryCoursesInResults}</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-lg animate-pulse">
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 rounded-lg bg-slate-800" />
                <div className="w-20 h-6 rounded bg-slate-800" />
              </div>
              <div className="h-4 w-2/3 bg-slate-800 rounded mb-2" />
              <div className="h-3 w-full bg-slate-800 rounded mb-1" />
              <div className="h-3 w-5/6 bg-slate-800 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedVisibleItems.map(item => (
            <div key={item.id} className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition shadow-lg group relative">
              <div className="flex justify-between items-start mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColor(item.type)}`}>
                  {getIcon(item.type)}
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => setPreviewItem(item)} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white" title="Visualizar">
                    <Eye size={16} />
                  </button>
                  <button onClick={() => handleDownload(item)} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white" title={item.type === 'Link' ? 'Abrir link' : 'Baixar'}>
                    <Download size={16} />
                  </button>
                  <button onClick={() => openEdit(item)} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white" title="Editar">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(item)} className="p-2 hover:bg-rose-500/10 rounded text-rose-500/40 hover:text-rose-500" title="Excluir">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-bold text-white truncate pr-2">{item.title}</h3>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${item.status === 'Ativo' ? 'border-teal-500/20 bg-teal-500/10 text-teal-400' : 'border-rose-500/20 bg-rose-500/10 text-rose-400'}`}>{item.status || 'Ativo'}</span>
              </div>
              {item.type !== 'Link' && (
                <p className="text-slate-500 text-xs mb-3 truncate" title={item.originalFileName || ''}>{item.originalFileName || 'Sem arquivo'}</p>
              )}
              {String(item.description || '').trim() ? (
                <p className="text-slate-400 text-sm mb-4 line-clamp-2">{item.description}</p>
              ) : (
                <div className="mb-4" />
              )}

              {Array.isArray(item.linked_course_ids) && item.linked_course_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {item.linked_course_ids.map((courseId) => {
                    const cid = String(courseId);
                    const course = courses.find(c => c.id === cid);
                    return course ? (
                      <span key={String(courseId)} className="flex items-center gap-1 text-[10px] font-bold bg-blue-500/10 text-blue-400 px-2 py-1 rounded border border-blue-500/20">
                        <BookOpen size={10} /> {course.title}
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              <div className="flex justify-between items-center text-xs text-slate-500 font-mono border-t border-slate-800 pt-4 mt-auto">
                <span className="truncate pr-2" title={item.categoryName}>{item.categoryName}</span>
                <span>{String(item.date || '').slice(0, 10)}</span>
              </div>
            </div>
          ))}
          {visibleItems.length === 0 && (
            <div className="col-span-full py-12 flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-800 rounded-xl">
              <BookOpen size={48} className="mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum conteúdo encontrado com os filtros selecionados.</p>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/70 px-2 py-1.5">
            <button
              onClick={() => setLibraryPage(prev => Math.max(prev - 1, 1))}
              disabled={safeLibraryPage === 1}
              className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-slate-800 text-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Página anterior"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="px-2 text-xs font-semibold text-slate-300">
              Página <span className="text-white">{safeLibraryPage}</span> de <span className="text-white">{libraryTotalPages}</span>
            </span>
            <button
              onClick={() => setLibraryPage(prev => Math.min(prev + 1, libraryTotalPages))}
              disabled={safeLibraryPage === libraryTotalPages}
              className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-slate-800 text-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition"
              aria-label="Próxima página"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{editing ? 'Editar Conteúdo' : 'Novo Conteúdo'}</h3>
              <button onClick={handleCancel} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>

            <form onSubmit={handleSave} className="flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Coluna da Esquerda: Informações Básicas e Arquivo */}
                <div className="flex flex-col gap-4">
              {(() => {
                const createFiles = Array.isArray(form.files) ? form.files : [];
                const isMultiCreate = !editing && (editing?.type === 'Link' || form.type === 'Link' ? false : createFiles.length > 1);
                const titleValue = editing ? editing.title : form.title;
                return (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título</label>
                <input
                  type="text"
                  required={!isMultiCreate}
                  disabled={isMultiCreate}
                  placeholder={isMultiCreate ? 'Será usado o nome de cada arquivo' : undefined}
                  value={isMultiCreate ? '' : titleValue}
                  onChange={e => {
                    if (isMultiCreate) return;
                    if (editing) setEditing({ ...editing, title: e.target.value });
                    else setForm({ ...form, title: e.target.value });
                  }}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none"
                />
              </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</label>
                  <select
                    value={editing ? editing.type : form.type}
                    onChange={e => {
                      const v = e.target.value;
                      if (editing) setEditing({
                        ...editing,
                        type: v,
                        url: v === 'Link' ? editing.url : '',
                        htmlContent: v === 'HTML' ? (editing.htmlContent || '') : '',
                        allowPdfDownload: v === 'HTML' ? Boolean(editing.allowPdfDownload) : false
                      });
                      setForm({
                        ...form,
                        type: v,
                        url: v === 'Link' ? form.url : '',
                        htmlContent: v === 'HTML' ? (form.htmlContent || '') : '',
                        allowPdfDownload: v === 'HTML' ? Boolean(form.allowPdfDownload) : false,
                        file: v === 'Link' ? null : form.file,
                        files: v === 'Link' ? [] : (form.files || [])
                      });
                    }}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none"
                  >
                    {fileTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status</label>
                  <select
                    value={editing ? (editing.status || 'Ativo') : form.status}
                    onChange={e => editing ? setEditing({ ...editing, status: e.target.value }) : setForm({ ...form, status: e.target.value })}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none"
                  >
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  {(editing?.type === 'Link' || form.type === 'Link') ? 'URL' : (editing?.type === 'HTML' || form.type === 'HTML') ? 'Arquivo HTML (Opcional)' : 'Arquivo'}
                </label>
                {(editing?.type === 'Link' || form.type === 'Link') ? (
                  <input
                    type="url"
                    placeholder="https://"
                    value={editing ? (editing.url || '') : form.url}
                    onChange={e => editing ? setEditing({ ...editing, url: e.target.value }) : setForm({ ...form, url: e.target.value })}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none"
                  />
                ) : (
                  <div
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsFileDragging(true); }}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsFileDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsFileDragging(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsFileDragging(false);
                      const files = e.dataTransfer?.files;
                      if (!files || !files.length) return;
                      setSelectedFile(files);
                    }}
                    className={`border border-slate-800 rounded-lg p-3 bg-slate-950 text-slate-300 text-sm ${isFileDragging ? 'border-teal-500/60 bg-teal-500/5' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <label className="cursor-pointer flex items-center gap-2">
                        <input
                          type="file"
                          className="hidden"
                          multiple={!editing}
                          accept={getAcceptForType(editing ? (editing.type || 'PDF') : form.type)}
                          onChange={e => {
                            const files = e.target.files;
                            if (!files || !files.length) return;
                            setSelectedFile(files, { clearInput: () => { try { e.target.value = ''; } catch {} } });
                          }}
                        />
                        <UploadCloud size={18} className="text-slate-400" />
                        <span>{(() => {
                          const createFiles = Array.isArray(form.files) ? form.files : [];
                          const activeFiles = editing ? (form.file ? [form.file] : []) : createFiles;
                          if (activeFiles.length === 1) return `${activeFiles[0].name} (${formatBytes(activeFiles[0].size)})`;
                          if (activeFiles.length > 1) return `${activeFiles.length} arquivos selecionados`;
                          return isFileDragging ? 'Solte para selecionar' : 'Selecionar ou arrastar arquivo';
                        })()}</span>
                      </label>

                      {editing && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const { data: row, error } = await supabase.from('library_contents').select('id, file_path, original_filename, url, type').eq('id', editing.id).single();
                                if (error) throw error;
                                await handleDownload(row);
                              } catch (err) {
                                setAppNotification({ message: String(err?.message || err || 'Falha ao baixar arquivo.'), type: 'error' });
                              }
                            }}
                            className="px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition font-bold"
                          >
                            Baixar
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            disabled={saving}
                            className={`px-3 py-2 rounded-lg text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 transition font-bold ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            Remover
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {(editing?.type === 'HTML' || form.type === 'HTML') && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Código HTML</label>
                  <textarea
                    rows="6"
                    placeholder="<html>...</html>"
                    value={editing ? (editing.htmlContent || '') : (form.htmlContent || '')}
                    onChange={e => editing ? setEditing({ ...editing, htmlContent: e.target.value }) : setForm({ ...form, htmlContent: e.target.value })}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none resize-y min-h-[120px] font-mono text-xs"
                  />
                  <p className="text-[11px] text-slate-500">Quando preenchido, o HTML colado será salvo como arquivo `.html` e usado na visualização.</p>
                  <div className="mt-2 border border-slate-800 rounded-lg p-3 bg-slate-950 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">Permitir Download</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (editing) {
                          setEditing({ ...editing, allowPdfDownload: !editing.allowPdfDownload });
                        } else {
                          setForm({ ...form, allowPdfDownload: !form.allowPdfDownload });
                        }
                      }}
                      className={`${
                        (editing ? editing.allowPdfDownload : form.allowPdfDownload) ? 'bg-orange-500' : 'bg-slate-700'
                      } w-10 h-5 rounded-full relative transition-colors duration-300`}
                      aria-pressed={editing ? Boolean(editing.allowPdfDownload) : Boolean(form.allowPdfDownload)}
                      aria-label="Permitir download em PDF do conteúdo HTML"
                    >
                      <div
                        className={`${
                          (editing ? editing.allowPdfDownload : form.allowPdfDownload) ? 'left-6' : 'left-1'
                        } absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}
                      ></div>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Categoria</label>
                <select
                  value={editing ? (editing.categoryId || '') : form.categoryId}
                  onChange={e => editing ? setEditing({ ...editing, categoryId: e.target.value }) : setForm({ ...form, categoryId: e.target.value })}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none"
                >
                  <option value="">Sem categoria</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

                </div>

                {/* Coluna da Direita: Descrição e Vínculos */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5 h-full">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Descrição</label>
                    <textarea
                      rows="4"
                      value={editing ? (editing.description || '') : form.description}
                      onChange={e => editing ? setEditing({ ...editing, description: e.target.value }) : setForm({ ...form, description: e.target.value })}
                      className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none resize-none h-full min-h-[120px]"
                    ></textarea>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cursos Vinculados</label>
                    <div className="max-h-48 overflow-y-auto custom-scrollbar border border-slate-800 rounded-lg p-2 bg-slate-950">
                      {courses.map(course => {
                        const isLinked = editing
                          ? (editing.linkedCourses || []).includes(course.id)
                          : (form.linkedCourses || []).includes(course.id);
                        return (
                          <div key={course.id} onClick={() => toggleCourseLink(course.id)} className={`p-2 rounded cursor-pointer text-sm flex items-center justify-between transition ${isLinked ? 'bg-teal-500/20 text-teal-400' : 'hover:bg-slate-800 text-slate-400'}`}>
                            <span>{course.title}</span>
                            {isLinked && <Check size={14} />}
                          </div>
                        );
                      })}
                      {courses.length === 0 && <div className="text-slate-600 text-xs italic p-2">Nenhum curso disponível.</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                <button type="button" onClick={handleCancel} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
                <button type="submit" disabled={saving} style={{ backgroundColor: partner.color }} className={`px-8 py-2 rounded-lg text-white font-bold shadow-lg transition hover:opacity-90 ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}>{saving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {authModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Entrar como Admin</h3>
              <button onClick={() => setAuthModalOpen(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>

            <form onSubmit={handleAuth} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">E-mail</label>
                <input
                  type="email"
                  value={authForm.email}
                  onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none"
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Senha</label>
                <input
                  type="password"
                  value={authForm.password}
                  onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 mt-2">
                <button type="button" onClick={() => setAuthModalOpen(false)} className="px-4 py-2 text-slate-400 font-bold">Cancelar</button>
                <button type="submit" disabled={authLoading} style={{ backgroundColor: partner.color }} className={`px-8 py-2 rounded-lg text-white font-bold shadow-lg ${authLoading ? 'opacity-60 cursor-not-allowed' : ''}`}>{authLoading ? 'Entrando...' : 'Entrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Preview */}
      <LibraryPreviewModal 
        item={previewItem} 
        onClose={() => setPreviewItem(null)} 
      />

      {/* categorias gerenciadas em Gestão Admin */}
    </div>
  );
};

const Footer = ({ partner, role, hasSidebar = false, isSidebarCollapsed = false, onPrivacyPolicy, onTermsOfUse }) => (
  (() => {
    const normalizePartnerSiteHref = (rawSite) => {
      let site = String(rawSite || '').trim();
      if (!site) return null;

      // Accept malformed variants like "https//domain.com" or "http/domain.com"
      site = site.replace(/^https?:\/\/+/i, (match) => (match.toLowerCase().startsWith('http://') ? 'http://' : 'https://'));
      site = site.replace(/^https?:\/(?!\/)/i, (match) => (match.toLowerCase().startsWith('http:/') ? 'http://' : 'https://'));
      site = site.replace(/^https?(?!:)/i, (match) => `${match.toLowerCase()}:`);
      site = site.replace(/^https?:\/\//i, (match) => (match.toLowerCase().startsWith('http://') ? 'http://' : 'https://'));

      if (!/^https?:\/\//i.test(site)) {
        site = `https://${site.replace(/^\/+/, '')}`;
      }

      try {
        return new URL(site).toString();
      } catch {
        return null;
      }
    };

    const formatPartnerSiteLabel = (rawSite) => {
      const site = String(rawSite || '').trim();
      if (!site) return '';
      return site
        .replace(/^https?:\/\/+/i, '')
        .replace(/^https?:\/(?!\/)/i, '')
        .replace(/^https?(?!:)/i, '')
        .replace(/^:+/, '')
        .replace(/^\/+/, '');
    };

    const partnerSiteLabel = formatPartnerSiteLabel(partner?.site);
    const partnerSiteHref = normalizePartnerSiteHref(partner?.site)
      || (partnerSiteLabel ? `https://${partnerSiteLabel}` : null);

    return (
  <footer className={`
    fixed bottom-0 right-0 z-[60] 
    bg-slate-950/90 backdrop-blur-md border-t border-slate-900 
    py-3 px-4 md:px-8 
    flex flex-col md:flex-row justify-between items-center gap-2 md:gap-0
    text-[10px] md:text-xs text-slate-500 transition-all duration-300
    ${hasSidebar ? (isSidebarCollapsed ? 'left-0 md:left-16' : 'left-0 md:left-64') : 'left-0'}
  `}>
      <div className="flex flex-wrap justify-center md:justify-start items-center gap-2 md:gap-3 text-center">
        <span>© {new Date().getFullYear()} Todos os direitos reservados</span>
        <span className="text-slate-800">|</span>
        <span>Powered by <span className="font-bold text-slate-400">A2Care</span></span>
        <span className="text-slate-800">|</span>
        <a
          href="#privacy"
          className="px-2 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-700 transition font-bold cursor-pointer select-none"
          onClick={(e) => { e.preventDefault(); if (typeof onPrivacyPolicy === 'function') onPrivacyPolicy(); }}
        >
          Política de Privacidade
        </a>
        <a
          href="#terms"
          className="px-2 py-1 rounded bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-700 transition font-bold cursor-pointer select-none"
          onClick={(e) => { e.preventDefault(); if (typeof onTermsOfUse === 'function') onTermsOfUse(); }}
        >
          Termos de Uso
        </a>
      </div>
      
      <div className="flex items-center gap-2 md:gap-3">
        <span>Parceiro Oficial <span className="font-bold text-slate-300">{partner.name}</span></span>
        <span className="text-slate-800">|</span>
        {partnerSiteLabel ? (
          <a href={partnerSiteHref} target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition flex items-center gap-1 text-slate-400">
            {partnerSiteLabel || partner.site} <LinkIcon size={10} />
          </a>
        ) : (
          <span className="text-slate-500">{partnerSiteLabel || partner.site || 'Site indisponível'}</span>
        )}
      </div>
  </footer>
    );
  })()
);

// --- UTILITÁRIOS DE MÁSCARA ---

const maskCPF = (v) => {
  v = v.replace(/\D/g, "");
  if (v.length > 11) v = v.substring(0, 11);
  v = v.replace(/(\d{3})(\d)/, "$1.$2");
  v = v.replace(/(\d{3})(\d)/, "$1.$2");
  v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  return v;
};

const maskWhatsApp = (v) => {
  v = v.replace(/\D/g, "");
  if (v.length > 11) v = v.substring(0, 11);
  if (v.length <= 10) {
    v = v.replace(/(\d{2})(\d)/, "($1) $2");
    v = v.replace(/(\d{4})(\d)/, "$1-$2");
  } else {
    v = v.replace(/(\d{2})(\d)/, "($1) $2");
    v = v.replace(/(\d{5})(\d)/, "$1-$2");
  }
  return v;
};

const normalizeWhatsappNumber = (value) => String(value || '').replace(/\D/g, '');

const formatDate = (v) => {
  try {
    const d = new Date(v);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return v || '';
  }
};

const normalizeBrandColor = (value) => {
  const color = String(value || '').trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color)) return color;
  if (/^rgba?\([^)]+\)$/i.test(color)) return color;
  if (/^hsla?\([^)]+\)$/i.test(color)) return color;
  return DEFAULT_PARTNER.color;
};

const buildDynamicFaviconHref = (colorValue) => {
  const color = normalizeBrandColor(colorValue);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="14" fill="${color}" />
      <path fill="#ffffff" d="M40.5 18.2c-2.1-1.4-5.2-2.2-8.8-2.2-8.2 0-13.4 4.2-13.4 10.2 0 5.8 4 8.4 11 10.2 4.8 1.2 6.4 2.1 6.4 4.1 0 2.2-1.8 3.5-5.2 3.5-3.3 0-6.3-1-9-3.1l-4.1 4.9c3.3 2.9 8.1 4.5 12.9 4.5 8.7 0 13.7-4.3 13.7-10.7 0-6.2-3.7-8.7-10.9-10.5-4.9-1.3-6.4-2.1-6.4-4.1 0-1.8 1.6-3.1 4.6-3.1 2.6 0 4.8.7 7.3 2.5l3.9-5.2z"/>
    </svg>
  `.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export function App() {
  const [appNotification, setAppNotification] = useState(null);
  const auth = useAuth();
  const userId = auth.session?.user?.id || auth.session?.user?.email || 'anon';
  // Treat anyone who is not explicitly 'Aluno' as 'admin' to access the admin layout
  const isPureStudent = !auth.isAdmin && (!auth.profileRole || auth.profileRole === 'Aluno');
  // Se for Parceiro SAS ou qualquer outro perfil administrativo que não Aluno, ele deve ser tratado como "admin" no nível de roteamento principal (para carregar o AdminSidebar)
  const role = isPureStudent ? (auth.isAuthenticated ? 'student' : 'guest') : 'admin';
  const initialNavRef = useRef(null);
  if (initialNavRef.current === null) {
    const verify = parseVerifyFromLocation();
    const internal = parseInternalRouteFromLocation();
    const stored = loadRouteState();
    initialNavRef.current = { verify, internal, stored };
    if (verify?.code != null) (window).__SOFERA_VERIFY_CODE__ = verify.code;
  }
  const storedParamsRef = useRef(initialNavRef.current?.internal?.params || initialNavRef.current?.stored?.params || null);
  const storedSnapshotRef = useRef(initialNavRef.current?.stored?.snapshot || null);
  const [currentView, setCurrentView] = useState(() => {
    const verify = initialNavRef.current?.verify;
    if (verify) return 'public_verify';
    const internalView = initialNavRef.current?.internal?.view;
    const storedView = initialNavRef.current?.stored?.view;
    return internalView || storedView || 'login';
  });
  const [postLoginView, setPostLoginView] = useState(() => {
    const stored = initialNavRef.current?.stored?.postLoginView;
    if (stored) return stored;
    const internal = initialNavRef.current?.internal?.view;
    if (internal && internal !== 'login' && internal !== 'public_verify' && !['reset_password', 'accept_invitation', 'invitation_confirmed', 'invitation_invalid', 'central_auth_callback'].includes(internal)) {
      return internal;
    }
    return null;
  });
  const [currentPartner, setCurrentPartner] = useState(DEFAULT_PARTNER);
  const [centralAuthError, setCentralAuthError] = useState(null);
  const [centralAuthReady, setCentralAuthReady] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState({ number: '', enabled: false });
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const color = normalizeBrandColor(currentPartner?.color);
    const href = buildDynamicFaviconHref(color);
    let favicon = document.querySelector('link[data-sofera-dynamic-favicon="true"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('data-sofera-dynamic-favicon', 'true');
      favicon.setAttribute('rel', 'icon');
      favicon.setAttribute('type', 'image/svg+xml');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('href', href);
  }, [currentPartner?.color]);

  // Carregar cursos reais do Supabase se estiver logado
  useEffect(() => {
    if (!auth.session?.user) return;

    let cancelled = false;
    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        const statusesToFetch = role === 'admin'
          ? ['Ativo', 'published', 'Publicado', 'Rascunho', 'draft']
          : ['Ativo', 'published', 'Publicado'];

        const { data, error } = await supabase
          .from('courses')
          .select(`
            id, category_id, name, slug, description, workload_hours,
            responsible_instructor_id, period_start, period_end, status,
            created_by, created_at, updated_at, assessment_enabled, assessment_id,
            min_grade, max_attempts, questions_per_attempt, sequential_enforcement,
            is_featured, coordinator_id,
            instructor_profile:profiles!courses_responsible_instructor_id_fkey(full_name),
            coordinator_profile:profiles!courses_coordinator_id_fkey(full_name),
            categories:course_category_junction(category_id),
            modules:course_modules(
              *,
              lessons:course_lessons(*)
            ),
            course_lessons(*),
            evaluations:course_evaluations(rating, comment, created_at, approved, email, author_name, profiles!course_evaluations_user_id_fkey(full_name))
          `)
          .in('status', statusesToFetch);

        if (cancelled) return;

        if (error) {
            console.error('Error fetching courses:', error);
            setLoadingCourses(false);
            return;
        }

        // A coluna `courses.image_url` guarda imagens em base64 (até ~2MB por
        // curso). Buscá-la junto com a listagem estourava o statement_timeout do
        // role authenticated (500/57014) e derrubava o carregamento do curso.
        // As capas são buscadas em uma segunda chamada, fora do caminho crítico.
        if (data) {
          supabase
            .from('courses')
            .select('id, image_url')
            .in('id', data.map((c) => c.id))
            .then(({ data: coverRows }) => {
              if (cancelled || !coverRows?.length) return
              const coverById = new Map(coverRows.map((row) => [String(row.id), row.image_url]))
              setCourses((prev) => prev.map((c) => {
                const cover = coverById.get(String(c.id))
                return cover ? { ...c, image_url: cover, thumb: cover } : c
              }))
            })
            .catch(() => {})
        }

        if (data && data.length > 0) {
            // Mapear para o formato esperado pelo frontend
            const mappedCourses = data.map(c => {
                const evaluations = (c.evaluations || []).filter(e => e.approved);
                const ratingCount = evaluations.length;
                const averageRating = ratingCount > 0
                  ? evaluations.reduce((sum, e) => sum + e.rating, 0) / ratingCount
                  : 0;

                const s = String(c.status || '').toLowerCase();
                const mappedStatus = (s === 'rascunho' || s === 'draft') ? 'draft' : 'published';

                return {
                ...c,
                id: c.id, // UUID
                status: mappedStatus,
                title: c.name || c.title,
                description: c.description,
                thumb: c.image_url || c.thumb || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=800",
                workload: formatCourseWorkloadHHMM(c.workload_hours || c.workload),
                instructor: c.instructor_profile?.full_name || c.responsible_instructor_id || "Instrutor",
                coordinator: c.coordinator_profile?.full_name || c.coordinator_id || "Coordenador",
                categories: c.categories?.map(cat => cat.category_id) || (c.category_id ? [c.category_id] : []) || [],
                modules: c.modules?.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).map(m => ({
                  ...m,
                  lessons: m.lessons?.sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                })),
                standaloneLessons: c.course_lessons?.filter(l => l.module_id === null).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
                isFeatured: c.is_featured,
                assessmentEnabled: c.assessment_enabled,
                assessmentId: c.assessment_id,
                minGrade: c.min_grade,
                maxAttempts: c.max_attempts,
                questionsPerAttempt: c.questions_per_attempt,
                sequentialEnforcement: c.sequential_enforcement,
                averageRating,
                ratingCount,
                evaluations
            }});

            // Set real courses directly
            setCourses(mappedCourses);
        } else {
            setCourses([]);
        }
      } catch (err) {
        console.error('Failed to load courses:', err);
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    };

    fetchCourses();
    return () => { cancelled = true; };
  }, [auth.session?.user?.id, userId, role]);

  const studentCourseIdsKey = React.useMemo(
    () => courses.map((course) => String(course.id)).sort().join('|'),
    [courses]
  )

  useEffect(() => {
    if (!auth.session?.user?.id || !studentCourseIdsKey) return

    let cancelled = false

    const hydrateStudentCourseState = async () => {
      try {
        const courseIds = studentCourseIdsKey.split('|').filter(Boolean)
        if (!courseIds.length) return

        const [
          { data: enrollmentRows },
          { data: completionRows },
          { data: attemptRows },
          { data: lessonRows }
        ] = await Promise.all([
          supabase
            .from('enrollments')
            .select(`
              progress_percent,
              status,
              expires_at,
              offering:offering_id (
                course_id
              )
            `)
            .eq('student_id', auth.session.user.id),
          supabase
            .from('course_completions')
            .select('course_id')
            .eq('student_id', auth.session.user.id)
            .in('course_id', courseIds),
          supabase
            .from('assessment_attempts')
            .select('course_id, score')
            .eq('student_id', auth.session.user.id)
            .is('lesson_id', null)
            .eq('status', 'completed')
            .in('course_id', courseIds),
          supabase
            .from('course_lessons')
            .select('id, course_id')
            .in('course_id', courseIds)
        ])

        if (cancelled) return

        const progressByCourse = new Map()
        for (const row of (enrollmentRows || []).filter((entry) => normalizeEnrollmentStatus(entry?.status, entry?.expires_at) === 'Ativa')) {
          const courseId = String(row?.offering?.course_id || '')
          if (!courseId) continue
          const currentValue = progressByCourse.get(courseId) || 0
          progressByCourse.set(courseId, Math.max(currentValue, Number(row?.progress_percent) || 0))
        }

        const completionSet = new Set((completionRows || []).map((row) => String(row.course_id)))
        const lessonIdsByCourse = new Map()
        const allLessonIds = []
        for (const row of lessonRows || []) {
          const courseId = String(row?.course_id || '')
          const lessonId = row?.id
          if (!courseId || !lessonId) continue
          const lessonIds = lessonIdsByCourse.get(courseId) || []
          lessonIds.push(lessonId)
          lessonIdsByCourse.set(courseId, lessonIds)
          allLessonIds.push(lessonId)
        }

        let completedLessonIdSet = new Set()
        if (allLessonIds.length > 0) {
          const { data: lessonProgressRows } = await supabase
            .from('lesson_progress')
            .select('lesson_id')
            .eq('student_id', auth.session.user.id)
            .in('lesson_id', allLessonIds)
            .not('completed_at', 'is', null)

          completedLessonIdSet = new Set((lessonProgressRows || []).map((row) => row.lesson_id))
        }

        const dbProgressByCourse = new Map()
        for (const [courseId, lessonIds] of lessonIdsByCourse.entries()) {
          if (!lessonIds.length) {
            dbProgressByCourse.set(courseId, 0)
            continue
          }
          const completedCount = lessonIds.filter((lessonId) => completedLessonIdSet.has(lessonId)).length
          dbProgressByCourse.set(courseId, Math.round((completedCount / lessonIds.length) * 100))
        }

        const bestScoreByCourse = new Map()
        for (const row of attemptRows || []) {
          const courseId = String(row?.course_id || '')
          if (!courseId) continue
          const currentValue = bestScoreByCourse.get(courseId) ?? null
          const nextValue = Number(row?.score)
          if (!Number.isFinite(nextValue)) continue
          bestScoreByCourse.set(courseId, currentValue == null ? nextValue : Math.max(currentValue, nextValue))
        }

        setCourses((prev) => prev.map((course) => {
          const courseId = String(course.id)
          const uiProgressPercent = progressByCourse.get(courseId) || 0
          const dbProgressPercent = dbProgressByCourse.get(courseId) || 0
          const bestAssessmentScore = bestScoreByCourse.get(courseId) ?? null
          const minGrade = Number(course.minGrade ?? course.min_grade ?? 7)
          const completionEligibility = getCourseCompletionEligibility({
            progressPercent: resolveUnifiedProgress(
              course.progressPercent,
              course.db_progress_percent,
              course.ui_progress_percent,
              uiProgressPercent,
              dbProgressPercent
            ),
            assessmentEnabled: Boolean(course.assessment_enabled || course.assessmentEnabled || course.assessment_id || course.assessmentId),
            assessmentId: course.assessment_id || course.assessmentId || null,
            bestScore: bestAssessmentScore,
            minGrade,
          })
          const isCourseCompleted = completionEligibility.assessmentRequired
            ? completionEligibility.canComplete
            : (completionSet.has(courseId) || completionEligibility.canComplete)
          const normalizedProgress = isCourseCompleted
            ? 100
            : resolveUnifiedProgress(
              course.progressPercent,
              course.db_progress_percent,
              course.ui_progress_percent,
              uiProgressPercent,
              dbProgressPercent
            )

          return {
            ...course,
            best_assessment_score: bestAssessmentScore,
            is_course_completed: isCourseCompleted,
            db_progress_percent: isCourseCompleted ? 100 : dbProgressPercent,
            ui_progress_percent: isCourseCompleted ? 100 : uiProgressPercent,
            progressPercent: normalizedProgress,
            progress: normalizedProgress
          }
        }))
      } catch (err) {
        console.error('Failed to hydrate student course state:', err)
      }
    }

    hydrateStudentCourseState()

    return () => {
      cancelled = true
    }
  }, [auth.session?.user?.id, studentCourseIdsKey])


  const [myEnrolledCourseIds, setMyEnrolledCourseIds] = useState(new Set());
  const [myCourseEnrollmentStatusById, setMyCourseEnrollmentStatusById] = useState(new Map());
  const [myCourseEnrollmentExpiresAtById, setMyCourseEnrollmentExpiresAtById] = useState(new Map());
  const [classes, setClasses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [models, setModels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedClass, setSelectedClass] = useState(() => {
    const id = storedParamsRef.current?.classId
    if (id == null) return null
    const snap = storedSnapshotRef.current
    if (snap && String(snap.id) === String(id)) return snap
    const found = (MOCK_CLASSES || []).find(c => String(c.id) === String(id))
    return found || null
  });
  const [selectedCourse, setSelectedCourse] = useState(() => {
    const id = storedParamsRef.current?.courseId
    if (id == null) return null
    const snap = storedSnapshotRef.current
    if (snap && String(snap.id) === String(id)) return snap
    // Fallback search when courses load is handled in a useEffect
    return null
  });
  const [templates, setTemplates] = useState([]);
  const [defaultEmailHeader, setDefaultEmailHeader] = useState('');
  const [defaultEmailFooter, setDefaultEmailFooter] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCourseInviteGateActive, setIsCourseInviteGateActive] = useState(false);
  // Curso/Turma por "Convite Link" que o aluno está acessando (usado pelo botão "Sair").
  const [inviteLinkCourseId, setInviteLinkCourseId] = useState(null);
  const [inviteLinkClassId, setInviteLinkClassId] = useState(null);
  // Callback estável: o curso informa que pertence a uma turma de "Convite Link".
  const handleInviteLinkCourseChange = React.useCallback((courseId, classId) => {
    setInviteLinkCourseId(courseId || null);
    setInviteLinkClassId(classId || null);
  }, []);
  // Tela do código exibida ao clicar em "Sair" dentro de um curso de "Convite Link".
  const [inviteCodeScreen, setInviteCodeScreen] = useState(null); // { courseId, classId } | null
  const [assessments, setAssessments] = useState([]);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ 
    fullName: '', 
    cpf: '', 
    whatsapp: '',
    status: '',
    role: '',
    companyId: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);

  const getCourseEnrollmentStatus = React.useCallback((course) => {
    if (!course?.id) return '';
    return String(myCourseEnrollmentStatusById.get(String(course.id)) || course.enrollment_status || '');
  }, [myCourseEnrollmentStatusById]);

  const getCourseEnrollmentExpiresAt = React.useCallback((course) => {
    if (!course?.id) return course?.enrollment_expires_at || null;
    return myCourseEnrollmentExpiresAtById.get(String(course.id)) || course?.enrollment_expires_at || null;
  }, [myCourseEnrollmentExpiresAtById]);

  const formatCourseEnrollmentExpiresAt = React.useCallback((course) => {
    const expiresAt = getCourseEnrollmentExpiresAt(course);
    if (!expiresAt) return '';
    return formatEnrollmentExpiresAt(expiresAt);
  }, [getCourseEnrollmentExpiresAt]);

  const formatCourseEnrollmentExpiresAtShort = React.useCallback((course) => {
    const expiresAt = getCourseEnrollmentExpiresAt(course);
    if (!expiresAt) return '';
    return formatEnrollmentExpiresAt(expiresAt, { year: '2-digit' });
  }, [getCourseEnrollmentExpiresAt]);

  const resolveCoursePersonName = React.useCallback((course, roleKey) => {
    const isInstructor = roleKey === 'instructor';
    const directNameCandidates = isInstructor
      ? [
          course?.instructor_name,
          course?.responsible_instructor_name,
        ]
      : [
          course?.coordinator_name,
        ];
    const nameFromCourse = directNameCandidates
      .map((value) => String(value || '').trim())
      .find(Boolean);
    if (nameFromCourse) return nameFromCourse;

    const rawValue = isInstructor
      ? (course?.instructor || course?.responsible_instructor_id)
      : (course?.coordinator || course?.coordinator_id);

    const userMatch = users.find(
      (user) =>
        user?.id === rawValue
        || user?.name === rawValue
        || user?.full_name === rawValue
    );
    if (userMatch) {
      return String(userMatch?.name || userMatch?.full_name || '').trim() || '-';
    }

    const fallbackValue = String(rawValue || '').trim();
    if (!fallbackValue) return '-';
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(fallbackValue);
    return looksLikeUuid ? '-' : fallbackValue;
  }, [users]);

  const isCourseAccessBlocked = React.useCallback((course) => {
    return isEnrollmentAccessBlocked(getCourseEnrollmentStatus(course));
  }, [getCourseEnrollmentStatus]);

  // Aluno só tem acesso ao conteúdo do curso se possuir matrícula (mesma regra de "Meus Cursos").
  // Considera tanto matrículas diretas (enrollments) quanto vínculos via turma (class_students),
  // pois turmas no modo "Convite Link" matriculam o aluno apenas em class_students.
  const classEnrolledCourseIds = React.useMemo(() => {
    const ids = new Set();
    for (const cls of classes || []) {
      for (const cid of cls?.courses || []) {
        if (cid) ids.add(String(cid));
      }
    }
    return ids;
  }, [classes]);

  const isEnrolledInCourse = React.useCallback((course) => {
    if (!course?.id) return false;
    const courseId = String(course.id);
    if (myEnrolledCourseIds && myEnrolledCourseIds.has(courseId)) return true;
    if (classEnrolledCourseIds.has(courseId)) return true;
    return course.is_enrolled === true;
  }, [myEnrolledCourseIds, classEnrolledCourseIds]);

  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyDoc, setPolicyDoc] = useState(null);

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsDoc, setTermsDoc] = useState(null);
  const [showWhatsAppSupportModal, setShowWhatsAppSupportModal] = useState(false);

  // --- Avisos para usuários ---
  const [userNotices, setUserNotices] = useState([])
  const [showUserNoticesModal, setShowUserNoticesModal] = useState(false)
  const isPublicOnlyView = ['login', 'reset_password', 'accept_invitation', 'invitation_confirmed', 'invitation_invalid'].includes(currentView)
  // Rota pública de "Convite Link": /courses/{courseId}?turma={classId} aberta sem login.
  // O aluno digita o código e a Edge Function `class-invite-login` emite a sessão dele.
  const publicInviteClassId = currentView === 'student_course_view' ? (storedParamsRef.current?.classId ?? null) : null;
  const publicInviteCourseId = currentView === 'student_course_view' ? (storedParamsRef.current?.courseId ?? null) : null;
  const isPublicInviteAccess = !auth.isAuthenticated && currentView === 'student_course_view' && !!publicInviteClassId;
  const shouldShowUserNotices = auth.isAuthenticated && !auth.isAdmin && !isPublicOnlyView
  const shouldShowLoggedAreaOverlays = auth.isAuthenticated && !isPublicOnlyView && !isCourseInviteGateActive

  const loadUserNotices = async () => {
    try {
      const uid = auth?.session?.user?.id
      if (!uid || !shouldShowUserNotices) return
      const nowIso = new Date().toISOString()
      const { data: all, error } = await supabase
        .from('system_notices')
        .select('*')
        .eq('is_active', true)
        .or(`valid_from.is.null,valid_from.lte.${nowIso}`)
        .or(`valid_until.is.null,valid_until.gte.${nowIso}`)
        .order('created_at', { ascending: false })
      if (error) throw error

      const { data: reads } = await supabase
        .from('system_notice_reads')
        .select('notice_id')
        .eq('user_id', uid)
      const readSet = new Set((reads||[]).map(r=>r.notice_id))

      const roleName = auth?.profileRole || auth?.profile?.role || ''
      const companyId = auth?.profile?.companyId || auth?.profile?.company_id || null
      const partnerId = auth?.profile?.sasPartnerId || auth?.profile?.sas_partner_id || null
      const enrolledIds = Array.from(myEnrolledCourseIds || new Set()).map(String)

      const applicable = (all||[]).filter(n => {
        if (readSet.has(n.id)) return false
        const rolesOk = !n.target_roles || n.target_roles.length === 0 || (roleName && n.target_roles.includes(roleName))
        const companiesOk = !n.target_companies || n.target_companies.length === 0 || (companyId && n.target_companies.includes(String(companyId)))
        const partnersOk = !n.target_partners || n.target_partners.length === 0 || (partnerId && n.target_partners.includes(String(partnerId)))
        const coursesOk = !n.target_courses || n.target_courses.length === 0 || n.target_courses.some(cid => enrolledIds.includes(String(cid)))
        return rolesOk && companiesOk && partnersOk && coursesOk
      })

      const limited = applicable.slice(0, 5)
      setUserNotices(limited)
      setShowUserNoticesModal(limited.length > 0)
    } catch (e) {
      console.error('Falha ao carregar avisos do usuário:', e)
    }
  }

  useEffect(() => {
    if (!shouldShowUserNotices) {
      setShowUserNoticesModal(false)
      return
    }
    loadUserNotices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowUserNotices, myEnrolledCourseIds.size])

  useEffect(() => {
    if (!shouldShowLoggedAreaOverlays) {
      setShowUserProfile(false)
      setShowUserNoticesModal(false)
    }
  }, [shouldShowLoggedAreaOverlays])

  const handleMarkNoticeRead = async (noticeId) => {
    try {
      const uid = auth?.session?.user?.id
      if (!uid || !noticeId) return
      await supabase.from('system_notice_reads').upsert({ user_id: uid, notice_id: noticeId, read_at: new Date().toISOString() }, { onConflict: 'user_id,notice_id' })
      
      const updatedNotices = userNotices.filter(n => n.id !== noticeId)
      setUserNotices(updatedNotices)
      
      if (updatedNotices.length === 0) {
        setShowUserNoticesModal(false)
      }
    } catch (e) {
      console.error('Erro ao marcar aviso como lido:', e)
      setShowUserNoticesModal(false)
    }
  }

  const loadLatestLegalDocument = async (kind) => {
    const normalizeKind = (raw) => {
      const v = String(raw || '').trim().toLowerCase()
      if (['privacy', 'privacy_policy', 'politica_privacidade', 'politica-de-privacidade'].includes(v)) return 'privacy'
      if (['terms', 'terms_of_use', 'termos', 'termos_de_uso', 'termos-de-uso'].includes(v)) return 'terms'
      return v === 'privacy' || v === 'terms' ? v : 'terms'
    }
    const kindNormalized = normalizeKind(kind)

    const normalizeDoc = (raw) => {
      if (!raw || typeof raw !== 'object') return null
      const content = raw.content ?? raw.html ?? raw.document_html ?? raw.body_html ?? raw.body ?? null
      if (!content) return null
      return {
        ...raw,
        content: String(content),
        last_updated: raw.last_updated || raw.updated_at || new Date().toISOString(),
        version: raw.version || raw.document_version || 'local',
      }
    }
    const extractDocFromPayload = (payload) => {
      const queue = [payload]
      const visited = new Set()
      while (queue.length > 0) {
        const current = queue.shift()
        if (!current || typeof current !== 'object') continue
        if (visited.has(current)) continue
        visited.add(current)

        const directDoc = normalizeDoc(current)
        if (directDoc) return directDoc

        if (Array.isArray(current)) {
          for (const item of current) queue.push(item)
          continue
        }

        for (const key of ['doc', 'document', 'latest_document', 'item', 'items', 'result', 'data', 'docs']) {
          if (current[key] != null) queue.push(current[key])
        }
      }
      return null
    }

    const systemNames = []
    const addSystemName = (v) => {
      const s = String(v || '').trim()
      if (!s) return
      if (!systemNames.includes(s)) systemNames.push(s)
    }
    addSystemName('SOFERA')
    addSystemName(currentPartner?.name)
    addSystemName(String(currentPartner?.name || '').toUpperCase())

    const payloadBody = { kind: kindNormalized, system_names: systemNames }
    const payloads = []
    try {
      const data = await invokeEdgeFunction('get_legal_document', {
        body: payloadBody,
        accessToken: auth?.session?.access_token,
      })
      payloads.push(data)
    } catch {}
    try {
      const { data, error } = await supabase.functions.invoke('get_legal_document', { body: payloadBody })
      if (!error) payloads.push(data)
    } catch {}

    for (const payload of payloads) {
      const doc = extractDocFromPayload(payload)
      if (doc) return doc
    }
    return null
  }

  const openPrivacyPolicy = async () => {
    setPolicyModalOpen(true)
    setPolicyLoading(true)
    try {
      const doc = await loadLatestLegalDocument('privacy')
      setPolicyDoc(doc || { content: '<p class="text-slate-300">Política de Privacidade indisponível no momento.</p>', last_updated: new Date().toISOString(), version: '-' })
    } catch {
      setPolicyDoc({ content: '<p class="text-slate-300">Não foi possível carregar a Política de Privacidade.</p>', last_updated: new Date().toISOString(), version: '-' })
    } finally {
      setPolicyLoading(false)
    }
  }

  const openTermsOfUse = async () => {
    setTermsModalOpen(true)
    setTermsLoading(true)
    try {
      const doc = await loadLatestLegalDocument('terms')
      setTermsDoc(doc || { content: '<p class="text-slate-300">Termos de Uso indisponíveis no momento.</p>', last_updated: new Date().toISOString(), version: '-' })
    } catch {
      setTermsDoc({ content: '<p class="text-slate-300">Não foi possível carregar os Termos de Uso.</p>', last_updated: new Date().toISOString(), version: '-' })
    } finally {
      setTermsLoading(false)
    }
  }

  const handleOpenWhatsAppSupportModal = () => {
    setShowWhatsAppSupportModal(true);
  };

  const handleConfirmWhatsAppSupport = async () => {
    const userName = String(auth?.profile?.fullName || auth?.profile?.full_name || auth?.session?.user?.user_metadata?.full_name || '').trim();
    const userEmail = String(auth?.session?.user?.email || auth?.profile?.email || auth?.session?.user?.user_metadata?.email || '').trim();
    let companyName = String(auth?.profile?.companyName || auth?.profile?.company_name || '').trim();

    if (!companyName) {
      const companyId = auth?.profile?.companyId || auth?.profile?.company_id || null;
      if (companyId) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('name')
            .eq('id', companyId)
            .maybeSingle();
          if (!error) {
            companyName = String(data?.name || '').trim();
          }
        } catch (err) {
          console.error('Erro ao resolver empresa para mensagem de suporte WhatsApp:', err);
        }
      }
    }

    const supportMessage = [
      `Olá! Sou ${userName || 'um usuário'} da SOFERA e preciso de suporte.`,
      `E-mail: ${userEmail || 'não informado'}`,
      `Empresa: ${companyName || 'não informada'}`
    ].join('\n');
    const whatsappDigits = String(whatsappConfig.number || '').replace(/\D/g, '')
    const whatsappFullNumber = (whatsappDigits.startsWith('55') && (whatsappDigits.length === 12 || whatsappDigits.length === 13))
      ? whatsappDigits
      : `55${whatsappDigits}`
    const whatsappUrl = `https://wa.me/${whatsappFullNumber}?text=${encodeURIComponent(supportMessage)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    setShowWhatsAppSupportModal(false);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const updateData = {
        full_name: profileForm.fullName,
        cpf: profileForm.cpf.replace(/\D/g, ""),
        whatsapp: profileForm.whatsapp.replace(/\D/g, "")
      };

      if (auth.isAdmin) {
        updateData.status = profileForm.status;
        updateData.role = profileForm.role;
        updateData.company_id = profileForm.companyId || null;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', auth.session?.user?.id);
      
      if (error) throw error;
      
      await auth.refreshAuthorization();
      if (typeof setAppNotification === 'function') {
        setAppNotification({ message: 'Perfil atualizado com sucesso.', type: 'success' });
      } else {
        console.log('Perfil atualizado com sucesso.');
      }
      setProfileEditing(false);
    } catch (err) {
      console.error(err);
      if (typeof setAppNotification === 'function') {
        setAppNotification({ message: 'Erro ao atualizar perfil.', type: 'error' });
      } else {
        console.error('Erro ao atualizar perfil.', err);
      }
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => {
    if (showUserProfile && auth.profile) {
      setProfileForm({
        fullName: auth.profile.fullName || '',
        cpf: auth.profile.cpf ? maskCPF(auth.profile.cpf) : '',
        whatsapp: auth.profile.whatsapp ? maskWhatsApp(auth.profile.whatsapp) : '',
        status: auth.profile.status || 'Ativo',
        role: auth.profile.role || 'student',
        companyId: auth.profile.companyId || ''
      });
      setProfileEditing(false);
    }
  }, [showUserProfile, auth.profile]);

  const adminRenderableViews = useRef(new Set([
    'admin_dashboard',
    'admin_reports',
    'admin_companies',
    'admin_users',
    'admin_users_ro',
    'admin_communications',
    'admin_whatsapp',
    'admin_certs',
    'admin_certs_issued',
    'admin_certs_manual',
    'admin_certs_models',
    'public_verify',
    'admin_partners',
    'admin_categories',
    'admin_connections',
    'admin_courses',
    'admin_classes',
    'admin_class_details',
    'admin_enrollments',
    'admin_library',
    'admin_idiomas',
    'admin_assessments',
    'admin_service_orders',
    'admin_invites',
    'student_course_view',
    'student_courses',
    'student_enrollments',
    'student_certs'
  ]))

  useEffect(() => {
    if (auth.bootLoading) return;
    if (!auth.isAuthenticated || !auth.isAdmin) return;
    const relevantViews = new Set(['admin_certs_issued','admin_certs_manual','admin_certs_models', 'admin_communications', 'admin_classes', 'admin_class_details']);
    if (!relevantViews.has(currentView)) return;
    const load = async () => {
      let rows = []
      let t = []
      let es = []

      try {
        const results = await Promise.allSettled([
          supabase.from('templates').select('*').order('created_at', { ascending: false }),
          supabase.from('email_settings').select('*').order('updated_at', { ascending: false }).limit(1),
          listCertificateTemplates()
        ]);
        
        if (results[0].status === 'fulfilled' && results[0].value.data) t = results[0].value.data;
        if (results[1].status === 'fulfilled' && results[1].value.data) es = results[1].value.data;
        if (results[2].status === 'fulfilled') rows = results[2].value || [];
      } catch (err) {
        console.error('Error loading initial data', err);
      }

      if (t.length) setTemplates(t);
      if (es.length) {
        setDefaultEmailHeader(es[0].default_header_url || '');
        setDefaultEmailFooter(es[0].default_footer_url || '');
      }

      try {
        const mapped = await Promise.all((rows||[]).map(async (r) => {
          try {
            const [frontUrl, backUrl] = await Promise.all([
              getSignedUrl('certificate-templates', r.front_image_path),
              getSignedUrl('certificate-templates', r.back_image_path)
            ]);
            
            let creatorName = 'Desconhecido';
            if (r.created_by) {
               const { data: p } = await supabase.from('profiles').select('full_name, email').eq('id', r.created_by).single();
               if (p) creatorName = p.full_name || p.email || 'Desconhecido';
            }

            const meta = r.meta || {};
            const sec = meta.back_sections || meta.back || {};
            const legacyHtml = meta.back_html || '';
            const stripLegacyDeclarationContainer = (html) => String(html || '').replace(/<div[^>]*data-averbacao=\"true\"[^>]*>[\s\S]*?<\/div>/, '').trim();
            const extractLegacyDeclarationHtml = (html) => {
              const m = String(html || '').match(/<div[^>]*data-averbacao=\"true\"[^>]*>([\s\S]*?)<\/div>/);
              return m ? String(m[1] || '').trim() : '';
            };
            const backShowQrCode = (typeof sec.show_qr_code === 'boolean')
              ? sec.show_qr_code
              : (typeof meta.back_qr_enabled === 'boolean')
                ? meta.back_qr_enabled
                : /data-qr=\"true\"|qrserver|qrcode_url/i.test(legacyHtml);
            const backHeaderHtml = sec.header_html || '';
            const backMainHtml = sec.main_html || stripLegacyDeclarationContainer(legacyHtml) || '';
            const backInstitutionalHtml = sec.institutional_html || '';
            const backDeclarationHtml = sec.declaration_html || (meta.averbacao_text ? plainTextToHtml(meta.averbacao_text) : extractLegacyDeclarationHtml(legacyHtml));
            return {
              id: r.id,
              name: r.name,
              isDefault: !!r.is_default,
              sasPartnerId: r.sas_partner_id || null,
              description: r.description || '',
              status: r.active ? 'Ativo' : 'Inativo',
              frontImage: frontUrl,
              backImage: backUrl,
              variables: Array.isArray(r.variables) ? r.variables : (r.variables || []),
              frontFields: meta.front_fields || [],
              backFields: meta.back_fields || [],
              customTextFront: r.html || '',
              customTextBack: meta.back_html || '',
              meta,
              sasPartnerIds: r.sas_partner_ids || [],
              backHeaderHtml,
              backMainHtml,
              backInstitutionalHtml,
              backDeclarationHtml,
              backShowQrCode,
              useHtml: (typeof r.use_html === 'boolean' ? r.use_html : (typeof meta.use_html === 'boolean' ? meta.use_html : true)),
              createdAt: r.created_at,
              updatedAt: r.updated_at,
              createdBy: creatorName,
              lineHeightFront: (meta.layout_front && typeof meta.layout_front.lineHeight === 'number') ? meta.layout_front.lineHeight : 1.6,
              lineHeightBack: (meta.layout_back && typeof meta.layout_back.lineHeight === 'number') ? meta.layout_back.lineHeight : 1.6,
              xFront: (meta.layout_front && typeof meta.layout_front.x === 'number') ? meta.layout_front.x : 15,
              yFront: (meta.layout_front && typeof meta.layout_front.y === 'number') ? meta.layout_front.y : 18,
              widthFront: (meta.layout_front && typeof meta.layout_front.width === 'number') ? meta.layout_front.width : 70,
              xBack: (meta.layout_back && typeof meta.layout_back.x === 'number') ? meta.layout_back.x : 15,
              yBack: (meta.layout_back && typeof meta.layout_back.y === 'number') ? meta.layout_back.y : 18,
              widthBack: (meta.layout_back && typeof meta.layout_back.width === 'number') ? meta.layout_back.width : 70,
              backQrX: (meta.layout_back && meta.layout_back.qr && typeof meta.layout_back.qr.x === 'number') ? meta.layout_back.qr.x : 85,
              backQrY: (meta.layout_back && meta.layout_back.qr && typeof meta.layout_back.qr.y === 'number') ? meta.layout_back.qr.y : 50,
              backDeclarationX: (meta.layout_back && meta.layout_back.declaration && typeof meta.layout_back.declaration.x === 'number') ? meta.layout_back.declaration.x : 50,
              backDeclarationY: (meta.layout_back && meta.layout_back.declaration && typeof meta.layout_back.declaration.y === 'number') ? meta.layout_back.declaration.y : 85,
              backDeclarationWidth: (meta.layout_back && meta.layout_back.declaration && typeof meta.layout_back.declaration.width === 'number') ? meta.layout_back.declaration.width : ((meta.layout_back && typeof meta.layout_back.width === 'number') ? meta.layout_back.width : 70)
            }
          } catch (err) {
            console.error('Error mapping model', r.id, err);
            return null;
          }
        }))
        setModels(mapped.filter(Boolean))
      } catch (err) {
        console.error('Error loading models', err);
      }
    };
    load();
  }, [auth.bootLoading, auth.isAuthenticated, auth.isAdmin, currentView]);

  useEffect(() => {
    if (!auth.session?.user) return;
    const loadCategoriesFromSupabase = async () => {
      try {
        const { data, error } = await supabase
          .from('course_categories')
          .select('*')
          .order('name', { ascending: true });
        if (error) throw error;
        const mapped = (data || []).map(r => {
          const s = String(r.status || '').toLowerCase();
          const isActive = (typeof r.status === 'boolean') ? r.status : (s === 'ativo' || s === 'publicado' || s === 'true' || s === 'active');
          
          return {
            id: r.id,
            name: r.name,
            description: r.description || '',
            status: isActive,
            domain: r.domain || null,
            linked_category_ids: r.linked_category_ids || [], // Incluído para vitrine
            ordem: r.ordem || 0, // Incluído para ordenação
            image_url: r.image_url || null
          };
        });
        setCategories(mapped);
      } catch {
        const { data, error } = await supabase
          .from('course_categories')
          .select('id, name, description, status, domain, linked_category_ids, ordem, image_url')
          .order('name', { ascending: true });
        if (!error) setCategories((data || []).map(r => {
            const s = String(r.status || '').toLowerCase();
            const isActive = (typeof r.status === 'boolean') ? r.status : (s === 'ativo' || s === 'publicado' || s === 'true' || s === 'active');
            return { 
                id: r.id, 
                name: r.name, 
                description: r.description || '', 
                status: isActive, 
                domain: r.domain || null,
                linked_category_ids: r.linked_category_ids || [],
                ordem: r.ordem || 0,
                image_url: r.image_url || null
            };
        }));
      }
    };
    loadCategoriesFromSupabase();
  }, [auth.session?.user?.id]);

  useEffect(() => {
    if (auth.bootLoading) return;
    if (!auth.isAuthenticated) {
      setCurrentPartner(DEFAULT_PARTNER);
      return;
    }

    const loadPartnerData = async () => {
      try {
        const partnerId = auth.profile?.sasPartnerId || auth.profile?.sas_partner_id;
        
        if (partnerId) {
          const { data, error } = await supabase
            .from('sas_partners')
            .select('id, name, site, color, default_url, status, logo_url, created_at')
            .eq('id', partnerId)
            .maybeSingle();

          if (!error && data) {
            setCurrentPartner({
              id: data.id,
              name: data.name,
              site: data.site || '',
              color: data.color || '#f97316',
              defaultUrl: data.default_url || '',
              status: data.status || 'Ativo',
              logo: data.logo_url || '',
            });
            return;
          }
        }
        
        // Se não tiver parceiro ou erro na busca, usar o primeiro parceiro disponível (se admin) ou default
        if (auth.isAdmin) {
          const { data: partnersRes } = await supabase
            .from('sas_partners')
            .select('id, name, site, color, default_url, status, logo_url, created_at')
            .eq('status', 'Ativo')
            .order('created_at', { ascending: false });

          if (partnersRes && partnersRes.length > 0) {
            const p = partnersRes[0];
            setCurrentPartner({
              id: p.id,
              name: p.name,
              site: p.site || '',
              color: p.color || '#f97316',
              defaultUrl: p.default_url || '',
              status: p.status || 'Ativo',
              logo: p.logo_url || '',
            });
            return;
          }
        }

        setCurrentPartner(DEFAULT_PARTNER);
      } catch (err) {
        console.error('Error loading partner data:', err);
        setCurrentPartner(DEFAULT_PARTNER);
      }
    };

    loadPartnerData();
  }, [auth.bootLoading, auth.isAuthenticated, auth.profile?.sasPartnerId]);

  useEffect(() => {
    let mounted = true;

    const loadWhatsAppConfig = async () => {
      try {
        const fetchConfig = async ({ partnerId } = {}) => {
          let query = supabase
          .from('email_settings')
          .select('whatsapp_number, whatsapp_enabled, updated_at')
            .eq('whatsapp_enabled', true)
            .not('whatsapp_number', 'is', null)
          .order('updated_at', { ascending: false })
          .limit(1);

          if (partnerId) {
            query = query.eq('partner_id', partnerId);
          }

          const { data, error } = await query.maybeSingle();
          if (error) throw error;
          return data || null;
        };

        let data = null;

        if (currentPartner?.id) {
          data = await fetchConfig({ partnerId: currentPartner.id });
        }

        if (!data) {
          data = await fetchConfig();
        }

        if (!mounted) return;

        const number = normalizeWhatsappNumber(data?.whatsapp_number || '');
        setWhatsappConfig({
          number,
          enabled: Boolean(data?.whatsapp_enabled && number)
        });
      } catch (err) {
        console.error('Erro ao carregar configuração de WhatsApp para usuário:', err);
        if (mounted) setWhatsappConfig({ number: '', enabled: false });
      }
    };

    loadWhatsAppConfig();
    return () => { mounted = false; };
  }, [currentPartner?.id]);

  useEffect(() => {
    if (auth.bootLoading) return;
    if (!auth.isAuthenticated) return;
    if (!auth.isAdmin) return;

    const loadAdminLookups = async () => {
      try {
        let companiesQuery = supabase.from('companies').select('id, name, created_at').order('name', { ascending: true });
        
        if (isPartnerScopedRole(auth.profileRole)) {
          const partnerId = auth.profile?.sasPartnerId;
          
          if (partnerId) {
            const [{ data: pData }, { data: cData }] = await Promise.all([
              supabase.from('profiles').select('company_id').eq('sas_partner_id', partnerId).not('company_id', 'is', null),
              supabase.from('classes').select('company_id').eq('partner_id', partnerId).not('company_id', 'is', null)
            ]);
              
            const compIdsFromProfiles = pData ? pData.map(p => p.company_id) : [];
            const compIdsFromClasses = cData ? cData.map(c => c.company_id) : [];
            const allowedCompanyIds = [...new Set([...compIdsFromProfiles, ...compIdsFromClasses])];
            
            if (allowedCompanyIds.length > 0) {
              companiesQuery = companiesQuery.in('id', allowedCompanyIds);
            } else {
              // If no companies allowed, we simulate an empty response
              companiesQuery = Promise.resolve({ data: [], error: null });
            }
          }
        }

        const [companiesRes, partnersRes, profilesRes, assessmentsRes, companyLinksRes] = await Promise.all([
          companiesQuery,
          supabase.from('sas_partners').select('id, name, site, color, default_url, status, logo_url, created_at').order('created_at', { ascending: false }),
          supabase.from('profiles').select('id, full_name, email, role, status, company_id, sas_partner_id, area, cargo, whatsapp, central_person_id, companies:companies!profiles_company_id_fkey(name), sas_partners(name)').order('full_name', { ascending: true }),
          supabase.from('assessments').select('*').order('title', { ascending: true }),
          supabase.from('profile_company_links').select('profile_id, company_id'),
        ]);

        if (!companiesRes.error) {
          const nextCompanies = (companiesRes.data || []).map(c => ({ id: c.id, name: c.name }));
          setCompanies(nextCompanies);
        }

        // Vínculos ponto focal ↔ empresas (inclui empresas adicionais além da principal).
        const linkedCompaniesByProfile = new Map();
        if (!companyLinksRes.error) {
          for (const link of (companyLinksRes.data || [])) {
            if (!link?.profile_id || !link?.company_id) continue;
            const key = String(link.profile_id);
            const list = linkedCompaniesByProfile.get(key) || [];
            list.push(link.company_id);
            linkedCompaniesByProfile.set(key, list);
          }
        }

        if (!profilesRes.error) {
          const nextUsers = (profilesRes.data || []).map(p => {
            const primaryCompanyIds = p.company_id ? [p.company_id] : [];
            const extraCompanyIds = linkedCompaniesByProfile.get(String(p.id)) || [];
            const companyIds = Array.from(new Set([...primaryCompanyIds, ...extraCompanyIds].map(String)));
            return {
              id: p.id,
              name: p.full_name || p.email,
              full_name: p.full_name,
              email: p.email,
              profile: p.role,
              status: p.status,
              company_id: p.company_id,
              companyIds,
              sas_partner_id: p.sas_partner_id,
              area: p.area,
              cargo: p.cargo,
              central_person_id: p.central_person_id,
              whatsapp: p.whatsapp || '',
              company_name: p.companies?.name,
              partner_name: p.sas_partners?.name
            };
          });
          setUsers(nextUsers);
        }
        
        if (!assessmentsRes.error) {
            setAssessments(assessmentsRes.data || []);
        }

        if (!partnersRes.error) {
          const nextPartners = (partnersRes.data || []).map(p => ({
            id: p.id,
            name: p.name,
            site: p.site || '',
            color: p.color || '#f97316',
            defaultUrl: p.default_url || '',
            status: p.status || 'Ativo',
            logo: p.logo_url || '',
          }));
          setPartners(nextPartners);
        }
      } catch {}
    };

    loadAdminLookups();
  }, [auth.bootLoading, auth.isAuthenticated, auth.isAdmin, auth.profile?.sasPartnerId]);

  useEffect(() => {
    if (!auth.isAdmin) return;

    const loadClasses = async () => {
      setLoadingClasses(true);
      try {
        const { data, error } = await supabase
          .from('classes')
          .select(`
            *,
            class_courses(course_id),
            class_students(student_id)
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        const formatted = (data || []).map(normalizeClassRow);
        setClasses(formatted);
      } catch (err) {
        console.error('Error loading classes:', err);
      } finally {
        setLoadingClasses(false);
      }
    };
    loadClasses();
  }, [auth.isAdmin]);

  useEffect(() => {
    if (auth.bootLoading) return;
    if (auth.isAuthenticated && auth.authzLoading) return;
    if (!auth.isAuthenticated || auth.isAdmin) return;
    const loadMyClassesAndEnrollments = async () => {
      setLoadingClasses(true);
      try {
        const uid = auth.session?.user?.id;
        
        // 1. Buscar turmas
        const { data: classData, error: classError } = await supabase
          .from('classes')
          .select(`
            *,
            class_courses(course_id),
            class_students(student_id)
          `)
          .order('created_at', { ascending: false });
        if (classError) throw classError;
        
        const formattedClasses = (classData || []).map(c => ({
          ...c,
          companyId: c.company_id,
          partnerId: c.partner_id,
          courses: c.class_courses.map(cc => cc.course_id),
          students: c.class_students.map(cs => cs.student_id)
        })).filter(c => (c.students || []).includes(uid));
        
        setClasses(formattedClasses);
        
        // 2. Buscar matrículas individuais para atualizar o state global
        const enrollData = await listMyVisibleEnrollments(auth, { allowedStatuses: COURSE_VISIBLE_ENROLLMENT_STATUSES });
        
        // Opcional: atualizar os cursos globais com a flag is_enrolled para facilitar a renderização
        if (enrollData && enrollData.length > 0) {
          const statusPriority = {
            Ativa: 3,
            Inativada: 2,
            Expirada: 1,
          };
          const statusByCourseId = new Map();
          const expiresAtByCourseId = new Map();
          enrollData.forEach((enrollment) => {
            const courseId = String(enrollment?.offering?.course_id || '');
            const status = String(enrollment?.status || '');
            const expiresAt = enrollment?.expires_at || null;
            if (!courseId || !status) return;
            const currentStatus = statusByCourseId.get(courseId);
            const currentExpiresAt = expiresAtByCourseId.get(courseId) || null;
            const nextPriority = statusPriority[status] || 0;
            const currentPriority = statusPriority[currentStatus] || 0;
            const nextExpiresAtMs = expiresAt ? new Date(expiresAt).getTime() : Number.NaN;
            const currentExpiresAtMs = currentExpiresAt ? new Date(currentExpiresAt).getTime() : Number.NaN;
            const shouldReplace = !currentStatus
              || nextPriority > currentPriority
              || (
                nextPriority === currentPriority
                && Number.isFinite(nextExpiresAtMs)
                && (!Number.isFinite(currentExpiresAtMs) || nextExpiresAtMs > currentExpiresAtMs)
              );
            if (shouldReplace) {
              statusByCourseId.set(courseId, status);
              expiresAtByCourseId.set(courseId, expiresAt);
            }
          });

          const enrolledIds = new Set(Array.from(statusByCourseId.keys()));

          // --- Convite Link ---
          // Em turmas de Convite Link, o vínculo via class_students é a fonte de
          // verdade do acesso. Se o aluno está em uma turma de Convite Link para um
          // curso, a matrícula é válida, mesmo que exista uma matrícula antiga na
          // tabela `enrollments` com status Expirada/Inativada.
          const nowTs = Date.now();
          for (const cls of formattedClasses || []) {
            const inviteMode = String(cls?.settings?.inviteMode || 'system').trim();
            if (inviteMode !== 'link') continue;
            const startDateRaw = String(cls?.settings?.startDate || '').trim();
            const hasFutureStart = startDateRaw
              ? new Date(`${startDateRaw}T00:00:00-03:00`).getTime() > nowTs
              : false;
            const classStatus = hasFutureStart ? 'Programado' : 'Ativa';
            for (const cid of cls?.courses || []) {
              if (!cid) continue;
              const courseId = String(cid);
              const current = statusByCourseId.get(courseId);
              if (current !== 'Ativa') {
                statusByCourseId.set(courseId, classStatus);
                enrolledIds.add(courseId);
              }
            }
          }

          setMyEnrolledCourseIds(enrolledIds);
          setMyCourseEnrollmentStatusById(statusByCourseId);
          setMyCourseEnrollmentExpiresAtById(expiresAtByCourseId);
          setCourses(prev => prev.map(c => 
            enrolledIds.has(String(c.id))
              ? {
                  ...c,
                  is_enrolled: true,
                  enrollment_status: statusByCourseId.get(String(c.id)) || null,
                  enrollment_expires_at: expiresAtByCourseId.get(String(c.id)) || null,
                  access_blocked: isEnrollmentAccessBlocked(statusByCourseId.get(String(c.id)))
                }
              : {
                  ...c,
                  is_enrolled: false,
                  enrollment_status: null,
                  enrollment_expires_at: null,
                  access_blocked: false
                }
          ));
        } else {
          // Mesmo sem matrículas individuais, o aluno pode estar em turmas de
          // Convite Link. Nesse caso, o vínculo via class_students concede acesso.
          const classStatusByCourseId = new Map();
          const classEnrolledIds = new Set();
          const nowTs = Date.now();
          for (const cls of formattedClasses || []) {
            const inviteMode = String(cls?.settings?.inviteMode || 'system').trim();
            if (inviteMode !== 'link') continue;
            const startDateRaw = String(cls?.settings?.startDate || '').trim();
            const hasFutureStart = startDateRaw
              ? new Date(`${startDateRaw}T00:00:00-03:00`).getTime() > nowTs
              : false;
            const classStatus = hasFutureStart ? 'Programado' : 'Ativa';
            for (const cid of cls?.courses || []) {
              if (!cid) continue;
              const courseId = String(cid);
              classStatusByCourseId.set(courseId, classStatus);
              classEnrolledIds.add(courseId);
            }
          }
          setMyEnrolledCourseIds(classEnrolledIds);
          setMyCourseEnrollmentStatusById(classStatusByCourseId);
          setMyCourseEnrollmentExpiresAtById(new Map());
          setCourses(prev => prev.map(c => 
            classEnrolledIds.has(String(c.id))
              ? {
                  ...c,
                  is_enrolled: true,
                  enrollment_status: classStatusByCourseId.get(String(c.id)) || null,
                  enrollment_expires_at: null,
                  access_blocked: isEnrollmentAccessBlocked(classStatusByCourseId.get(String(c.id)))
                }
              : {
                  ...c,
                  is_enrolled: false,
                  enrollment_status: null,
                  enrollment_expires_at: null,
                  access_blocked: false
                }
          ));
        }
        
      } catch (err) {
        console.error('Error loading my classes and enrollments:', err);
      } finally {
        setLoadingClasses(false);
      }
    };
    loadMyClassesAndEnrollments();
  }, [
    auth.bootLoading,
    auth.authzLoading,
    auth.isAuthenticated,
    auth.isAdmin,
    auth.profileRole,
    auth.profile?.companyId,
    auth.session?.user?.id,
    auth.session?.user?.email,
  ]);

  useEffect(() => {
    const openVerifyFromLocation = () => {
      try {
        const res = parseVerifyFromLocation();
        if (!res) return;
        setCurrentView('public_verify');
        (window).__SOFERA_VERIFY_CODE__ = res.code || '';
      } catch {}
    };
    openVerifyFromLocation();
    window.addEventListener('hashchange', openVerifyFromLocation);
    window.addEventListener('popstate', openVerifyFromLocation);
    return () => {
      window.removeEventListener('hashchange', openVerifyFromLocation);
      window.removeEventListener('popstate', openVerifyFromLocation);
    };
  }, []);

  // Navegação programática via eventos internos (ex.: Cadastro Automático na OS)
  useEffect(() => {
    const onNavigate = (ev) => {
      try {
        const v = sanitizeView(ev?.detail?.view);
        if (!v) return;
        setCurrentView(v);
      } catch {}
    };
    window.addEventListener('sofera:navigate', onNavigate);
    return () => window.removeEventListener('sofera:navigate', onNavigate);
  }, []);

  useEffect(() => {
    if (auth.bootLoading) return;
    if (auth.isAuthenticated && auth.authzLoading) return;
    if (currentView === 'public_verify') return;
    if (currentView === 'impersonation_callback') return;
    if (currentView === 'central_auth_callback') return;
    if (!auth.isAuthenticated) {
      // Rota pública de "Convite Link": mantém o aluno na tela do código (sem login).
      if (currentView === 'student_course_view' && storedParamsRef.current?.classId) {
        return;
      }
      if (currentView !== 'login' && currentView !== 'reset_password' && currentView !== 'accept_invitation' && currentView !== 'invitation_confirmed' && currentView !== 'invitation_invalid') {
        const intended = sanitizeView(currentView);
        if (intended && intended !== 'login' && intended !== 'access_denied') setPostLoginView(intended);
        setCurrentView('login');
      }
      return;
    }

    const isPureStudent = !auth.isAdmin && (!auth.profileRole || auth.profileRole === 'Aluno');
    const resolved = resolveRestoredView(currentView, { isAdmin: auth.isAdmin, roleName: auth.profileRole, permissionsJson: auth.permissionsJson });
    if (!resolved) {
      clearRouteState();
      const fallback = isPureStudent ? 'student_home' : 'admin_dashboard';
      if (currentView !== fallback) setCurrentView(fallback);
      return;
    }
    if (resolved !== currentView) {
      setCurrentView(resolved);
      return;
    }

    if (currentView === 'login') {
      const resolvedPost = resolveRestoredView(postLoginView, { isAdmin: auth.isAdmin, roleName: auth.profileRole, permissionsJson: auth.permissionsJson });
      const target = resolvedPost || (isPureStudent ? 'student_home' : 'admin_dashboard');
      setPostLoginView(null);
      setCurrentView(target);
    }
  }, [auth.bootLoading, auth.authzLoading, auth.isAuthenticated, auth.isAdmin, auth.session?.user?.id, currentView, postLoginView]);

  useEffect(() => {
    if (currentView !== 'impersonation_callback') return;
    try {
      const params = new URLSearchParams(window.location.search || '');
      const returned = params.get('returned') || '';
      const nonce = params.get('nonce') || '';
      if (returned) {
        try { window.sessionStorage.removeItem('sofera:impersonation:return_nonce'); } catch {}
        try { window.sessionStorage.removeItem('sofera:impersonation:strict'); } catch {}
      }
      if (nonce) {
        try { window.sessionStorage.setItem('sofera:impersonation:return_nonce', nonce); } catch {}
        try { window.sessionStorage.setItem('sofera:impersonation:strict', '1'); } catch {}
      }
    } catch {}

    if (auth.bootLoading) return;
    if (auth.isAuthenticated && auth.authzLoading) return;
    if (!auth.isAuthenticated) return;

    const isPureStudent = !auth.isAdmin && (!auth.profileRole || auth.profileRole === 'Aluno');
    setCurrentView(isPureStudent ? 'student_home' : 'admin_dashboard');
  }, [currentView, auth.bootLoading, auth.authzLoading, auth.isAuthenticated, auth.isAdmin, auth.profileRole]);

  // Retorno do Acesso Único (AUTH central): troca o `code` central por uma
  // sessão GoTrue real da SOFERA.
  useEffect(() => {
    if (currentView !== 'central_auth_callback') return;
    let cancelled = false;
    completeCentralLogin()
      .then(() => { if (!cancelled) { setCentralAuthError(null); setCentralAuthReady(true); } })
      .catch((err) => { if (!cancelled) { setCentralAuthError(describeCentralLoginError(err)); setCentralAuthReady(false); } });
    return () => { cancelled = true; };
  }, [currentView]);

  useEffect(() => {
    if (currentView !== 'central_auth_callback') return;
    if (!centralAuthReady) return;
    if (auth.bootLoading || auth.authzLoading) return;
    if (!auth.isAuthenticated) return;

    const isPureStudent = !auth.isAdmin && (!auth.profileRole || auth.profileRole === 'Aluno');
    const resolvedPost = resolveRestoredView(postLoginView, { isAdmin: auth.isAdmin, roleName: auth.profileRole, permissionsJson: auth.permissionsJson });
    setPostLoginView(null);
    setCurrentView(resolvedPost || (isPureStudent ? 'student_home' : 'admin_dashboard'));
  }, [currentView, centralAuthReady, auth.bootLoading, auth.authzLoading, auth.isAuthenticated, auth.isAdmin, auth.profileRole, auth.permissionsJson, postLoginView]);

  useEffect(() => {
    const onUnhandledRejection = (ev) => {
      try {
        logError('unhandled_rejection', ev?.reason, { view: currentView })
      } catch {}
    }
    const onError = (ev) => {
      try {
        logError('window_error', ev?.error || ev?.message, { view: currentView, filename: ev?.filename || null, lineno: ev?.lineno || null, colno: ev?.colno || null })
      } catch {}
    }
    window.addEventListener('unhandledrejection', onUnhandledRejection)
    window.addEventListener('error', onError)
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      window.removeEventListener('error', onError)
    }
  }, [currentView])

  useEffect(() => {
    saveRouteState({
      view: currentView,
      postLoginView,
      params: {
        classId: selectedClass?.id ?? storedParamsRef.current?.classId ?? null,
        courseId: selectedCourse?.id ?? storedParamsRef.current?.courseId ?? null,
      },
      snapshot: currentView === 'admin_class_details'
        ? (selectedClass || null)
        : (currentView === 'student_course_view' ? (selectedCourse || null) : null)
    });
  }, [currentView, postLoginView, selectedClass?.id, selectedCourse?.id]);

  useEffect(() => {
    try {
      const verifyCode = (window && window.__SOFERA_VERIFY_CODE__) || ''
      const action = new URLSearchParams(window.location.search).get('action') || undefined
      const next = viewToPath(currentView, {
        classId: selectedClass?.id ?? storedParamsRef.current?.classId ?? null,
        courseId: selectedCourse?.id ?? storedParamsRef.current?.courseId ?? null,
        verifyCode,
        token: storedParamsRef.current?.token || undefined,
      })
      
      // Preserve the action parameter for public_verify view
      let finalNext = next;
      if (currentView === 'public_verify' && action && finalNext) {
        // Se a URL original já tinha o action gerado pelo viewToPath, não adiciona de novo
        if (!finalNext.includes('action=')) {
          finalNext = finalNext.includes('?') ? `${finalNext}&action=${encodeURIComponent(action)}` : `${finalNext}?action=${encodeURIComponent(action)}`;
        }
      }
      if (!finalNext) return
      const current = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`
      const lowerCurrent = current.toLowerCase()
      const hasRecoveryHints =
        currentView === 'reset_password' &&
        (lowerCurrent.includes('type=recovery') ||
          lowerCurrent.includes('token_hash=') ||
          lowerCurrent.includes('access_token=') ||
          lowerCurrent.includes('refresh_token=') ||
          lowerCurrent.includes('code='))
      const hasInviteHints =
        currentView === 'accept_invitation' &&
        hasInviteHintsInUrl(window.location)
      if (hasRecoveryHints) return
      if (hasInviteHints) return
      if (currentView === 'central_auth_callback') return
      if (current === finalNext) return
      window.history.replaceState({}, '', finalNext)
    } catch {}
  }, [currentView, selectedClass?.id, selectedCourse?.id])

  // Reseta o estado de "Convite Link" quando o usuário autenticado muda.
  useEffect(() => {
    setInviteCodeScreen(null);
    setInviteLinkCourseId(null);
    setInviteLinkClassId(null);
  }, [auth.session?.user?.id]);

  useEffect(() => {
    if (auth.bootLoading || loadingClasses) return
    if (!auth.isAuthenticated) return
    if (currentView !== 'admin_class_details') return
    if (!auth.isAdmin) return
    if (selectedClass) return

    const id = storedParamsRef.current?.classId
    if (id == null) {
      setCurrentView('admin_classes')
      return
    }
    const found = (classes || []).find(c => String(c.id) === String(id))
    if (found) setSelectedClass(found)
    else setCurrentView('admin_classes')
  }, [auth.bootLoading, loadingClasses, auth.isAuthenticated, auth.isAdmin, currentView, selectedClass, classes])

  useEffect(() => {
    if (auth.bootLoading || loadingCourses) return
    if (!auth.isAuthenticated) return
    if (currentView !== 'student_course_view') return
    if (selectedCourse) return

    const id = storedParamsRef.current?.courseId
    if (id == null) {
      setCurrentView('student_home')
      return
    }
    const found = (courses || []).find(c => String(c.id) === String(id))
    if (found) setSelectedCourse(found)
    else setCurrentView('student_home')
  }, [auth.bootLoading, loadingCourses, auth.isAuthenticated, currentView, selectedCourse, courses])

  // Manter selectedCourse em sincronia com a lista de cursos (progresso, etc)
  useEffect(() => {
    if (!selectedCourse || !courses.length) return;
    const updated = courses.find(c => String(c.id) === String(selectedCourse.id));
    // Comparação por referência: `courses` só é substituído quando há refetch real.
    // Evita serializar objetos com imagens base64 (MBs) a cada render.
    if (updated && updated !== selectedCourse) {
      setSelectedCourse(updated);
    }
  }, [courses, selectedCourse]);

  // We no longer patch MOCK_COURSES into local storage

  if (auth.bootLoading || (auth.isAuthenticated && auth.authzLoading)) {
    return (
      <div className="font-sans antialiased text-slate-100 bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-slate-800 border-t-orange-500 rounded-full animate-spin"></div>
          <p className="text-slate-500 text-sm animate-pulse">Carregando...</p>
        </div>
      </div>
    )
  }

  if (currentView === 'public_verify') {
    return (
      <div className="font-sans antialiased text-slate-100 bg-slate-950 min-h-screen flex flex-col relative">
        <style>{`
          ::selection { background-color: ${currentPartner.color}40; color: white; }
          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: ${currentPartner.color}40; border-radius: 10px; }
        `}</style>
        
        <header className="h-[72px] bg-slate-950/90 backdrop-blur-md border-b border-slate-900 px-4 md:px-12 flex items-center justify-between shrink-0 fixed top-0 w-full z-50">
          <div className="flex flex-col">
            <span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent font-black text-2xl tracking-tighter">SOFERA</span>
            <span className="text-white font-bold text-[10px] tracking-[0.25em] select-none -mt-0.5 ml-0.5 uppercase">EVOLUÇÃO INTELIGENTE</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pt-[72px]">
          <AppErrorBoundary context={{ view: currentView }}>
            <VerifyCertificate partner={currentPartner} defaultCode={(window && window.__SOFERA_VERIFY_CODE__) || ''} />
          </AppErrorBoundary>
        </main>
        <PublicWhatsAppSupportButton
          whatsappNumber={whatsappConfig.number}
          enabled={Boolean(whatsappConfig.enabled)}
          message="Olá! Preciso de suporte com a validação de certificado na plataforma SOFERA."
        />
      </div>
    );
  }

  return (
    <div className="font-sans antialiased text-slate-100 bg-slate-950 min-h-screen relative">
      <style>{`
        :root {
          --primary-color: ${currentPartner.color};
          --primary-color-glow: ${currentPartner.color}4d;
          --primary-color-strong: ${currentPartner.color}80;
          --primary-shadow: 0 8px 25px -5px ${currentPartner.color}66, 0 4px 10px -5px ${currentPartner.color}4d;
          --primary-glow: 0 0 25px 3px ${currentPartner.color}66;
          --primary-glow-intense: 0 0 35px 5px ${currentPartner.color}80;
        }
        ::selection { background-color: ${currentPartner.color}40; color: white; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: ${currentPartner.color}; border-radius: 10px; box-shadow: 0 0 10px ${currentPartner.color}80; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(15, 23, 42, 0.5); }
        
        /* Forçar cores do parceiro em elementos comuns */
        .text-primary { color: ${currentPartner.color} !important; }
        .bg-primary-solid { background-color: ${currentPartner.color} !important; color: white !important; }
        .bg-primary-light { background-color: ${currentPartner.color}1a !important; }
        .border-primary { border-color: ${currentPartner.color} !important; }
        .focus-within-primary:focus-within { border-color: ${currentPartner.color} !important; }
        
        /* Utilitários de Sombra e Brilho Reforçados */
        .shadow-primary { box-shadow: var(--primary-shadow) !important; }
        .glow-primary { box-shadow: var(--primary-glow) !important; }
        .glow-primary-intense { box-shadow: var(--primary-glow-intense) !important; }
        .hover-glow-primary:hover { box-shadow: var(--primary-glow) !important; transition: all 0.3s ease; }
        .hover-glow-primary-intense:hover { box-shadow: var(--primary-glow-intense) !important; transition: all 0.3s ease; }
        
        /* Sobrescrever classes do Tailwind que usam laranja/amber para o parceiro */
        .focus\:border-orange-500:focus { border-color: ${currentPartner.color} !important; box-shadow: 0 0 0 3px ${currentPartner.color}4d !important; }
        .text-orange-500, .text-orange-400, .text-amber-500, .text-amber-400 { color: ${currentPartner.color} !important; }
        .bg-orange-500, .bg-orange-600, .bg-amber-500, .bg-amber-600 { background-color: ${currentPartner.color} !important; }
        .border-orange-500, .border-amber-500 { border-color: ${currentPartner.color} !important; }
        .accent-orange-500, .accent-amber-500 { accent-color: ${currentPartner.color} !important; }
        
        /* Variantes com opacidade */
        .bg-orange-500\/10, .bg-amber-500\/10 { background-color: ${currentPartner.color}1a !important; }
        .bg-orange-500\/5, .bg-amber-500\/5 { background-color: ${currentPartner.color}0d !important; }
        .bg-orange-500\/80, .bg-amber-500\/80 { background-color: ${currentPartner.color}cc !important; }
        .bg-orange-500\/90, .bg-amber-500\/90 { background-color: ${currentPartner.color}e6 !important; }
        .border-orange-500\/30, .border-amber-500\/30 { border-color: ${currentPartner.color}4d !important; }
        .border-orange-500\/20, .border-amber-500\/20 { border-color: ${currentPartner.color}33 !important; }
        .text-orange-500\/50, .text-amber-500\/50 { color: ${currentPartner.color}80 !important; }
        
        /* Sombras e Gráficos Reforçados */
        .shadow-orange-500\/20, .shadow-orange-900\/20 { --tw-shadow-color: ${currentPartner.color}4d !important; box-shadow: 0 10px 20px -5px var(--tw-shadow-color) !important; }
        
        /* Reforço automático de sombra em botões e cards de destaque */
        .bg-primary.shadow-lg, .bg-orange-500.shadow-lg, .bg-orange-600.shadow-lg, .glow-primary { 
          box-shadow: 0 8px 20px -4px ${currentPartner.color}66, 0 4px 12px -6px ${currentPartner.color}4d !important; 
        }
        .glow-primary-intense { 
          box-shadow: 0 0 25px 5px ${currentPartner.color}66, 0 0 10px 2px ${currentPartner.color}33 !important; 
        }
        
        /* Gradients */
        .from-orange-500 { --tw-gradient-from: ${currentPartner.color} !important; --tw-gradient-to: ${currentPartner.color}00 !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
        .to-amber-400, .to-amber-500 { --tw-gradient-to: ${currentPartner.color} !important; }
      `}</style>
      
      {!isCourseInviteGateActive && !isPublicInviteAccess && !inviteCodeScreen && (
      <Navbar 
        partner={currentPartner} 
        role={role}
        currentView={currentView}
        setCurrentView={(v) => {
          if (v === 'public_verify') return setCurrentView(v);
          if (!auth.isAuthenticated) {
            setPostLoginView(v);
            return setCurrentView('login');
          }
          if (String(v || '').startsWith('admin_') && !auth.isAdmin) {
            setPostLoginView(null);
            return setCurrentView('access_denied');
          }
          setCurrentView(v);
        }}
        user={!auth.isAuthenticated ? { name: 'Visitante' } : { 
          name: auth.profile?.fullName || auth.session?.user?.user_metadata?.full_name || auth.session?.user?.user_metadata?.name || auth.session?.user?.email || 'Usuário',
          profile: auth.profileRole || 'Aluno'
        }}
        onMenuClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        isAuthenticated={auth.isAuthenticated}
        onLogin={() => setCurrentView('login')}
        onLogout={async () => {
          // Curso por "Convite Link": "Sair" NÃO encerra a sessão — reabre a tela do
          // código (a mesma do link público), mantendo o usuário logado. Vale para
          // QUALQUER papel (Aluno, Admin, Parceiro), pois o código é vinculado à
          // pessoa, não ao perfil.
          if (currentView === 'student_course_view' && inviteLinkCourseId && inviteLinkClassId) {
            setInviteCodeScreen({ courseId: inviteLinkCourseId, classId: inviteLinkClassId });
            setIsMobileMenuOpen(false);
            return;
          }
          try { await auth.logout(); } catch {}
          setCurrentView('login');
        }}
        onOpenProfile={() => setShowUserProfile(true)}
        auth={auth}
        setAppNotification={setAppNotification}
      />
      )}
      <CookieConsent onOpenPrivacy={openPrivacyPolicy} onOpenTerms={openTermsOfUse} />
      
      <div className={`flex ${(isPublicInviteAccess || inviteCodeScreen) ? '' : 'pt-[72px]'}`}>
        {inviteCodeScreen ? (
          <AppErrorBoundary context={{ view: 'invite_code' }}>
            <main className="flex-1 min-h-[calc(100vh-72px)] bg-slate-950 pb-24 overflow-x-hidden">
              <ClassInviteAccessView
                courseId={inviteCodeScreen.courseId}
                classId={inviteCodeScreen.classId}
                whatsappSupport={whatsappConfig}
                authenticated={auth.isAuthenticated}
                onAuthenticated={() => setInviteCodeScreen(null)}
              />
            </main>
          </AppErrorBoundary>
        ) : currentView === 'login' ? (
          <AppErrorBoundary context={{ view: currentView }}>
            <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
              <LoginView
                onLoggedIn={() => {}}
                initialEmail={(() => { try { return new URLSearchParams(window.location.search).get('prefill_email') || ''; } catch { return ''; } })()}
                whatsappSupport={whatsappConfig}
              />
            </main>
          </AppErrorBoundary>
        ) : currentView === 'reset_password' ? (
          <AppErrorBoundary context={{ view: currentView }}>
            <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
              <ResetPasswordView onBackToLogin={() => setCurrentView('login')} whatsappSupport={whatsappConfig} />
            </main>
          </AppErrorBoundary>
        ) : (currentView === 'accept_invitation' || currentView === 'invitation_confirmed' || currentView === 'invitation_invalid') ? (
          <AppErrorBoundary context={{ view: currentView }}>
            <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
              <AcceptInvitationView onOpenPrivacyPolicy={openPrivacyPolicy} onOpenTermsOfUse={openTermsOfUse} whatsappSupport={whatsappConfig} />
            </main>
          </AppErrorBoundary>
        ) : currentView === 'impersonation_callback' ? (
          <AppErrorBoundary context={{ view: currentView }}>
            <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
              <div className="p-6 md:p-10">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center gap-3 text-white font-black text-lg">
                    <Loader2 size={22} className="animate-spin" />
                    Processando autenticação…
                  </div>
                  <div className="text-slate-400 text-sm mt-2">
                    Aguarde alguns segundos.
                  </div>
                  {!auth.isAuthenticated && (
                    <button
                      type="button"
                      onClick={() => setCurrentView('login')}
                      className="mt-5 px-4 py-2.5 rounded-lg font-bold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700"
                    >
                      Ir para o Login
                    </button>
                  )}
                </div>
              </div>
            </main>
          </AppErrorBoundary>
        ) : currentView === 'central_auth_callback' ? (
          <AppErrorBoundary context={{ view: currentView }}>
            <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
              <div className="p-6 md:p-10">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  {centralAuthError ? (
                    <>
                      <div className="text-white font-black text-lg">
                        Não foi possível entrar com o Acesso Único
                      </div>
                      <div className="text-slate-400 text-sm mt-2">{centralAuthError}</div>
                      <button
                        type="button"
                        onClick={() => { setCentralAuthError(null); setCurrentView('login'); }}
                        className="mt-5 px-4 py-2.5 rounded-lg font-bold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700"
                      >
                        Voltar ao Login
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 text-white font-black text-lg">
                        <Loader2 size={22} className="animate-spin" />
                        Processando autenticação…
                      </div>
                      <div className="text-slate-400 text-sm mt-2">
                        Aguarde alguns segundos.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </AppErrorBoundary>
        ) : (currentView === 'student_course_view' && role === 'guest') ? (
          isPublicInviteAccess ? (
            <AppErrorBoundary context={{ view: currentView }}>
              <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
                <ClassInviteAccessView
                  courseId={publicInviteCourseId}
                  classId={publicInviteClassId}
                  whatsappSupport={whatsappConfig}
                />
              </main>
            </AppErrorBoundary>
          ) : selectedCourse ? (
            <StudentCourseViewLazy 
              course={selectedCourse} 
              role={role}
              onInviteLinkCourseChange={handleInviteLinkCourseChange}
              onInviteGateChange={setIsCourseInviteGateActive}
              onBack={() => {
                setSelectedCourse(null);
                setCurrentView(role === 'admin' ? 'admin_courses' : 'student_home');
              }} 
            />
          ) : (
            <AppErrorBoundary context={{ view: currentView, role }}>
              <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
                <div className="p-6 text-slate-500">Carregando curso...</div>
              </main>
            </AppErrorBoundary>
          )
        ) : role === 'admin' ? (
          <>
            {!isCourseInviteGateActive && !inviteLinkCourseId && (
            <>
            <AdminSidebar 
              currentView={currentView} 
              setCurrentView={(view) => {
                const isPureStudent = !auth.isAdmin && (!auth.profileRole || auth.profileRole === 'Aluno');
                if (String(view || '').startsWith('admin_') && isPureStudent) {
                  setPostLoginView(null);
                  setCurrentView('access_denied');
                  return;
                }
                setCurrentView(view);
                setIsMobileMenuOpen(false);
              }} 
              partner={currentPartner}
              isOpen={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
              role="admin"
              auth={auth}
            />
            {/* Mobile Overlay */}
            {isMobileMenuOpen && (
              <div 
                className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              ></div>
            )}
            </>
            )}
            <AppErrorBoundary context={{ view: currentView, role }}>
              <main className={`w-full ${isCourseInviteGateActive ? 'min-h-[calc(100vh-104px)]' : inviteLinkCourseId ? 'h-[calc(100vh-104px)] pb-32' : `${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} h-[calc(100vh-104px)] pb-32`} flex-1 overflow-y-auto bg-slate-950`}>
                {currentView === 'student_course_view' && (
                  selectedCourse ? (
                    <StudentCourseViewLazy 
                      course={selectedCourse} 
                      role={role}
                      onInviteLinkCourseChange={handleInviteLinkCourseChange}
                      onInviteGateChange={setIsCourseInviteGateActive}
                      onBack={() => {
                        setSelectedCourse(null);
                        setCurrentView(isPartnerScopedRole(auth?.profileRole) ? 'student_courses' : 'admin_courses');
                      }} 
                    />
                  ) : (
                    <div className="p-6 text-slate-500">Carregando curso...</div>
                  )
                )}
                {currentView === 'student_certs' && (
                  <StudentCertificates partner={currentPartner} />
                )}
                {currentView === 'student_courses' && (
                  loadingCourses ? (
                    <div className="flex items-center justify-center min-h-[50vh] text-slate-500">
                      <div className="flex flex-col items-center gap-4">
                        <Settings size={48} className="animate-spin-slow opacity-20" />
                        <p className="animate-pulse">Carregando cursos...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 md:px-12 py-6 md:py-10">
                    <div className="flex items-center gap-3 mb-4 md:mb-6">
                      <div className="w-1 h-6 md:h-8 bg-teal-400 rounded-full"></div>
                      <h2 className="text-lg md:text-2xl font-bold text-white">Meus Cursos</h2>
                    </div>
                    {(() => {
                      const enrolledCourses = courses.filter((c) => {
                        return (typeof myEnrolledCourseIds !== 'undefined' && myEnrolledCourseIds.has(String(c.id))) || c.is_enrolled === true;
                      });
                      
                      if (enrolledCourses.length === 0) {
                        return (
                          <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 md:p-16 flex flex-col items-center justify-center text-center shadow-lg">
                            <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center mb-6 border border-slate-700">
                              <BookOpen size={40} className="text-slate-500" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">Nenhuma matrícula encontrada</h3>
                            <p className="text-slate-400 max-w-md text-base leading-relaxed">
                              Você ainda não está matriculado em nenhum curso. Quando houver uma matrícula, ela aparecerá aqui.
                            </p>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                          {enrolledCourses.map((course) => {
                            const enrollmentStatus = getCourseEnrollmentStatus(course);
                            const courseAccessBlocked = isCourseAccessBlocked(course);
                            const isActiveEnrollment = enrollmentStatus === 'Ativa';
                            const isExpiredEnrollment = enrollmentStatus === 'Expirada';
                            const expirationLabel = formatCourseEnrollmentExpiresAtShort(course);
                            const instructorName = resolveCoursePersonName(course, 'instructor');
                            const coordinatorName = resolveCoursePersonName(course, 'coordinator');
                            const blockedBadgeClass = isExpiredEnrollment
                              ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                              : 'bg-slate-900/90 text-slate-200 border-slate-700';
                            const blockedBadgeLabel = isExpiredEnrollment ? 'EXPIRADO' : 'INATIVADO';
                            const blockedMessage = isExpiredEnrollment
                              ? `Sua matrícula expirou${expirationLabel ? ` em ${expirationLabel}` : ''}. Sendo assim, não é possível iniciar ou retomar seu curso`
                              : courseAccessBlocked
                                ? 'Matrícula inativada: o curso continua visível, mas o acesso para continuar está bloqueado.'
                                : '';
                            const activeWarningMessage = isActiveEnrollment && expirationLabel
                              ? `Atenção: sua matrícula expira em ${expirationLabel}. Conclua seu curso no prazo acordado com sua empresa.`
                              : '';
                            return (
                            <div
                              key={course.id}
                              className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-lg hover:border-slate-700 transition group flex flex-col"
                            >
                            <div className="h-56 relative shrink-0">
                              <CourseWorkloadBadge workload={course.workload_hours || course.workload} />
                              <img
                                src={course.thumb}
                                alt={course.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                              />
                              <div className="absolute top-2 right-2 flex gap-2">
                                {courseAccessBlocked && (
                                  <span className={`text-[10px] font-bold px-2 py-1 rounded shadow-sm backdrop-blur-sm border ${blockedBadgeClass}`}>
                                    {blockedBadgeLabel}
                                  </span>
                                )}
                                {course.status === 'draft' && (
                                  <span className="bg-amber-500/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm backdrop-blur-sm">
                                    RASCUNHO
                                  </span>
                                )}
                              </div>
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (courseAccessBlocked) return;
                                    setSelectedCourse(course);
                                    setCurrentView('student_course_view');
                                  }}
                                  disabled={courseAccessBlocked}
                                  className={`backdrop-blur-sm p-2 rounded-full text-white transition shadow-lg ${courseAccessBlocked ? 'bg-slate-700/80 cursor-not-allowed opacity-70' : 'bg-orange-500/80 hover:bg-orange-500'}`}
                                  title={courseAccessBlocked ? (isExpiredEnrollment ? 'Matrícula expirada' : 'Curso inativado') : 'Visão Aluno'}
                                >
                                  {courseAccessBlocked ? <Lock size={20} /> : <Eye size={20} />}
                                </button>
                              </div>
                            </div>
                            <div className="p-5 flex flex-col flex-1">
                              <div className="mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                  {course.categories && course.categories.length > 0
                                    ? course.categories.map(catId => categories.find(c => c.id === catId)?.name).filter(Boolean).join(', ')
                                    : categories.find((c) => c.id === (course.category_id || course.category))?.name ||
                                      course.category ||
                                      'Geral'}
                                </span>
                              </div>
                              <h3 className="text-lg font-bold text-white mb-1">{course.name || course.title}</h3>
                              <p className="text-slate-400 text-sm mb-4 line-clamp-2">{course.description || 'Sem descrição.'}</p>
  
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">
                                <div className="flex items-center gap-1" title="Módulos">
                                  <List size={12} className="text-orange-500" />
                                  <span>{course.modules?.length || 0}</span>
                                </div>
                                <div className="flex items-center gap-1" title="Aulas">
                                  <Video size={12} className="text-orange-500" />
                                  <span>
                                    {(course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0) +
                                      (course.standaloneLessons?.length || 0)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1" title="Arquivos">
                                  <FileText size={12} className="text-orange-500" />
                                  <span>
                                    {(course.modules?.reduce(
                                      (acc, m) =>
                                        acc +
                                        (m.lessons?.reduce(
                                          (lAcc, l) =>
                                            lAcc +
                                            (Array.isArray(l.contents)
                                              ? l.contents.filter(
                                                  (c) =>
                                                    c.type !== 'quiz' &&
                                                    c.type !== 'video' &&
                                                    c.type !== 'vimeo' &&
                                                    c.type !== 'youtube' &&
                                                    !c.url?.includes('youtube.com') &&
                                                    !c.url?.includes('vimeo.com')
                                                ).length
                                              : 0),
                                          0
                                        ) || 0),
                                      0
                                    ) || 0) +
                                      (course.standaloneLessons?.reduce(
                                        (acc, l) =>
                                          acc +
                                          (Array.isArray(l.contents)
                                            ? l.contents.filter(
                                                (c) =>
                                                  c.type !== 'quiz' &&
                                                  c.type !== 'video' &&
                                                  c.type !== 'vimeo' &&
                                                  c.type !== 'youtube' &&
                                                  !c.url?.includes('youtube.com') &&
                                                  !c.url?.includes('vimeo.com')
                                              ).length
                                            : 0),
                                        0
                                      ) || 0)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1" title="Quizes">
                                  <HelpCircle size={12} className="text-orange-500" />
                                  <span>
                                    {(course.modules?.reduce(
                                      (acc, m) =>
                                        acc +
                                        (m.lessons?.reduce(
                                          (lAcc, l) =>
                                            lAcc +
                                            (Array.isArray(l.contents)
                                              ? l.contents.filter((c) => c.type === 'quiz').length
                                              : 0),
                                          0
                                        ) || 0),
                                      0
                                    ) || 0) +
                                      (course.standaloneLessons?.reduce(
                                        (acc, l) =>
                                          acc +
                                          (Array.isArray(l.contents)
                                            ? l.contents.filter((c) => c.type === 'quiz').length
                                            : 0),
                                        0
                                      ) || 0)}
                                  </span>
                                </div>
                                {(course.assessmentEnabled || course.assessment_id) && (
                                  <div className="flex items-center gap-1" title="Prova Final">
                                    <CheckCircle size={12} className="text-orange-500" />
                                    <span>1</span>
                                  </div>
                                )}
                              </div>
  
                              <div className="mt-4 pt-3 border-t border-slate-800/50 flex items-center gap-3 text-xs text-slate-400">
                                <div
                                  className="flex items-center gap-2 min-w-0 flex-1"
                                  title={`Instrutor: ${instructorName}`}
                                >
                                  <User size={14} className="text-orange-500 shrink-0" />
                                  <span className="truncate">{instructorName}</span>
                                </div>
                                <div
                                  className="flex items-center gap-2 min-w-0 flex-1"
                                  title={`Coordenador: ${coordinatorName}`}
                                >
                                  <UserCheck size={14} className="text-orange-500 shrink-0" />
                                  <span className="truncate">{coordinatorName}</span>
                                </div>
                              </div>

                              <div className="mt-5 space-y-1.5">
                                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-slate-500">
                                  <span>Progresso Geral</span>
                                  <span className="text-teal-400">{resolveUnifiedProgress(
                                    getLocalCourseProgress(course, loadCompleted(userId, course.id) || []),
                                    course.progressPercent,
                                    course.db_progress_percent,
                                    course.ui_progress_percent,
                                    course.progress
                                  )}%</span>
                                </div>
                                <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-1000"
                                    style={{ width: `${resolveUnifiedProgress(
                                      getLocalCourseProgress(course, loadCompleted(userId, course.id) || []),
                                      course.progressPercent,
                                      course.db_progress_percent,
                                      course.ui_progress_percent,
                                      course.progress
                                    )}%` }}
                                  />
                                </div>
                              </div>
                              <div className="mt-6 mt-auto pt-4">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (courseAccessBlocked) return;
                                    setSelectedCourse(course);
                                    setCurrentView('student_course_view');
                                  }}
                                  disabled={courseAccessBlocked}
                                  className={`w-full font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${courseAccessBlocked ? 'bg-slate-700 text-slate-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20'}`}
                                >
                                  {courseAccessBlocked ? <Lock size={20} /> : <Play size={20} />}
                                  {courseAccessBlocked ? (isExpiredEnrollment ? 'MATRÍCULA EXPIRADA' : 'CURSO INATIVADO') : 'INICIAR / RETOMAR'}
                                </button>
                                {blockedMessage && (
                                  <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${isExpiredEnrollment ? 'border-amber-500/30 bg-amber-500/10 text-amber-200' : 'border-slate-700 bg-slate-800/80 text-slate-300'}`}>
                                    {blockedMessage}
                                  </div>
                                )}
                                {activeWarningMessage && (
                                  <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300">
                                    {activeWarningMessage}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          )})}
                      </div>
                    );
                  })()}
                  </div>
                  )
                )}
                
                {currentView === 'student_enrollments' && (
                  <StudentEnrollments courses={courses} classes={classes} />
                )}
                
                {currentView === 'admin_dashboard' && (
                  loadingCourses ? (
                    <div className="flex items-center justify-center min-h-[50vh] text-slate-500">
                      <div className="flex flex-col items-center gap-4">
                        <Settings size={48} className="animate-spin-slow opacity-20" />
                        <p className="animate-pulse">Carregando vitrine...</p>
                      </div>
                    </div>
                  ) : (
                    <AdminDashboard 
                      partner={currentPartner} 
                      courses={courses} 
                      onViewStudent={(course) => { setSelectedCourse(course); setCurrentView('student_course_view'); }}
                      users={users}
                      categories={categories}
                    />
                  )
                )}
                {currentView === 'admin_reports' && <AdminReports partner={currentPartner} />}
                {currentView === 'admin_companies' && <AdminCompanies partner={currentPartner} companies={companies} setCompanies={setCompanies} role={auth.profileRole} auth={auth} />}
                {currentView === 'admin_users' && <AdminUsers partner={currentPartner} companies={companies} users={users} setUsers={setUsers} partners={partners} role={auth.profileRole} userCompanyId={auth.profile?.companyId} auth={auth} />}
                {currentView === 'admin_users_ro' && <AdminUsers partner={currentPartner} companies={companies} users={users} setUsers={setUsers} partners={partners} role={auth.profileRole} userCompanyId={auth.profile?.companyId} readOnly={true} auth={auth} />}
                {currentView === 'admin_communications' && <AdminCommunicationsLazy partner={currentPartner} partners={partners} templates={templates} setTemplates={setTemplates} defaultEmailHeader={defaultEmailHeader} setDefaultEmailHeader={setDefaultEmailHeader} defaultEmailFooter={defaultEmailFooter} setDefaultEmailFooter={setDefaultEmailFooter} role={auth.profileRole} userCompanyId={auth.profile?.companyId} />}
                {currentView === 'admin_whatsapp' && <AdminCommunicationsLazy partner={currentPartner} partners={partners} templates={templates} setTemplates={setTemplates} defaultEmailHeader={defaultEmailHeader} setDefaultEmailHeader={setDefaultEmailHeader} defaultEmailFooter={defaultEmailFooter} setDefaultEmailFooter={setDefaultEmailFooter} role={auth.profileRole} userCompanyId={auth.profile?.companyId} initialTab="whatsapp" />}
                {currentView === 'admin_certs' && <AdminCertificatesLazy partner={currentPartner} auth={auth} tab="issued" courses={courses} users={users} models={models} setModels={setModels} setCurrentView={setCurrentView} />}
                {currentView === 'admin_certs_issued' && <AdminCertificatesLazy partner={currentPartner} auth={auth} tab="issued" courses={courses} users={users} models={models} setModels={setModels} setCurrentView={setCurrentView} />}
                {currentView === 'admin_certs_manual' && <AdminCertificatesLazy partner={currentPartner} auth={auth} tab="manual" courses={courses} users={users} models={models} setModels={setModels} setCurrentView={setCurrentView} />}
                {currentView === 'admin_certs_models' && <AdminCertificatesLazy partner={currentPartner} auth={auth} tab="models" courses={courses} models={models} setModels={setModels} setCurrentView={setCurrentView} />}
                {currentView === 'admin_partners' && <AdminPartners currentPartner={currentPartner} onPartnerChange={setCurrentPartner} partners={partners} setPartners={setPartners} />}
                {currentView === 'admin_categories' && <AdminCategories partner={currentPartner} categories={categories} setCategories={setCategories} />}
                {currentView === 'admin_connections' && <AdminConnections partner={currentPartner} />}
                
                {currentView === 'admin_courses' && <AdminCoursesLazy partner={currentPartner} users={users} categories={categories} courses={courses} setCourses={setCourses} assessments={assessments} onViewStudent={(course) => { setSelectedCourse(course); setCurrentView('student_course_view'); }} />}
                {currentView === 'admin_idiomas' && <AdminIdiomas partner={currentPartner} />}
                {currentView === 'admin_classes' && <AdminClasses partner={currentPartner} courses={courses} classes={classes} setClasses={setClasses} companies={companies} partners={partners} users={users} onViewClass={(c) => { setSelectedClass(c); setCurrentView('admin_class_details'); }} />}
                {currentView === 'admin_class_details' && (
                  selectedClass
                    ? <AdminClassDetails classData={selectedClass} onBack={() => { setSelectedClass(null); setCurrentView('admin_classes'); }} users={users} courses={courses} templates={templates} companies={companies} sasPartners={partners} />
                    : <div className="p-6 text-slate-500">Carregando turma...</div>
                )}
                {currentView === 'admin_enrollments' && <AdminEnrollments partner={currentPartner} users={users} courses={courses} classes={classes} setClasses={setClasses} companies={companies} partners={partners} />}
                {currentView === 'admin_library' && <AdminLibrary partner={currentPartner} categories={categories} courses={courses} />}
                {currentView === 'admin_assessments' && <AdminAssessments partner={currentPartner} courses={courses} assessments={assessments} setAssessments={setAssessments} />}
                {currentView === 'admin_service_orders' && <AdminServiceOrders partner={currentPartner} auth={auth} />}
                {currentView === 'admin_invites' && (
                  <div className="p-6 md:p-20 flex flex-col items-center justify-center text-slate-700">
                    <Settings size={64} className="mb-4 animate-spin-slow opacity-10" />
                    <h2 className="text-2xl font-bold text-slate-500">Módulo em Integração</h2>
                    <p className="text-slate-600 mt-2 italic text-center">Configurando credenciais dinâmicas para o ecossistema {currentPartner.name}...</p>
                  </div>
                )}

                {!adminRenderableViews.current.has(currentView) && (
                  <div className="p-6 md:p-10">
                    <div className="max-w-2xl rounded-xl border border-slate-800 bg-slate-900/60 p-5">
                      <div className="text-white font-bold text-lg">Tela não encontrada</div>
                      <div className="text-slate-400 mt-2 text-sm">A navegação restaurou um estado inválido. Use os botões abaixo para voltar para uma tela segura.</div>
                      <div className="mt-1 text-xs text-slate-500 font-mono break-words">{String(currentView || '')}</div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <button
                          type="button"
                          onClick={() => {
                            try { clearRouteState() } catch {}
                            const isPureStudent = !auth.isAdmin && (!auth.profileRole || auth.profileRole === 'Aluno');
                            setCurrentView(isPureStudent ? 'student_home' : 'admin_dashboard')
                          }}
                          className="px-4 py-2 rounded-lg bg-slate-800 text-slate-100 font-bold hover:bg-slate-700 transition"
                        >
                          Ir para início
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            try { clearRouteState() } catch {}
                            window.location.href = '/'
                          }}
                          className="px-4 py-2 rounded-lg bg-slate-950 text-slate-200 font-bold hover:bg-slate-900 transition border border-slate-800"
                        >
                          Recarregar na raiz
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </main>
            </AppErrorBoundary>
          </>
        ) : role === 'student' ? (
          <>
            {!isCourseInviteGateActive && !inviteLinkCourseId && (
            <>
            <AdminSidebar 
              currentView={currentView === 'student_home' ? 'admin_dashboard' : currentView} 
              setCurrentView={(view) => {
                if (view === 'admin_dashboard') {
                  setCurrentView('student_home');
                  setIsMobileMenuOpen(false);
                  return;
                }
                if (String(view || '').startsWith('admin_') && !auth.isAdmin) {
                  setPostLoginView(null);
                  setCurrentView('access_denied');
                  return;
                }
                setCurrentView(view);
                setIsMobileMenuOpen(false);
              }} 
              partner={currentPartner}
              isOpen={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
              role="student"
              auth={auth}
            />
            {isMobileMenuOpen && (
              <div 
                className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              ></div>
            )}
            </>
            )}
            <AppErrorBoundary context={{ view: currentView, role }}>
              <main className={`w-full ${isCourseInviteGateActive ? 'min-h-[calc(100vh-104px)]' : inviteLinkCourseId ? 'h-[calc(100vh-104px)] pb-32' : `${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} h-[calc(100vh-104px)] pb-32`} flex-1 overflow-y-auto bg-slate-950`}>
                {currentView === 'student_course_view' ? (
                  selectedCourse ? (
                    loadingClasses ? (
                      <div className="p-6 text-slate-500">Verificando matrícula...</div>
                    ) : !isEnrolledInCourse(selectedCourse) ? (
                      <div className="p-6 md:p-10">
                        <div className="max-w-2xl rounded-xl border border-slate-800 bg-slate-900/60 p-6 shadow-lg">
                          <div className="flex items-center gap-3 text-white font-bold text-xl">
                            <Lock size={22} className="text-slate-300" />
                            Matrícula necessária
                          </div>
                          <p className="mt-3 text-slate-300 leading-relaxed">
                            Você não possui matrícula neste curso. Para acessar o conteúdo, solicite sua matrícula ao administrador.
                          </p>
                          <div className="mt-5">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCourse(null);
                                setCurrentView('student_home');
                              }}
                              className="px-4 py-2 rounded-lg bg-slate-800 text-white font-bold hover:bg-slate-700 transition"
                            >
                              Voltar
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : isCourseAccessBlocked(selectedCourse) ? (
                      <div className="p-6 md:p-10">
                        <div className="max-w-2xl rounded-xl border border-slate-800 bg-slate-900/60 p-6 shadow-lg">
                          <div className="flex items-center gap-3 text-white font-bold text-xl">
                            <Lock size={22} className="text-slate-300" />
                            Curso Inativado
                          </div>
                          <p className="mt-3 text-slate-300 leading-relaxed">
                            Esta matrícula foi inativada pelo Admin. O curso continua aparecendo no seu perfil, mas o acesso para continuar está bloqueado.
                          </p>
                          <div className="mt-5">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCourse(null);
                                setCurrentView('student_home');
                              }}
                              className="px-4 py-2 rounded-lg bg-slate-800 text-white font-bold hover:bg-slate-700 transition"
                            >
                              Voltar
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <StudentCourseViewLazy 
                        course={selectedCourse} 
                        role={role}
                        onInviteLinkCourseChange={handleInviteLinkCourseChange}
                        onInviteGateChange={setIsCourseInviteGateActive}
                        onBack={() => {
                          setSelectedCourse(null);
                          setCurrentView('student_home');
                        }} 
                      />
                    )
                  ) : (
                    <div className="p-6 text-slate-500">Carregando curso...</div>
                  )
                ) : currentView === 'access_denied' ? (
                  <AccessDenied onGoHome={() => setCurrentView('student_home')} />
                ) : currentView === 'student_courses' ? (
                  loadingCourses ? (
                    <div className="flex items-center justify-center min-h-[50vh] text-slate-500">
                      <div className="flex flex-col items-center gap-4">
                        <Settings size={48} className="animate-spin-slow opacity-20" />
                        <p className="animate-pulse">Carregando cursos...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 md:px-12 py-6 md:py-10">
                    <div className="flex items-center gap-3 mb-4 md:mb-6">
                      <div className="w-1 h-6 md:h-8 bg-teal-400 rounded-full"></div>
                      <h2 className="text-lg md:text-2xl font-bold text-white">Meus Cursos</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                      {courses
                        .filter((c) => ((typeof myEnrolledCourseIds !== 'undefined' && myEnrolledCourseIds.has(String(c.id))) || c.is_enrolled === true))
                        .map((course) => {
                          const enrollmentStatus = getCourseEnrollmentStatus(course);
                          const courseAccessBlocked = isCourseAccessBlocked(course);
                          const isActiveEnrollment = enrollmentStatus === 'Ativa';
                          const isExpiredEnrollment = enrollmentStatus === 'Expirada';
                          const expirationLabel = formatCourseEnrollmentExpiresAtShort(course);
                          const instructorName = resolveCoursePersonName(course, 'instructor');
                          const coordinatorName = resolveCoursePersonName(course, 'coordinator');
                          const blockedBadgeClass = isExpiredEnrollment
                            ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                            : 'bg-slate-900/90 text-slate-200 border-slate-700';
                          const blockedBadgeLabel = isExpiredEnrollment ? 'EXPIRADO' : 'INATIVADO';
                          const blockedMessage = isExpiredEnrollment
                            ? `Sua matrícula expirou${expirationLabel ? ` em ${expirationLabel}` : ''}. Sendo assim, não é possível iniciar ou retomar seu curso`
                            : courseAccessBlocked
                              ? 'Matrícula inativada: o curso continua visível, mas o acesso para continuar está bloqueado.'
                              : '';
                          const activeWarningMessage = isActiveEnrollment && expirationLabel
                            ? `Atenção: sua matrícula expira em ${expirationLabel}. Conclua seu curso no prazo acordado com sua empresa.`
                            : '';
                          return (
                          <div
                            key={course.id}
                            className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-lg hover:border-slate-700 transition group flex flex-col"
                          >
                            <div className="h-56 relative shrink-0">
                              <CourseWorkloadBadge workload={course.workload_hours || course.workload} />
                              <img
                                src={course.thumb}
                                alt={course.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                              />
                              <div className="absolute top-2 right-2 flex gap-2">
                                {courseAccessBlocked && (
                                  <span className={`text-[10px] font-bold px-2 py-1 rounded shadow-sm backdrop-blur-sm border ${blockedBadgeClass}`}>
                                    {blockedBadgeLabel}
                                  </span>
                                )}
                                {course.status === 'draft' && (
                                  <span className="bg-amber-500/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm backdrop-blur-sm">
                                    RASCUNHO
                                  </span>
                                )}
                              </div>
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (courseAccessBlocked) return;
                                    setSelectedCourse(course);
                                    setCurrentView('student_course_view');
                                  }}
                                  disabled={courseAccessBlocked}
                                  className={`bg-orange-500/80 backdrop-blur-sm p-2 rounded-full text-white transition shadow-lg ${courseAccessBlocked ? 'bg-slate-700/80 cursor-not-allowed opacity-70' : 'hover:bg-orange-500'}`}
                                  title={courseAccessBlocked ? (isExpiredEnrollment ? 'Matrícula expirada' : 'Curso inativado') : 'Visão Aluno'}
                                >
                                  {courseAccessBlocked ? <Lock size={20} /> : <Eye size={20} />}
                                </button>
                              </div>
                            </div>
                            <div className="p-5 flex flex-col flex-1">
                              <div className="mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                  {course.categories && course.categories.length > 0
                                    ? course.categories.map(catId => categories.find(c => c.id === catId)?.name).filter(Boolean).join(', ')
                                    : categories.find((c) => c.id === (course.category_id || course.category))?.name ||
                                      course.category ||
                                      'Geral'}
                                </span>
                              </div>
                              <h3 className="text-lg font-bold text-white mb-1">{course.name || course.title}</h3>
                              <p className="text-slate-400 text-sm mb-4 line-clamp-2">{course.description || 'Sem descrição.'}</p>
  
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">
                                <div className="flex items-center gap-1" title="Módulos">
                                  <List size={12} className="text-orange-500" />
                                  <span>{course.modules?.length || 0}</span>
                                </div>
                                <div className="flex items-center gap-1" title="Aulas">
                                  <Video size={12} className="text-orange-500" />
                                  <span>
                                    {(course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0) +
                                      (course.standaloneLessons?.length || 0)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1" title="Arquivos">
                                  <FileText size={12} className="text-orange-500" />
                                  <span>
                                    {(course.modules?.reduce(
                                      (acc, m) =>
                                        acc +
                                        (m.lessons?.reduce(
                                          (lAcc, l) =>
                                            lAcc +
                                            (Array.isArray(l.contents)
                                              ? l.contents.filter(
                                                  (c) =>
                                                    c.type !== 'quiz' &&
                                                    c.type !== 'video' &&
                                                    c.type !== 'vimeo' &&
                                                    c.type !== 'youtube' &&
                                                    !c.url?.includes('youtube.com') &&
                                                    !c.url?.includes('vimeo.com')
                                                ).length
                                              : 0),
                                          0
                                        ) || 0),
                                      0
                                    ) || 0) +
                                      (course.standaloneLessons?.reduce(
                                        (acc, l) =>
                                          acc +
                                          (Array.isArray(l.contents)
                                            ? l.contents.filter(
                                                (c) =>
                                                  c.type !== 'quiz' &&
                                                  c.type !== 'video' &&
                                                  c.type !== 'vimeo' &&
                                                  c.type !== 'youtube' &&
                                                  !c.url?.includes('youtube.com') &&
                                                  !c.url?.includes('vimeo.com')
                                              ).length
                                            : 0),
                                        0
                                      ) || 0)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1" title="Quizes">
                                  <HelpCircle size={12} className="text-orange-500" />
                                  <span>
                                    {(course.modules?.reduce(
                                      (acc, m) =>
                                        acc +
                                        (m.lessons?.reduce(
                                          (lAcc, l) =>
                                            lAcc +
                                            (Array.isArray(l.contents)
                                              ? l.contents.filter((c) => c.type === 'quiz').length
                                              : 0),
                                          0
                                        ) || 0),
                                      0
                                    ) || 0) +
                                      (course.standaloneLessons?.reduce(
                                        (acc, l) =>
                                          acc +
                                          (Array.isArray(l.contents)
                                            ? l.contents.filter((c) => c.type === 'quiz').length
                                            : 0),
                                        0
                                      ) || 0)}
                                  </span>
                                </div>
                                {(course.assessmentEnabled || course.assessment_id) && (
                                  <div className="flex items-center gap-1" title="Prova Final">
                                    <CheckCircle size={12} className="text-orange-500" />
                                    <span>1</span>
                                  </div>
                                )}
                              </div>
  
                              <div className="mt-4 pt-3 border-t border-slate-800/50 flex items-center gap-3 text-xs text-slate-400">
                                <div
                                  className="flex items-center gap-2 min-w-0 flex-1"
                                  title={`Instrutor: ${instructorName}`}
                                >
                                  <User size={14} className="text-orange-500 shrink-0" />
                                  <span className="truncate">{instructorName}</span>
                                </div>
                                <div
                                  className="flex items-center gap-2 min-w-0 flex-1"
                                  title={`Coordenador: ${coordinatorName}`}
                                >
                                  <UserCheck size={14} className="text-orange-500 shrink-0" />
                                  <span className="truncate">{coordinatorName}</span>
                                </div>
                              </div>

                              <div className="mt-5 space-y-1.5">
                                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-slate-500">
                                  <span>Progresso Geral</span>
                                  <span className="text-teal-400">{resolveUnifiedProgress(
                                    getLocalCourseProgress(course, loadCompleted(userId, course.id) || []),
                                    course.progressPercent,
                                    course.db_progress_percent,
                                    course.ui_progress_percent,
                                    course.progress
                                  )}%</span>
                                </div>
                                <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-1000"
                                    style={{ width: `${resolveUnifiedProgress(
                                      getLocalCourseProgress(course, loadCompleted(userId, course.id) || []),
                                      course.progressPercent,
                                      course.db_progress_percent,
                                      course.ui_progress_percent,
                                      course.progress
                                    )}%` }}
                                  />
                                </div>
                              </div>
                              <div className="mt-6 mt-auto pt-4">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (courseAccessBlocked) return;
                                    setSelectedCourse(course);
                                    setCurrentView('student_course_view');
                                  }}
                                  disabled={courseAccessBlocked}
                                  className={`w-full font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${courseAccessBlocked ? 'bg-slate-700 text-slate-300 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20'}`}
                                >
                                  {courseAccessBlocked ? <Lock size={20} /> : <Play size={20} />}
                                  {courseAccessBlocked ? (isExpiredEnrollment ? 'MATRÍCULA EXPIRADA' : 'CURSO INATIVADO') : 'INICIAR / RETOMAR'}
                                </button>
                                {blockedMessage && (
                                  <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${isExpiredEnrollment ? 'border-amber-500/30 bg-amber-500/10 text-amber-200' : 'border-slate-700 bg-slate-800/80 text-slate-300'}`}>
                                    {blockedMessage}
                                  </div>
                                )}
                                {activeWarningMessage && (
                                  <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300">
                                    {activeWarningMessage}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )})}
                      </div>
                    </div>
                  )
                ) : currentView === 'student_enrollments' ? (
                  <StudentEnrollments courses={courses} classes={classes} />
                ) : currentView === 'student_certs' ? (
                  <StudentCertificates partner={currentPartner} />
                ) : (
                  currentView === 'student_home' ? (
                    loadingCourses ? (
                      <div className="flex items-center justify-center min-h-[50vh] text-slate-500">
                        <div className="flex flex-col items-center gap-4">
                          <Settings size={48} className="animate-spin-slow opacity-20" />
                          <p className="animate-pulse">Carregando vitrine...</p>
                        </div>
                      </div>
                    ) : (
                      <AdminDashboard 
                        partner={currentPartner} 
                        courses={courses} 
                        onViewStudent={(course) => { setSelectedCourse(course); setCurrentView('student_course_view'); }}
                        users={users}
                        categories={categories}
                        canViewCourse={isEnrolledInCourse}
                      />
                    )
                  ) : (
                    <div className="p-6 md:p-10">
                      <div className="max-w-2xl rounded-xl border border-slate-800 bg-slate-900/60 p-5">
                        <div className="text-white font-bold text-lg">Tela não encontrada</div>
                        <div className="text-slate-400 mt-2 text-sm">A navegação restaurou um estado inválido. Use os botões abaixo para voltar para uma tela segura.</div>
                        <div className="mt-1 text-xs text-slate-500 font-mono break-words">{String(currentView || '')}</div>
                        <div className="flex flex-wrap gap-2 mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              try { clearRouteState() } catch {}
                              setCurrentView('student_home')
                            }}
                            className="px-4 py-2 rounded-lg bg-slate-800 text-slate-100 font-bold hover:bg-slate-700 transition"
                          >
                            Ir para início
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              try { clearRouteState() } catch {}
                              window.location.href = '/'
                            }}
                            className="px-4 py-2 rounded-lg bg-slate-950 text-slate-200 font-bold hover:bg-slate-900 transition border border-slate-800"
                          >
                            Recarregar na raiz
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </main>
            </AppErrorBoundary>
          </>
        ) : (
          <AppErrorBoundary context={{ view: currentView, role }}>
            <main className="flex-1 min-h-[calc(100vh-104px)] bg-slate-950 pb-24 overflow-x-hidden">
              {currentView === 'access_denied' ? (
                <AccessDenied onGoHome={() => setCurrentView('student_home')} />
              ) : (
                loadingCourses ? (
                  <div className="flex items-center justify-center min-h-[50vh] text-slate-500">
                    <div className="flex flex-col items-center gap-4">
                      <Settings size={48} className="animate-spin-slow opacity-20" />
                      <p className="animate-pulse">Carregando vitrine...</p>
                    </div>
                  </div>
                ) : (
                  <AdminDashboard 
                    partner={currentPartner} 
                    courses={courses} 
                    onViewStudent={(course) => { setSelectedCourse(course); setCurrentView('student_course_view'); }}
                    users={users}
                    categories={categories}
                    canViewCourse={isEnrolledInCourse}
                  />
                )
              )}
            </main>
          </AppErrorBoundary>
        )}
      </div>

      {shouldShowLoggedAreaOverlays && <AdminAIAssistantWidget auth={auth} />}

      {auth?.isAuthenticated && whatsappConfig.enabled && whatsappConfig.number && (
        <button
          type="button"
          onClick={handleOpenWhatsAppSupportModal}
          className="fixed bottom-20 right-4 md:right-6 z-[140] h-14 w-14 rounded-full bg-green-600 hover:bg-green-500 text-white shadow-xl transition flex items-center justify-center"
          title="Falar no WhatsApp"
          aria-label="Falar no WhatsApp"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {auth?.isAuthenticated && showWhatsAppSupportModal && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Suporte via WhatsApp</h3>
              <button
                type="button"
                onClick={() => setShowWhatsAppSupportModal(false)}
                className="text-slate-500 hover:text-white transition"
                aria-label="Fechar modal de suporte WhatsApp"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-slate-300 leading-relaxed">
              Você será direcionado para o WhatsApp da <span className="font-semibold text-white">A2Care</span>,
              desenvolvedora da plataforma, para receber o suporte necessário.
            </p>
            <p className="text-slate-300 leading-relaxed mt-3">
              Ao continuar, seus dados (nome, e-mail e empresa) serão carregados automaticamente na mensagem para o suporte.
            </p>
            <p className="text-slate-300 leading-relaxed mt-3">
              Depois disso, complemente no WhatsApp com a descrição da sua necessidade ou problema, com o máximo de detalhes.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowWhatsAppSupportModal(false)}
                className="px-4 py-2 rounded-lg text-slate-300 hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmWhatsAppSupport}
                className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-bold transition"
              >
                Continuar para o WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {shouldShowUserNotices && (
        <UserNoticesModal
          isOpen={showUserNoticesModal}
          notices={userNotices}
          onClose={() => setShowUserNoticesModal(false)}
          onMarkRead={handleMarkNoticeRead}
        />
      )}

      {shouldShowLoggedAreaOverlays && showUserProfile && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-8 sm:p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-5 border-b border-slate-800 flex-shrink-0">
              <h3 className="text-base sm:text-xl font-bold text-white">Perfil do Usuário</h3>
              <button onClick={() => setShowUserProfile(false)} className="text-slate-400 hover:text-white transition p-1"><X size={24} /></button>
            </div>
            
            <form onSubmit={handleSaveProfile} className="p-3 sm:p-6 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
                
                {/* Coluna Esquerda */}
                <div className="space-y-3 sm:space-y-6">
                  {/* Nome */}
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                    <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">Nome Completo</div>
                    {profileEditing ? (
                      <input 
                        type="text" 
                        className="w-full bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-white focus:border-orange-500 outline-none transition text-xs sm:text-base"
                        value={profileForm.fullName}
                        onChange={e => setProfileForm({...profileForm, fullName: e.target.value})}
                        required
                      />
                    ) : (
                      <div className="text-white text-sm sm:text-lg font-medium">{auth?.profile?.fullName || '-'}</div>
                    )}
                  </div>

                  {/* CPF */}
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                    <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">CPF</div>
                    {profileEditing ? (
                      <input 
                        type="text" 
                        className="w-full bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-white focus:border-orange-500 outline-none transition text-xs sm:text-base"
                        value={profileForm.cpf}
                        onChange={e => setProfileForm({...profileForm, cpf: maskCPF(e.target.value)})}
                        placeholder="000.000.000-00"
                      />
                    ) : (
                      <div className="text-white text-sm sm:text-lg font-medium">{auth?.profile?.cpf || '-'}</div>
                    )}
                  </div>

                  {/* E-mail (Read-only) */}
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                    <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">E-mail</div>
                    <div className="text-white truncate text-sm sm:text-lg" title={auth?.session?.user?.email}>{auth?.session?.user?.email || '-'}</div>
                  </div>
                </div>

                {/* Coluna Direita */}
                <div className="space-y-3 sm:space-y-6">
                  {/* WhatsApp */}
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                    <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">WhatsApp</div>
                    {profileEditing ? (
                      <input 
                        type="text" 
                        className="w-full bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-white focus:border-orange-500 outline-none transition text-xs sm:text-base"
                        value={profileForm.whatsapp}
                        onChange={e => setProfileForm({...profileForm, whatsapp: maskWhatsApp(e.target.value)})}
                        placeholder="(00) 00000-0000"
                      />
                    ) : (
                      <div className="text-white text-sm sm:text-lg font-medium">{auth?.profile?.whatsapp ? maskWhatsApp(auth.profile.whatsapp) : '-'}</div>
                    )}
                  </div>

                  {/* Empresa */}
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                    <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">Empresa</div>
                    {profileEditing && auth.isAdmin ? (
                      <select 
                        className="w-full bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-white focus:border-orange-500 outline-none transition text-xs sm:text-base"
                        value={profileForm.companyId || ''}
                        onChange={e => setProfileForm({...profileForm, companyId: e.target.value})}
                      >
                        <option value="">Nenhuma</option>
                        {companies.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-white text-sm sm:text-lg font-medium">{auth?.profile?.companyName || (companies || []).find(c => String(c.id) === String(auth?.profile?.companyId || ''))?.name || '-'}</div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                    {/* Perfil */}
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                      <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">Perfil</div>
                      {profileEditing && auth.isAdmin ? (
                        <select 
                          className="w-full bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-white focus:border-orange-500 outline-none transition text-xs sm:text-base"
                          value={profileForm.role}
                          onChange={e => setProfileForm({...profileForm, role: e.target.value})}
                        >
                          <option value="Aluno">Aluno</option>
                          <option value="Coordenador">Coordenador</option>
                          <option value="Instrutor">Instrutor</option>
                          <option value="Parceiro SAS">Parceiro SAS</option>
                          <option value="Admin">Admin</option>
                        </select>
                      ) : (
                        <div className="text-white text-xs sm:text-base font-medium">{auth?.profileRole || (auth?.isAdmin ? 'Admin' : 'Usuário')}</div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5 sm:p-4">
                      <div className="text-slate-500 font-bold uppercase text-[9px] sm:text-xs tracking-wider mb-0.5 sm:mb-2">Status</div>
                      {profileEditing && auth.isAdmin ? (
                        <select 
                          className="w-full bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-white focus:border-orange-500 outline-none transition text-xs sm:text-base"
                          value={profileForm.status}
                          onChange={e => setProfileForm({...profileForm, status: e.target.value})}
                        >
                          <option value="Pendente">Pendente</option>
                          <option value="Ativo">Ativo</option>
                          <option value="Inativo">Inativo</option>
                        </select>
                      ) : (
                        <div className="text-white text-xs sm:text-base font-medium">{auth?.profileStatus || 'Ativo'}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4">
                <div className="flex flex-col">
                  <span className="text-[9px] sm:text-xs text-slate-500 uppercase tracking-wider mb-0.5 sm:mb-1">ID do Usuário</span>
                  <span className="font-mono text-[9px] sm:text-xs text-slate-400 break-all">{auth?.session?.user?.id || '-'}</span>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                  {profileEditing ? (
                    <>
                      <button 
                        type="button"
                        onClick={() => setProfileEditing(false)} 
                        className="px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg bg-slate-800 text-slate-300 font-bold hover:bg-slate-700 transition text-xs sm:text-sm"
                        disabled={profileSaving}
                      >
                        Cancelar
                      </button>
                      <button 
                        type="submit" 
                        className="px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-500 transition text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20"
                        disabled={profileSaving}
                      >
                        {profileSaving ? <RefreshCcw size={16} className="animate-spin sm:w-[18px] sm:h-[18px]" /> : <Check size={16} className="sm:w-[18px] sm:h-[18px]" />}
                        Salvar Alterações
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setShowUserProfile(false)} type="button" className="px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg bg-slate-800 text-white font-bold hover:bg-slate-700 text-xs sm:text-sm transition">Fechar</button>
                      {auth.isAdmin && (
                        <button 
                          type="button" 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setProfileEditing(true); }} 
                          className="px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg text-white font-bold transition text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg"
                          style={{ 
                            backgroundColor: currentPartner.color, 
                            boxShadow: `0 4px 12px ${currentPartner.color}40` 
                          }}
                        >
                          <Edit2 size={16} className="sm:w-[18px] sm:h-[18px]" /> Editar Perfil
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      {appNotification && <Notification message={appNotification.message} type={appNotification.type} onClose={() => setAppNotification(null)} />}

      {policyModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl max-h-[75vh] shadow-2xl flex flex-col overflow-hidden mx-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-800 flex-shrink-0">
              <h3 className="text-xl font-bold text-white">Política de Privacidade</h3>
              <button onClick={() => setPolicyModalOpen(false)} className="text-slate-500 hover:text-white transition p-1"><X size={20} /></button>
            </div>
            <div className="px-6 py-4 overflow-y-auto custom-scrollbar flex-1 min-h-0">
              {policyLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-400 gap-3">
                  <RefreshCcw size={20} className="animate-spin" />
                  <span>Carregando...</span>
                </div>
              ) : (
                <div className="text-slate-200 text-sm leading-relaxed prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: policyDoc?.content || '' }} />
              )}
            </div>
            <div className="px-6 py-4 text-xs text-slate-500 border-t border-slate-800 flex-shrink-0 bg-slate-900/50">
              {policyDoc && (
                <span>Atualizado em {formatDate(policyDoc.last_updated)} – Versão {policyDoc.version}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {termsModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl max-h-[75vh] shadow-2xl flex flex-col overflow-hidden mx-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-800 flex-shrink-0">
              <h3 className="text-xl font-bold text-white">Termos de Uso</h3>
              <button onClick={() => setTermsModalOpen(false)} className="text-slate-500 hover:text-white transition p-1"><X size={20} /></button>
            </div>
            <div className="px-6 py-4 overflow-y-auto custom-scrollbar flex-1 min-h-0">
              {termsLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-400 gap-3">
                  <RefreshCcw size={20} className="animate-spin" />
                  <span>Carregando...</span>
                </div>
              ) : (
                <div className="text-slate-200 text-sm leading-relaxed prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: termsDoc?.content || '' }} />
              )}
            </div>
            <div className="px-6 py-4 text-xs text-slate-500 border-t border-slate-800 flex-shrink-0 bg-slate-900/50">
              {termsDoc && (
                <span>Atualizado em {formatDate(termsDoc.last_updated)} – Versão {termsDoc.version}</span>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer partner={currentPartner} role={role} hasSidebar={role !== 'guest' && !isCourseInviteGateActive && !inviteLinkCourseId} isSidebarCollapsed={isSidebarCollapsed} onPrivacyPolicy={openPrivacyPolicy} onTermsOfUse={openTermsOfUse} />
    </div>
  );
}
